import {
  ApiError,
  CsrfError,
  ForbiddenError,
  NotAuthenticatedError,
  NotFoundError,
  ResumeUnavailableError,
} from './errors.js';
import type {
  CreateSupportRequestInput,
  CreateSupportRequestResult,
  ReplySupportRequestInput,
  ReplySupportRequestResult,
  SupportRequestDetail,
  SupportRequestList,
  SupportRequestTypesResponse,
  SupportDefaultReply,
  SupportHandlerList,
  SupportInboxActionResult,
  SupportInboxDetail,
  SupportInboxList,
  SupportInboxMessageInput,
  SupportInboxQuery,
  SupportPriority,
  SupportTarget,
  CreateSupportTypeInput,
  PatchSupportTypeInput,
  SupportTypeAdmin,
  SupportTypesAdminList,
} from './support.js';
import type {
  GetRepairersParams,
  RepairerActivityResponse,
  RepairerDetail,
  RepairerListResponse,
  RepairerParentChange,
  RepairerPatchRequest,
  RepairerPatchResponse,
  RepairerUnderOptionsResponse,
  RepairerIntegrations,
  RepairerIntegrationWriteResponse,
  RepairerChannelsPatchRequest,
  RepairerSecretPutRequest,
  RepairerExchangeTestResponse,
  RepairerAddress,
  RepairerAddressPatchRequest,
  RepairerAddressPatchResponse,
  RepairerReplacementOption,
  RepairerContacts,
  RepairerContactSegment,
  RepairerContactCreateRequest,
  RepairerContactPatchRequest,
  RepairerComments,
  RepairerOption,
  RepairerAttachments,
  RepairerUsers,
  RepairerBovagCheck,
  RepairerWorkMethod,
  RepairerWorkMethodOptions,
  RepairerInvoicing,
  RepairerInvoicingOptions,
  RepairerC2PatchRequest,
  RepairerC2PatchResponse,
  RepairerWorkArea,
  RepairerHistoryRules,
  RepairerHistoryRulesCreateRequest,
  RepairerCreateRequest,
  RepairerCreateResponse,
  RepairerKvkCheckResponse,
  RepairerNewPreview,
} from './repairers.js';
import type {
  BillingRunCheckRequest,
  BillingRunCheckResult,
  BillingRunDetail,
  BillingRunInvoice,
  BillingRunList,
  BillingRunOptions,
  BillingRunReportsAccepted,
  BillingRunStartAccepted,
  BillingRunStartRequest,
} from './billingRuns.js';
import type {
  MessageTemplateChannel,
  MessageTemplateDetail,
  MessageTemplateField,
  MessageTemplateList,
  MessageTemplatePreview,
  MessageTemplatePreviewRequest,
  MessageTemplateWriteRequest,
} from './messageTemplates.js';
import type { MenuSettingChangeRequest, MenuSettings } from './menu.js';
import type {
  InventoryCountCommitRequest,
  InventoryCountPreview,
  InventoryCountPreviewRequest,
  InventoryCountResult,
  InventoryInvoiceCheck,
  InvoiceInventoryRequest,
  OrderReceiptCompleteResult,
  InventoryLocation,
  InventoryLocationList,
  InventoryLocationPatchRequest,
  InventoryWarehouse,
  InventoryWarehouseCreateRequest,
  InventoryWarehouseList,
  InventoryWarehousePatchRequest,
  RepairerInventory,
  RepairerInventoryPutRequest,
  ReleaseReservationRequest,
  ReservationCleanupResult,
  ReservationReleaseResult,
  ReservationResult,
  ReserveOrderListLineRequest,
} from './inventory.js';
import type {
  InternalSupplierAddStockRequest,
  InternalSupplierCreateRequest,
  InternalSupplierImportCommitRequest,
  InternalSupplierImportPreview,
  InternalSupplierImportRequest,
  InternalSupplierImportResult,
  InternalSupplierList,
  InternalSupplierOwnerCandidate,
  InternalSupplierPatchRequest,
  InternalSupplierPool,
  InternalSupplierStockOverview,
  InternalSupplierStockPatchRequest,
  InternalSupplierStockRow,
  InternalSupplierSyncResult,
} from './internalSuppliers.js';
import type {
  DealerSurchargeBulkApply,
  DealerSurchargeBulkPreview,
  DealerSurchargeBulkRequest,
  DealerSurchargeCandidate,
  DealerSurchargeExchangeDetail,
  DealerSurchargeImportMapping,
  DealerSurchargeImportPreview,
  DealerSurchargeImportResult,
  DealerSurchargeInsurerDetail,
  DealerSurchargeOverview,
  DealerSurchargeRateSet,
  DealerSurchargeRateSetWrite,
  DealerSurchargeSetting,
  DealerSurchargeSettings,
  DealerSurchargeSettingWrite,
  DealerSurchargeTemplate,
  DossierSurchargeContext,
  DossierSurchargeInput,
  DossierSurchargePreview,
  DossierSurchargeSavePayload,
  DossierSurchargeSaveResponse,
  DossierSurchargeWithdrawPayload,
  DossierSurchargeWithdrawResponse,
} from './dealerSurcharge.js';
import type { TraceLogComponents, TraceLogDetail, TraceLogList, TraceLogListQuery, TraceLogStatus } from './traceLog.js';
import type {
  PasswordResetAvailability,
  PasswordResetCheckBody,
  PasswordResetCheckResponse,
  PasswordResetCompleteBody,
  PasswordResetRequestBody,
} from './passwordReset.js';
import type {
  ContactVerificationConfirmBody,
  ContactVerificationConfirmResponse,
  ContactVerificationRequestBody,
  ContactVerificationRequestResponse,
  ContactVerificationState,
} from './contactVerification.js';
import type {
  UserAdminActiveWindowRequest,
  UserAdminCompany,
  UserAdminDetail,
  UserAdminList,
  UserAdminListQuery,
  UserAdminPasswordReset,
  UserAdminPatchRequest,
} from './users.js';
import type {
  Partner,
  PartnerApiKeyCreated,
  PartnerCallbackDomains,
  PartnerCallbackEventList,
  PartnerCallbackSecretCreated,
  PartnerCreateRequest,
  PartnerHeadquartersPreview,
  PartnerList,
  PartnerPatchRequest,
  PartnerPendingRequestList,
} from './partners.js';
import type {
  AccessTypeOption,
  ClaimFilterOverview,
  ClaimFilterRoundList,
  ClaimFilterRoundDetail,
  ClaimFilterDossierList,
  ClaimFilterHealth,
  ClaimFilterOfferedCount,
  ClaimFilterRecordList,
  ClaimFilterRecordDetail,
  FacturatieDashboard,
  AddCalculationRowRequestDto,
  AddCalculationRowResponseDto,
  AdditiveGroupsResponseDto,
  CalcProgressResponseDto,
  AddressLookupResult,
  AnnotationGetResponse,
  AnnotationPutRequest,
  AnnotationPutResponse,
  AttachmentDto,
  OdometerReadStatus,
  GreenCardReadStatus,
  AttachmentPutRequest,
  AttachmentPutResponse,
  AttachmentUploadFile,
  AttachmentUploadResponse,
  DossierPhotoUpload,
  PhotoChecklist,
  SigningStatus,
  SigningDocumentsResponse,
  SigningPreviewRequest,
  SignRequest,
  SignResponse,
  DossierUsedParts,
  UsedPartsPatchRequest,
  ChecklistPart,
  ChecklistPartsList,
  ChecklistPartDeleteResult,
  DossierAudit,
  BulkAddCalculationRowsRequestDto,
  BulkAddCalculationRowsResponseDto,
  BulkDeleteCalculationRowsRequestDto,
  BulkDeleteCalculationRowsResponseDto,
  CalculationHistorySnapshotDto,
  PurchasePriceOriginDto,
  CalculationPricelistDto,
  CalculationRowDto,
  GrossPriceRefreshRequestDto,
  GrossPriceRefreshResponseDto,
  DamageCause,
  DebtorSearchResult,
  FlowResponse,
  SetNewPasswordRequest,
  EnrollBeginResponse,
  EnrollVerifyResponse,
  RecoveryRegenerateResponse,
  DamageLookupOption,
  DamagePatchRequest,
  DamageSchemaResponse,
  DossierCreateOption,
  DossierCreateRequest,
  DossierContacts,
  DossierCreateResponse,
  DossierDetail,
  DossierFittersResponse,
  DossierRentalCarResponse,
  DossierRentalCarWriteRequest,
  AanvullendeFactuurOptions,
  DispatchActionAvailability,
  DispatchActionRequest,
  DispatchActionResult,
  ProvisionInvoiceRequest,
  ExtraSalesInvoiceRequest,
  AanvullendeInvoiceCreated,
  DossierRulesCheckGetResult,
  DossierRulesCheckResult,
  DossierListResponse,
  DossierStatusOption,
  // Story fe-be-dossieroverzicht-hersteller-kolom-filter (2026-08-05).
  DossierRepairerOptionsResponse,
  InsurerWorklistResponse,
  InsurerWorklistFilter,
  InsurerDossierListResponse,
  DashboardSummary,
  DashboardWorklists,
  DashboardHqSummary,
  DashboardSystemSummary,
  DashboardRestrictedSummary,
  GetDossiersParams,
  DossierPatchResponse,
  CopyDossierSectionsRequest,
  CopyDossierSectionsResponse,
  ArchiveDossierResponse,
  DossierCreditPreview,
  DossierCreditRequest,
  DossierCreditResult,
  DossierCentralFlagsRequest,
  DossierCentralFlagsResponse,
  ExchangeCloseResult,
  DossierHandling,
  HandlingSendRequest,
  HandlingSendResult,
  HandlingCloseResult,
  HandlingMakeDocumentsRequest,
  HandlingMakeDocumentsResult,
  CopyDossierBody,
  CopyDossierResponse,
  SmsOptions,
  SmsRenderResult,
  SmsSendBody,
  SmsSendResult,
  EntitySchema,
  InsuranceType,
  InsurerLookupResult,
  InsurerSpecification,
  IntakePatchRequest,
  IntakeSchemaResponse,
  EurocodeSearchResponseDto,
  EurocodeSuggestResponseDto,
  ItemSuggestionDto,
  ItemSearchHit,
  ItemDetail,
  ItemWritePayload,
  ItemPriceRowDto,
  ItemPriceWriteRequest,
  ItemSetDto,
  ItemSetComponentDto,
  ItemSetPriceListOptionDto,
  ItemSetCreateRequest,
  ItemSetUpdateRequest,
  ItemSetComponentCreateRequest,
  ItemSetComponentUpdateRequest,
  GlassDefinitionDto,
  GlassDefinitionWritePayload,
  GlassLookups,
  InterchangeablesDto,
  ItemFkLookups,
  ItemTypeListItem,
  ItemTypeWriteRequest,
  ControlReason,
  ControlReasonWriteRequest,
  ControlReasonDeleteResult,
  Supplier,
  SupplierWriteRequest,
  SupplierSchema,
  SupplierDeleteResult,
  RentalCar,
  RentalCarWriteRequest,
  RentalCarDeleteResult,
  CustomDossierType,
  CustomDossierTypeWriteRequest,
  CustomDossierTypeSaveResponse,
  CustomDossierTypeOptions,
  CustomDossierTypeDeleteResult,
  Fitter,
  FittersListResponse,
  FitterWriteRequest,
  FitterStatusValue,
  FitterDeleteResult,
  FitterCreateUserRequest,
  FitterCreateUserResult,
  FitterOnlyRequest,
  FitterOnlyResult,
  FitterCapabilityRefreshResponse,
  FitterAgendaResponse,
  FitterBranding,
  FitterDossierCreateRequest,
  FitterDossierCreateResponse,
  MailAccount,
  MailAccountWriteRequest,
  MailRepairer,
  DeclarationChain,
  DeclarationChainWriteRequest,
  DeclarationBranch,
  DeclarationBranchWriteRequest,
  DeclarationTrait,
  DeclarationTraitWriteRequest,
  DeclarationBranchCode,
  DeclarationCodeWriteRequest,
  DeclarationPartyOption,
  DeclarationChainRecognition,
  DeclarationChainRecognitionWriteRequest,
  DeclarationIntakeOverview,
  DeclarationLinkResult,
  SystemMessage,
  ActiveSystemMessage,
  SystemMessageWriteRequest,
  NewsMessage,
  NewsMessageWriteRequest,
  MailConnectionTestRequest,
  MailConnectionTestResult,
  LinkRelationResponse,
  CreateInsuredRelationRequest,
  CreateInsuredRelationResponse,
  LoginRequest,
  LookupOption,
  OwnItemDto,
  OwnItemsLookupsDto,
  OwnItemsSearchParams,
  BulkAddOwnItemsRequestDto,
  PurchasePricesResult,
  PlaceOnOrderListRequest,
  OrderListResult,
  OrderListDto,
  OrderStatusSegment,
  OrderListSearchDto,
  OrderDetailDto,
  OrderReceiptRequest,
  OrderReceiptResult,
  OrderRetourRequest,
  OrderRetourResult,
  OrderMarkReceivedResult,
  SupplierPriceRequest,
  SupplierPriceResult,
  PurchaseOrderRequest,
  PurchaseOrderRoundResult,
  PurchaseOrderRoundActionResult,
  PartialCalculationRowUpdate,
  PrintReport,
  PrintReportListsResponse,
  MailRecipientsResponse,
  RelationAdministration,
  RelationAdministrationWritePayload,
  MailTemplate,
  MailTemplateRender,
  CrediteerFactuurResult,
  DeliveryConfig,
  DeliveryRule,
  DeliveryRuleWriteRequest,
  DefaultDeliveryConfig,
  DefaultDeliveryRule,
  DefaultDeliveryRuleWriteRequest,
  DefaultDeliverySeedResult,
  PartyMailTemplateWriteRequest,
  InvoiceMailTemplatesResponse,
  InvoiceMailSendRequest,
  InvoiceMailDefaultTemplateResponse,
  SendMailRequest,
  RelationDetail,
  RelationKlantDetail,
  RelationComments,
  RelationWritePayload,
  RelationCreatePayload,
  RelationCreated,
  RelationContacts,
  RelationContactWritePayload,
  RelationContactCreatePayload,
  RelationContactCreated,
  RelationContactType,
  RelationFinancial,
  RelationFinancialWritePayload,
  RelationSales,
  RelationListResponse,
  RelationSearchResult,
  RelationSearchScope,
  PolicySuggestion,
  GetRelationsParams,
  InsurerListResponse,
  GetInsurersParams,
  InsurerDetail,
  InsurerWritePayload,
  InsurerCreatePayload,
  InsurerCreated,
  InsurerDebtorWritePayload,
  InsurerComments,
  InsurerCommentType,
  ChainDetail,
  ChainWritePayload,
  ChainWriteResponse,
  ChainAdditiveGroupsWritePayload,
  ChainAdditiveGroupsResponse,
  ChainReportRow,
  ChainReportWritePayload,
  ChainAttachmentRow,
  ChainAttachmentWritePayload,
  ChainListOption,
  RepairLocationAddress,
  RepairLocationOption,
  StatusTransitionLookupResult,
  StatusTransitionResult,
  HeadquartersReasonSelection,
  HeadquartersReason,
  HeadquartersReasonApplies,
  DossierHeadquartersReason,
  EhboPatchRequest,
  EhboPatchResponse,
  EhboStatusOption,
  EhboInsuranceType,
  EhboLogEntry,
  ClaimFilterRulesResult,
  ClaimFilterRuleScopes,
  ClaimFilterRuleSet,
  ClaimFilterRuleSaveRequest,
  ClaimFilterRuleHistory,
  AlarmCenterOption,
  ClaimFilterRuleRepairers,
  ConfigurationOverview,
  PracticeDossierDto,
  TrainingSafetyResponse,
  TrainingSeedRequest,
  TrainingSeedResponse,
  ServiceTariffsResponse,
  ServiceTariffAdminResponse,
  ServiceTariffCreateRequest,
  DossierRepairerInfo,
  ExchangeNotesThread,
  IntakeCallCenterSnapshot,
  IntakeCreateResponse,
  IntakeDetailsRequest,
  IntakeOutcomeRequest,
  IntakeOutcomeResult,
  IntakeTriageRequest,
  IntakeUpdateResult,
  RepairerMatchResult,
  RepairerSearchResult,
  ExchangeNoteWriteRequest,
  ExchangePrecheckResult,
  ExchangeUpdateCategory,
  ExchangeUpdateResult,
  InvoicePreviewResult,
  DeliveryProposal,
  DeliveryProposalAcceptRequest,
  DeliveryProposalAcceptResult,
  CreateInvoicesResult,
  InvoiceListResult,
  PaymentLink,
  PaymentLinkListResult,
  PaymentLinkRefreshResult,
  UpdateContactsRequest,
  PolicyCheckResponse,
  PolicyPatchRequest,
  OwnCoverageRequest,
  CoverageCheckRequest,
  InsurerExpertiseRequest,
  VoorexpertisePreflightResult,
  VoorexpertiseRequest,
  VoorexpertiseResult,
  PreExpertiseResultRequest,
  PriceListListItem,
  PriceListWriteRequest,
  PriceListTiersResponse,
  PriceListTiersWriteRequest,
  PriceListSupplierPricesResponse,
  PriceListSupplierPricesWriteRequest,
  PriceListSupplierPricePreviewRequest,
  PriceListSupplierPricePreviewResponse,
  VatRateDto,
  ApplyRdwLookupResponse,
  Complaint,
  ComplaintWriteRequest,
  ChassisLookupRequest,
  ChassisLookupResponse,
  VehicleLookupOption,
  VehicleLookupResult,
  VehiclePatchRequest,
  VehicleSchemaResponse,
  WhoAmIResponse,
  SwitchUser,
  ProfileContactResponse,
  ProfileContactDto,
  ProfileBillingResponse,
  ProfileBillingDto,
  ChangePasswordRequest,
  TwoFactorStatusResponse,
  SelfEnrollBeginResponse,
  ProfileCompanyResponse,
  ProfileCompanyUpdateDto,
  ExchangeStatusResponse,
  MollieSettingsResponse,
  MollieSettingsUpdateRequest,
  MollieTestResponse,
  ProfileInvoicesResponse,
  ProfileInvoicePdfResponse,
  KetenInvoicesResponse,
  PlanbordResponse,
  UnscheduledResponse,
  CreateAppointmentRequest,
  UpdateAppointmentRequest,
  AppointmentResponse,
  RepairerOpenDaysResponse,
  PutRepairerOpenDaysRequest,
  FitterWorkingHoursResponse,
  PutFitterWorkingHoursRequest,
  ResourceProfile,
  ResourceProfilesResponse,
  PutResourceProfileRequest,
  PutFitterUserRequest,
  FitterUserResponse,
  WorkOrderType,
  WorkOrderTypesResponse,
  SaveWorkOrderTypeRequest,
  PlanbordMigrationResult,
  DossierIndicatorType,
  DossierIndicator,
  DossierIndicatorCreateRequest,
  DossierIndicatorUpdateRequest,
  RuitIdCreateRequest,
  RuitIdCreateResponse,
  RuitIdPollResponse,
  RuitIdListResponse,
  NotificationListResponse,
  UnreadCountResponse,
  IntakeDetail,
  IntakeAcceptRequest,
  IntakeAcceptResponse,
  IntakeRejectRequest,
  IntakeRejectResponse,
  IntakeListItem,
  IntakeListResponse,
  GetIntakesParams,
  GlobalSearchResponse,
  FacturatieExportStatus,
  FacturatieExportStarted,
  FacturatieExportList,
  GetFacturenParams,
  FacturenListResponse,
  FactuurDetail,
  FactuurJournalLines,
  FactuurMailRequest,
  JournalLedgerEdit,
  LedgersBootstrap,
  LedgerRow,
  ExportSettingsBootstrap,
  ExportSettingsSaveRequest,
  NumberingBootstrap,
  NumberingSaveRequest,
  InvoiceDocumentsMatrix,
  InvoiceDocument,
  InvoiceReportOptions,
  InvoiceTypeOptions,
  AddInvoiceDocumentRequest,
  ReorderInvoiceDocumentsRequest,
  InvoiceDocumentCell,
  VehicleDamageHistory,
  HqEurocodeHistory,
  DocTreeResponse,
  DocDocumentDetail,
  DocGroupCreateRequest,
  DocGroupUpdateRequest,
  DocCategoryCreateRequest,
  DocCategoryUpdateRequest,
  DocDocumentCreateRequest,
  DocDocumentUpdateRequest,
  DocNodeMutationResponse,
  DocAttachmentUploadResponse,
  ReportMenuResponse,
  ReportRoleAssignment,
  ReportMenuOption,
  AssignRoleMenuRequest,
  ReportParamScreen,
  ReportRunValues,
} from './types.js';

// Waarde-import (geen type): de chunk-grens voor `fetchDossierCentralFlags`,
// die de BE-cap `DossiersController.CentralFlagsMaxIds` spiegelt.
import { DOSSIER_CENTRAL_FLAGS_MAX_IDS } from './types.js';

export interface ApiClientOptions {
  /** Absolute base URL (bv. `https://carculate.example.com`) of relatief `/api` voor same-origin via Vite-proxy. */
  baseUrl: string;
  /** CSRF cookie naam — wat de backend zet, default `XSRF-TOKEN`. */
  csrfCookieName?: string;
  /** Header naam waarin het CSRF-token gestuurd wordt, default `X-CSRF-Token`. */
  csrfHeaderName?: string;
  /** Optionele hook die ge-trigerd wordt bij 401 (bv. router.push('/login')). */
  onUnauthenticated?: () => void;
  /**
   * Story `fe-be-monteur-s1-basis` (S021.1, TA-review F-16): optionele hook bij een
   * 403 met `reason: "fitter_only_not_allowed"` — de sessie is (inmiddels)
   * alleen-monteur. De app haalt daarop whoami opnieuw op, zodat de route-guard naar
   * `/monteur` stuurt. NIET bij `fitter_only_field_not_allowed`: dat is een veldfout op
   * een open route.
   */
  onFitterOnlyBlocked?: () => void;
  /**
   * Story `be-fe-contactgegevens-bevestigen` (AC 5/7) — optionele hook bij een 403 met
   * `reason: "contact_verification_required"`: de gebruiker moet eerst zijn eigen e-mailadres bevestigen. De app zet
   * daarop het whoami-veld aan, zodat de poort het scherm toont — centraal, niet per call.
   */
  onContactVerificationRequired?: () => void;
  /**
   * Story `fe-be-monteur-s5c-ingelogd-blijven` (S021.5c) §3.2 — optionele hook die bij een 401
   * EERST probeert de sessie te hervatten met het toestel-token, vóórdat `onUnauthenticated`
   * vuurt. De app beslist zelf of dat mag (alleen op `/monteur…`, nooit op een inlogscherm, niet
   * na uitloggen) en deelt één poging per 401-golf. Uitkomst:
   * - `resumed` → het verzoek wordt ÉÉN keer herhaald, met een vers gelezen CSRF-token (B-T4);
   * - `unavailable` → `ResumeUnavailableError`, en `onUnauthenticated` vuurt NIET (geen inlogscherm);
   * - `login-required` / `skipped` → het bestaande gedrag.
   * Nooit voor 401's van de inlog-routes zelf ({@link isResumeExcludedPath}).
   */
  resumeSession?: (info: UnauthenticatedInfo) => Promise<ResumeAttemptOutcome>;
}

/** Waar een 401 vandaan kwam (S021.5c). */
export interface UnauthenticatedInfo {
  path: string;
  method: string;
}

/** Uitkomst van een hervat-poging (S021.5c §3.2). */
export type ResumeAttemptOutcome = 'resumed' | 'login-required' | 'unavailable' | 'skipped';

/**
 * S021.5c review A-12 — een 401 van deze routes start NOOIT een hervatting: een fout wachtwoord
 * (`flow/login`) zou anders stil inloggen als de eigenaar van het toestel-token, een 401 van
 * `resume` zelf zou een lus geven, en een 401 op `logout` mag de monteur niet weer binnenlaten.
 */
export function isResumeExcludedPath(path: string): boolean {
  const p = (path.split('?')[0] ?? '').toLowerCase();
  return p.startsWith('/api/auth/flow/') || p === '/api/auth/login' || p === '/api/auth/logout';
}

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Story U (PB-U19, R-U11) — de client-timeout van `POST /api/dossiers/{id}/handling/send`: 180 s. Eén send kan
 * tot twee SOAP-aanroepen naar uBench doen (recheck + declaratie, standaard 60 s elk).
 */
export const HANDLING_SEND_TIMEOUT_MS = 180_000;

/**
 * KB-06 — leest `X-Calculation-Skipped-Items` (komma-gescheiden id's). Leeg/afwezig ⇒ `[]`.
 */
export function parseSkippedItemsHeader(value: string | null | undefined): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const c of cookies) {
    const [k, ...v] = c.trim().split('=');
    if (k === name) return decodeURIComponent(v.join('='));
  }
  return null;
}

/**
 * Dunne fetch-wrapper. Verwacht een `AppServices`-backend die:
 * - HttpOnly sessie-cookie zet bij login
 * - `XSRF-TOKEN`-cookie zet (niet HttpOnly) zodat JS hem kan lezen
 * - alle mutaties (POST/PUT/PATCH/DELETE) `X-CSRF-Token`-header valideert
 * - HTTP 401 stuurt bij ongeldige/verlopen sessie
 *
 * Voor dev gaat dit via Vite-proxy naar `http://localhost:62361/api` zodat
 * cookies same-origin werken zonder CORS-rituelen.
 */
export class ApiClient {
  private readonly baseUrl: string;
  private readonly csrfCookieName: string;
  private readonly csrfHeaderName: string;
  private readonly onUnauthenticated: (() => void) | undefined;
  private readonly onFitterOnlyBlocked: (() => void) | undefined;
  private readonly onContactVerificationRequired: (() => void) | undefined;
  private readonly resumeSession: ((info: UnauthenticatedInfo) => Promise<ResumeAttemptOutcome>) | undefined;

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, '');
    this.csrfCookieName = options.csrfCookieName ?? 'XSRF-TOKEN';
    this.csrfHeaderName = options.csrfHeaderName ?? 'X-CSRF-Token';
    this.onUnauthenticated = options.onUnauthenticated;
    this.onFitterOnlyBlocked = options.onFitterOnlyBlocked;
    this.onContactVerificationRequired = options.onContactVerificationRequired;
    this.resumeSession = options.resumeSession;
  }

  private async request<T>(
    method: string,
    path: string,
    init: {
      body?: unknown;
      /**
       * Story `bijlagen-tab-v1` (2026-05-30): wanneer de body een `FormData`
       * is, sturen we 'm direct mee zonder JSON.stringify en zonder
       * Content-Type-header (de browser zet de multipart-boundary zelf).
       * Gezet via een aparte `formData`-shortcut zodat consumers expliciet
       * blijven.
       */
      formData?: FormData;
      /**
       * Query-params. Een `string[]`-waarde wordt als **herhaalde** param
       * geserialiseerd (`?k=a&k=b`) — nodig voor multi-value-filters waarvan de
       * waarden komma's/spaties kunnen bevatten (story
       * `fe-be-dossier-indicator-filter`: `indicatorCodes`). Een scalair gebruikt
       * `set` (één waarde); een array gebruikt `append` per entry.
       */
      query?: Record<string, string | number | boolean | string[] | undefined>;
      /**
       * Optionele AbortSignal — gebruikt door autocomplete-hooks (S009.5) om
       * lopende requests te annuleren bij volgende keystroke. Bestaande
       * callers zonder signal-arg gedragen zich onveranderd.
       */
      signal?: AbortSignal;
      /**
       * Story `calculatie-inline-edit-en-delete` AC 3.4 force-commit-pad —
       * `keepalive: true` zodat een fetch tijdens `beforeunload` niet door
       * de browser wordt afgebroken. Alleen ingezet door
       * `deleteCalculationRow` op het force-commit-tab-close-pad.
       */
      keepalive?: boolean;
      /**
       * Story `documenten-printen-mailen` (Iter C): wanneer `'blob'` geeft de
       * method bij een 2xx de ruwe `Blob` terug (binaire PDF) i.p.v. JSON te
       * parsen. Foutresponses (4xx/5xx) blijven JSON/text → ApiError, zodat
       * `{ reason }`-bodies gewoon doorkomen.
       */
      responseType?: 'blob' | 'raw';
      /**
       * Extra request-headers (additief, gemerged ná de defaults + CSRF).
       * Gebruikt o.a. door `addCalculationRow` voor de optionele
       * `X-Calc-Progress-Id`-voortgang-header. Bestaande callers zonder
       * `headers` gedragen zich onveranderd.
       */
      headers?: Record<string, string>;
      /**
       * S021.5c — geen auth-hooks bij een 401: geen hervatting en geen `onUnauthenticated`.
       * Voor de hervat-routes zelf en de stille whoami-controle van de hervat-coördinator; die
       * beslissen zelf wat een 401 betekent.
       */
      skipAuthHooks?: boolean;
      /** S021.5c B-T4 — intern: dit is al de herhaling na een geslaagde hervatting. */
      isResumeRetry?: boolean;
    } = {},
  ): Promise<T> {
    const url = new URL(`${this.baseUrl}${path}`, window.location.origin);
    if (init.query) {
      for (const [k, v] of Object.entries(init.query)) {
        if (v === undefined) continue;
        if (Array.isArray(v)) {
          // Herhaalde param: één entry per waarde (ondubbelzinnig bij komma's
          // in de waarde, story `fe-be-dossier-indicator-filter`).
          for (const item of v) url.searchParams.append(k, item);
        } else {
          url.searchParams.set(k, String(v));
        }
      }
    }

    const headers: Record<string, string> = {
      Accept: 'application/json',
    };
    // Bewust GEEN Content-Type bij FormData — de browser zet 'm met de
    // multipart-boundary die hij intern genereert. Een eigen waarde zou
    // de parse-pad server-side breken.
    if (init.body !== undefined && !init.formData) {
      headers['Content-Type'] = 'application/json';
    }

    if (MUTATING_METHODS.has(method)) {
      // Stuur CSRF-header alleen als de cookie bestaat. Login-call (eerste
      // request, nog geen sessie) heeft 'm nog niet — server-side enforcement
      // via [CsrfToken]-attribuut bepaalt welke endpoints 'm echt vereisen.
      const csrf = readCookie(this.csrfCookieName);
      if (csrf) headers[this.csrfHeaderName] = csrf;
    }

    // Caller-headers als laatste (additief). Bewust ná CSRF zodat een
    // toekomstige caller de CSRF-header niet per ongeluk overschrijft tenzij
    // expliciet bedoeld.
    if (init.headers) {
      for (const [k, v] of Object.entries(init.headers)) headers[k] = v;
    }

    const res = await fetch(url.toString(), {
      method,
      credentials: 'include',
      headers,
      body: init.formData
        ? init.formData
        : init.body !== undefined
          ? JSON.stringify(init.body)
          : undefined,
      // Signal-doorgifte: zonder signal blijft het oude gedrag (geen abort);
      // met signal kan de hook lopende requests cancellen (M-eur.03).
      signal: init.signal,
      // keepalive: alleen ingezet voor force-commit-DELETE op `beforeunload`
      // zodat de browser de fetch niet afbreekt bij page-unload (AC 3.4).
      keepalive: init.keepalive,
    });

    if (res.status === 204) return undefined as T;

    // Foutpaden eerst: altijd JSON/text parsen zodat `{ reason }`-bodies in de
    // error doorkomen, ongeacht het gevraagde responseType.
    if (!res.ok) {
      const ct = res.headers.get('content-type') ?? '';
      const payload: unknown = ct.includes('application/json') ? await res.json() : await res.text();
      if (res.status === 401) {
        if (init.skipAuthHooks) throw new NotAuthenticatedError(payload);
        // S021.5c §3.2: eerst (één keer per golf) hervatten met het toestel-token; de app
        // bepaalt of dat hier mag. Gelukt → dit verzoek één keer opnieuw (B-T4): een 401 van
        // [SessionAuth] betekent dat de actie nooit draaide, en `request` leest de CSRF-cookie
        // opnieuw, dus de herhaling draagt het verse token van de nieuwe sessie.
        if (this.resumeSession && !init.isResumeRetry && !isResumeExcludedPath(path)) {
          let outcome: ResumeAttemptOutcome = 'skipped';
          try {
            outcome = await this.resumeSession({ path, method });
          } catch {
            outcome = 'unavailable';
          }
          if (outcome === 'resumed') return this.request<T>(method, path, { ...init, isResumeRetry: true });
          if (outcome === 'unavailable') throw new ResumeUnavailableError(payload);
        }
        this.onUnauthenticated?.();
        throw new NotAuthenticatedError(payload);
      }
      if (res.status === 403) {
        // S021.1 (F-16): het alleen-monteur-slot is dicht voor deze call. Laat de app
        // whoami verversen; de route-guard stuurt dan naar /monteur.
        if (
          payload !== null &&
          typeof payload === 'object' &&
          (payload as { reason?: unknown }).reason === 'fitter_only_not_allowed'
        ) {
          this.onFitterOnlyBlocked?.();
        }
        // be-fe-contactgegevens-bevestigen (AC 5/7): de stap "contactgegevens bevestigen" staat open. De app toont
        // dan het scherm; een andere call hoeft dit niet zelf af te handelen.
        if (
          payload !== null &&
          typeof payload === 'object' &&
          (payload as { reason?: unknown }).reason === 'contact_verification_required'
        ) {
          this.onContactVerificationRequired?.();
        }
        throw new ForbiddenError(payload);
      }
      if (res.status === 404) throw new NotFoundError(payload);
      throw new ApiError(res.status, res.statusText, payload);
    }

    if (init.responseType === 'blob') return (await res.blob()) as T;
    // 'raw': geef het rauwe Response-object terug zodat de caller zelf de
    // content-type kan inspecteren (bytes-download vs. JSON-link — rapport-run).
    if (init.responseType === 'raw') return res as unknown as T;

    const ct = res.headers.get('content-type') ?? '';
    const payload: unknown = ct.includes('application/json') ? await res.json() : await res.text();
    return payload as T;
  }

  // ─── Generieke escape hatch voor feature-eigen wire-lagen ───────────────

  /**
   * Generieke JSON-POST voor een **feature-eigen wire-laag** die wél de
   * credentials-/CSRF-plumbing van deze client moet gebruiken maar z'n
   * verzoek-/antwoordcontract elders bewaart.
   *
   * Bestaat voor story `fe-be-importraamwerk-fase2-controleren` (S2-AC6b +
   * S2-AC12a): de wire-laag van het import-controle-endpoint moet onder
   * `apps/web/src/features/import/framework/` staan — dáár loopt de
   * structurele borg (SCAN D) die afdwingt dat de backend geen uitkomst- of
   * actie-naam op de lijn zet. Zou dat contract hier landen, dan scant SCAN D
   * nul bestanden en is de belangrijkste keuze van die slice groen zonder
   * bewaakt te zijn.
   *
   * ⚠ Deze methode noemt met opzet **geen** route: het pad komt van de
   * aanroeper. Een `endpoint`-specifieke methode hier zou de scan-borg juist
   * omzeilen. Gebruik voor nieuwe, gewone endpoints een eigen benoemde methode
   * in deze klasse — dit is geen algemene voorkeursweg.
   */
  postJson<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>('POST', path, { body });
  }

  /**
   * Generieke JSON-GET, de tegenhanger van {@link postJson} en met **exact
   * dezelfde beperking**: hij is er voor een feature-eigen wire-laag die de
   * credentials-plumbing van deze client moet gebruiken maar z'n contract
   * elders bewaart.
   *
   * Bestaat voor story `fe-be-importraamwerk-relaties` (S4): de
   * voortgangs-route van de relatie-import
   * (`GET /api/relation-import/progress/{runId}`) hoort bij dezelfde wire-laag
   * als het controle-endpoint, en die laag moet onder
   * `apps/web/src/features/import/framework/` blijven staan — dáár loopt de
   * structurele borg die afdwingt dat de backend geen uitkomst- of actie-naam
   * op de lijn zet. Zou het pad hier landen, dan scant die borg nul bestanden.
   *
   * ⚠ Deze methode noemt met opzet **geen** route: het pad komt van de
   * aanroeper. Net als `postJson` is dit **geen algemene voorkeursweg** —
   * gebruik voor een gewoon endpoint een eigen benoemde methode in deze klasse.
   * De aanroepers-lijst van beide methodes is met een scan op **exacte
   * gelijkheid** gepind, zodat een derde aanroeper een bewuste keuze is.
   */
  getJson<T>(path: string): Promise<T> {
    return this.request<T>('GET', path);
  }

  // ─── Auth ──────────────────────────────────────────────────────────────

  login(req: LoginRequest): Promise<WhoAmIResponse> {
    return this.request('POST', '/api/auth/login', { body: req });
  }

  // ─── Epic veilige-authenticatie — multi-step login-flow ──────────────────
  // Stap 1: wachtwoord. Bij `authenticated` zit de identity in de respons +
  // is de sessie-cookie gezet; anders een status + stepToken voor de vervolgstap.

  flowLogin(req: LoginRequest): Promise<FlowResponse> {
    return this.request('POST', '/api/auth/flow/login', { body: req });
  }

  flowSetNewPassword(req: SetNewPasswordRequest): Promise<FlowResponse> {
    return this.request('POST', '/api/auth/flow/set-new-password', { body: req });
  }

  flowEnrollBegin(stepToken: string): Promise<EnrollBeginResponse> {
    return this.request('POST', '/api/auth/flow/2fa/enroll-begin', { body: { stepToken } });
  }

  /**
   * S021.5c §2.2 / F-1: `rememberDevice` gaat mee op elke flow-stap (de server telt hem alleen
   * op de stap die de sessie maakt). Weggelaten (`undefined`) = de sleutel staat niet in de body —
   * zo stuurt het kantoor-inlogscherm hem nooit (AC 15).
   */
  flowEnrollVerify(stepToken: string, code: string, rememberDevice?: boolean): Promise<EnrollVerifyResponse> {
    return this.request('POST', '/api/auth/flow/2fa/enroll-verify', {
      body: rememberDevice === undefined ? { stepToken, code } : { stepToken, code, rememberDevice },
    });
  }

  flow2faVerify(stepToken: string, code: string, trustDevice: boolean, rememberDevice?: boolean): Promise<FlowResponse> {
    return this.request('POST', '/api/auth/flow/2fa/verify', {
      body: rememberDevice === undefined ? { stepToken, code, trustDevice } : { stepToken, code, trustDevice, rememberDevice },
    });
  }

  /**
   * S021.5c §2.3 — POST /api/auth/flow/resume: met het toestel-token (HttpOnly-cookie
   * `CARC.PL`, alleen op `/api/auth/`) een nieuwe sessie. Zonder body. Een 401 hier vuurt GEEN
   * auth-hooks: de hervat-coördinator beslist zelf (anders een lus, review A-12).
   */
  flowResume(): Promise<FlowResponse> {
    return this.request('POST', '/api/auth/flow/resume', { skipAuthHooks: true });
  }

  /**
   * S021.5c review A-11 — POST /api/auth/flow/forget-device: dit toestel afmelden, ook zonder
   * (geldige) sessie. De FE roept dit bij uitloggen ALTIJD aan.
   */
  flowForgetDevice(): Promise<{ forgotten: boolean }> {
    return this.request('POST', '/api/auth/flow/forget-device', { skipAuthHooks: true });
  }

  // ─── Story be-fe-wachtwoord-vergeten — zelf een nieuw wachtwoord kiezen via een link ──
  // Publiek en zonder sessie: een 401 hoort hier niet voor te komen, en als hij toch komt
  // mag hij geen hervatting of doorverwijzing naar het inlogscherm starten (skipAuthHooks).

  /** GET /api/auth/password-reset/availability — altijd 200 `{ available }` (P-9). */
  getPasswordResetAvailability(): Promise<PasswordResetAvailability> {
    return this.request('GET', '/api/auth/password-reset/availability', { skipAuthHooks: true });
  }

  /** POST /api/auth/password-reset/request — altijd dezelfde 200 (AC 4/5); 404 als de functie uit staat. */
  requestPasswordReset(body: PasswordResetRequestBody): Promise<unknown> {
    return this.request('POST', '/api/auth/password-reset/request', { body, skipAuthHooks: true });
  }

  /** POST /api/auth/password-reset/check — het token gaat alleen in de body, nooit in pad of query (P-14). */
  checkPasswordReset(body: PasswordResetCheckBody): Promise<PasswordResetCheckResponse> {
    return this.request('POST', '/api/auth/password-reset/check', { body, skipAuthHooks: true });
  }

  /** POST /api/auth/password-reset/complete — 200 opgeslagen; 400 beleid; 410 `link_invalid`; 503/500 `try_again`. */
  completePasswordReset(body: PasswordResetCompleteBody): Promise<unknown> {
    return this.request('POST', '/api/auth/password-reset/complete', { body, skipAuthHooks: true });
  }

  // ─── Story be-fe-contactgegevens-bevestigen — eigen e-mailadres invullen en bevestigen ──
  // Met sessie (de stap hangt aan de sessie, P-2). Deze drie routes staan op de allowlist van de poort.

  /** GET /api/auth/contact-verification — naam, voorvulling en een lopende code (scherm A). */
  getContactVerification(): Promise<ContactVerificationState> {
    return this.request('GET', '/api/auth/contact-verification');
  }

  /** POST /api/auth/contact-verification/request-code — 200; 400 `email_invalid`/`mobile_invalid`; 409 grenzen. */
  requestContactVerificationCode(body: ContactVerificationRequestBody): Promise<ContactVerificationRequestResponse> {
    return this.request('POST', '/api/auth/contact-verification/request-code', { body });
  }

  /** POST /api/auth/contact-verification/confirm — alleen de code (P-6); 400 `code_wrong`, 410 `code_expired`. */
  confirmContactVerification(body: ContactVerificationConfirmBody): Promise<ContactVerificationConfirmResponse> {
    return this.request('POST', '/api/auth/contact-verification/confirm', { body });
  }

  flowAdminReset2fa(targetUserId: string): Promise<{ reset: boolean }> {
    return this.request('POST', '/api/auth/flow/2fa/admin-reset', { body: { targetUserId } });
  }

  /**
   * Regenereert de recovery-codes van de ingelogde (post-2FA) user (F-4/F-5).
   * Vereist een geldige sessie + CSRF. De nieuwe codes komen eenmalig terug —
   * nooit loggen, niet in de URL.
   */
  flowRegenerateRecoveryCodes(currentPassword?: string): Promise<RecoveryRegenerateResponse> {
    // S021.5c B-T5: vanuit een hervatte sessie eerst het wachtwoord (step-up).
    return this.request('POST', '/api/auth/flow/2fa/recovery/regenerate', {
      body: currentPassword === undefined ? {} : { currentPassword },
    });
  }

  logout(): Promise<void> {
    return this.request('POST', '/api/auth/logout', { body: {} });
  }

  /**
   * GET /api/auth/whoami. Met `{ quiet: true }` (S021.5c) zonder auth-hooks: de
   * hervat-coördinator gebruikt dat om na het wachten op de Web Lock te zien of een ander
   * tabblad al hervat heeft, zonder zelf een nieuwe hervatting te starten.
   */
  whoAmI(options?: { quiet?: boolean }): Promise<WhoAmIResponse> {
    return this.request('GET', '/api/auth/whoami', options?.quiet ? { skipAuthHooks: true } : {});
  }

  // ─── Switch-user / "Login als vestiging" (story fe-be-switch-user, S1) ────

  /**
   * GET /api/auth/switch-users — de toegestane doel-vestigingen voor de echte
   * user. Keten-tak (via `CompanySwitchUsers`) negeert `q`. System-tak (S2) is
   * systeembreed + doorzoekbaar: geef `q` (≥2 tekens) mee om te zoeken; zonder
   * `q` geeft de server een lege lijst terug. 403 wanneer niet switch-gerechtigd.
   */
  getSwitchUsers(q?: string): Promise<SwitchUser[]> {
    const path = q && q.trim().length > 0
      ? `/api/auth/switch-users?q=${encodeURIComponent(q.trim())}`
      : '/api/auth/switch-users';
    return this.request('GET', path);
  }

  /**
   * POST /api/auth/switch — impersonate een vestiging. De sleutel is de
   * `loginName` (globaal uniek; de server resolvet via natural key). De server
   * her-valideert het target tegen de toegestane set (de FE-lijst is nooit de
   * autoriteit) en muteert de sessie in-place op dezelfde `CARC.SID`. Retourneert
   * de nieuwe whoami-payload.
   */
  switchUser(loginName: string): Promise<WhoAmIResponse> {
    return this.request('POST', '/api/auth/switch', { body: { targetLoginName: loginName } });
  }

  /**
   * POST /api/auth/switch-back — herstel naar de echte identiteit. Retourneert de
   * whoami-payload van de echte user (zonder `actingAs`).
   */
  switchBack(): Promise<WhoAmIResponse> {
    return this.request('POST', '/api/auth/switch-back', { body: {} });
  }

  // ─── Mijn gegevens (story fe-mijn-gegevens-scherm) ──────────────────────

  /**
   * GET /api/profile/contact — het eigen `rb_Contact` van de ingelogde user
   * + de aanhef-/taal-lookups. Het doelcontact wordt server-side uit de
   * sessie afgeleid (geen contact-id in de call).
   */
  getProfileContact(): Promise<ProfileContactResponse> {
    return this.request('GET', '/api/profile/contact');
  }

  /**
   * PUT /api/profile/contact — werkt het eigen contact bij (volledige editbare
   * set; één Opslaan per tab). Retourneert het bijgewerkte contact.
   */
  updateProfileContact(body: ProfileContactDto): Promise<ProfileContactDto> {
    return this.request('PUT', '/api/profile/contact', { body });
  }

  /**
   * GET /api/profile/billing — het facturatie-/debiteur-NAW-contact van het
   * bedrijf (ander contact dan het eigen contact). Doelcontact server-side
   * bepaald (debiteur-NAW → fallback `_default`); geen contact-id in de call.
   */
  getProfileBilling(): Promise<ProfileBillingResponse> {
    return this.request('GET', '/api/profile/billing');
  }

  /**
   * PUT /api/profile/billing — werkt het facturatiecontact bij. De server
   * resolvet het doelcontact opnieuw; een client-id wordt niet geaccepteerd.
   */
  updateProfileBilling(body: ProfileBillingDto): Promise<ProfileBillingDto> {
    return this.request('PUT', '/api/profile/billing', { body });
  }

  // ─── Mijn gegevens — beveiliging (S-D, self-service, post-sessie) ────────

  /** POST /api/auth/self/change-password — wijzigt het eigen wachtwoord. */
  changePassword(body: ChangePasswordRequest): Promise<{ changed: boolean }> {
    return this.request('POST', '/api/auth/self/change-password', { body });
  }

  /** GET /api/auth/self/2fa/status — staat 2FA aan + ongebruikte herstelcodes. */
  twoFactorStatus(): Promise<TwoFactorStatusResponse> {
    return this.request('GET', '/api/auth/self/2fa/status');
  }

  /** POST /api/auth/self/2fa/enroll-begin — start (her)enrollment; otpauth-URI terug. */
  twoFactorEnrollBegin(currentPassword?: string): Promise<SelfEnrollBeginResponse> {
    // S021.5c B-T5: vanuit een hervatte sessie eerst het wachtwoord (step-up).
    return this.request('POST', '/api/auth/self/2fa/enroll-begin', {
      body: currentPassword === undefined ? {} : { currentPassword },
    });
  }

  /**
   * POST /api/auth/self/2fa/enroll-verify — verifieert de code, schakelt 2FA in
   * en levert eenmalig nieuwe herstelcodes (zelfde shape als regenerate).
   */
  twoFactorEnrollVerify(code: string): Promise<RecoveryRegenerateResponse> {
    return this.request('POST', '/api/auth/self/2fa/enroll-verify', { body: { code } });
  }

  /** POST /api/auth/self/2fa/disable — schakelt 2FA voor de eigen user uit. */
  twoFactorDisable(currentPassword?: string): Promise<{ disabled: boolean }> {
    // S021.5c B-T5: vanuit een hervatte sessie eerst het wachtwoord (step-up).
    return this.request('POST', '/api/auth/self/2fa/disable', {
      body: currentPassword === undefined ? {} : { currentPassword },
    });
  }

  // ─── Mijn gegevens — bedrijf (S-E) ───────────────────────────────────────

  /** GET /api/profile/company — bedrijf (+ hersteller-config), met edit-gate. */
  getProfileCompany(): Promise<ProfileCompanyResponse> {
    return this.request('GET', '/api/profile/company');
  }

  /** PUT /api/profile/company — werkt bedrijf/hersteller bij (monteur → 403). */
  updateProfileCompany(body: ProfileCompanyUpdateDto): Promise<ProfileCompanyResponse> {
    return this.request('PUT', '/api/profile/company', { body });
  }

  /** GET /api/profile/company/exchange-status — best-effort eXchange-verbindingscheck. */
  profileExchangeStatus(): Promise<ExchangeStatusResponse> {
    return this.request('GET', '/api/profile/company/exchange-status');
  }

  // ─── Mijn gegevens — Betalen (Mollie) (story fe-be-mollie-config-mijn-gegevens) ─

  /** GET /api/profile/mollie-settings — veilige config-state (nooit de key). */
  getMollieSettings(): Promise<MollieSettingsResponse> {
    return this.request('GET', '/api/profile/mollie-settings');
  }

  /** PUT /api/profile/mollie-settings — zet enabled + redirect + optioneel nieuwe key. */
  putMollieSettings(body: MollieSettingsUpdateRequest): Promise<MollieSettingsResponse> {
    return this.request('PUT', '/api/profile/mollie-settings', { body });
  }

  /** POST /api/profile/mollie-settings/test — toetst de OPGESLAGEN key (altijd HTTP 200). */
  testMollieConnection(): Promise<MollieTestResponse> {
    return this.request('POST', '/api/profile/mollie-settings/test');
  }

  /** GET /api/profile/company/invoices — facturen van het bedrijf (Factuur online). */
  getCompanyInvoices(): Promise<ProfileInvoicesResponse> {
    return this.request('GET', '/api/profile/company/invoices');
  }

  /** GET /api/profile/company/invoices/{nr}/pdf — één factuur als base64-PDF. */
  getCompanyInvoicePdf(invoiceNumber: number): Promise<ProfileInvoicePdfResponse> {
    return this.request('GET', `/api/profile/company/invoices/${encodeURIComponent(invoiceNumber)}/pdf`);
  }

  /**
   * GET /api/profile/company/keten-invoices — keten-facturen van het lid (Bron 2,
   * story fe-factuur-online-keten). `applicable:false` → geen keten-tab.
   */
  getKetenInvoices(): Promise<KetenInvoicesResponse> {
    return this.request('GET', '/api/profile/company/keten-invoices');
  }

  /**
   * GET /api/profile/company/keten-invoices/{invoiceId}/pdf — één keten-factuur als
   * base64-PDF. Download-sleutel = `IN_Invoice.ID` (Guid-string), niet het nummer.
   */
  getKetenInvoicePdf(invoiceId: string): Promise<ProfileInvoicePdfResponse> {
    return this.request('GET', `/api/profile/company/keten-invoices/${encodeURIComponent(invoiceId)}/pdf`);
  }

  // ─── Dossiers ──────────────────────────────────────────────────────────

  /**
   * GET /api/dossiers — keyset-gepagineerd list-endpoint voor het dossier-
   * overzicht (story `fe-dossier-infinite-scroll-search`, 2026-06-07).
   *
   * Geeft één batch (`{ items, nextCursor, hasMore }`) terug — GEEN `total`.
   * De caller appendt batches via infinite-scroll met `cursor = nextCursor`
   * tot `hasMore === false` / `nextCursor === null`.
   *
   * Query-param-regels:
   *  - `cursor` opaque string uit de vorige respons; leeg/`undefined`/`null`
   *    → weggelaten (eerste pagina). Nooit `cursor=null` op de wire.
   *  - `q` vrije zoekterm; leeg/`undefined` → weggelaten.
   *  - `sort`/`dir` → exact de BE-allowlist-keys (camelCase) + `asc`/`desc`.
   *    Weggelaten → BE-default `number`/`desc`.
   *  - `pageSize` → meegestuurd indien gezet (FE stuurt 50; BE capt op 100).
   *  - `isExchange` `true` → `?isExchange=true`; `false`/`undefined` → NIET
   *    meegestuurd (BE ziet ze als identiek aan ontbrekend; TA-fix F3) zodat
   *    de URL/cache-key niet onnodig vervuilt.
   *
   * `signal` (optioneel) — laat de caller een lopende fetch annuleren bij een
   * snelle herzoek (laatste-wint, AC 6).
   */
  getDossiers(
    opts?: GetDossiersParams,
    signal?: AbortSignal,
  ): Promise<DossierListResponse> {
    const query: Record<string, string | number | boolean | string[] | undefined> = {};
    // Opaque cursor — alleen meesturen bij een niet-lege string. `null`/leeg
    // = eerste pagina; nooit een rauwe `null`/`""` op de wire (BE zou dat als
    // "geen cursor" parsen, maar we houden de URL schoon).
    if (opts?.cursor) {
      query.cursor = opts.cursor;
    }
    // Zoekterm — leeg/whitespace-only laat de caller weg via een lege string;
    // hier strippen we alleen `undefined`/`""`.
    if (opts?.q) {
      query.q = opts.q;
    }
    if (opts?.sort) {
      query.sort = opts.sort;
    }
    if (opts?.dir) {
      query.dir = opts.dir;
    }
    if (typeof opts?.pageSize === 'number') {
      query.pageSize = opts.pageSize;
    }
    if (opts?.isExchange === true) {
      query.isExchange = 'true';
    }
    // Status-filter (story `fe-dossier-openstaand-filter-frame`) — alleen
    // meesturen als gezet. De FE stuurt bij default-stand expliciet 'open' mee;
    // 'all' dropt de openstaand-clause server-side.
    if (opts?.status) {
      query.status = opts.status;
    }
    // Story fe-dossier-filter-condities-uitbreiding — vier extra condities.
    // Booleans alleen bij `true` op de wire (consistent met isExchange);
    // statusValue alleen wanneer gezet (een numerieke waarde, incl. 0).
    if (opts?.isOffer === true) {
      query.isOffer = 'true';
    }
    if (opts?.fitterReady === true) {
      query.fitterReady = 'true';
    }
    if (opts?.repairToday === true) {
      query.repairToday = 'true';
    }
    // Story fe-be-monteur-s2-mijn-werk (S021.2) — Mijn werk: eigen dossiers (set uit
    // de sessie), morgen, kenteken zoeken en de kaartvelden. Alleen op de wire als ze
    // aan staan / gevuld zijn.
    if (opts?.mine === true) {
      query.mine = 'true';
    }
    if (opts?.repairTomorrow === true) {
      query.repairTomorrow = 'true';
    }
    if (opts?.licenseSearch) {
      query.licenseSearch = opts.licenseSearch;
    }
    if (opts?.view) {
      query.view = opts.view;
    }
    if (typeof opts?.statusValue === 'number') {
      query.statusValue = opts.statusValue;
    }
    // Story fe-be-dossier-indicator-filter — multi-value indicator-type-filter.
    // Elke niet-lege code als aparte `indicatorCodes`-entry (herhaalde param;
    // codes mogen spaties/komma's bevatten → geen CSV). Lege/whitespace-only
    // codes worden gestript; een lege selectie komt NIET op de wire.
    if (opts?.indicatorCodes && opts.indicatorCodes.length > 0) {
      const cleaned = opts.indicatorCodes
        .map((c) => c.trim())
        .filter((c) => c !== '');
      if (cleaned.length > 0) {
        query.indicatorCodes = cleaned;
      }
    }
    // Story dashboard-fase1-hersteller — "In behandeling"-statusset. Alleen
    // meesturen wanneer gezet (enige geldige waarde 'in_progress').
    if (opts?.statusSet) {
      query.statusSet = opts.statusSet;
    }
    // Story fe-be-dossieroverzicht-hersteller-kolom-filter — multi-value
    // hersteller-filter. Elke niet-lege id als APARTE `repairerIds`-entry
    // (herhaalde param, spiegelt `indicatorCodes` — geen CSV). Lege/whitespace-only
    // waarden worden gestript; een lege selectie komt NIET op de wire (no-op).
    if (opts?.repairerIds && opts.repairerIds.length > 0) {
      const cleaned = opts.repairerIds
        .map((id) => id.trim())
        .filter((id) => id !== '');
      if (cleaned.length > 0) {
        query.repairerIds = cleaned;
      }
    }
    // Story fe-be-verzekeraar-gebonden-hersteller-dashboard (EPIC-019 S-D) — de
    // twee doorklik-filters van de verzekeraar-gebonden variant. Beide alleen
    // meesturen wanneer gezet; leeg/whitespace → niet op de wire (no-op), zodat
    // een verzekeraar-gebonden hersteller de filter kan weghalen en z'n oude
    // dossiers bij een andere verzekeraar terugziet (filter, geen poort).
    if (opts?.insuranceCompanyId && opts.insuranceCompanyId.trim() !== '') {
      query.insuranceCompanyId = opts.insuranceCompanyId.trim();
    }
    if (opts?.actionRequired) {
      query.actionRequired = 'true';
    }
    // Story be-fe-dashboard-te-verzenden (story E) — "Te verzenden". Alleen meesturen
    // wanneer gezet: zonder filter blijft de request byte-gelijk aan vóór de story.
    if (opts?.handling === 'to_send') {
      query.handling = opts.handling;
    }
    return this.request('GET', '/api/dossiers', { query, signal });
  }

  /**
   * GET /api/dossiers/repairer-options — de optielijst voor de multi-select
   * hersteller-filter op het dossier-overzicht (story
   * `fe-be-dossieroverzicht-hersteller-kolom-filter`, 2026-08-05).
   *
   * Read-only en stabiel/cachebaar (de herstellerlijst wijzigt zelden) → de caller
   * geeft 'm een lange `staleTime`. **Verplicht LAZY**: pas ophalen wanneer het
   * filter-control wordt geopend, NOOIT bij page-mount — `/api` serialiseert per
   * sessie, dus een eager call zou de tijd-tot-eerste-rij van het overzicht
   * verlengen voor élke geprivilegieerde gebruiker.
   *
   * Alleen `System`/`RepairerHQ` mogen dit endpoint aanroepen; andere rollen krijgen
   * `403 { reason: "forbidden" }` en geen/verlopen sessie een `401`. De caller
   * degradeert dan het control (disabled + inline melding) en laat de lijst intact.
   */
  getDossierRepairerOptions(
    signal?: AbortSignal,
  ): Promise<DossierRepairerOptionsResponse> {
    return this.request('GET', '/api/dossiers/repairer-options', { signal });
  }

  /**
   * GET /api/dossiers/status-options — de gecureerde glas-status-opties voor de
   * status-select-dropdown (story `fe-dossier-filter-condities-uitbreiding`).
   * Read-only, stabiel/cachebaar — de caller geeft 'm typisch een lange
   * `staleTime`. Gededupliceerd op `statusValue`, oplopend gesorteerd.
   */
  getDossierStatusOptions(): Promise<DossierStatusOption[]> {
    return this.request('GET', '/api/dossiers/status-options');
  }

  // ─── Verzekeraar-portaal ─────────────────────────────────────────────────

  /**
   * GET /api/insurer/worklist — de verzekeraar-worklist "Te beoordelen" (story
   * `fe-be-verzekeraar-rol-scope-worklist`, 2026-06-29). Read-only lijst van de
   * dossiers binnen de verzekeraar-scope in status 222/240, gearchiveerde
   * dossiers uitgesloten. Een niet-verzekeraar krijgt een lege lijst (BE
   * fail-closed) — geen lek.
   *
   * `filter` (optioneel) = snelfilter: `'all'` (default; weggelaten op de wire),
   * `'222'`/`'240'`/`'parked'` → `?status=…`.
   */
  getInsurerWorklist(
    filter?: InsurerWorklistFilter,
    signal?: AbortSignal,
  ): Promise<InsurerWorklistResponse> {
    const query: Record<string, string | undefined> = {};
    // 'all' is de default → niet op de wire; alleen een concrete snelfilter.
    if (filter && filter !== 'all') {
      query.status = filter;
    }
    return this.request('GET', '/api/insurer/worklist', { query, signal });
  }

  /**
   * GET /api/insurer/dossiers — het verzekeraar-"Dossiers"-zoekscherm (story
   * `fe-be-verzekeraar-dossiers-zoeken`, 2026-06-30). Read-only lijst van de
   * dossiers binnen de verzekeraar-scope over ALLE statussen + archief. Een
   * niet-verzekeraar (of sessie zonder company) krijgt een lege lijst (BE
   * fail-closed).
   *
   * `term` (optioneel) = zoekterm; numeriek → dossiernummer exact, niet-numeriek
   * → kenteken-prefix. Een term < 2 (getrimde) tekens wordt NIET op de wire gezet
   * (de FE toont dan de TOP-N recent-lijst, geen DB-hit).
   */
  getInsurerDossiers(
    term?: string,
    signal?: AbortSignal,
  ): Promise<InsurerDossierListResponse> {
    const query: Record<string, string | undefined> = {};
    const trimmed = term?.trim();
    if (trimmed && trimmed.length >= 2) {
      query.q = trimmed;
    }
    return this.request('GET', '/api/insurer/dossiers', { query, signal });
  }

  /**
   * GET /api/dashboard/summary — het rol-aware Hersteller-dashboard-aggregaat
   * (story `dashboard-fase1-hersteller`, 2026-06-12). Eén call → KPI-counts +
   * werkrij-previews + omzet-reeks. Degradeert als één eenheid; de FE toont bij
   * een fout één fout-tegel en houdt de rest van de app werkend.
   */
  getDashboardSummary(signal?: AbortSignal): Promise<DashboardSummary> {
    return this.request('GET', '/api/dashboard/summary', { signal });
  }

  /**
   * Story `dashboard-hersteller-werklijsten` (2026-06-20) — de operationele
   * werklijsten (aanbiedingen · zonder reservering · eXchange-openstaand) van
   * het Hersteller-dashboard. Apart van de summary zodat de lijstjes
   * progressief renderen en zelfstandig degraderen.
   */
  getDashboardWorklists(signal?: AbortSignal): Promise<DashboardWorklists> {
    return this.request('GET', '/api/dashboard/worklists', { signal });
  }

  /**
   * GET /api/dashboard/summary — de Hoofdkantoor-tak (story
   * `dashboard-fase2-hoofdkantoor`, 2026-06-13). Zelfde endpoint; de BE kiest
   * de HK-vorm op `session.UserContextType === RepairerHQ`. Eén call →
   * HK-KPI's + HK-werkrijen + HK-grafieken. Aanroepen door de FE wanneer de
   * auth-context `userContextType === 'RepairerHQ'` is.
   */
  getDashboardHqSummary(signal?: AbortSignal): Promise<DashboardHqSummary> {
    return this.request('GET', '/api/dashboard/summary', { signal });
  }

  /**
   * GET /api/dashboard/summary — de System-tak (story
   * `dashboard-fase3-system`, 2026-06-13). Zelfde endpoint; de BE kiest de
   * System-vorm op `session.UserContextType === System`. Eén call →
   * platform-KPI's + signalen + volume + health (systeembreed, alle tenants).
   * Aanroepen door de FE wanneer de auth-context `userContextType === 'System'`
   * is.
   */
  getDashboardSystemSummary(signal?: AbortSignal): Promise<DashboardSystemSummary> {
    return this.request('GET', '/api/dashboard/summary', { signal });
  }

  /**
   * GET /api/dashboard/summary — de **verzekeraar-gebonden hersteller**-tak (story
   * `fe-be-verzekeraar-gebonden-hersteller-dashboard`, EPIC-019 S-D, 2026-08-21).
   *
   * **Bewust HETZELFDE pad**, geen eigen route: de padlijst in
   * `Global.asax.cs` die routes op `SessionStateBehavior.Disabled` zet matcht
   * EXACT, dus een nieuw `~/api/dashboard/*`-pad zou stilzwijgend
   * lock-GEBONDEN zijn en het lock-vrij-traject voor juist deze groep ongedaan
   * maken.
   *
   * De BE kiest de vorm op de sessie-capability
   * (`RestrictedRepairer.Active`) — nooit uit query/body, dus een client kan de
   * scope van een ander niet opvragen. Eén call → de vijf gescoopte tegels + de
   * werkrij "Nog afmaken". De variant roept `GET /api/dashboard/worklists`
   * NIET aan (offers/geen-reserveringen/eXchange-branch spelen voor deze groep
   * niet).
   */
  getDashboardRestrictedSummary(signal?: AbortSignal): Promise<DashboardRestrictedSummary> {
    return this.request('GET', '/api/dashboard/summary', { signal });
  }

  /**
   * GET /api/dossiers/{id}. Gooit `NotFoundError` (404), `ForbiddenError` (403)
   * of `NotAuthenticatedError` (401) zodat de UI per status-code een eigen
   * empty-state kan tonen. De id-parameter wordt niet client-side gevalideerd;
   * server geeft 400 terug voor non-GUID via `{id:guid}`-route-constraint.
   */
  getDossier(id: string): Promise<DossierDetail> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(id)}`);
  }

  // ─── Relatie + bestuurder (story `appservices-relaties-en-bestuurder`) ──

  /**
   * GET /api/dossiers/{id}/contacts — aparte sub-resource met de Relatie-
   * (customer) en Bestuurder- (driver) velden + `genericRelationId`. Apart
   * van de hoofd-GET zodat die z'n single-load-invariant houdt. Endpoint nog
   * te bouwen in AppServices (iter G).
   */
  getDossierContacts(id: string): Promise<DossierContacts> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(id)}/contacts`);
  }

  /**
   * PUT /api/dossiers/{id}/contacts — schrijft de Relatie- (customer) en
   * Bestuurder- (driver) velden. `null` voor een sectie = niet aanraken
   * (verkoop/offerte → driver=null; lease/fleet → customer=null). Bij een
   * gekoppelde relatie schrijft de backend de customer-velden ook naar de
   * `RB_GenericRelation`. Endpoint nog te bouwen in AppServices.
   */
  updateDossierContacts(id: string, req: UpdateContactsRequest): Promise<void> {
    return this.request('PUT', `/api/dossiers/${encodeURIComponent(id)}/contacts`, { body: req });
  }

  /**
   * B2B-12 — GET /api/dossiers/{id}/relation-administration: de administratiegegevens van de
   * gekoppelde klant (relatiebreed, geldt voor alle dossiers van die klant).
   */
  getDossierRelationAdministration(id: string, signal?: AbortSignal): Promise<RelationAdministration> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(id)}/relation-administration`, { signal });
  }

  /**
   * B2B-12 — PUT /api/dossiers/{id}/relation-administration (partiële merge). Geeft de verse
   * stand terug. Schrijft niets in het dossier zelf (geen dossierversie).
   */
  updateDossierRelationAdministration(
    id: string,
    payload: RelationAdministrationWritePayload,
  ): Promise<RelationAdministration> {
    return this.request('PUT', `/api/dossiers/${encodeURIComponent(id)}/relation-administration`, {
      body: payload,
    });
  }

  /** GET /api/lookups/company-types — Persoon/Bedrijf (`RB_CompanyType`). */
  getCompanyTypes(): Promise<LookupOption[]> {
    return this.request('GET', '/api/lookups/company-types');
  }

  /** GET /api/lookups/titles — aanhef-opties (`RB_Title`). */
  getTitles(): Promise<LookupOption[]> {
    return this.request('GET', '/api/lookups/titles');
  }

  /** GET /api/lookups/languages — taal-opties (`GT_Language`, label = ShortDescription). */
  getLanguages(): Promise<LookupOption[]> {
    return this.request('GET', '/api/lookups/languages');
  }

  /** GET /api/lookups/debtor-groups — debiteurengroep-opties (`RB_DebtorGroup`). */
  getDebtorGroups(): Promise<LookupOption[]> {
    return this.request('GET', '/api/lookups/debtor-groups');
  }

  /** GET /api/lookups/debtor-lists — debiteurenlijst-opties (`RB_DebtorList`). */
  getDebtorLists(): Promise<LookupOption[]> {
    return this.request('GET', '/api/lookups/debtor-lists');
  }

  /** GET /api/lookups/currencies — valuta-opties (`UE_Currency`). */
  getCurrencies(): Promise<LookupOption[]> {
    return this.request('GET', '/api/lookups/currencies');
  }

  /** GET /api/lookups/pricelists — ALLE prijslijsten (`UE_PriceList`, geen filter). */
  getPricelists(): Promise<LookupOption[]> {
    return this.request('GET', '/api/lookups/pricelists');
  }

  /**
   * GET /api/relations/search?query= — relatie-zoeken met ÉÉN zoekterm die de
   * BE tegelijk matcht op naam, adres, postcode, plaats en debiteurnummer
   * (migratie 102); een treffer in één van die velden is genoeg. De term gaat
   * ongewijzigd mee — de veldenset staat alleen in de SP.
   *
   * Scope (backlog 32, besluit eigenaar 15-10-2026): standaard alleen de EIGEN
   * klanten van het bedrijf van de sessie — dezelfde set die koppelen accepteert.
   * `'own_and_parent'` neemt ook het bovenliggende bedrijf mee en is uitsluitend
   * voor Inrichting → Eigen dossiersoorten (standaardrelatie).
   */
  searchRelations(
    query: string,
    signal?: AbortSignal,
    scope: RelationSearchScope = 'own',
  ): Promise<RelationSearchResult[]> {
    return this.request('GET', '/api/relations/search', {
      query: scope === 'own_and_parent' ? { query, scope } : { query },
      signal,
    });
  }

  /**
   * GET /api/dossiers/{id}/policy-suggestions?license= — read-only polis-
   * suggestie-lookup op kenteken (story `fe-polis-suggestie-kenteken`). De BE
   * zoekt company-gescoped in `UA_Policy` via `Interaction.PolicyByLicense`
   * (normaliseert het kenteken zelf). Retourneert een (mogelijk lege) array
   * `PolicySuggestion`, gesorteerd op `validTo` aflopend (null-`validTo`
   * onderaan). Spiegelt {@link searchRelations}: GET + query-param +
   * optioneel `AbortSignal` om een lopende lookup te annuleren.
   */
  searchPoliciesForLicense(
    dossierId: string,
    license: string,
    signal?: AbortSignal,
  ): Promise<PolicySuggestion[]> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/policy-suggestions`,
      { query: { license }, signal },
    );
  }

  /**
   * GET /api/relations — gepagineerde, company-gescopede klantenlijst (story
   * `fe-relatiebeheer-fase1-A`, 2026-06-23). Mirror van {@link getDossiers}:
   * geeft één batch (`{ items, nextCursor, hasMore }`) terug — GEEN `total`.
   *
   * Query-param-regels (mirror dossiers):
   *  - `cursor` opaque offset-string uit de vorige `nextCursor`; leeg/`undefined`
   *    → weggelaten (eerste pagina).
   *  - `q` vrije zoekterm; leeg/`undefined` → weggelaten (BE toont dan alle eigen
   *    relaties — de lijst-SP short-circuit NIET op een lege term).
   *  - `activeOnly` "Alleen actieve"-toggle: alleen `false` meesturen (`true` is
   *    de BE-default; weglaten houdt de URL schoon en is identiek aan `true`).
   *  - `sort`/`dir` → BE-allowlist (`name`/`debtorNumber`/`city`) + `asc`/`desc`;
   *    weglaten → BE-default `name`/`asc`.
   *  - `pageSize` → meegestuurd indien gezet (FE stuurt 50; BE capt op 200).
   *
   * `signal` (optioneel) — laat de caller een lopende fetch annuleren.
   */
  listRelations(
    opts?: GetRelationsParams,
    signal?: AbortSignal,
  ): Promise<RelationListResponse> {
    const query: Record<string, string | number | boolean | string[] | undefined> = {};
    if (opts?.cursor) {
      query.cursor = opts.cursor;
    }
    if (opts?.q) {
      query.q = opts.q;
    }
    // `activeOnly` default-AAN: alleen `false` op de wire (mirror isExchange-regel
    // op getDossiers — een ontbrekende param = BE-default `true`).
    if (opts?.activeOnly === false) {
      query.activeOnly = 'false';
    }
    if (opts?.sort) {
      query.sort = opts.sort;
    }
    if (opts?.dir) {
      query.dir = opts.dir;
    }
    if (typeof opts?.pageSize === 'number') {
      query.pageSize = opts.pageSize;
    }
    return this.request('GET', '/api/relations', { query, signal });
  }

  /**
   * GET /api/insurers — gepagineerde verzekeraarslijst (story
   * `fe-relatiebeheer-verzekeraars-fase-a1`). Mirror van {@link listRelations}:
   * geeft één batch (`{ items, nextCursor, hasMore }`) terug, GEEN `total`.
   * Verzekeraars zijn (grotendeels) globale referentiedata → de lijst toont alle
   * actieve verzekeraars, zichtbaar voor elke ingelogde user (F-07).
   *
   * Query-param-regels (mirror dossiers/relaties): `cursor` opaque offset
   * (weglaten = eerste pagina); `q` vrije zoekterm (OR over naam/WamCode/PorCode/
   * debiteurnr.); `activeOnly` alleen `false` meesturen (`true` = BE-default);
   * `type` type-filter; `sort`/`dir` → BE-allowlist (`name` + `asc`/`desc`).
   */
  listInsurers(
    opts?: GetInsurersParams,
    signal?: AbortSignal,
  ): Promise<InsurerListResponse> {
    const query: Record<string, string | number | boolean | string[] | undefined> = {};
    if (opts?.cursor) {
      query.cursor = opts.cursor;
    }
    if (opts?.q) {
      query.q = opts.q;
    }
    if (opts?.activeOnly === false) {
      query.activeOnly = 'false';
    }
    if (opts?.type) {
      query.type = opts.type;
    }
    // Filter-chips "Centrale klant" + "eXchange" (story
    // `fe-verzekeraars-lijst-filter-chips`, 2026-06-25): `true`-only-conventie —
    // alleen bij truthy meesturen (`?centralOnly=true`/`?exchangeOnly=true`).
    // Anders dan `activeOnly` (dat juist `=false` op de wire zet): hier laat een
    // ontbrekende key het filter uit. `false`/`undefined` → key afwezig.
    if (opts?.centralOnly) {
      query.centralOnly = 'true';
    }
    if (opts?.exchangeOnly) {
      query.exchangeOnly = 'true';
    }
    if (opts?.sort) {
      query.sort = opts.sort;
    }
    if (opts?.dir) {
      query.dir = opts.dir;
    }
    if (typeof opts?.pageSize === 'number') {
      query.pageSize = opts.pageSize;
    }
    return this.request('GET', '/api/insurers', { query, signal });
  }

  // ─── Herstellerbeheer (story be-fe-relatiebeheer-herstellers, increment A) ───

  /** Query-params van de herstellerlijst/-export; `activeOnly` alleen bij `false` (BE-default true). */
  private repairerListQuery(
    opts?: GetRepairersParams,
  ): Record<string, string | number | boolean | string[] | undefined> {
    const query: Record<string, string | number | boolean | string[] | undefined> = {};
    if (opts?.search) query.search = opts.search;
    if (opts?.activeOnly === false) query.activeOnly = 'false';
    if (opts?.underCompanyId) query.underCompanyId = opts.underCompanyId;
    if (opts?.sort) query.sort = opts.sort;
    if (opts?.dir) query.dir = opts.dir;
    if (opts?.cursor) query.cursor = opts.cursor;
    if (typeof opts?.pageSize === 'number') query.pageSize = opts.pageSize;
    // Increment B: meerkeuze-filters, komma-gescheiden (BE splitst op ',').
    if (opts?.klantVertellen && opts.klantVertellen.length > 0) query.klantVertellen = opts.klantVertellen.join(',');
    if (opts?.channel && opts.channel.length > 0) query.channel = opts.channel.join(',');
    return query;
  }

  /**
   * GET /api/repairers — herstellerlijst (`{ items, nextCursor, hasMore, total }`). Alleen
   * System en het keten-HK; anders 403/503 (`reason` in `ApiError.body`).
   */
  listRepairers(opts?: GetRepairersParams, signal?: AbortSignal): Promise<RepairerListResponse> {
    return this.request('GET', '/api/repairers', { query: this.repairerListQuery(opts), signal });
  }

  /** GET /api/repairers/export — CSV (UTF-8 met BOM) met dezelfde filters, als `Blob`. */
  exportRepairers(opts?: Omit<GetRepairersParams, 'cursor' | 'pageSize'>): Promise<Blob> {
    return this.request('GET', '/api/repairers/export', {
      query: this.repairerListQuery(opts),
      responseType: 'blob',
    });
  }

  // ─── Facturatie-runs: controle vooraf (story R1 be-fe-facturatie-run-controle) ─────────────

  /**
   * GET /api/facturatie/billing-runs/options — toegangssignaal van het tabblad Runs (200 / 403
   * `not_billing_owner` / 503 `billing_owner_unknown`) én de keuzelijsten. `ownerCompanyId` alleen voor `system`.
   */
  getBillingRunOptions(ownerCompanyId?: string | null, signal?: AbortSignal): Promise<BillingRunOptions> {
    return this.request('GET', '/api/facturatie/billing-runs/options', {
      query: ownerCompanyId ? { ownerCompanyId } : undefined,
      signal,
    });
  }

  /** POST /api/facturatie/billing-runs/check — de controle vooraf (alleen lezen). */
  checkBillingRun(body: BillingRunCheckRequest): Promise<BillingRunCheckResult> {
    return this.request('POST', '/api/facturatie/billing-runs/check', { body });
  }

  /** POST /api/facturatie/billing-runs/check/export — dezelfde controle als CSV (UTF-8 met BOM), als `Blob`. */
  exportBillingRunCheck(body: BillingRunCheckRequest): Promise<Blob> {
    return this.request('POST', '/api/facturatie/billing-runs/check/export', { body, responseType: 'blob' });
  }

  // ─── Facturatie-runs: overzicht en starten (story R2 be-fe-facturatie-runs) ─────────────────

  /** GET /api/facturatie/billing-runs — runs van de eigenaar plus lopende/mislukte start-taken (pagina 1). */
  listBillingRuns(ownerCompanyId: string | null, page = 1, signal?: AbortSignal): Promise<BillingRunList> {
    return this.request('GET', '/api/facturatie/billing-runs', {
      query: { ownerCompanyId: ownerCompanyId ?? undefined, page },
      signal,
    });
  }

  /** GET /api/facturatie/billing-runs/{runId} — kop, vastlegging, voorspeld/werkelijk, status. */
  getBillingRun(runId: string, ownerCompanyId: string | null, signal?: AbortSignal): Promise<BillingRunDetail> {
    return this.request('GET', `/api/facturatie/billing-runs/${encodeURIComponent(runId)}`, {
      query: { ownerCompanyId: ownerCompanyId ?? undefined },
      signal,
    });
  }

  /** GET /api/facturatie/billing-runs/{runId}/invoices — de facturen van de run. */
  getBillingRunInvoices(runId: string, ownerCompanyId: string | null, signal?: AbortSignal): Promise<BillingRunInvoice[]> {
    return this.request('GET', `/api/facturatie/billing-runs/${encodeURIComponent(runId)}/invoices`, {
      query: { ownerCompanyId: ownerCompanyId ?? undefined },
      signal,
    });
  }

  /** Het pad naar de nieuwste PDF van een factuur van de run (voor een `<a href>`). */
  billingRunInvoicePdfUrl(runId: string, invoiceId: string, ownerCompanyId?: string | null): string {
    const base = `/api/facturatie/billing-runs/${encodeURIComponent(runId)}/invoices/${encodeURIComponent(invoiceId)}/pdf`;
    return ownerCompanyId ? `${base}?ownerCompanyId=${encodeURIComponent(ownerCompanyId)}` : base;
  }

  /** POST /api/facturatie/billing-runs — een run starten (202, of 400/409/503 met `reason`). */
  startBillingRun(body: BillingRunStartRequest): Promise<BillingRunStartAccepted> {
    return this.request('POST', '/api/facturatie/billing-runs', { body });
  }

  /** POST /api/facturatie/billing-runs/{runId}/reports — "PDF's opnieuw maken" (optioneel één factuur). */
  requestBillingRunReports(runId: string, invoiceId?: string | null): Promise<BillingRunReportsAccepted> {
    return this.request('POST', `/api/facturatie/billing-runs/${encodeURIComponent(runId)}/reports`, {
      body: invoiceId ? { invoiceId } : {},
    });
  }

  /** GET /api/repairers/under-options — keuzes voor het filter "Onder hoofdkantoor". */
  getRepairerUnderOptions(search?: string, signal?: AbortSignal): Promise<RepairerUnderOptionsResponse> {
    return this.request('GET', '/api/repairers/under-options', {
      query: search ? { search } : undefined,
      signal,
    });
  }

  /** GET /api/repairers/{id} — hersteller-detail (rail, breadcrumbs, tab Algemeen, rechten). */
  getRepairer(id: string, signal?: AbortSignal): Promise<RepairerDetail> {
    return this.request('GET', `/api/repairers/${encodeURIComponent(id)}`, { signal });
  }

  /**
   * PATCH /api/repairers/{id}/general — velden van tab Algemeen (één entiteit per PATCH). Fouten als
   * `ApiError` met `{ reason, field?, rule? }`; 409 `chain_change_confirmation_required` draagt de
   * gevolgen (`leavesChain`, `affectedDescendants`, …).
   */
  patchRepairerGeneral(id: string, body: RepairerPatchRequest): Promise<RepairerPatchResponse> {
    return this.request('PATCH', `/api/repairers/${encodeURIComponent(id)}/general`, { body });
  }

  /** GET /api/repairers/{id}/parent-change-preview — gevolgen van een nieuw bovenliggend bedrijf (System). */
  getRepairerParentChangePreview(id: string, parentCompanyId: string): Promise<RepairerParentChange> {
    return this.request('GET', `/api/repairers/${encodeURIComponent(id)}/parent-change-preview`, {
      query: { parentCompanyId },
    });
  }

  /** GET /api/repairers/{id}/parent-options — kandidaat-bovenliggende bedrijven (System). */
  getRepairerParentOptions(id: string, search?: string): Promise<RepairerUnderOptionsResponse> {
    return this.request('GET', `/api/repairers/${encodeURIComponent(id)}/parent-options`, {
      query: search ? { search } : undefined,
    });
  }

  // ─── Herstellerbeheer, increment D — nieuwe hersteller aanmaken (System) ───

  /**
   * POST /api/repairers — een hersteller aanmaken. 201 (nieuw) of 200 (replay op dezelfde `clientRequestId`). Fouten als
   * `ApiError` met `{ reason, field?, rule?, matches?, entersChain?, chainName? }`.
   */
  createRepairer(body: RepairerCreateRequest): Promise<RepairerCreateResponse> {
    return this.request('POST', '/api/repairers', { body });
  }

  /** GET /api/repairers/kvk-check?kvkNumber= — bestaande herstellers met hetzelfde KvK-nummer (D-5). */
  checkRepairerKvk(kvkNumber: string, signal?: AbortSignal): Promise<RepairerKvkCheckResponse> {
    return this.request('GET', '/api/repairers/kvk-check', { query: { kvkNumber }, signal });
  }

  /** GET /api/repairers/new/parent-options?search= — kandidaat-bovenliggende bedrijven voor een nieuwe hersteller. */
  getNewRepairerParentOptions(search?: string, signal?: AbortSignal): Promise<RepairerUnderOptionsResponse> {
    return this.request('GET', '/api/repairers/new/parent-options', { query: search ? { search } : undefined, signal });
  }

  /** GET /api/repairers/new/preview?parentCompanyId= — keten, Klanten Vertellen en Dispatch onder dit bedrijf. */
  getNewRepairerPreview(parentCompanyId: string, signal?: AbortSignal): Promise<RepairerNewPreview> {
    return this.request('GET', '/api/repairers/new/preview', { query: { parentCompanyId }, signal });
  }

  // ─── Herstellerbeheer, increment B — tab Koppelingen ───

  /** GET /api/repairers/{id}/integrations — koppelingen en toestanden (nooit een geheime waarde). */
  getRepairerIntegrations(id: string, signal?: AbortSignal): Promise<RepairerIntegrations> {
    return this.request('GET', `/api/repairers/${encodeURIComponent(id)}/integrations`, { signal });
  }

  /** PUT /api/repairers/{id}/external-values/{systemCode} — niet-geheime waarde op het eigen bedrijf. */
  putRepairerExternalValue(id: string, systemCode: string, body: { value: string; ownVersion: number | null }): Promise<RepairerIntegrationWriteResponse> {
    return this.request('PUT', `/api/repairers/${encodeURIComponent(id)}/external-values/${encodeURIComponent(systemCode)}`, { body });
  }

  /** DELETE /api/repairers/{id}/external-values/{systemCode}?ownVersion= — eigen regel wissen (weer erven). */
  deleteRepairerExternalValue(id: string, systemCode: string, ownVersion: number): Promise<RepairerIntegrationWriteResponse> {
    return this.request('DELETE', `/api/repairers/${encodeURIComponent(id)}/external-values/${encodeURIComponent(systemCode)}`, {
      query: { ownVersion },
    });
  }

  /**
   * PUT /api/repairers/{id}/secrets/{secretKey} — geheim vervangen. ⚠ De waarde gaat uitsluitend in de body
   * (nooit in de URL); de aanroeper bewaart hem nergens anders dan in de lokale state van de dialoog.
   */
  putRepairerSecret(id: string, secretKey: string, body: RepairerSecretPutRequest): Promise<RepairerIntegrationWriteResponse> {
    return this.request('PUT', `/api/repairers/${encodeURIComponent(id)}/secrets/${encodeURIComponent(secretKey)}`, { body });
  }

  /** DELETE /api/repairers/{id}/secrets/{secretKey} — geheim wissen (kanaal: version; extern: ownVersion). */
  deleteRepairerSecret(id: string, secretKey: string, versions: { version?: number; ownVersion?: number }): Promise<RepairerIntegrationWriteResponse> {
    return this.request('DELETE', `/api/repairers/${encodeURIComponent(id)}/secrets/${encodeURIComponent(secretKey)}`, {
      query: { version: versions.version, ownVersion: versions.ownVersion },
    });
  }

  /** PATCH /api/repairers/{id}/channels — niet-geheime kanaalvelden ({ fields, version }). */
  patchRepairerChannels(id: string, body: RepairerChannelsPatchRequest): Promise<RepairerIntegrationWriteResponse> {
    return this.request('PATCH', `/api/repairers/${encodeURIComponent(id)}/channels`, { body });
  }

  /** POST /api/repairers/{id}/exchange/test-connection — de OPGESLAGEN eXchange-gegevens testen. */
  testRepairerExchangeConnection(id: string): Promise<RepairerExchangeTestResponse> {
    return this.request('POST', `/api/repairers/${encodeURIComponent(id)}/exchange/test-connection`, {});
  }

  // ─── Herstellerbeheer, increment C1 — Adres, Contacten, Opmerkingen & bijlagen, Gebruikers, Bovag ───

  /** GET /api/repairers/{id}/address — tab Adres & bereikbaarheid. */
  getRepairerAddress(id: string, signal?: AbortSignal): Promise<RepairerAddress> {
    return this.request('GET', `/api/repairers/${encodeURIComponent(id)}/address`, { signal });
  }

  /** PATCH /api/repairers/{id}/address — één veld van één entiteit (contact, repairer of instelling). */
  patchRepairerAddress(id: string, body: RepairerAddressPatchRequest): Promise<RepairerAddressPatchResponse> {
    return this.request('PATCH', `/api/repairers/${encodeURIComponent(id)}/address`, { body });
  }

  /** GET /api/repairers/{id}/replacement-options — keuzes *Vervangende hersteller*. */
  getRepairerReplacementOptions(id: string, search?: string): Promise<{ items: RepairerReplacementOption[] }> {
    return this.request('GET', `/api/repairers/${encodeURIComponent(id)}/replacement-options`, {
      query: search ? { search } : undefined,
    });
  }

  /** GET /api/repairers/{id}/contacts?segment=&includeArchived= — contacten per segment + tellers. */
  getRepairerContacts(id: string, segment: RepairerContactSegment, includeArchived: boolean, signal?: AbortSignal): Promise<RepairerContacts> {
    return this.request('GET', `/api/repairers/${encodeURIComponent(id)}/contacts`, {
      query: { segment, includeArchived: includeArchived ? 'true' : 'false' },
      signal,
    });
  }

  /** POST /api/repairers/{id}/contacts — contact toevoegen. */
  addRepairerContact(id: string, body: RepairerContactCreateRequest): Promise<RepairerContacts> {
    return this.request('POST', `/api/repairers/${encodeURIComponent(id)}/contacts`, { body });
  }

  /** PATCH /api/repairers/{id}/contacts/{contactId} — wijzigen, archiveren (`status: "archived"`) of herstellen. */
  patchRepairerContact(id: string, contactId: string, body: RepairerContactPatchRequest): Promise<RepairerContacts> {
    return this.request('PATCH', `/api/repairers/${encodeURIComponent(id)}/contacts/${encodeURIComponent(contactId)}`, { body });
  }

  /** GET /api/repairers/{id}/comments — opmerkingen, nieuwste eerst. */
  getRepairerComments(id: string, signal?: AbortSignal): Promise<RepairerComments> {
    return this.request('GET', `/api/repairers/${encodeURIComponent(id)}/comments`, { signal });
  }

  /** GET /api/repairers/{id}/comment-types — actieve opmerkingtypes. */
  getRepairerCommentTypes(id: string): Promise<RepairerOption[]> {
    return this.request('GET', `/api/repairers/${encodeURIComponent(id)}/comment-types`);
  }

  /** POST /api/repairers/{id}/comments — opmerking toevoegen. */
  addRepairerComment(id: string, body: { text: string; communicationTypeId?: string | null }): Promise<RepairerComments> {
    return this.request('POST', `/api/repairers/${encodeURIComponent(id)}/comments`, { body });
  }

  /** GET /api/repairers/{id}/attachments — bijlagen (zonder data). */
  getRepairerAttachments(id: string, signal?: AbortSignal): Promise<RepairerAttachments> {
    return this.request('GET', `/api/repairers/${encodeURIComponent(id)}/attachments`, { signal });
  }

  /** GET /api/repairers/{id}/attachment-categories — kiesbare categorieën. */
  getRepairerAttachmentCategories(id: string): Promise<RepairerOption[]> {
    return this.request('GET', `/api/repairers/${encodeURIComponent(id)}/attachment-categories`);
  }

  /** POST /api/repairers/{id}/attachments — upload (multipart, één bestand). */
  uploadRepairerAttachment(id: string, file: File, categoryId: string, description?: string | null): Promise<RepairerAttachments> {
    const fd = new FormData();
    fd.append('file', file, file.name);
    fd.append('categoryId', categoryId);
    if (description) fd.append('description', description);
    return this.request('POST', `/api/repairers/${encodeURIComponent(id)}/attachments`, { formData: fd });
  }

  /** DELETE /api/repairers/{id}/attachments/{attachmentId}?version= — bijlage verwijderen. */
  deleteRepairerAttachment(id: string, attachmentId: string, version: number): Promise<RepairerAttachments> {
    return this.request('DELETE', `/api/repairers/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachmentId)}`, {
      query: { version },
    });
  }

  /** URL van de eigen download-route (eigenaarstoets op de server) — voor een `<a href>`. */
  getRepairerAttachmentDataUrl(id: string, attachmentId: string): string {
    return `${this.baseUrl}/api/repairers/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachmentId)}/data`;
  }

  /** GET /api/repairers/{id}/users — gebruikers, alleen lezen. */
  getRepairerUsers(id: string, signal?: AbortSignal): Promise<RepairerUsers> {
    return this.request('GET', `/api/repairers/${encodeURIComponent(id)}/users`, { signal });
  }

  /** POST /api/repairers/{id}/bovag-check — het OPGESLAGEN Bovag-nummer controleren (betaald per aanroep). */
  checkRepairerBovag(id: string): Promise<RepairerBovagCheck> {
    return this.request('POST', `/api/repairers/${encodeURIComponent(id)}/bovag-check`, {});
  }

  // ─── Herstellerbeheer, increment C2 — Werkwijze, Facturatie, Werkgebied ───

  /** GET /api/repairers/{id}/work-method — tab Werkwijze. */
  getRepairerWorkMethod(id: string, signal?: AbortSignal): Promise<RepairerWorkMethod> {
    return this.request('GET', `/api/repairers/${encodeURIComponent(id)}/work-method`, { signal });
  }

  /** PATCH /api/repairers/{id}/work-method — één veld van één entiteit. */
  patchRepairerWorkMethod(id: string, body: RepairerC2PatchRequest): Promise<RepairerC2PatchResponse> {
    return this.request('PATCH', `/api/repairers/${encodeURIComponent(id)}/work-method`, { body });
  }

  /** GET /api/repairers/{id}/work-method-options — keuzelijsten van DEZE hersteller. */
  getRepairerWorkMethodOptions(id: string, signal?: AbortSignal): Promise<RepairerWorkMethodOptions> {
    return this.request('GET', `/api/repairers/${encodeURIComponent(id)}/work-method-options`, { signal });
  }

  /** GET /api/repairers/{id}/invoicing — tab Facturatie. */
  getRepairerInvoicing(id: string, signal?: AbortSignal): Promise<RepairerInvoicing> {
    return this.request('GET', `/api/repairers/${encodeURIComponent(id)}/invoicing`, { signal });
  }

  /** PATCH /api/repairers/{id}/invoicing — één veld van één entiteit (aanzetten: `confirmInvoicingActivation: true`). */
  patchRepairerInvoicing(id: string, body: RepairerC2PatchRequest): Promise<RepairerC2PatchResponse> {
    return this.request('PATCH', `/api/repairers/${encodeURIComponent(id)}/invoicing`, { body });
  }

  /** GET /api/repairers/{id}/invoicing-options — factuurroutines, ketenroutines, rekenmethoden. */
  getRepairerInvoicingOptions(id: string, signal?: AbortSignal): Promise<RepairerInvoicingOptions> {
    return this.request('GET', `/api/repairers/${encodeURIComponent(id)}/invoicing-options`, { signal });
  }

  /** GET /api/repairers/{id}/delivery-rules-from-history — voorvertoning per partij (niets geschreven). */
  getRepairerHistoryRules(id: string, signal?: AbortSignal): Promise<RepairerHistoryRules> {
    return this.request('GET', `/api/repairers/${encodeURIComponent(id)}/delivery-rules-from-history`, { signal });
  }

  /** POST /api/repairers/{id}/delivery-rules-from-history — maakt de regels voor partijen zonder regels (idempotent). */
  createRepairerHistoryRules(id: string, body: RepairerHistoryRulesCreateRequest = {}): Promise<RepairerHistoryRules> {
    return this.request('POST', `/api/repairers/${encodeURIComponent(id)}/delivery-rules-from-history`, { body });
  }

  /**
   * GET /api/repairers/{id}/work-area — het werkgebied (postcodes), alleen lezen. Er is bewust geen PUT: het werkgebied
   * is alleen-lezen voor iedereen (besluit gebruiker 10-10-2026).
   */
  getRepairerWorkArea(id: string, signal?: AbortSignal): Promise<RepairerWorkArea> {
    return this.request('GET', `/api/repairers/${encodeURIComponent(id)}/work-area`, { signal });
  }

  /** GET /api/repairers/{id}/activity — activiteitenlog, nieuwste eerst, pagina's van 200. */
  getRepairerActivity(id: string, cursor?: string, signal?: AbortSignal): Promise<RepairerActivityResponse> {
    return this.request('GET', `/api/repairers/${encodeURIComponent(id)}/activity`, {
      query: cursor ? { cursor } : undefined,
      signal,
    });
  }

  /**
   * GET /api/insurers/{id} — read-only verzekeraar-detail (story
   * `fe-relatiebeheer-verzekeraars-fase-a1`): RB_Company-NAW +
   * UA_InsuranceCompany-instellingen + eigen-hersteller-debiteur +
   * de BE-berekende permissie-vlaggen `canManageInsurer`/`canEditDebtor`.
   * 404 bij onbestaande id (geen cross-tenant-leak).
   */
  getInsurerDetail(insurerId: string, signal?: AbortSignal): Promise<InsurerDetail> {
    return this.request(
      'GET',
      `/api/insurers/${encodeURIComponent(insurerId)}`,
      { signal },
    );
  }

  /**
   * PUT /api/insurers/{id} — werk verzekeraar-velden bij (per-veld auto-save,
   * story `fe-relatiebeheer-verzekeraars-fase-a2`). Partiële merge: stuur alleen
   * de gewijzigde velden. 403 bij niet-`canManageInsurer` of een read-only veld
   * (EuroMotiveCode/Parent → `field_not_editable`); 404 bij onbestaande id.
   */
  updateInsurer(
    insurerId: string,
    payload: InsurerWritePayload,
  ): Promise<import('./types.js').InsurerUpdateResult> {
    return this.request('PUT', `/api/insurers/${encodeURIComponent(insurerId)}`, {
      body: payload,
    });
  }

  /**
   * POST /api/insurers — maak een nieuwe verzekeraar aan (`UA_InsuranceCompanyAdd`).
   * Minimaal `name` + `wamCode` + `porCode`; 400 bij ontbreken, 409 bij dubbele
   * Wam/Por-combinatie. Geeft de nieuwe id terug voor navigatie naar
   * `/verzekeraars/{insurerId}`. 403 bij niet-`canManageInsurer`.
   */
  createInsurer(payload: InsurerCreatePayload): Promise<InsurerCreated> {
    return this.request('POST', '/api/insurers', { body: payload });
  }

  /**
   * PUT /api/insurers/{id}/debtor — werk de debiteur (RB_Debtor) van een
   * hersteller tegen deze verzekeraar bij (story
   * `fe-relatiebeheer-verzekeraars-fase-a2`). Een glashersteller laat
   * `repairerCompanyId` leeg (eigen company); System/HK sturen de gekozen
   * hersteller mee. Partiële merge; 403/404 bij cross-company / niet-gemachtigd.
   */
  updateInsurerDebtor(
    insurerId: string,
    payload: InsurerDebtorWritePayload,
  ): Promise<{ insurerId: string }> {
    return this.request('PUT', `/api/insurers/${encodeURIComponent(insurerId)}/debtor`, {
      body: payload,
    });
  }

  /**
   * GET /api/insurers/{id}/comments — read-only verzekeraar-opmerkingen (bron
   * `GD_Communication`, story `fe-relatiebeheer-verzekeraars-opmerkingen-thread`).
   * Mirror van de relatie-opmerkingen-shape (`{ count, items }`). 404 bij
   * cross-tenant (scope-guard).
   */
  getInsurerComments(insurerId: string, signal?: AbortSignal): Promise<InsurerComments> {
    return this.request(
      'GET',
      `/api/insurers/${encodeURIComponent(insurerId)}/comments`,
      { signal },
    );
  }

  /**
   * POST /api/insurers/{id}/comments — voeg een opmerking toe (via
   * `GD_CommunicationAdd`, `SubjectID = UA_InsuranceCompany.CompanyID`). Story
   * `fe-relatiebeheer-verzekeraars-opmerkingen-thread`. 400 bij lege body; 403
   * zonder `canManageInsurer`; 404 bij cross-tenant.
   */
  addInsurerComment(
    insurerId: string,
    body: string,
    communicationTypeId?: string | null,
  ): Promise<{ id: string | null }> {
    const payload: { body: string; communicationTypeId?: string } = { body };
    if (communicationTypeId) payload.communicationTypeId = communicationTypeId;
    return this.request('POST', `/api/insurers/${encodeURIComponent(insurerId)}/comments`, {
      body: payload,
    });
  }

  /**
   * GET /api/insurers/{id}/comment-types — actieve opmerkingtypes
   * (`GD_CommunicationType`) voor de toevoeg-dialog-dropdown.
   */
  getInsurerCommentTypes(insurerId: string, signal?: AbortSignal): Promise<InsurerCommentType[]> {
    return this.request(
      'GET',
      `/api/insurers/${encodeURIComponent(insurerId)}/comment-types`,
      { signal },
    );
  }

  /**
   * GET /api/insurers/{id}/contacts — contactenlijst van de verzekeraar-company
   * (story `fe-relatiebeheer-verzekeraars-fase-a2`). Hergebruikt de klant-
   * contact-shape (`RelationContacts`); hoofdcontact bovenaan, inactieve
   * gefilterd.
   */
  listInsurerContacts(insurerId: string, signal?: AbortSignal): Promise<RelationContacts> {
    return this.request(
      'GET',
      `/api/insurers/${encodeURIComponent(insurerId)}/contacts`,
      { signal },
    );
  }

  /**
   * POST /api/insurers/{id}/contacts — nieuw contact bij de verzekeraar
   * (`RB_ContactAdd`). Minimaal `contactTypeCode` + `surName`. 403 bij
   * niet-`canManageInsurer`.
   */
  createInsurerContact(
    insurerId: string,
    payload: RelationContactCreatePayload,
  ): Promise<RelationContactCreated> {
    return this.request('POST', `/api/insurers/${encodeURIComponent(insurerId)}/contacts`, {
      body: payload,
    });
  }

  /**
   * PUT /api/insurers/{id}/contacts/{contactId} — contact bijwerken (per-veld
   * auto-save) óf soft-deleten (`status: "inactive"`). 403 bij
   * niet-`canManageInsurer`; 404 bij cross-company contactId.
   */
  updateInsurerContact(
    insurerId: string,
    contactId: string,
    payload: RelationContactWritePayload,
  ): Promise<{ contactId: string }> {
    return this.request(
      'PUT',
      `/api/insurers/${encodeURIComponent(insurerId)}/contacts/${encodeURIComponent(contactId)}`,
      { body: payload },
    );
  }

  /**
   * GET /api/insurers/{id}/chain — keten-/centrale-klant-instellingen
   * (`RB_CompanyCompany`) van een verzekeraar (story
   * `fe-relatiebeheer-verzekeraars-keten-b1`). 403 voor HK/glashersteller
   * (`chain_not_editable`); 404 bij onbestaande id. Bestaat het record nog niet →
   * lege/default-staat (de eerste PUT doet `RB_CompanyCompanyAdd`).
   */
  getChainDetail(insurerId: string, signal?: AbortSignal): Promise<ChainDetail> {
    return this.request('GET', `/api/insurers/${encodeURIComponent(insurerId)}/chain`, { signal });
  }

  /**
   * PUT /api/insurers/{id}/chain — werk keten-velden bij (per-veld auto-save;
   * partiële merge). 403 bij niet-`canManageChain` / EM-code door niet-System;
   * 400 bij onbekende artikelcode of onvolledige charge-koppeling; 409
   * `euromotive_code_required` bij `underConstruction:false` zonder EM-code.
   */
  updateChain(insurerId: string, payload: ChainWritePayload): Promise<ChainWriteResponse> {
    return this.request('PUT', `/api/insurers/${encodeURIComponent(insurerId)}/chain`, {
      body: payload,
    });
  }

  /**
   * PUT /api/insurers/{id}/chain/additive-groups — schrijft de additive-group-
   * selecties voor dit keten-record (B2 "Artikelen & adviesprijzen"). Patroon:
   * delete-all-then-re-add; max 2 ID's; lege array = alles wissen.
   * 400 `too_many_additive_groups` bij > 2; 400 `additive_group_not_found` bij
   * onbekend ID.
   */
  updateChainAdditiveGroups(
    insurerId: string,
    payload: ChainAdditiveGroupsWritePayload,
  ): Promise<ChainAdditiveGroupsResponse> {
    return this.request('PUT', `/api/insurers/${encodeURIComponent(insurerId)}/chain/additive-groups`, {
      body: payload,
    });
  }

  // ─── Keten-fase B3: extra declaratie-rapporten (RB_CompanyCompanyReport) ───

  /**
   * GET /api/insurers/{id}/chain/reports — lijst van extra declaratie-rapporten
   * (gesorteerd op `ordinal`). Geen keten-record → lege array. 403 voor
   * HK/glashersteller (`chain_not_editable`).
   */
  getChainReports(insurerId: string, signal?: AbortSignal): Promise<ChainReportRow[]> {
    return this.request('GET', `/api/insurers/${encodeURIComponent(insurerId)}/chain/reports`, {
      signal,
    });
  }

  /**
   * POST /api/insurers/{id}/chain/reports — voeg een extra declaratie-rapport toe.
   * `reportId` verplicht (400 `report_required`); onbekende FK → 400. `ordinal`
   * wordt server-toegekend.
   */
  addChainReport(insurerId: string, payload: ChainReportWritePayload): Promise<ChainReportRow> {
    return this.request('POST', `/api/insurers/${encodeURIComponent(insurerId)}/chain/reports`, {
      body: payload,
    });
  }

  /** PUT /api/insurers/{id}/chain/reports/{rowId} — bewerk een rapport-regel. */
  updateChainReport(
    insurerId: string,
    rowId: string,
    payload: ChainReportWritePayload,
  ): Promise<ChainReportRow> {
    return this.request(
      'PUT',
      `/api/insurers/${encodeURIComponent(insurerId)}/chain/reports/${encodeURIComponent(rowId)}`,
      { body: payload },
    );
  }

  /** DELETE /api/insurers/{id}/chain/reports/{rowId} — verwijder een rapport-regel. */
  deleteChainReport(insurerId: string, rowId: string): Promise<{ deleted: boolean; id: string }> {
    return this.request(
      'DELETE',
      `/api/insurers/${encodeURIComponent(insurerId)}/chain/reports/${encodeURIComponent(rowId)}`,
    );
  }

  /**
   * GET /api/insurers/{id}/chain/report-options — rapport-keuzelijst voor beide
   * rapport-dropdowns (gefilterd op `IsInsurerDeclarationFormat`).
   */
  getChainReportOptions(insurerId: string): Promise<ChainListOption[]> {
    return this.request(
      'GET',
      `/api/insurers/${encodeURIComponent(insurerId)}/chain/report-options`,
    );
  }

  // ─── Keten-fase B3: dossier-bijlagen (RB_CompanyCompanyAttachment) ─────────

  /**
   * GET /api/insurers/{id}/chain/attachments — lijst van dossier-bijlage-regels.
   * Geen keten-record → lege array. 403 voor HK/glashersteller.
   */
  getChainAttachments(insurerId: string, signal?: AbortSignal): Promise<ChainAttachmentRow[]> {
    return this.request('GET', `/api/insurers/${encodeURIComponent(insurerId)}/chain/attachments`, {
      signal,
    });
  }

  /**
   * POST /api/insurers/{id}/chain/attachments — voeg een bijlage-regel toe.
   * `attachmentCategoryId` verplicht (400 `attachment_category_required`); dubbele
   * categorie → 400 `attachment_category_duplicate`.
   */
  addChainAttachment(
    insurerId: string,
    payload: ChainAttachmentWritePayload,
  ): Promise<ChainAttachmentRow> {
    return this.request('POST', `/api/insurers/${encodeURIComponent(insurerId)}/chain/attachments`, {
      body: payload,
    });
  }

  /** PUT /api/insurers/{id}/chain/attachments/{rowId} — bewerk een bijlage-regel. */
  updateChainAttachment(
    insurerId: string,
    rowId: string,
    payload: ChainAttachmentWritePayload,
  ): Promise<ChainAttachmentRow> {
    return this.request(
      'PUT',
      `/api/insurers/${encodeURIComponent(insurerId)}/chain/attachments/${encodeURIComponent(rowId)}`,
      { body: payload },
    );
  }

  /** DELETE /api/insurers/{id}/chain/attachments/{rowId} — verwijder een bijlage-regel. */
  deleteChainAttachment(
    insurerId: string,
    rowId: string,
  ): Promise<{ deleted: boolean; id: string }> {
    return this.request(
      'DELETE',
      `/api/insurers/${encodeURIComponent(insurerId)}/chain/attachments/${encodeURIComponent(rowId)}`,
    );
  }

  /**
   * GET /api/insurers/{id}/chain/attachment-category-options — bijlage-categorie-
   * keuzelijst (`GD_AttachmentCategory_SelectList()`, gesorteerd op Description).
   */
  getChainAttachmentCategoryOptions(insurerId: string): Promise<ChainListOption[]> {
    return this.request(
      'GET',
      `/api/insurers/${encodeURIComponent(insurerId)}/chain/attachment-category-options`,
    );
  }

  /** GET /api/lookups/calculation-origins — calculatiebron-opties (`UA_CalculationOrigin`). */
  getCalculationOrigins(): Promise<LookupOption[]> {
    return this.request('GET', '/api/lookups/calculation-origins');
  }

  /** GET /api/lookups/export-statuses — export-status-opties (`WF_CaseState`). */
  getExportStatuses(): Promise<LookupOption[]> {
    return this.request('GET', '/api/lookups/export-statuses');
  }

  /**
   * GET /api/lookups/reports — rapport-opties (`UA_Report_SelectList()`).
   * B2: voedt 4 rapport-dropdowns op de keten-sub-tabs (Rapporten & mails,
   * Intake, Voorexpertise).
   */
  getReports(): Promise<LookupOption[]> {
    return this.request('GET', '/api/lookups/reports');
  }

  /**
   * GET /api/lookups/pre-expertise-rules — voorexpertise-regel-opties
   * (`UA_InsurerPreExpertiseRule_SelectList()`). B2: Voorexpertise-sub-tab.
   */
  getPreExpertiseRules(): Promise<LookupOption[]> {
    return this.request('GET', '/api/lookups/pre-expertise-rules');
  }

  /**
   * GET /api/lookups/additive-groups — additive-group-opties voor de keten-
   * multiselect (B2 "Artikelen & adviesprijzen"). Hergebruikt het dossier-loze
   * endpoint dat al bestond via `UA_AdditiveGroup_SelectList()`.
   *
   * Noot: de kladblok-variant ({@link getAdditiveGroupsNoDossier}) heeft sinds
   * KB-06 (14-10-2026) een eigen route (`/api/lookups/additive-groups/no-dossier`);
   * tot dan botsten de twee op dit pad (500 bij elke aanroep). De bestaande
   * `getAdditiveGroups()` op de client retourneert `DossierAdditiveGroup[]`
   * (ander shape). Gebruik `getChainAdditiveGroupLookup()` om de keten-lookuplijst
   * als `LookupOption[]` te krijgen.
   */
  getChainAdditiveGroupLookup(): Promise<LookupOption[]> {
    return this.request('GET', '/api/lookups/additive-groups');
  }

  /** GET /api/lookups/policy-services — polis-service-opties (`UA_PolicyService`). */
  getPolicyServices(): Promise<LookupOption[]> {
    return this.request('GET', '/api/lookups/policy-services');
  }

  /** GET /api/lookups/policy-formats — polisformaat-opties (`GS_FileFormatDef`). */
  getPolicyFormats(): Promise<LookupOption[]> {
    return this.request('GET', '/api/lookups/policy-formats');
  }

  /** GET /api/lookups/countries — land-opties (`GS_Country`, label = Name). */
  getCountries(): Promise<LookupOption[]> {
    return this.request('GET', '/api/lookups/countries');
  }

  /**
   * GET /api/lookups/debtors?query= — debiteur-autocomplete voor het
   * Opdrachtgever-veld op de Intake-tab (story
   * `fe-opdrachtgever-debtor-autocomplete`, 2026-06-06). De BE filtert
   * company-scoped, in-memory op bedrijfsnaam OF debiteurnummer (max 50,
   * gesorteerd op naam). De ≥2-chars-gate wordt FE-side geclamped; lege array
   * bij geen-match (geen fout). Optionele `signal` zodat de combobox-hook
   * lopende requests kan annuleren bij een volgende keystroke.
   */
  searchDebtors(query: string, signal?: AbortSignal): Promise<DebtorSearchResult[]> {
    return this.request('GET', '/api/lookups/debtors', { query: { query }, signal });
  }

  /**
   * GET /api/relations/{id} — volledige relatie-detail (gesplitste adres-velden +
   * telefoon/mobiel/e-mail). Gebruikt door de bestuurder-zoekselectie om het
   * formulier volledig te vullen; de zoek-shape levert alleen `address` als
   * samengestelde string.
   */
  getRelationDetail(relationId: string, signal?: AbortSignal): Promise<RelationDetail> {
    return this.request('GET', `/api/relations/${encodeURIComponent(relationId)}`, { signal });
  }

  /**
   * GET /api/relations/{id}/klant-detail — uitgebreide klant-detail voor het
   * klant-detailscherm (story `fe-relatiebeheer-fase1-B`). Levert het
   * **bezoekadres** (`VisitorAddress*`) + KvK/BTW/website/parent/bedrijfstype/
   * status/debiteurnr./Bovag-partner. Apart van `getRelationDetail` (bestuurder-
   * shape) zodat die flow niet breekt.
   */
  getRelationKlantDetail(relationId: string, signal?: AbortSignal): Promise<RelationKlantDetail> {
    return this.request(
      'GET',
      `/api/relations/${encodeURIComponent(relationId)}/klant-detail`,
      { signal },
    );
  }

  /**
   * GET /api/relations/{id}/audit — read-only WF_Trace-audit-historie van een
   * relatie voor de audit-drawer (klok-icoon rechtsbovenin het klant-detail).
   * 1-op-1 gespiegeld aan `getDossierAudit`: nieuwste boven (BE sorteert),
   * gecapt op 200 met `truncated`-signaal, dezelfde wire-shape (`DossierAudit`).
   * Lazy: de drawer roept dit pas bij openen aan. Cross-tenant / geen toegang →
   * 404; geen WF_Case / geen traces → `{ items: [], truncated: false }`.
   */
  getRelationAudit(relationId: string, signal?: AbortSignal): Promise<DossierAudit> {
    return this.request(
      'GET',
      `/api/relations/${encodeURIComponent(relationId)}/audit`,
      { signal },
    );
  }

  /**
   * GET /api/relations/{id}/comments — read-only relatie-opmerkingen (bron
   * `GD_Communication`, story `fe-relatiebeheer-fase1-B`). Mirror van de
   * dossier-opmerkingen-shape (`{ count, items }`).
   */
  getRelationComments(relationId: string, signal?: AbortSignal): Promise<RelationComments> {
    return this.request(
      'GET',
      `/api/relations/${encodeURIComponent(relationId)}/comments`,
      { signal },
    );
  }

  /**
   * PUT /api/relations/{id} — werk relatie-velden bij (per-veld auto-save,
   * story `fe-relatiebeheer-fase1-C`). Partiële merge: stuur alleen de
   * gewijzigde velden. `companyType` is de natural-key-string ("Persoon" |
   * "Bedrijf"), niet een GUID. 400 bij ongeldig BTW / lege Naam; 404 bij
   * cross-tenant (scope-guard).
   */
  updateRelation(relationId: string, payload: RelationWritePayload): Promise<{ relationId: string }> {
    return this.request('PUT', `/api/relations/${encodeURIComponent(relationId)}`, {
      body: payload,
    });
  }

  /**
   * POST /api/relations — maak een nieuwe klant aan via de workflow "Relaties"
   * (`RB_CompanyAdd` → **`RB_GenericRelationAddAppServices`**). Minimaal `name` +
   * `companyType`; status default Actief. Geeft de nieuwe relatie-id terug voor
   * navigatie naar `/klanten/{relationId}`.
   *
   * ⚠⚠ **Het is de AppServices-variant, NIET `RB_GenericRelationAdd`** — deze
   * regel stond tot 12-09-2026 andersom en is de eerste bron die een
   * FE-developer leest. De legacy-variant doet een **N+1 over álle ~908
   * herstellers** ónder de sessie-lock; dat is niet zichtbaar, alleen traag.
   * Bouw hier dus nooit een eigen schrijfpad naast: gebruik dit endpoint.
   * Bewijs: `Relations\RelationWriteKernel.cs` stap 3 →
   * `InteractionRelationWriteSink.cs:178`.
   */
  createRelation(payload: RelationCreatePayload): Promise<RelationCreated> {
    return this.request('POST', '/api/relations', { body: payload });
  }

  /**
   * POST /api/relations/{id}/comments — voeg een opmerking toe (via
   * `GD_CommunicationAdd`, `SubjectID = relatie-id`). Story
   * `fe-relatiebeheer-fase1-C`. 400 bij lege body; 404 bij cross-tenant.
   */
  addRelationComment(relationId: string, body: string): Promise<{ id: string | null }> {
    return this.request('POST', `/api/relations/${encodeURIComponent(relationId)}/comments`, {
      body: { body },
    });
  }

  /**
   * GET /api/relations/{id}/contacts — actieve contactenlijst van een relatie
   * (story `fe-relatiebeheer-fase2-contacten-tab`). Owner-gescoped (SD3 404 bij
   * cross-tenant); inactieve contacten worden BE-side gefilterd. Hoofdcontact
   * (`isDefault`) bovenaan.
   */
  listRelationContacts(relationId: string, signal?: AbortSignal): Promise<RelationContacts> {
    return this.request(
      'GET',
      `/api/relations/${encodeURIComponent(relationId)}/contacts`,
      { signal },
    );
  }

  /**
   * GET /api/relations/contact-types — beperkte contacttype-keuzelijst
   * (uitsluitend Medewerker + Crediteurenadministratie). Per request via
   * `RB_ContactType_SelectList` op de stabiele natural-key Code.
   */
  getRelationContactTypes(signal?: AbortSignal): Promise<RelationContactType[]> {
    return this.request('GET', '/api/relations/contact-types', { signal });
  }

  /**
   * GET /api/insurers/contact-types — de contacttype-keuzelijst van de
   * VERZEKERAAR-contactentab. Breder dan de klant-lijst hierboven: naast
   * Medewerker en Crediteurenadministratie ook Declaratie, Admin, Expert
   * Communicatie en Debiteuren naw.
   *
   * ⚠ Niet vervangen door `getRelationContactTypes`: de klant-lijst is bewust
   * smal, en een verzekeraar-contact van het type `declaratie` zou daarmee in
   * het formulier als "Medewerker" verschijnen. Dezelfde lijst is BE-zijde ook
   * de schrijfpoort.
   */
  getInsurerContactTypes(signal?: AbortSignal): Promise<RelationContactType[]> {
    return this.request('GET', '/api/insurers/contact-types', { signal });
  }

  /**
   * POST /api/relations/{id}/contacts — nieuw contact aanmaken
   * (`RB_ContactAdd`). Minimaal `contactTypeCode` + `surName`; de Code wordt
   * server-side gegenereerd ("New_N"). 400 bij ontbrekende verplichte velden;
   * 404 bij cross-tenant.
   */
  createRelationContact(
    relationId: string,
    payload: RelationContactCreatePayload,
  ): Promise<RelationContactCreated> {
    return this.request('POST', `/api/relations/${encodeURIComponent(relationId)}/contacts`, {
      body: payload,
    });
  }

  /**
   * PUT /api/relations/{id}/contacts/{contactId} — contact bijwerken (per-veld
   * auto-save) óf soft-deleten. Onderscheid via de body: een veld-update stuurt
   * de gewijzigde velden; een soft-delete stuurt `status: "inactive"`
   * (→ RecordStatus → Archive, geen hard delete). 404 bij cross-tenant/
   * cross-relation contactId.
   */
  updateRelationContact(
    relationId: string,
    contactId: string,
    payload: RelationContactWritePayload,
  ): Promise<{ contactId: string }> {
    return this.request(
      'PUT',
      `/api/relations/${encodeURIComponent(relationId)}/contacts/${encodeURIComponent(contactId)}`,
      { body: payload },
    );
  }

  /**
   * GET /api/relations/{id}/financial — de debiteur-instellingen (`RB_Debtor`)
   * van de relatie (story `fe-relatiebeheer-fase3-financieel-tab`).
   * Owner-gescoped (SD3 404 bij cross-tenant). Het bestaande debtor-record;
   * geen create.
   */
  getRelationFinancial(relationId: string, signal?: AbortSignal): Promise<RelationFinancial> {
    return this.request(
      'GET',
      `/api/relations/${encodeURIComponent(relationId)}/financial`,
      { signal },
    );
  }

  /**
   * GET /api/relations/{id}/sales — read-only verkoophistorie van een relatie
   * voor de Verkopen-tab (story `fe-relatiebeheer-fase4-verkopen-tab`). De
   * verkoop-dossiers (`UA_Dossier.GenericRelationID`) per dossier gegroepeerd met
   * hun regels (`UA_DossierRow`) + OPGESLAGEN totalen (excl./btw/incl.) +
   * schadedatum + status. Owner-gescoped (SD3 404 bij cross-tenant). Gecapt op de
   * 100 recentste dossiers met `truncated`-signaal; `dossierCount` +
   * `totalRevenueInclVat` tellen over ALLE dossiers. Bedragen/BTW worden NIET
   * herberekend (overgenomen uit de bron).
   */
  getRelationSales(relationId: string, signal?: AbortSignal): Promise<RelationSales> {
    return this.request(
      'GET',
      `/api/relations/${encodeURIComponent(relationId)}/sales`,
      { signal },
    );
  }

  /**
   * PUT /api/relations/{id}/financial — werk debiteur-velden bij (per-veld
   * auto-save). Partiële merge: stuur alleen de gewijzigde velden; een aanwezig
   * `null` wist het veld (dropdown leegmaken). 400 bij negatieve/niet-integer
   * betalingstermijn; 404 bij cross-tenant.
   */
  updateRelationFinancial(
    relationId: string,
    payload: RelationFinancialWritePayload,
  ): Promise<{ relationId: string }> {
    return this.request('PUT', `/api/relations/${encodeURIComponent(relationId)}/financial`, {
      body: payload,
    });
  }

  /**
   * PUT /api/relations/{id}/financial/debtor-number — wijzig het debiteurnummer
   * (aparte actie; BE schrijft `RB_DebtorUpdate` + een `WF_Trace`-regel,
   * `DateTime.Now`). Alleen bij een echte wijziging schrijft de BE de trace.
   * 400 bij leeg; 404 bij cross-tenant.
   */
  updateRelationDebtorNumber(
    relationId: string,
    debtorNumber: number,
  ): Promise<{ relationId: string; debtorNumber: number }> {
    return this.request(
      'PUT',
      `/api/relations/${encodeURIComponent(relationId)}/financial/debtor-number`,
      { body: { debtorNumber } },
    );
  }

  /** GET /api/relations/search-by-address — bestaande-klant-suggestie op postcode+huisnummer. */
  searchRelationsByAddress(
    zipCode: string,
    houseNumber: string,
    signal?: AbortSignal,
  ): Promise<RelationSearchResult[]> {
    return this.request('GET', '/api/relations/search-by-address', {
      query: { zipCode, houseNumber },
      signal,
    });
  }

  /**
   * GET /api/dossiers/{id}/print/report-lists — beschikbare afdruklijsten
   * (rapportlijsten) voor het dossier. Story
   * `appservices-documenten-printen-mailen` (Iter A).
   */
  getPrintReportLists(
    dossierId: string,
    signal?: AbortSignal,
  ): Promise<PrintReportListsResponse> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/print/report-lists`,
      { signal },
    );
  }

  /**
   * GET /api/dossiers/{id}/print/reports?reportListId=… — de rapporten in een
   * gekozen afdruklijst (V1: `{ reportId, name }`). Story
   * `appservices-documenten-printen-mailen` (Iter B).
   */
  getPrintReports(
    dossierId: string,
    reportListId: string,
    signal?: AbortSignal,
  ): Promise<PrintReport[]> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/print/reports`,
      { query: { reportListId }, signal },
    );
  }

  /**
   * POST /api/dossiers/{id}/print/generate — genereert de geselecteerde
   * rapporten en geeft de (eventueel samengevoegde) PDF als `Blob` terug.
   * Story `appservices-documenten-printen-mailen` (Iter C / Fase 1 stap 2).
   *
   * `reprint` = de subset van `reportIds` die opnieuw gegenereerd moet worden;
   * rapporten buiten die set waarvoor al een document bestaat hergebruiken de
   * cached PDF (de generate persisteert tevens per rapport een
   * `UF_InvoiceDocument` + `GD_Attachment`).
   */
  generatePrint(dossierId: string, reportIds: string[], reprint?: string[]): Promise<Blob> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(dossierId)}/print/generate`,
      { body: { reportIds, reprint: reprint ?? [] }, responseType: 'blob' },
    );
  }

  /**
   * GET /api/dossiers/{id}/print/documents/{attachmentId} — download een eerder
   * gegenereerd document (PDF) van het dossier als `Blob`. Access is
   * dossier-gescoped (het document moet via `UF_InvoiceDocument` aan dit dossier
   * hangen). Story `appservices-documenten-printen-mailen` (Fase 1 stap 3).
   */
  getPrintDocument(dossierId: string, attachmentId: string): Promise<Blob> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/print/documents/${encodeURIComponent(attachmentId)}`,
      { responseType: 'blob' },
    );
  }

  /**
   * GET /api/dossiers/{id}/print/mail-recipients — recipient-suggesties
   * (per rol een e-mailadres uit de dossier-relatiegraaf) + een voorgesteld
   * eerste ontvangstadres. Story `appservices-documenten-printen-mailen` (Fase 2).
   */
  getMailRecipients(dossierId: string, signal?: AbortSignal): Promise<MailRecipientsResponse> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/print/mail-recipients`,
      { signal },
    );
  }

  /**
   * POST /api/dossiers/{id}/print/send-mail — genereert (of hergebruikt) de
   * geselecteerde rapporten en mailt ze als bijlagen. Markeert de gemailde
   * documenten. Story `appservices-documenten-printen-mailen` (Fase 2).
   */
  sendMail(dossierId: string, req: SendMailRequest): Promise<{ ok: boolean }> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(dossierId)}/print/send-mail`,
      { body: req },
    );
  }

  /**
   * GET /api/dossiers/{id}/print/mail-templates — de beschikbare mail-templates
   * (rapporten uit de mail-template-lijst van de company). Story
   * `appservices-documenten-printen-mailen` (Fase 3).
   */
  getMailTemplates(dossierId: string, signal?: AbortSignal): Promise<MailTemplate[]> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/print/mail-templates`,
      { signal },
    );
  }

  /**
   * GET /api/dossiers/{id}/print/mail-templates/{templateId}/render — rendert
   * een template tegen het dossier → `{ subject, body }` (body = HTML). Story
   * `appservices-documenten-printen-mailen` (Fase 3).
   */
  renderMailTemplate(
    dossierId: string,
    templateId: string,
    signal?: AbortSignal,
  ): Promise<MailTemplateRender> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/print/mail-templates/${encodeURIComponent(templateId)}/render`,
      { signal },
    );
  }

  /** GET /api/address/lookup — postcode+huisnummer → straat/plaats (Pro6PP). `null` = niet gevonden. */
  lookupAddress(
    zipCode: string,
    houseNumber: string,
    signal?: AbortSignal,
  ): Promise<AddressLookupResult | null> {
    return this.request('GET', '/api/address/lookup', { query: { zipCode, houseNumber }, signal });
  }

  /**
   * POST /api/dossiers/{id}/relation/link — koppelt een bestaande relatie.
   * Backend vult de customer-velden via `SetCustomerByGenericRelation`+`FillDossier`
   * en geeft de direct-betrokken delta terug (`genericRelationId` + `customer`).
   */
  linkRelation(id: string, relationId: string): Promise<LinkRelationResponse> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(id)}/relation/link`, {
      body: { relationId },
    });
  }

  /** POST /api/dossiers/{id}/relation/unlink — ontkoppelt + leegt de customer-velden. */
  unlinkRelation(id: string): Promise<void> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(id)}/relation/unlink`, {
      body: {},
    });
  }

  /**
   * POST /api/dossiers/{id}/relation/create-insured — maakt de verzekerde als
   * relatie aan én koppelt 'm meteen aan het dossier. Gebruikt door de popup
   * "Dekkingscontrole handmatig aanvragen" wanneer het dossier nog geen klant
   * heeft: de maatschappij kan niets nakijken zonder verzekerde.
   *
   * Foutvormen die de call-site kent:
   *  - 400 `{ reason: "insured_validation", missingFields: string[] }` — één of
   *    meer verplichte velden ontbreken (wire-keys uit
   *    `name|street|houseNumber|zipCode|city`).
   *  - 403 — het dossier staat op slot voor bewerken.
   *  - 404 — dossier bestaat niet (meer).
   */
  createInsuredRelation(
    id: string,
    req: CreateInsuredRelationRequest,
  ): Promise<CreateInsuredRelationResponse> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(id)}/relation/create-insured`,
      { body: req },
    );
  }

  /**
   * POST /api/dossiers/{id}/relation/create-from-dossier — maakt een nieuwe
   * relatie uit de (net opgeslagen) customer-velden. Zwaar; FE wacht synchroon.
   */
  createRelationFromDossier(
    id: string,
    req: { companyTypeId?: string | null; titleId?: string | null },
  ): Promise<void> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(id)}/relation/create-from-dossier`,
      { body: req },
    );
  }

  // ─── Dossier-validatie (story `appservices-dossier-validatie`) ──────────

  /**
   * GET /api/dossiers/{id}/rules-check — leest het laatste cached resultaat
   * van de verzekeraar-validatie-SP. Bij 404 onderscheidt het response-body-
   * veld `hasProcedure` tussen "nog niet gedraaid" (`true`) en "geen
   * validatie ingericht" (`false`); deze method vangt de 404 en geeft een
   * gediscrimineerde union terug zodat call-sites geen try/catch hoeven.
   */
  async getDossierRulesCheck(id: string): Promise<DossierRulesCheckGetResult> {
    try {
      const data = await this.request<DossierRulesCheckResult>(
        'GET',
        `/api/dossiers/${encodeURIComponent(id)}/rules-check`,
      );
      return { kind: 'cached', data };
    } catch (e) {
      if (e instanceof NotFoundError) {
        const body = e.body as { hasProcedure?: boolean } | null | undefined;
        return { kind: 'no-cache', hasProcedure: body?.hasProcedure === true };
      }
      throw e;
    }
  }

  /**
   * POST /api/dossiers/{id}/rules-check — draait de validatie-SP (60s
   * server-side timeout) en persisteert het resultaat. Synchroon: caller
   * wacht met spinner. 422 `reason=no_procedure` wanneer er geen SP
   * toepasselijk is.
   */
  runDossierRulesCheck(id: string): Promise<DossierRulesCheckResult> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(id)}/rules-check`, {
      body: {},
    });
  }

  // ─── Status-transitie (story `fe-be-status-transitie-...-hoofdkantoor`) ──

  /**
   * GET /api/dossiers/status-transitions — cachebare lookup van de
   * BE-authoritatieve transitie-map (dossier-onafhankelijk). De FE spiegelt 'm
   * voor knop-zichtbaarheid + disabled-reden; agressief te cachen (lange
   * `staleTime`).
   */
  getStatusTransitions(): Promise<StatusTransitionLookupResult> {
    return this.request('GET', '/api/dossiers/status-transitions');
  }

  /**
   * POST /api/dossiers/{id}/status-transition — voert een status-overgang uit
   * (V1: `submit-to-headquarters` → 241). Synchroon: caller wacht met een
   * "Bezig…"-state. Faal-responsen (400/409/422/500) komen als `ApiError` met
   * een `{ reason }`-body; call-sites discrimineren op `error.status` +
   * `error.body.reason`.
   */
  transitionDossierStatus(
    id: string,
    action: string,
    options?: { reasons?: HeadquartersReasonSelection[] },
  ): Promise<StatusTransitionResult> {
    // Backward-compatible: `reasons` is optioneel. Alleen meesturen wanneer
    // opgegeven (de drie HK-acties); submit-to-headquarters laat 'm weg.
    const body: { action: string; reasons?: HeadquartersReasonSelection[] } = { action };
    if (options && options.reasons !== undefined) {
      body.reasons = options.reasons;
    }
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(id)}/status-transition`,
      { body },
    );
  }

  /**
   * GET /api/dossiers/headquarters-reasons — lookup van de actieve
   * HK-controle-redenen, gesorteerd op `sortOrder`. Dossier-onafhankelijk +
   * systeembreed; agressief te cachen. Optionele `applies`-filter
   * (`approve`/`reject`/`park`) filtert server-side op de bijbehorende vlag;
   * een onbekende waarde geeft 400 `invalid_applies_filter`.
   */
  listHeadquartersReasons(applies?: HeadquartersReasonApplies): Promise<HeadquartersReason[]> {
    const query = applies ? `?applies=${encodeURIComponent(applies)}` : '';
    return this.request('GET', `/api/dossiers/headquarters-reasons${query}`);
  }

  /**
   * GET /api/dossiers/{id}/headquarters-reasons — historie van de vastgelegde
   * redenen voor dit dossier, gesorteerd op `createdAt` desc (laatste ronde
   * bovenaan). Leeg dossier → `[]`. Geen toegang → 404 (geen 403-leak).
   */
  getDossierHeadquartersReasons(id: string): Promise<DossierHeadquartersReason[]> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(id)}/headquarters-reasons`);
  }

  // ─── EHBO-tab (story `fe-be-ehbo-tab`, 2026-07-04) ──────────────────────

  /**
   * PATCH /api/dossiers/{id}/ehbo — forceert precies één EHBO-veld (of de
   * status) met verplichte reden + optimistic locking (1 actie = 1 trace).
   * 403 `not_system_user` / `field_not_editable`, 400 `reason_required` /
   * `reason_too_long` / `invalid_status`, 409 bij version-conflict/status-race.
   * Retourneert het veld + de post-write version.
   */
  patchEhbo(id: string, body: EhboPatchRequest): Promise<EhboPatchResponse> {
    return this.request('PATCH', `/api/dossiers/${encodeURIComponent(id)}/ehbo`, { body });
  }

  /** GET /api/dossiers/ehbo/statuses — alle WF_CaseState-waarden voor de dropdown. */
  listEhboStatuses(): Promise<EhboStatusOption[]> {
    return this.request('GET', `/api/dossiers/ehbo/statuses`);
  }

  /** GET /api/dossiers/ehbo/insurance-types — verzekeringstypes voor de dropdown. */
  listEhboInsuranceTypes(): Promise<EhboInsuranceType[]> {
    return this.request('GET', `/api/dossiers/ehbo/insurance-types`);
  }

  /** GET /api/dossiers/{id}/ehbo/log — read-only EHBO-logboek (WF_Trace cat. "EHBO"). */
  getEhboLog(id: string): Promise<EhboLogEntry[]> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(id)}/ehbo/log`);
  }

  // ─── Claimfilter-info (story `fe-be-verzekeraar-claimfilter-info`) ────────

  /**
   * GET /api/dossiers/{id}/claimfilter-rules — read-only claimfilter-regels van
   * één dossier, voor het "Claimfilter"-blok op de Verzekeraar-tab (status 240).
   *
   * Zichtbaar voor de verzekeraar-rol (binnen de eigen insurer-scope) en voor de
   * system-gebruiker; buiten scope / geen access / onbekend dossier → **404** met
   * exact dezelfde body (geen existence-lek), access zonder verzekeraar-rol →
   * **403** `forbidden_not_insurer`. Nul regels → `200 { rules: [] }`.
   *
   * De route draait onder de exclusieve per-sessie-lock: **niet** parallel aan de
   * detail-GET prefetchen en `retry: false` gebruiken (zie `useClaimFilterRules`).
   */
  getClaimFilterRules(id: string): Promise<ClaimFilterRulesResult> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(id)}/claimfilter-rules`);
  }

  // --- Hersteller-blok in de rail (PO 2026-08-18) --------------------------

  /**
   * GET /api/dossiers/{id}/repairer - naam + plaats van de hersteller, voor het
   * rail-blok dat de verzekeraar ziet. Zelfde poort als claimfilter-rules: buiten
   * de insurer-scope / onbekend dossier -> 404 met dezelfde body; access zonder
   * verzekeraar-rol -> 403. Read-only; retry uit wegens de per-sessie-lock.
   */
  getDossierRepairer(id: string): Promise<DossierRepairerInfo> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(id)}/repairer`);
  }
  // ─── eXchange-precheck (story `fe-be-exchange-aanbieden-precheck`) ───────

  /**
   * GET /api/dossiers/{id}/exchange-precheck — read-only resultaat van de
   * eXchange-controle (eigen risico vs. eXchange + voortgang) vóór aanbieden.
   * Faal-veilig op SOAP: een onbereikbare eXchange-service levert 200 met
   * `soapReachable=false`/`blockReason="exchange_unreachable"` (geen error).
   * Alleen aan te roepen voor een eXchange-dossier (`exchangeDossierNumber > 0`).
   */
  getDossierExchangePrecheck(id: string): Promise<ExchangePrecheckResult> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(id)}/exchange-precheck`);
  }

  /**
   * POST /api/dossiers/{id}/exchange-precheck/ignore-own-risk — schrijft de
   * "negeer eigen risico"-audit-notitie en geeft de VERSE precheck-DTO terug
   * (zodat de FE direct `overridden=true`/`canProceed=true` ziet). Idempotent
   * (bestaande notitie → geen tweede write). 409 `own_risk_not_mismatched`
   * wanneer er geen afwijking te negeren is, 409 `exchange_unreachable` wanneer
   * de controle niet kon draaien.
   */
  ignoreExchangeOwnRisk(id: string): Promise<ExchangePrecheckResult> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(id)}/exchange-precheck/ignore-own-risk`,
      { body: {} },
    );
  }

  /**
   * POST /api/dossiers/{id}/exchange-update/{category} — UITBREIDING
   * auto-voltooien (PB-6/PB-7). Pusht één **pushbare** voortgang-categorie naar
   * eXchange. Faal-veilig per categorie: een mislukte push (SOAP-fout, offline)
   * levert 200 met `success:false` (geen error) zodat de sequentiële FE-loop
   * niet breekt. `polis`/onbekend → 400 (de FE roept 'm nooit voor polis aan).
   * De FE roept dit endpoint sequentieel aan, één categorie tegelijk, voor de
   * live per-categorie status (concept STATE 4a).
   */
  updateDossierExchangeCategory(
    id: string,
    category: ExchangeUpdateCategory,
  ): Promise<ExchangeUpdateResult> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(id)}/exchange-update/${encodeURIComponent(category)}`,
      { body: {} },
    );
  }

  // ─── Facturen aanmaken (story `fe-be-aanmaken-facturen`, 2026-06-07) ─────

  /**
   * GET /api/dossiers/{id}/invoice-preview — read-only preview die zowel de
   * knop-gate (`canCreate`/`blockReason`) als de popup-inhoud (preview-grid +
   * betaaloverzicht-matrix) voedt. Eén call. Maakt niets aan (in-memory);
   * `HasInvoices` blijft ongewijzigd. Cross-tenant → 404; onverwachte fout
   * → 500 (`correlationId`) zodat de popup de faal-veilige error-state toont.
   */
  getDossierInvoicePreview(id: string): Promise<InvoicePreviewResult> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(id)}/invoice-preview`);
  }

  /**
   * GET /api/dossiers/{id}/delivery-proposal?party=n — story `be-fe-geen-documenten-voorstel` (G1). Het voorstel
   * voor een partij die in stap 2 geen documenten krijgt (`emptyReason`). Read-only. Alleen ophalen als het
   * overzicht een `emptyReason` toont (PB-G12). 409 `proposal_not_applicable` als de partij geen voorstel nodig heeft.
   */
  getDossierDeliveryProposal(id: string, party: number): Promise<DeliveryProposal> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(id)}/delivery-proposal?party=${encodeURIComponent(String(party))}`,
    );
  }

  /**
   * POST /api/dossiers/{id}/delivery-proposal/accept — G1 (PB-G8). Legt het voorstel vast als verzendregel(s) voor
   * de vestiging. Idempotent (`alreadyExists`). Fouten: 400 `proposal_condition_mismatch` / `unknown_document` /
   * `mail_template_required`, 403 `not_executing_repairer`.
   */
  acceptDossierDeliveryProposal(
    id: string,
    body: DeliveryProposalAcceptRequest,
  ): Promise<DeliveryProposalAcceptResult> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(id)}/delivery-proposal/accept`, { body });
  }

  /**
   * GET /api/dossiers/{id}/handling — de stappenbalk van de tab "Afhandeling"
   * (story `fe-be-afhandeling-tab`). Read-only. 404 zonder toegang; 403
   * `role_not_allowed` voor een verzekeraar; 403
   * `restricted_repairer_not_applicable` voor een verzekeraar-gebonden hersteller.
   */
  getDossierHandling(id: string): Promise<DossierHandling> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(id)}/handling`);
  }

  /**
   * POST /api/dossiers/{id}/handling/send — verzenden via mail (story
   * `be-fe-afhandeling-verzenden-mail`). Idempotent op `clientRequestId` (een
   * herhaling geeft `replayed: true` en mailt niets). Fouten: 409
   * `send_in_progress`/`version_conflict`/`nothing_left_to_send`, 422
   * `insurer_address_missing`/`recipient_address_missing`, 503
   * `mail_not_configured`.
   */
  sendDossierHandling(id: string, body: HandlingSendRequest): Promise<HandlingSendResult> {
    // Story U (PB-U19, R-U11): één send kan tot twee SOAP-aanroepen naar uBench doen (recheck + declaratie,
    // standaard 60 s elk). Na HANDLING_SEND_TIMEOUT_MS breekt de client af; de caller hergebruikt dan
    // DEZELFDE clientRequestId (geen antwoord ⇒ onbekend of de server hem verwerkte).
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), HANDLING_SEND_TIMEOUT_MS) : null;
    return this.request<HandlingSendResult>('POST', `/api/dossiers/${encodeURIComponent(id)}/handling/send`, {
      body,
      signal: controller ? controller.signal : undefined,
    }).finally(() => {
      if (timer !== null) clearTimeout(timer);
    });
  }

  /**
   * POST /api/dossiers/{id}/handling/close — *Afsluiten* (archiveren zonder export,
   * na bevestiging). 409 `awaiting_export` als er inmiddels wél een exportstatus is.
   */
  closeDossierHandling(id: string, body: { clientRequestId: string; version: number }): Promise<HandlingCloseResult> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(id)}/handling/close`, { body });
  }

  /**
   * POST /api/dossiers/{id}/handling/make-documents — story G3 (`be-fe-geen-documenten-achteraf`). Stap 3, voor een
   * partij die bij factureren geen documenten kreeg: "Overnemen en documenten maken" (verzendregel + documenten) of
   * "Alleen voor dit dossier" (`onlyThisDossier`, zonder regel). Na een gesloten mail-/Dispatch-ronde start dit een
   * tweede verzendronde. Fouten: 409 `documents_missing_not_applicable` / `second_round_not_allowed` /
   * `send_in_progress` / `send_step_not_active`, 403 `not_executing_repairer`, 400 (zoals G1-accept), 502
   * `documents_render_failed`.
   */
  makeDossierHandlingDocuments(id: string, body: HandlingMakeDocumentsRequest): Promise<HandlingMakeDocumentsResult> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(id)}/handling/make-documents`, { body });
  }

  /**
   * POST /api/dossiers/{id}/invoices — maakt de facturen aan via de bestaande
   * BE-functie (`CreateInvoicesForUA_Dossier(..., Repairer, "filiaal", "")`).
   * Hervalideert de drie voorwaarden defense-in-depth: `422` met `reason`
   * (`no_accessible_debtor`/`no_calculation`), `409` `reason:"already_invoiced"`
   * bij `HasInvoices`, `500` (`correlationId`) bij een onverwachte fout. Bij
   * succes invalideert de caller `['dossiers', id]`.
   */
  createDossierInvoices(
    id: string,
    /**
     * Optionele voortgang-id. Aanwezig → meegestuurd als
     * `X-Calc-Progress-Id`-header zodat de BE de live factuur-aanmaak-fasen naar
     * de poll-store schrijft (uit te lezen via {@link getCalcProgress}, gedeeld
     * met het calc-add-pad). Afwezig → exact het oude gedrag (geen tracking).
     */
    progressId?: string,
    /**
     * Optioneel maatschappij-adres uit het aanmaakscherm, ingevuld wanneer het
     * document op "handmatig versturen" stond omdat er geen adres resolvde.
     * `remember` legt het vast voor deze hersteller × maatschappij, zodat een
     * volgende factuur het vanzelf vindt — als **invaller**, dus ónder een later
     * centraal ingevuld declaratie-contact. Weglaten → exact het oude gedrag.
     */
    insurerEmail?: { address: string; remember: boolean },
    /**
     * Story `be-fe-geen-documenten-voorstel` (G1, PB-G11) — de gebruiker bevestigde "Toch factureren zonder
     * documenten?". Alleen een audit-signaal (één trace); de BE dwingt het niet af. Weglaten = niets meesturen.
     */
    options?: {
      noDocumentsConfirmed?: boolean;
      /**
       * EPIC-022 S022.5 (AC 18) — "uit eigen voorraad afboeken". Alleen meegestuurd met minstens één keuze; anders
       * blijft de body exact zoals vóór de story (geen `inventory`-sleutel).
       */
      inventory?: InvoiceInventoryRequest;
    },
  ): Promise<CreateInvoicesResult> {
    const body: Record<string, unknown> = insurerEmail
      ? {
          insurerEmailAddress: insurerEmail.address,
          rememberInsurerEmailAddress: insurerEmail.remember,
        }
      : {};
    if (options?.noDocumentsConfirmed === true) body.noDocumentsConfirmed = true;
    if (options?.inventory && options.inventory.consumeFromStock.length > 0) body.inventory = options.inventory;
    return this.request('POST', `/api/dossiers/${encodeURIComponent(id)}/invoices`, {
      body,
      headers: progressId ? { 'X-Calc-Progress-Id': progressId } : undefined,
    });
  }

  // ─── Mollie-betaallinks (story `fe-be-mollie-betaallink-genereren`, EPIC-018) ─

  /**
   * POST /api/dossiers/{id}/payment-links — genereert een Mollie iDEAL-betaallink
   * (+ opgeslagen QR) voor de Klant-total. `201` → de link-DTO. Foutpaden:
   * `409 mollie_not_configured` (hersteller niet ingesteld), `422
   * customer_total_not_payable` (bedrag ≤ 0), `422 mollie_rejected` (Mollie wees
   * af), `503 mollie_unreachable` (provider onbereikbaar). Loopt FE-side via
   * `enqueueDossierMutation` (per-dossier-queue, hard-rule).
   */
  createDossierPaymentLink(id: string): Promise<PaymentLink> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(id)}/payment-links`, { body: {} });
  }

  /**
   * GET /api/dossiers/{id}/payment-links — de bestaande betaallink(s) op `Created`
   * desc PLUS de server-berekende `customerTotal` (de Klant-total). NIET rol-gated
   * (alleen cross-tenant → 404); elke DTO draagt de server-berekende `isExpired`.
   * Lichtgewicht (geen zware `invoice-preview`-keten) zodat de betaallink-sectie
   * zowel in de factuur-aanmaak-popup als in de read-only `FacturenCard` werkt.
   */
  getDossierPaymentLinks(id: string): Promise<PaymentLinkListResult> {
    return this.request<PaymentLinkListResult>(
      'GET',
      `/api/dossiers/${encodeURIComponent(id)}/payment-links`,
    );
  }

  /**
   * GET /api/dossiers/{id}/payment-links — convenience-variant die alleen de
   * **meest recente** (actieve) link of `null` (geen link) teruggeeft. Bouwt voort
   * op {@link getDossierPaymentLinks}.
   */
  async getDossierPaymentLink(id: string): Promise<PaymentLink | null> {
    const res = await this.getDossierPaymentLinks(id);
    return res.links[0] ?? null;
  }

  /**
   * POST /api/dossiers/{id}/payment-links/refresh — pull-t de betaalstatus van de
   * **meest recente** betaallink bij Mollie (story `fe-be-mollie-betaalstatus-
   * ophalen`, S-G). Vaste envelope `{ link, refreshed, reason? }`. Terminaal
   * (betaald/verlopen) of geen link → short-circuit (`refreshed:false`, géén
   * Mollie-call). Foutpaden: `503 mollie_unreachable`, `422 mollie_rejected` — de
   * opgeslagen status blijft dan behouden. NIET rol-gated (alleen cross-tenant →
   * 404). Loopt FE-side via `enqueueDossierMutation` (per-dossier-queue).
   */
  refreshDossierPaymentLink(id: string, linkId?: string): Promise<PaymentLinkRefreshResult> {
    // Zonder linkId = meest recente link (bestaand gedrag); mét linkId = díe
    // specifieke link (meerdere-betaallinks-weergave, story
    // `fe-be-meerdere-betaallinks-tonen`).
    const suffix = linkId ? `?linkId=${encodeURIComponent(linkId)}` : '';
    return this.request<PaymentLinkRefreshResult>(
      'POST',
      `/api/dossiers/${encodeURIComponent(id)}/payment-links/refresh${suffix}`,
      { body: {} },
    );
  }

  // ─── Facturen tonen (story `fe-be-facturen-tonen`, 2026-06-07) ───────────

  /**
   * GET /api/dossiers/{id}/invoices — read-only lijst van de aangemaakte
   * facturen, inclusief regels in één call (single-call, A1): de detail-overlay
   * en de factuur-switcher schakelen zonder een tweede netwerkcall. Alleen aan
   * te roepen voor een gefactureerd dossier (`hasInvoices === true`); de caller
   * gate't via react-query `enabled`. Cache-key `['dossier-invoices', id]`
   * (bewust losgekoppeld van `['dossiers', id]` zodat een dossier-PATCH-
   * invalidatie de facturen niet onnodig herhaalt). Cross-tenant → 404;
   * onverwachte fout → 500 (`correlationId`) → de kaart toont een nette fout +
   * retry. Geen facturen → `{ invoices: [] }`.
   */
  getDossierInvoices(id: string): Promise<InvoiceListResult> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(id)}/invoices`);
  }

  // ─── Audit-historie (story `fe-be-wf-trace-audit`, item 11, 2026-06-09) ──

  /**
   * GET /api/dossiers/{id}/audit — read-only WF_Trace-audit-historie voor de
   * audit-drawer (klok-icoon). Nieuwste boven (BE sorteert), gecapt op 200 met
   * `truncated`-signaal. Lazy: de drawer roept dit pas bij openen aan (niet
   * vooraf met het dossier-detail). Cross-tenant / geen toegang → 404; geen
   * traces → `{ items: [], truncated: false }`. Cache-key `['dossier-audit', id]`
   * (losgekoppeld van `['dossiers', id]`).
   */
  getDossierAudit(id: string): Promise<DossierAudit> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(id)}/audit`);
  }

  // ─── Eerdere schades op dit voertuig (story `fe-be-eerdere-schades-voertuig`) ─

  /**
   * GET /api/dossiers/{id}/vehicle-damage-history — read-only lijst + telling
   * van ANDERE dossiers op hetzelfde voertuig (kenteken) binnen de caller-scope.
   * Voedt het rail-blok "Andere schades op dit voertuig" + de accordion-popup.
   * De scope wordt server-side bepaald (hersteller/keten/system) — niet
   * client-side narrowen. Geen/leeg kenteken óf geen matches →
   * `{ license, count: 0, items: [] }`; cross-tenant / onbekend → 404.
   * Cache-key `['dossiers', id, 'vehicle-damage-history']`.
   */
  getVehicleDamageHistory(id: string, signal?: AbortSignal): Promise<VehicleDamageHistory> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(id)}/vehicle-damage-history`,
      { signal },
    );
  }

  /**
   * GET /api/dossiers/{id}/hq-eurocode-history — story
   * `fe-be-hk-eurocode-vergelijking` (2026-07-03). Read-only: per eurocode uit
   * de huidige calc van dossier `{id}` de eerdere volledige calculaties van
   * andere dossiers (met facturen, ≤ 2 jaar, access-scoped, max 20 nieuwste).
   * HK-only feature: niet-HK → 403 `not_headquarters`; niet-241 → 403
   * `not_in_hq_review`; cross-tenant/onbekend → 404. Alleen bevragen wanneer de
   * FE-gate (HK + status 241 + ≥1 eurocode) waar is.
   * Cache-key `['dossiers', id, 'hq-eurocode-history']`.
   */
  getHqEurocodeHistory(id: string, signal?: AbortSignal): Promise<HqEurocodeHistory> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(id)}/hq-eurocode-history`,
      { signal },
    );
  }

  // ─── Calculatie-tab (story calculatie-tab-readonly) ────────────────────

  /**
   * GET /api/dossiers/{dossierId}/calculation-rows?calculationCode=filiaal
   * — V1 leest read-only de filiaal-calculatieregels van een dossier.
   *
   * De backend valideert strict-lowercase: alleen `"filiaal"` is toegestaan;
   * elke andere waarde levert 400 op. Default-waarde van de tweede parameter
   * staat ook backend-side; toch sturen we hem expliciet mee zodat de query
   * stable is en niet impliciet afhangt van een server-default.
   */
  getDossierCalculationRows(
    dossierId: string,
    calculationCode: string = 'filiaal',
  ): Promise<CalculationRowDto[]> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/calculation-rows`,
      { query: { calculationCode } },
    );
  }

  /**
   * GET /api/dossiers/{dossierId}/calculation-history?calculationCode=filiaal
   * — read-only historische calculatie-snapshots (story
   * `fe-historische-calculaties`). Elk element is één weggeschreven versie
   * (`HistoryIndex`), nieuwste eerst. Wordt lazy opgevraagd zodra de popup
   * opent; geen retry — de UI biedt een expliciete retry-knop.
   */
  getDossierCalculationHistory(
    dossierId: string,
    calculationCode: string = 'filiaal',
  ): Promise<CalculationHistorySnapshotDto[]> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/calculation-history`,
      { query: { calculationCode } },
    );
  }

  /**
   * GET /api/dossiers/{dossierId}/purchase-price-origin — read-only
   * inkoopherleiding per eurocode (story `fe-be-inkoopherleiding-popup`).
   *
   * Wordt **lazy** opgevraagd zodra de popup opent; de calculatie-tab mag er niet
   * op wachten. Geen retry — de popup biedt een expliciete opnieuw-knop.
   */
  getDossierPurchasePriceOrigin(dossierId: string): Promise<PurchasePriceOriginDto> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/purchase-price-origin`,
    );
  }

  /**
   * GET /api/dossiers/{dossierId}/calculation-history/count — lichtgewicht
   * aantal-snapshots voor de badge op de "Historische calculaties"-knop.
   * Goedkoper dan de volledige history (`COUNT(DISTINCT HistoryIndex)`).
   */
  getDossierCalculationHistoryCount(
    dossierId: string,
    calculationCode: string = 'filiaal',
  ): Promise<{ count: number }> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/calculation-history/count`,
      { query: { calculationCode } },
    );
  }

  /**
   * GET /api/dossiers/{dossierId}/calculation-pricelist?calculationCode=filiaal
   * — V1 read-only display (story S009.4): retourneert de gebruikte/voor-
   * geselecteerde prijslijst plus de verplichte prijslijst (indien aanwezig)
   * zodat de toolbar + waarschuwingsbalk de juiste tekst kunnen tonen.
   *
   * Geen retry en geen automatische refetch — de hook houdt dezelfde
   * `enabled`-gate aan als `getDossierCalculationRows`. Foutpad (404 / 5xx)
   * wordt in de UI als korte tekst "Prijslijst-info niet beschikbaar"
   * getoond; cross-tenant levert 404 (TA-fix F-pl.13).
   */
  getDossierCalculationPricelist(
    dossierId: string,
    calculationCode: string = 'filiaal',
  ): Promise<CalculationPricelistDto> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/calculation-pricelist`,
      { query: { calculationCode } },
    );
  }

  // ─── Dossier — Aanmaken (S007.9) ───────────────────────────────────────

  /**
   * GET /api/dossiers/create-options — haalt de lijst dropdown-opties voor de
   * "+ Nieuw dossier"-knop op. Server filtert al op `repairerId`-match en
   * `RecordStatusID=Active`. FE doet geen extra filtering.
   *
   * 403 wanneer `repairerId === null` (refined story #9). FE moet de fetch
   * pas starten als `repairerId !== null` (knop conditioneel).
   */
  getDossierCreateOptions(): Promise<DossierCreateOption[]> {
    return this.request('GET', '/api/dossiers/create-options');
  }

  /**
   * POST /api/dossiers — maakt een nieuw dossier aan. Body bevat het server-
   * gestuurde `workflowToStart`, `newEntityTemplate` en (optioneel)
   * `customDossierTypeId` uit de gekozen `DossierCreateOption`.
   *
   * Returnt 201 met `{ id }`. 400 voor invalide payload, 403 voor non-
   * hersteller of cross-repairer custom-type. Error-body shape: `ApiErrorBody`
   * (zie types.ts) — vangstaan via try/catch op de mutation in de FE.
   */
  createDossier(req: DossierCreateRequest): Promise<DossierCreateResponse> {
    return this.request('POST', '/api/dossiers', { body: req });
  }

  // ─── S007.11 — Intake-tab ─────────────────────────────────────────────

  /**
   * GET /api/dossiers/{id}/intake/schema — haalt de per-veld policies + de
   * optimistic-concurrency-versie + de actuele state-code op voor de Intake-
   * tab.
   */
  getDossierIntakeSchema(id: string): Promise<IntakeSchemaResponse> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(id)}/intake/schema`);
  }

  /**
   * GET /api/dossiers/dossier-types — list actieve UA_DossierType-rijen
   * voor de Dossiersoort-dropdown op de Intake-tab. Systeem-breed (geen
   * per-hersteller filter); ge-cached via TanStack-Query met lange staleTime.
   */
  listDossierTypes(): Promise<{ id: string; description: string | null }[]> {
    return this.request('GET', '/api/dossiers/dossier-types');
  }

  /**
   * PATCH /api/dossiers/{id}/intake — schrijft een sparse veld-set weg op het
   * dossier.
   *
   * Story `appservices-lichte-response-en-resolver-fix` (2026-05-24): server
   * retourneert nu een delta-shape (`{ version, fields }`) i.p.v. een
   * complete `DossierDetail`-DTO. FE merget delta in de TanStack-cache van
   * `['dossiers', id]` zonder andere tabs te wissen. Bij noop (geen
   * dirty-velden): `{ version: current, fields: {} }` met 200 OK.
   */
  patchDossierIntake(id: string, req: IntakePatchRequest): Promise<DossierPatchResponse> {
    return this.request('PATCH', `/api/dossiers/${encodeURIComponent(id)}/intake`, { body: req });
  }

  // ─── Story-A `fe-schade-tab-editable` — Schade-tab ────────────────────

  /**
   * GET /api/dossiers/{id}/damage/schema — haalt de per-veld policies + de
   * optimistic-concurrency-versie + de actuele state-code op voor de
   * Schade-tab. Sjabloon = `getDossierIntakeSchema`.
   */
  getDossierDamageSchema(id: string): Promise<DamageSchemaResponse> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(id)}/damage/schema`);
  }

  /**
   * PATCH /api/dossiers/{id}/damage — schrijft een sparse veld-set weg op
   * het dossier. Server retourneert delta-shape (`{ version, fields }`).
   *
   * Cascade: bij mutatie van `carConditionId` of `repairTypeId` levert de
   * server-delta ook `carConditionName` / `repairTypeName` mee zodat de
   * FE-cache niet stale wordt (CLAUDE.md "Delta-derive-cascades op
   * mutatie-endpoints").
   *
   * Roest-pad: `rustInRabbets` wordt server-side via legacy
   * `Interaction.UA_DossierExtraAdd` (eerste write) of
   * `Interaction.UA_DossierExtraUpdate` (vervolg) gepersisteerd — niet via
   * `UA_Dossier`-update.
   *
   * 400 bij datum-validatie-violation met body
   * `{ reason: "damage_date_validation", field, rule }` (R1-R6).
   */
  patchDossierDamage(id: string, req: DamagePatchRequest): Promise<DossierPatchResponse> {
    return this.request('PATCH', `/api/dossiers/${encodeURIComponent(id)}/damage`, { body: req });
  }

  /**
   * GET /api/lookups/car-conditions — company-scoped lookup voor
   * "Toestand voertuig"-dropdown op Schade-tab. Server resolved per-request
   * via `Interaction.UA_CarCondition_SelectList()` (geen statische accessor).
   */
  getCarConditions(): Promise<DamageLookupOption[]> {
    return this.request('GET', '/api/lookups/car-conditions');
  }

  /**
   * GET /api/lookups/repair-types — company-scoped lookup voor "Soort
   * herstel"-dropdown op Schade-tab. Server resolved per-request via
   * `Interaction.UA_RepairType_SelectList()` (geen statische accessor).
   */
  getRepairTypes(): Promise<DamageLookupOption[]> {
    return this.request('GET', '/api/lookups/repair-types');
  }

  // ─── Story `fe-voertuig-tab-editable` — Voertuig-tab ──────────────────

  /**
   * GET /api/dossiers/{id}/vehicle/schema — haalt 12 field-policies + de
   * optimistic-concurrency-versie + de actuele state-code op voor de
   * Voertuig-tab. Read-only RDW-datums staan NIET in de policy-set.
   */
  getDossierVehicleSchema(id: string): Promise<VehicleSchemaResponse> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(id)}/vehicle/schema`);
  }

  /**
   * PATCH /api/dossiers/{id}/vehicle — schrijft een sparse veld-set weg.
   * Server retourneert delta-shape (`{ version, fields }`).
   *
   * Cascade: bij mutatie van `vehicleBodyId` levert de server-delta
   * `vehicleBodyName` (display-naam) mee, plus — bij Kampeerwagen-cleanup —
   * `isIntegral: null`. Bij mutatie van `carConditionId` cascadeert
   * `carConditionName`. De 7 cross-tab-velden (license/manufacturerText/
   * modelSetText/modelText/modelYear/chassisNumber/numberOfKilometers)
   * worden zowel onder `vehicle.*` als op DTO-root in de delta gezet, zodat
   * de Intake-tab niet stale wordt.
   *
   * 403 bij een wire-key buiten de allowlist (RDW-datums of buiten-Voertuig-
   * veld): body `{ reason: "field_not_editable", fields: [...] }`.
   */
  patchDossierVehicle(id: string, req: VehiclePatchRequest): Promise<DossierPatchResponse> {
    return this.request('PATCH', `/api/dossiers/${encodeURIComponent(id)}/vehicle`, { body: req });
  }

  /**
   * GET /api/lookups/vehicle-bodies — company-scoped lookup voor
   * Carrosserievorm-dropdown. Server resolved per-request via
   * `Interaction.UA_VehicleBody_SelectList()` (geen statische accessor) en
   * past de legacy-filter toe: `EuroMotiveCode` numeriek + `Description`
   * niet-numeriek (bron: `DossierV2.aspx.cs:77`).
   */
  getVehicleBodies(): Promise<VehicleLookupOption[]> {
    return this.request('GET', '/api/lookups/vehicle-bodies');
  }

  // ─── S007.14 — Polis-invoer ───────────────────────────────────────────

  /**
   * GET /api/insurers/lookup?role={role}&q={text}&take=50&sort={Verzekerd|Lease|Fleetowner}
   * — autocomplete-lookup voor verzekeraar (`role=insurer`) en volmacht
   * (`role=agent`). Server filtert server-side op naam (LIKE), 2-chars-min
   * wordt FE-side geclamped. Lege array bij geen-match.
   *
   * S007.14c — optionele `sort`-parameter filtert verder op het Type-verzekerd
   * (alleen voor `role=insurer`; bij `role=agent` wordt sort server-side
   * genegeerd). Niet meesturen of `sort=null` → backwards-compatible (geen
   * filter). Server doet case-sensitive enum-match en geeft 400 bij ongeldige
   * waarde.
   */
  getInsurerLookup(params: {
    query: string;
    role: 'insurer' | 'agent';
    sort?: 'Verzekerd' | 'Lease' | 'Fleetowner' | null;
  }): Promise<InsurerLookupResult[]> {
    const query: Record<string, string | number | boolean | undefined> = {
      q: params.query,
      role: params.role,
      take: 50,
    };
    // Stuur sort alleen mee als hij gevuld is — zo blijft backwards-compatible
    // gedrag voor callers die nog geen sort doorgeven.
    if (params.sort) {
      query.sort = params.sort;
    }
    return this.request('GET', '/api/insurers/lookup', { query });
  }

  /**
   * GET /api/policy/damage-causes — oorzaak-lijst voor de huidige bron.
   * Exact één van `dossierId`, `agentId` of `insurerId` moet gevuld zijn.
   * `dossierId` heeft voorrang en scope't de lijst op `dossier.PolicyService`
   * (de eXchange-aware afleiding waaronder de oorzaak ook wordt opgeslagen), zodat
   * een server-gezette oorzaak (import/polis-check) altijd in de lijst zit → geen
   * leeg veld + geen onterechte auto-clear (2026-07-02). De partij-modi
   * (`agentId`/`insurerId`, volmacht-eerst — L-02 uit S007.13) blijven bestaan.
   * Backend levert per item een `sourceName` voor het bron-label (AC #17).
   */
  getDamageCauses(params: {
    dossierId?: string | null;
    agentId?: string | null;
    insurerId?: string | null;
    /** BL-29 — `'fallback'`: de terugvallijst zonder dossier en zonder partij. */
    scope?: 'fallback' | null;
  }): Promise<DamageCause[]> {
    const query: Record<string, string | number | boolean | undefined> = {};
    if (params.dossierId) query.dossierId = params.dossierId;
    else if (params.agentId) query.agentId = params.agentId;
    else if (params.insurerId) query.insurerId = params.insurerId;
    else if (params.scope) query.scope = params.scope;
    return this.request('GET', '/api/policy/damage-causes', { query });
  }

  /**
   * GET /api/policy/insurance-types — verzekeringssoort-lijst voor de
   * "Soort dekking"-dropdown. Statische referentielijst (UA_InsuranceType,
   * 3 vaste codes), geen filter-params. Cache-vriendelijk in TanStack via
   * een lange staleTime.
   */
  getInsuranceTypes(): Promise<InsuranceType[]> {
    return this.request('GET', '/api/policy/insurance-types');
  }

  /**
   * GET /api/insurers/{id}/specifications — filiaal-lijst per verzekeraar
   * (legacy `ddlInsSpec`). Lege array → dropdown niet zichtbaar (AC #18).
   */
  getInsurerSpecifications(insurerId: string): Promise<InsurerSpecification[]> {
    return this.request(
      'GET',
      `/api/insurers/${encodeURIComponent(insurerId)}/specifications`,
    );
  }

  /**
   * PATCH /api/dossiers/{id}/policy — sparse update van polis-velden.
   * Eigen endpoint (L-14.01) — geen hergebruik van `/intake`.
   *
   * Story `appservices-lichte-response-en-resolver-fix` (2026-05-24): server
   * retourneert nu een delta-shape (`{ version, fields }`) i.p.v. een
   * complete `DossierDetail`-DTO. Bij noop (geen dirty-velden):
   * `{ version: current, fields: {} }` met 200 OK.
   */
  patchDossierPolicy(id: string, req: PolicyPatchRequest): Promise<DossierPatchResponse> {
    return this.request('PATCH', `/api/dossiers/${encodeURIComponent(id)}/policy`, { body: req });
  }

  /**
   * POST /api/dossiers/{id}/policy/check — voert dekkingscontrole uit via de
   * legacy `Interaction.GetPolicy()`-dispatcher (S007.15). Server switcht
   * intern op `PolicyService.Code` (eXchange / Mail / Dispatch / Carculate /
   * uBench / LocalDatabase). Retourneert bijgewerkte `DossierDetail` plus
   * een `policyCheck.outcome` (covered / not_covered / no_policy /
   * pending_async / pending_external / no_response).
   *
   * Latency: eXchange-call kan 10-30s duren; geen client-cancel in V1.
   */
  checkPolicy(id: string): Promise<PolicyCheckResponse> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(id)}/policy/check`, {});
  }

  /**
   * POST /api/dossiers/{id}/own-coverage — de popup "Dekking zelf opgeven" (story
   * `be-fe-dekking-d1-popup-en-slot`). De enige route waarlangs de hersteller zelf de
   * dekking vaststelt; de server toetst beschikbaarheid (403
   * `own_coverage_not_allowed`) en de popup-regels (400 met `reason`). Antwoord =
   * delta-shape `{ version, fields }` incl. `policy.coverageState/Source` en de nieuwe
   * `policy.ownCoverage`.
   */
  postOwnCoverage(id: string, req: OwnCoverageRequest): Promise<DossierPatchResponse> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(id)}/own-coverage`, { body: req });
  }

  /**
   * POST /api/dossiers/{id}/exchange/cancel — annuleer een eXchange-dossier.
   *
   * Legacy-flow (story `polis-delete-exchange-dossier`, BE-commit cc07a73):
   *  - Roept remote `ExchangeService.ExchangeDossierAnnuleren` aan met de
   *    door de gebruiker opgegeven reden (mag leeg zijn).
   *  - Schrijft optioneel een audit-Comment naar `GD_Communication` (alleen
   *    als de bestaande `opmerkingDekking` niet leeg is).
   *  - Reset 8 dossier-velden (insurance/agent/excess/etc.) en doet
   *    `UA_DossierUpdate`.
   *  - Retourneert delta-shape `{ version, fields }` analoog aan PATCH /policy
   *    met de geraakte root- + `policy.*`-sub-object-velden.
   *
   * Pre-flight error-volgorde: 404 (cross-tenant) → 409 (version-mismatch) →
   * 400 `exchange_dossier_required` (geen eXchange-koppeling op dossier).
   *
   * Body-shape: `{ version: int, reason: string }`. `reason` is een verplichte
   * string (FE stuurt `""` bij geen reden gevuld) zodat de server-side
   * normalisatie stabiel is.
   */
  cancelExchangeDossier(
    id: string,
    body: { version: number; reason: string },
  ): Promise<DossierPatchResponse> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(id)}/exchange/cancel`, {
      body,
    });
  }

  /**
   * PATCH /api/dossiers/{id}/fields-valid — zet `UA_Dossier.FieldsAreValid`
   * (story `fe-be-fieldsarevalid-autosync`, 2026-07-01). FE-gedreven
   * synchronisatie van het legacy "chkValidate"-vinkje met de Voertuig- +
   * Schade-tab-completeness; alleen op een echte flip aangeroepen door
   * `useFieldsValidAutoSync`.
   *
   * Body-shape: `{ version: int, value: bool }`. `value` MOET een echte
   * JSON-boolean zijn — de BE weigert string/getal/`null`/ontbrekend met 400
   * (geen stille JToken-coercion). Retourneert de post-write delta-shape
   * `{ version, fields }` (mirror PATCH /policy) met de nieuwe `fieldsAreValid`
   * + de post-write version; de FE merget dat in `['dossiers', id]` (geen
   * invalidate → geen extra lock-geserialiseerde refetch).
   *
   * Version-optimistic-concurrency + access (403/404) conform de andere
   * dossier-sub-mutatie-endpoints (`/vehicle`, `/damage`).
   */
  setDossierFieldsValid(
    id: string,
    body: { version: number; value: boolean },
  ): Promise<DossierPatchResponse> {
    return this.request('PATCH', `/api/dossiers/${encodeURIComponent(id)}/fields-valid`, {
      body,
    });
  }

  /**
   * POST /api/dossiers/{id}/coverage-check — verzekeraar slaat de
   * dekkingscontrole op (story `fe-be-verzekeraar-dekkingscontrole`,
   * 2026-06-29). Eén atomaire server-actie: persisteert de strikt-kleinere
   * dekking-allowlist (9 velden) via de bestaande `PATCH /policy`-machinerie
   * én zet de status door 222 → 240 (Expertise) via de transitie-machine met
   * een verzekeraar-rol-gate.
   *
   * Retourneert een delta `{ version, fields }` in dezelfde shape als
   * `PATCH /policy` — polisvelden genest onder `fields.policy`, plus een
   * top-level `status`-string ("240. Expertise") — zodat de FE de
   * `['dossiers', id]`-cache atomair bijwerkt via `mergeDeltaIntoDossierDetail`
   * zonder refetch.
   *
   * Fout-volgorde (BE, mirror gate-precedence): scope-404 → rol-403
   * (`forbidden_not_insurer`) → allowlist-400
   * (`field_not_allowed_for_coverage_check`, met `field`) → version-409
   * (met `currentVersion`) → bron-status-422 (`invalid_status_for_coverage_check`)
   * → range-400 (`policy_valid_range`, met `field`) / `excess_negative` /
   * `invalid-decimal`.
   */
  coverageCheck(
    id: string,
    body: CoverageCheckRequest,
  ): Promise<DossierPatchResponse> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(id)}/coverage-check`, {
      body,
    });
  }

  /**
   * POST /api/dossiers/{id}/insurer-expertise — verzekeraar-expertise-uitkomst
   * vanuit status 240 (story `fe-be-verzekeraar-expertise`):
   *  - `approve-insurer` → 280 (geen opmerking);
   *  - `return-for-adjustment-insurer` → 245 (verplichte opmerking + melding);
   *  - `reject-final-insurer` → 290 eindstatus (verplichte opmerking + melding).
   *
   * Retourneert een delta die de status kantelt (240→280/245/290). Fouten:
   * 400 (`unknown_action`/`comment_required`/`comment_too_long`), 403
   * (`forbidden_not_insurer`), 404 (buiten scope/niet meer beschikbaar), 409
   * (version-mismatch / `already_transitioned`), 422 (`invalid_source_status`).
   */
  insurerExpertise(
    id: string,
    body: InsurerExpertiseRequest,
  ): Promise<DossierPatchResponse> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(id)}/insurer-expertise`, {
      body,
    });
  }

  /**
   * GET /api/dossiers/{id}/voorexpertise/preflight (story
   * `fe-voorexpertise-aanvraag-popup`, EPIC-017, BS-4) — read-only preflight
   * voor de aanvraagpopup: de reden-opties (per route) + het route-afhankelijke
   * bovenblok (eXchange-precheck-booleans óf keten-instellingen-waarden). Geen
   * mutatie/version-bump. Precies één van `exchange`/`settings` is gevuld (TV-2).
   */
  getVoorexpertisePreflight(id: string): Promise<VoorexpertisePreflightResult> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(id)}/voorexpertise/preflight`,
    );
  }

  /**
   * POST /api/dossiers/{id}/voorexpertise (pass-1 contract) — vraagt
   * voorexpertise aan (eXchange óf gemaild rapport; de BE kiest de route).
   * Retourneert een {@link DossierPatchResponse}-delta die de status naar
   * "Voorexpertise aangevraagd" kantelt (dossier verlaat 210). Fout-reason-codes:
   * 400 (`reason_required`/`toelichting_required`), 403 (`forbidden_not_repairer`),
   * 409 (`invalid_status`), 422 (`pre_expertise_not_applicable`/
   * `toelichting_too_long`/`insufficient_photos`/`exchange_precondition_failed`),
   * 502 (eXchange-/mail-fout).
   */
  requestVoorexpertise(
    id: string,
    body: VoorexpertiseRequest,
  ): Promise<VoorexpertiseResult> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(id)}/voorexpertise`, {
      body,
    });
  }

  /**
   * POST /api/dossiers/{id}/pre-expertise-result — HK legt de voorexpertise-
   * uitslag vast vanuit status 252 (story `fe-be-voorexpertise-afhandelen-hk`,
   * EPIC-017):
   *  - `approve-pre-expertise` → 246 "Voorexpertise goedgekeurd";
   *  - `reject-pre-expertise` → 247 "Voorexpertise afgekeurd".
   *
   * Opmerking is bij BEIDE uitkomsten verplicht. Retourneert een
   * {@link DossierPatchResponse}-delta die de status kantelt (252→246/247).
   * Fout-reason-codes: 400 (`unknown_action`/`comment_required`/
   * `comment_too_long`), 403 (`forbidden_not_headquarters`), 404 (buiten
   * scope / niet meer beschikbaar), 409 (version-mismatch), 422
   * (`not_applicable_exchange` / `invalid_source_status`), 500
   * (`comment_persist_failed`).
   */
  requestPreExpertiseResult(
    id: string,
    body: PreExpertiseResultRequest,
  ): Promise<DossierPatchResponse> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(id)}/pre-expertise-result`,
      { body },
    );
  }

  /**
   * POST /api/dossiers/{targetId}/copy-sections — neem geselecteerde secties
   * van een ANDER (bron) dossier over naar het huidige (target) dossier
   * (story `fe-be-gegevens-overnemen-dossier`).
   *
   * CROSS-TENANT WRITE: de caller moet toegang hebben tot bron én target,
   * anders 404 (geen data-lek). 422 bij `sourceDossierId === targetId`; 403
   * bij een gelockt target. Calculatie wordt VERVANGEN; kenteken NOOIT
   * overgenomen.
   */
  copyDossierSections(
    targetId: string,
    body: CopyDossierSectionsRequest,
  ): Promise<CopyDossierSectionsResponse> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(targetId)}/copy-sections`,
      { body },
    );
  }

  /**
   * POST /api/dossiers/{id}/dispatch/clear — verwijder de koppeling met
   * Dispatch (Scenario 10 / AC 10.D, story `polisscherm-mvp-en-state`).
   *
   * Zet `UA_Dossier.LastDispachtResultCode` (DB-kolom-typo) op `null` via de
   * bestaande domain-save (`Interaction.UA_DossierUpdate` — geen raw SQL,
   * hard-rule). V1-scope: nult UITSLUITEND de dispatch-resultaatcode — NIET
   * verzekeraar/volmacht/ER/geldig-tot.
   *
   * Retourneert de **volledige** geüpdatete `DossierDetail` (zelfde shape als
   * GET, via `LoadDossierDetail`) inclusief verse `version` zodat de FE-cache
   * compleet blijft. Na de update is `lastDispatchResultCode` leeg → de
   * polis-invoervelden zijn weer editable en "Controleer polis" weer actief.
   *
   * Body-shape: `{ version: int }` (optimistic-locking; mismatch → 409 met
   * `currentVersion`). Pre-flight error-volgorde: 404 (cross-tenant) → 409
   * (version-mismatch).
   */
  clearDispatchLink(id: string, body: { version: number }): Promise<DossierDetail> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(id)}/dispatch/clear`, {
      body,
    });
  }

  /**
   * POST /api/dossiers/{id}/external/clear — "Verwijder extern dossier", de
   * uitweg uit een definitieve externe dekking (story
   * `fe-be-ubench-poliscontrole-definitief`, 2026-09-02). Tegenhanger van legacy
   * `btnDeleteExternal_Click` (`PolisV2.aspx.cs:3069-3087`).
   *
   * Wist legacy-conform de héle koppeling: volmacht, maatschappij,
   * eXchange-dossiernummer, Dispatch-resultaatcode, de drie
   * `ExternalDossier*`-velden, de drie eigen-risico-bedragen (naar 0) en
   * polis-geldig-tot. Daarmee vallen de polisvelden en "Controleer polis" weer
   * open. ⚠ Bewust breder dan `clearDispatchLink`, die in V1 uitsluitend de
   * dispatch-resultaatcode nult.
   *
   * Alleen van toepassing op de polisservices die de definitief-marker zelf
   * zetten (uBench / CarculateNew / CarculateService); eXchange en Dispatch
   * geven 400 `external_clear_not_applicable` — die hebben hun eigen actie.
   *
   * Retourneert de **volledige** geüpdatete `DossierDetail` inclusief verse
   * `version`. Body-shape `{ version: int }` (mismatch → 409 met
   * `currentVersion`).
   */
  clearExternalDossier(id: string, body: { version: number }): Promise<DossierDetail> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(id)}/external/clear`, {
      body,
    });
  }

  /**
   * POST /api/dossiers/{id}/archive — archiveert een dossier (story
   * `fe-be-archiveren-dossier`, 2026-06-08). Eénrichting (geen de-archiveren in
   * V1). `reason` is verplicht BE-zijde alleen wanneer er een intake bestaat;
   * laat 'm weg wanneer er geen intake is.
   *
   * Retourneert een post-write delta `{ isArchive, status, statusValue, version,
   * auditTraceWritten, noOp }` zodat de FE de `['dossiers', id]`-cache kan
   * bijwerken zonder refetch.
   *
   * Fout-volgorde (BE): 404 (geen toegang/bestaat niet) → 422
   * `exchange_not_cancelled` → 422 `status_blocks_archive` → 400
   * `archive_reason_required` / `archive_reason_too_long` → 500
   * `archive_*_persist_failed`.
   */
  archiveDossier(
    id: string,
    body: { reason?: string } = {},
  ): Promise<ArchiveDossierResponse> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(id)}/archive`, {
      body,
    });
  }

  /**
   * GET /api/dossiers/{id}/credit-preview — story R3 (`be-fe-dossier-crediteren-geheel`):
   * wat "Dossier crediteren" zou doen (facturen, korting-transacties, tegenboekingen,
   * achterblijvers) plus `canCredit`/`blockedReason` en de `previewFingerprint`.
   * Alleen `system` (TIJDELIJK — V3); anders 403 `role_not_allowed`. Een lopende
   * facturatie-run ⇒ 409 `billing_run_in_progress` met `dataBusy: true`.
   */
  getDossierCreditPreview(id: string): Promise<DossierCreditPreview> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(id)}/credit-preview`);
  }

  /**
   * POST /api/dossiers/{id}/credit — crediteert het hele dossier, alles of niets.
   * 409 `credit_preview_changed` draagt een nieuwe `preview`; 409 `version_conflict`,
   * `billing_run_in_progress`, `credit_in_progress` of een blokkade; 500 `credit_failed`
   * = er is niets gecrediteerd.
   */
  creditDossier(id: string, body: DossierCreditRequest): Promise<DossierCreditResult> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(id)}/credit`, { body });
  }

  /**
   * POST /api/dossiers/central-flags — haalt per dossier-id de **effectief-
   * centraal**-vlag op (story `fe-be-archiveren-vanuit-dossieroverzicht`,
   * Spoor 1). Voedt de fail-closed archiveer-gate op het dossieroverzicht.
   *
   * Bewust een apart, lichtgewicht endpoint: de effectief-centraal-afleiding
   * IN de lijst-query ankerde haar recursieve parent-walk op álle
   * `RB_CompanyCompany`-paren → SQL-timeout op de grote acc-catalogus
   * (incident 2026-07-20). Hier ankert dezelfde walk uitsluitend op de paren
   * van de aangeleverde id's.
   *
   * **Chunking.** De BE clampt stil op `CentralFlagsMaxIds` (= 200) en laat de
   * rest weg uit de respons. Omdat de FE-gate "afwezig" als onbekend leest en
   * dan géén knop toont, zou een langere lijst de archiveer-knop stil
   * verliezen. Deze methode splitst de id's daarom in blokken van
   * {@link DOSSIER_CENTRAL_FLAGS_MAX_IDS} en voegt de deelrespons samen. De
   * blokken gaan **sequentieel** omdat `/api` per sessie serialiseert
   * (CLAUDE.md) — parallel prefetchen levert geen winst, alleen lock-druk.
   *
   * @param ids Dossier-id's van de zichtbare rijen.
   * @returns Map van dossier-id → `true` (centraal) / `false` (niet centraal).
   *          Id's zonder toegang of onbekende id's **ontbreken** in de map;
   *          behandel die als onbekend, niet als `false`.
   */
  async fetchDossierCentralFlags(ids: string[]): Promise<Record<string, boolean>> {
    if (ids.length === 0) return {};

    const merged: Record<string, boolean> = {};
    for (let i = 0; i < ids.length; i += DOSSIER_CENTRAL_FLAGS_MAX_IDS) {
      const chunk = ids.slice(i, i + DOSSIER_CENTRAL_FLAGS_MAX_IDS);
      const response = await this.request<DossierCentralFlagsResponse>(
        'POST',
        '/api/dossiers/central-flags',
        { body: { ids: chunk } satisfies DossierCentralFlagsRequest },
      );
      // Defensief: een BE die (nog) geen `flags` meestuurt mag geen TypeError
      // geven — de gate valt dan terug op "onbekend" en toont geen knop.
      Object.assign(merged, response?.flags ?? {});
    }
    return merged;
  }

  /**
   * POST /api/dossiers/{id}/exchange-close — "Verzend naar maatschappij" (story
   * `be-fe-exchange-factuur-gate-en-verzend-maatschappij`, 2026-07-03). Sluit een
   * gefactureerd eXchange-dossier af bij de maatschappij (via de bestaande seam)
   * en archiveert het daarna lokaal. Synchroon.
   *
   * Retourneert bij 200 `{ closed: true, archived, archive }`: volledig succes
   * (`archived: true` + `archive`-delta) of semi-succes (`archived: false`,
   * `archive: null` — afgemeld maar lokaal archiveren mislukt, F-06).
   *
   * Fout-paden (BE): 404 (geen toegang/bestaat niet) → 409
   * (`not_exchange` / `no_invoices` / `already_archived`, seam NIET aangeroepen)
   * → 502 (`exchange_close_failed`, seam-fout → dossier blijft open).
   */
  exchangeCloseDossier(id: string): Promise<ExchangeCloseResult> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(id)}/exchange-close`, {
      body: {},
    });
  }

  /**
   * POST /api/dossiers/{id}/copy — maakt een kopie van het dossier (story
   * `kopieer-dossier`, 2026-06-09; port van legacy `Glas/CopyDossier.aspx`).
   * Stuurt UITSLUITEND de groep-keuze-booleans; de BE bouwt de veld-
   * uitsluitingen (`exclusiveProps`) + resolveert de standaard-workflow
   * server-side.
   *
   * Retourneert `{ id, number, archive }` (AC-A3a / R1): `id`/`number` voor de
   * navigatie + succes-toast; `archive` draagt het best-effort-archiveer-
   * resultaat van het bron-dossier (`requested`/`succeeded`/`reason`).
   *
   * Fout-paden (BE): 404 (geen toegang/bestaat niet) → 400
   * `workflow_config_missing` (geen bruikbare standaard-workflow) → 500
   * `copy_failed` / `internal_error`. Een mislukte best-effort-archivering is
   * GEEN fout: de copy slaagt (200) met `archive.succeeded=false`.
   */
  copyDossier(id: string, body: CopyDossierBody): Promise<CopyDossierResponse> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(id)}/copy`, {
      body,
    });
  }

  // ─── SMS versturen naar klant (story `sms-versturen-klant`, 2026-06-10) ──

  /**
   * GET /api/dossiers/{id}/sms/options — toegangspoort + voorgevuld klant-
   * nummer + beschikbare SMS-templates. `serviceAvailable=false` → de dialog
   * toont de "geen SMS-dienst"-fallback. Access-gated (404 bij geen toegang).
   */
  getSmsOptions(id: string): Promise<SmsOptions> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(id)}/sms/options`);
  }

  /**
   * GET /api/dossiers/{id}/sms/render?templateId=… — rendert één SMS-template
   * via de report-engine (dossier-context). Faalt netjes (422 `render_failed`)
   * bij een engine-fout / lege render → de FE laat het Bericht-veld ongewijzigd.
   */
  renderSmsTemplate(id: string, templateId: string): Promise<SmsRenderResult> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(id)}/sms/render`, {
      query: { templateId },
    });
  }

  /**
   * POST /api/dossiers/{id}/sms — verstuurt een SMS naar de klant via de
   * bestaande SMS-dienst. Status-matrix: 400 (ongeldig nummer / leeg bericht),
   * 403 (geen SMS-dienst), 404 (geen toegang), 200 `{ sent:false, error }`
   * (gateway-fout — geen trace/comm), 200 `{ sent:true }` (succes — trace +
   * comm geschreven).
   */
  sendSms(id: string, body: SmsSendBody): Promise<SmsSendResult> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(id)}/sms`, {
      body,
    });
  }

  // ─── Klachtenafhandeling (story fe-be-klachtenafhandeling, 2026-06-08) ──

  /**
   * GET /api/dossiers/{id}/complaint — bestaande klacht of een leeg template
   * met server-prefill (`acceptedBy` = ingelogde user, `complaintDate` =
   * vandaag). Geen rij → 200 met `exists:false` (NIET 404). De FE toont op
   * basis van `exists`/`id` de bijlagen-sectie disabled vóór de eerste save.
   */
  getComplaint(dossierId: string): Promise<Complaint> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(dossierId)}/complaint`);
  }

  /**
   * PUT /api/dossiers/{id}/complaint — upsert van de klacht (Add vs Update
   * server-side op aanwezigheid). `ordinal`/`subjectId` worden server-geforceerd
   * (E8). Foutpaden:
   *  - 400 `complaint_validation` (+ `fields`) bij een ontbrekend verplicht
   *    domain-veld (complainant/acceptedBy/complaintDate);
   *  - 409 `complaint_version_conflict` bij een stale `version` (SameVersion);
   *  - 409 `complaint_already_exists` bij een gelijktijdige tweede Add.
   * Retourneert de verse klacht (incl. nieuw `id` + post-write `version`).
   */
  saveComplaint(dossierId: string, body: ComplaintWriteRequest): Promise<Complaint> {
    return this.request('PUT', `/api/dossiers/${encodeURIComponent(dossierId)}/complaint`, { body });
  }

  // ─── Dossier-indicatoren (story fe-be-dossier-indicatoren, 2026-06-08) ──

  /**
   * GET /api/dossiers/indicator-types — actieve catalogus, gesorteerd op
   * `sortOrder`. Gedeactiveerde types komen niet terug (niet selecteerbaar).
   */
  getIndicatorTypes(): Promise<DossierIndicatorType[]> {
    return this.request('GET', `/api/dossiers/indicator-types`);
  }

  /**
   * GET /api/dossiers/{id}/indicators — de voor de caller zichtbare indicatoren
   * (eigen bedrijf / VisibleToAll / system). Leeg → `[]`.
   */
  getDossierIndicators(dossierId: string): Promise<DossierIndicator[]> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(dossierId)}/indicators`);
  }

  /**
   * POST /api/dossiers/{id}/indicators — toevoegen. Foutpaden: 400
   * `indicator_type_unknown`/`indicator_type_inactive`, 409
   * `indicator_already_exists` (dubbele toewijzing eigen bedrijf), 404 (geen
   * dossier-toegang). 201 met de aangemaakte indicator.
   */
  createDossierIndicator(
    dossierId: string,
    body: DossierIndicatorCreateRequest,
  ): Promise<DossierIndicator> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(dossierId)}/indicators`, {
      body,
    });
  }

  /**
   * PATCH /api/dossiers/{id}/indicators/{indicatorId} — `message`/`visibleToAll`
   * bewerken (partiële merge). 403 `indicator_not_owned` voor een vreemd
   * bedrijf; 404 cross-dossier/onbekend. 200 met de bijgewerkte indicator.
   */
  updateDossierIndicator(
    dossierId: string,
    indicatorId: string,
    body: DossierIndicatorUpdateRequest,
  ): Promise<DossierIndicator> {
    return this.request(
      'PATCH',
      `/api/dossiers/${encodeURIComponent(dossierId)}/indicators/${encodeURIComponent(indicatorId)}`,
      { body },
    );
  }

  /**
   * DELETE /api/dossiers/{id}/indicators/{indicatorId} — verwijderen. 204 bij
   * succes (resolved met `undefined`); 403/404 als bij PATCH.
   */
  deleteDossierIndicator(dossierId: string, indicatorId: string): Promise<void> {
    return this.request(
      'DELETE',
      `/api/dossiers/${encodeURIComponent(dossierId)}/indicators/${encodeURIComponent(indicatorId)}`,
    );
  }

  // ─── Opmerkingen (story fe-be-opmerkingen-aanmaken, 2026-06-10) ─────────

  /**
   * GET /api/dossiers/{id}/communication-types — de rol-gefilterde, actieve
   * communicatietypes voor de aanmaak-dropdown. De BE filtert server-side
   * (open-default-types + types waarvan minstens één rol-rij matcht op de
   * `WF_Role`-set van de user). Read-only GET (geen CSRF). Access-gated:
   * geen dossier-toegang → 404.
   */
  getCommunicationTypes(
    dossierId: string,
  ): Promise<import('./types.js').DossierCommunicationType[]> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/communication-types`,
    );
  }

  /**
   * POST /api/dossiers/{id}/comments — opmerking aanmaken (via
   * `Interaction.GD_CommunicationAdd`, server-side). Body
   * `{ communicationTypeId, message? }`. CSRF-header automatisch (mutating).
   * Foutpaden (onderscheidende `reason`): 400 `message_required` (lege body/
   * ontbrekend type), 404 `dossier_not_accessible`, 400
   * `communication_type_unknown` (onbekend/inactief), 403
   * `communication_type_not_allowed` (rol mag dit type niet). 201 met de
   * aangemaakte opmerking. De bestaande lijst komt via `getDossier`; de FE
   * invalideert `['dossiers', id]` na succes.
   */
  createDossierComment(
    dossierId: string,
    body: import('./types.js').DossierCommentCreateRequest,
  ): Promise<import('./types.js').DossierComment> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(dossierId)}/comments`,
      { body },
    );
  }

  /**
   * GET /api/dossiers/{id}/complaint/attachments — bijlagen gekoppeld aan het
   * klacht-subject (`UA_ComplaintForm`). De BE resolved de klacht server-side;
   * geen klacht → lege lijst.
   */
  listComplaintAttachments(
    dossierId: string,
  ): Promise<import('./types.js').AttachmentDto[]> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/complaint/attachments`,
    );
  }

  /**
   * POST /api/dossiers/{id}/complaint/attachments — multipart-upload op het
   * klacht-subject. De `SubjectID` wordt server-side op `complaint.id` gezet
   * (NIET het dossier-id). 409 `complaint_not_saved` wanneer er nog geen klacht
   * is (upload pas mogelijk na eerste save).
   */
  uploadComplaintAttachments(
    dossierId: string,
    files: import('./types.js').AttachmentUploadFile[],
    defaultCategoryId: string | null,
    signal?: AbortSignal,
  ): Promise<import('./types.js').AttachmentUploadResponse> {
    const fd = new FormData();
    if (defaultCategoryId) {
      fd.append('defaultCategoryId', defaultCategoryId);
    }
    files.forEach((f, idx) => {
      fd.append('files', f.file, f.file.name);
      if (f.categoryIdOverride !== null && f.categoryIdOverride !== undefined) {
        fd.append(`categoryIdsByIndex[${idx}]`, f.categoryIdOverride);
      }
      if (f.description !== null && f.description !== undefined && f.description !== '') {
        fd.append(`descriptionsByIndex[${idx}]`, f.description);
      }
    });
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(dossierId)}/complaint/attachments`,
      { formData: fd, signal },
    );
  }

  /**
   * DELETE /api/dossiers/{id}/complaint/attachments/{attachmentId} — verwijdert
   * één bijlage van de klacht (hard-delete via de Domain-laag).
   */
  deleteComplaintAttachment(dossierId: string, attachmentId: string): Promise<void> {
    return this.request(
      'DELETE',
      `/api/dossiers/${encodeURIComponent(dossierId)}/complaint/attachments/${encodeURIComponent(attachmentId)}`,
    );
  }

  // ─── Vehicles — OVI-lookup ─────────────────────────────────────────────

  /**
   * GET /api/vehicles/by-license/{license} — haalt voertuigdata op bij een
   * NL-kenteken via de OVI Open Data API (RDW dataset m9d7-ebf2). Endpoint
   * is dossier-loos zodat het bij intake (vóór dossier-save) bruikbaar is.
   *
   * Response: `{ status: 'found' | 'not_found' | 'error', ... }`. De caller
   * routeert respectievelijk naar overschrijven, leegmaken+melding, en
   * intactlaten+foutmelding (zie story-AC 14).
   *
   * License-input mag met of zonder streepjes en in any-case; de server
   * normaliseert en valideert tegen `^[A-Z0-9]{6}$` (400 anders).
   */
  getVehicleByLicense(license: string): Promise<VehicleLookupResult> {
    return this.request('GET', `/api/vehicles/by-license/${encodeURIComponent(license)}`);
  }

  /**
   * POST /api/dossiers/{id}/apply-rdw-lookup — server-side OVI-handler die
   * Socrata raadpleegt en de RDW-velden in één call op het dossier schrijft.
   *
   * Story `fe-voertuig-ovi-uitbreiding` (2026-05-30): drie RDW-datums
   * (`firstRegistration`, `nameRegistration`, `dutchRegistration`) zijn op
   * PATCH /vehicle read-only (403 `field_not_editable`). De OVI-flow gaat
   * daarom via een dedicated trusted endpoint dat de field-policy passeert.
   *
   * Scope V1 (M1-verdict — Socrata `m9d7-ebf2` bevat geen VIN): er worden
   * maximaal 5 velden geschreven (RDW-datums + Doors + VehicleBodyID).
   * MeldCode en ChassisNumber zijn niet meer onderdeel van de OVI-flow.
   *
   * Response heeft een `status` ('found' | 'not_found' | 'error') net als de
   * GET-lookup, plus `version` + `fields` in dezelfde delta-shape als PATCH
   * /vehicle (top-level + `vehicle.*` sub-object) zodat de FE-cache via
   * `mergeDeltaIntoDossierDetail` ververst kan worden zonder refetch.
   */
  applyRdwLookup(
    dossierId: string,
    body: { license: string },
  ): Promise<ApplyRdwLookupResponse> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(dossierId)}/apply-rdw-lookup`,
      { body },
    );
  }

  /**
   * Story `fe-voertuig-chassis-lookup` (2026-05-30) — Argic-SOAP-call voor
   * VIN. Roept legacy `Interaction.VehicleInformationMinimal(license, license)`
   * aan via een server-side wrapper. Bij `status='found'` is `fields` gevuld
   * met `chassisNumber` (+ optioneel `meldCode` = laatste 4 chars van VIN)
   * + `vehicle.*` sub-mirror voor cache-merge.
   */
  lookupChassis(
    dossierId: string,
    body: ChassisLookupRequest,
  ): Promise<ChassisLookupResponse> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(dossierId)}/lookup-chassis`,
      { body },
    );
  }

  /**
   * GET /api/dossiers/{id}/fitters — monteur-multi-select-data voor de
   * Intake-tab (story `fe-monteur-multiselect-intake`, 2026-06-06). Retourneert
   * de unie van actieve hersteller-monteurs ∪ reeds-toegewezen monteurs met
   * `selected`-vlag + een `editable`-flag (edit-lock). Lege `fitters` →
   * geen/onbekende hersteller (FE verbergt de sectie).
   */
  getDossierFitters(
    dossierId: string,
    signal?: AbortSignal,
  ): Promise<DossierFittersResponse> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/fitters`,
      { signal },
    );
  }

  /**
   * PUT /api/dossiers/{id}/fitters — schrijft de volledige gewenste set
   * monteur-id's (story `fe-monteur-multiselect-intake`). De BE diff't tegen de
   * huidige toewijzingen en past add/delete uitsluitend via de Domain-laag toe.
   * Een fitter-id buiten de hersteller-unie → 400 (atomair, geen mutatie). Edit-
   * lock → 403. Retourneert de verse lijst (zelfde shape als de GET).
   */
  setDossierFitters(
    dossierId: string,
    fitterIds: string[],
  ): Promise<DossierFittersResponse> {
    return this.request(
      'PUT',
      `/api/dossiers/${encodeURIComponent(dossierId)}/fitters`,
      { body: { fitterIds } },
    );
  }

  // ─── Vervangend vervoer op dossier (UA_RentalCarPerDossier) ────────────────

  /** GET /api/dossiers/{id}/rental-car — huidige koppeling (of null) +
   * keuzelijst actieve huurauto's + editable. */
  getDossierRentalCar(
    dossierId: string,
    signal?: AbortSignal,
  ): Promise<DossierRentalCarResponse> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/rental-car`,
      { signal },
    );
  }

  /** PUT /api/dossiers/{id}/rental-car — upsert van de ene koppeling.
   * `finalRate` wordt server-berekend. Edit-lock → 403. Retourneert de verse
   * respons (zelfde shape als GET). */
  saveDossierRentalCar(
    dossierId: string,
    body: DossierRentalCarWriteRequest,
  ): Promise<DossierRentalCarResponse> {
    return this.request(
      'PUT',
      `/api/dossiers/${encodeURIComponent(dossierId)}/rental-car`,
      { body },
    );
  }

  /** DELETE /api/dossiers/{id}/rental-car — koppeling verwijderen (idempotent). */
  deleteDossierRentalCar(dossierId: string): Promise<void> {
    return this.request(
      'DELETE',
      `/api/dossiers/${encodeURIComponent(dossierId)}/rental-car`,
    );
  }

  /** POST /api/dossiers/{id}/rental-car/invoice — klant-factuur (VH1) aanmaken
   * via de legacy CreateRentalInvoice. Alleen wanneer er een bedrag is
   * (`finalRate > 0`), de betaler de klant is en er nog niet gefactureerd is;
   * na succes blokkeert `isInvoiced` een tweede factuur. Retourneert de verse
   * respons (zelfde shape als GET, nu met `isInvoiced: true`). */
  createDossierRentalCarInvoice(
    dossierId: string,
  ): Promise<DossierRentalCarResponse> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(dossierId)}/rental-car/invoice`,
    );
  }

  // ─── Aanvullende factuur (provisienota / losse verkoopfactuur) ─────────────
  // Story `fe-be-aanvullende-factuur` (2026-07-15).

  /** GET /api/dossiers/{id}/invoices/aanvullend/options — kebab-gating +
   * dialog-lookups (soorten, debiteur-types, artikelen, btw, provisie-prefill). */
  getAanvullendeFactuurOptions(
    dossierId: string,
    signal?: AbortSignal,
  ): Promise<AanvullendeFactuurOptions> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/invoices/aanvullend/options`,
      { signal },
    );
  }

  // ─── Handmatige Dispatch-acties ────────────────────────────────────────────
  // Wens eigenaar 30-09-2026, port van legacy `Glas\DispatchActions.aspx`.
  // Alleen HK/support/system, pas ná HK-goedkeuring.

  /** GET /api/dossiers/{id}/dispatch-actions — mag deze gebruiker nú, en zo nee
   * waarom niet? Bewust een eigen endpoint: de "ooit goedgekeurd"-toets leest
   * WF_Trace en hoort niet in het hete dossier-open-pad. */
  getDispatchActionAvailability(
    dossierId: string,
    signal?: AbortSignal,
  ): Promise<DispatchActionAvailability> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/dispatch-actions`,
      { signal },
    );
  }

  /** POST /api/dossiers/{id}/dispatch-actions — aanbieden, aanbieden met
   * handmatige beoordeling, of crediteren. De STATUS verandert niet; omzetten
   * gaat bewust via de EHBO-tab. */
  postDispatchAction(
    dossierId: string,
    body: DispatchActionRequest,
  ): Promise<DispatchActionResult> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(dossierId)}/dispatch-actions`,
      { body },
    );
  }

  /** POST /api/dossiers/{id}/invoices/provision — provisienota (VP1). 422 →
   * `ApiError.body = { blockedByWarning, warnings }` (waarschuwings-flow). */
  createProvisionInvoice(
    dossierId: string,
    body: ProvisionInvoiceRequest,
  ): Promise<AanvullendeInvoiceCreated> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(dossierId)}/invoices/provision`,
      { body },
    );
  }

  /** POST /api/dossiers/{id}/invoices/extra-sales — losse verkoopfactuur (VF5).
   * 422 → `ApiError.body = { blockedByWarning, warnings }` (waarschuwings-flow). */
  createExtraSalesInvoice(
    dossierId: string,
    body: ExtraSalesInvoiceRequest,
  ): Promise<AanvullendeInvoiceCreated> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(dossierId)}/invoices/extra-sales`,
      { body },
    );
  }

  // ─── Artikelbeheer (story fe-artikelbeheer-zoek-detail-scoping) ─────────

  /**
   * GET /api/items/manage-search?q= — Zoek-tab van Artikelbeheer. Read-only,
   * tenant-gescopet server-side (`ItemListID` + `fUE_ItemListConnectionRecords`,
   * geen kale CompanyID). Lege/zeer-korte term → lege lijst (geen DB-call).
   * `signal` laat de debounced zoek-hook lopende requests annuleren.
   */
  searchManagedItems(
    q: string,
    itemTypeId?: string,
    signal?: AbortSignal,
  ): Promise<ItemSearchHit[]> {
    return this.request('GET', '/api/items/manage-search', {
      query: { q, itemTypeId },
      signal,
    });
  }

  /**
   * GET /api/items/{id} — artikel-detail (tien kernvelden + `canEdit` /
   * `isSystemArticle`). 404 bij onbestaand artikel; 403 bij geen toegang.
   */
  getItemDetail(id: string, signal?: AbortSignal): Promise<ItemDetail> {
    return this.request('GET', `/api/items/${encodeURIComponent(id)}`, { signal });
  }

  /**
   * POST /api/items — nieuw artikel via `Interaction.UE_ItemAdd`. Owner-check
   * op meegestuurd type/lijst server-side (403 bij vreemd type/lijst).
   */
  createItem(req: ItemWritePayload): Promise<ItemDetail> {
    return this.request('POST', '/api/items', { body: req });
  }

  /**
   * PUT /api/items/{id} — kernvelden bijwerken via `Interaction.UE_ItemUpdate`.
   * Owner-check op het item server-side (403 bij niet-eigen artikel).
   */
  updateItem(id: string, req: ItemWritePayload): Promise<ItemDetail> {
    return this.request('PUT', `/api/items/${encodeURIComponent(id)}`, { body: req });
  }

  /**
   * GET /api/items/{id}/prices — Prijzen-tab laden.
   * Per zichtbare prijslijst (gefilterd via `HasAccess`) de prijs,
   * de afgeleide-weergave en de flags `canEdit` / `isSystemBase` /
   * `isCalculated`. Sortering: eigen rijen eerst, dan basis, dan overige.
   */
  getItemPrices(itemId: string, signal?: AbortSignal): Promise<ItemPriceRowDto[]> {
    return this.request('GET', `/api/items/${encodeURIComponent(itemId)}/prices`, { signal });
  }

  /**
   * PUT /api/items/{id}/prices/{priceListId} — eigen prijs opslaan.
   * Owner-check server-side (403 bij niet-eigen of `PriceIsCalculated`).
   * Write via `act.SavePriceForItem` (CLAUDE.md hard-rule).
   * Respons: de bijgewerkte rij-DTO (dezelfde shape als GET).
   */
  saveItemPrice(itemId: string, priceListId: string, req: ItemPriceWriteRequest): Promise<ItemPriceRowDto> {
    return this.request(
      'PUT',
      `/api/items/${encodeURIComponent(itemId)}/prices/${encodeURIComponent(priceListId)}`,
      { body: req },
    );
  }

  /**
   * GET /api/lookups/item-types — owner-gefilterde Type-dropdown
   * (`UE_ItemType`). Niet-system: alleen eigen rijen; System: alle.
   */
  listItemTypeLookups(): Promise<LookupOption[]> {
    return this.request('GET', '/api/lookups/item-types');
  }

  /**
   * GET /api/lookups/item-types-filter — type-bron voor het zoekfilter:
   * System-types + eigen types (System: alle). Anders dan {@link listItemTypeLookups}
   * (owner-only, voor de create-dropdown) zodat een hersteller ook op System-types
   * kan filteren.
   */
  listItemTypeFilterLookups(): Promise<LookupOption[]> {
    return this.request('GET', '/api/lookups/item-types-filter');
  }

  /**
   * GET /api/lookups/item-fk — FK-dropdownbronnen voor de Artikel-tab:
   * Leverancier (owner-gescopet), BTW + Procestype (globale referentie).
   */
  listItemFkLookups(): Promise<ItemFkLookups> {
    return this.request('GET', '/api/lookups/item-fk');
  }

  /**
   * GET /api/lookups/item-lists — owner-gefilterde Artikellijst-dropdown
   * (`UE_ItemList`). Niet-system: alleen eigen rijen; System: alle.
   */
  listItemListLookups(): Promise<LookupOption[]> {
    return this.request('GET', '/api/lookups/item-lists');
  }

  // ─── Inrichting / UE_ItemType ──────────────────────────────────────────

  listItemTypes(): Promise<ItemTypeListItem[]> {
    return this.request('GET', '/api/inrichting/itemtypes');
  }

  getItemTypesSchema(): Promise<EntitySchema> {
    return this.request('GET', '/api/inrichting/itemtypes/schema');
  }

  listAccessTypes(): Promise<AccessTypeOption[]> {
    return this.request('GET', '/api/inrichting/itemtypes/access-types');
  }

  linkItemType(id: string): Promise<void> {
    return this.request('POST', `/api/inrichting/itemtypes/${id}/link`, { body: {} });
  }

  unlinkItemType(id: string): Promise<void> {
    return this.request('DELETE', `/api/inrichting/itemtypes/${id}/link`);
  }

  createItemType(req: ItemTypeWriteRequest): Promise<ItemTypeListItem> {
    return this.request('POST', '/api/inrichting/itemtypes', { body: req });
  }

  updateItemType(id: string, req: ItemTypeWriteRequest): Promise<ItemTypeListItem> {
    return this.request('PUT', `/api/inrichting/itemtypes/${id}`, { body: req });
  }

  getItemType(id: string): Promise<ItemTypeListItem> {
    return this.request('GET', `/api/inrichting/itemtypes/${id}`);
  }

  deleteItemType(id: string): Promise<void> {
    return this.request('DELETE', `/api/inrichting/itemtypes/${id}`);
  }

  // ─── Inrichting / UA_HeadquartersControlReason (controleredenen) ────────

  listControlReasons(): Promise<ControlReason[]> {
    return this.request('GET', '/api/inrichting/control-reasons');
  }

  getControlReasonsSchema(): Promise<EntitySchema> {
    return this.request('GET', '/api/inrichting/control-reasons/schema');
  }

  getControlReason(id: string): Promise<ControlReason> {
    return this.request('GET', `/api/inrichting/control-reasons/${id}`);
  }

  createControlReason(req: ControlReasonWriteRequest): Promise<ControlReason> {
    return this.request('POST', '/api/inrichting/control-reasons', { body: req });
  }

  updateControlReason(
    id: string,
    req: ControlReasonWriteRequest,
  ): Promise<ControlReason> {
    return this.request('PUT', `/api/inrichting/control-reasons/${id}`, { body: req });
  }

  /**
   * DELETE — BE bepaalt soft vs hard. Hard-delete = 204 → resolved met `null`.
   * Soft-delete (reden in gebruik) = 200 + body (`ControlReasonDeleteResult`).
   * De caller toont de juiste melding op basis van de respons.
   */
  deleteControlReason(id: string): Promise<ControlReasonDeleteResult | null> {
    return this.request<ControlReasonDeleteResult | null>(
      'DELETE',
      `/api/inrichting/control-reasons/${id}`,
    );
  }

  // ─── Inkoop / Leveranciers (suppliers) ─────────────────────────────────
  //
  // Story `fe-be-inkoop-leveranciers-beheer`. Proxy over de stock-API. Spiegelt
  // de control-reasons-methodes. Wachtwoord write-only: de FE krijgt 'm nooit
  // terug (alleen `hasPassword`); leeg laten = behoud.

  listSuppliers(): Promise<Supplier[]> {
    return this.request('GET', '/api/suppliers');
  }

  getSuppliersSchema(): Promise<SupplierSchema> {
    return this.request('GET', '/api/suppliers/schema');
  }

  getSupplier(id: string): Promise<Supplier> {
    return this.request('GET', `/api/suppliers/${id}`);
  }

  createSupplier(req: SupplierWriteRequest): Promise<Supplier> {
    return this.request('POST', '/api/suppliers', { body: req });
  }

  updateSupplier(id: string, req: SupplierWriteRequest): Promise<Supplier> {
    return this.request('PUT', `/api/suppliers/${id}`, { body: req });
  }

  /** DELETE = soft-delete (deactiveren) → 200 + body. */
  deleteSupplier(id: string): Promise<SupplierDeleteResult | null> {
    return this.request<SupplierDeleteResult | null>('DELETE', `/api/suppliers/${id}`);
  }

  // ─── Inrichting / Vervangend vervoer (rental cars) ─────────────────────────
  //
  // Story `fe-be-vervangend-vervoer-voertuigen`. Beheer van de huurauto's
  // (legacy `UA_RentalCar`) die een hersteller als vervangend vervoer meegeeft.
  // Writes lopen BE-zijdig via Interaction.UA_RentalCarAdd/Update/Delete
  // (geen raw SQL). Spiegelt de leveranciers-methodes; DELETE = soft-delete
  // (deactiveren → 200 + body).

  listRentalCars(): Promise<RentalCar[]> {
    return this.request('GET', '/api/rental-cars');
  }

  getRentalCar(id: string): Promise<RentalCar> {
    return this.request('GET', `/api/rental-cars/${id}`);
  }

  createRentalCar(req: RentalCarWriteRequest): Promise<RentalCar> {
    return this.request('POST', '/api/rental-cars', { body: req });
  }

  updateRentalCar(id: string, req: RentalCarWriteRequest): Promise<RentalCar> {
    return this.request('PUT', `/api/rental-cars/${id}`, { body: req });
  }

  /** DELETE = soft-delete (deactiveren) → 200 + body. */
  deleteRentalCar(id: string): Promise<RentalCarDeleteResult | null> {
    return this.request<RentalCarDeleteResult | null>('DELETE', `/api/rental-cars/${id}`);
  }

  // ─── Inrichting / Eigen dossiersoorten (UA_CustomDossierType) ──────────────
  //
  // Story `be-fe-eigen-dossiersoorten`. De soorten van de eigen vestiging;
  // writes BE-zijdig via Interaction.UA_CustomDossierTypeAdd/Update/Delete.

  listCustomDossierTypes(): Promise<CustomDossierType[]> {
    return this.request('GET', '/api/inrichting/custom-dossier-types');
  }

  getCustomDossierTypeOptions(): Promise<CustomDossierTypeOptions> {
    return this.request('GET', '/api/inrichting/custom-dossier-types/options');
  }

  getCustomDossierType(id: string): Promise<CustomDossierType> {
    return this.request('GET', `/api/inrichting/custom-dossier-types/${id}`);
  }

  createCustomDossierType(req: CustomDossierTypeWriteRequest): Promise<CustomDossierTypeSaveResponse> {
    return this.request('POST', '/api/inrichting/custom-dossier-types', { body: req });
  }

  updateCustomDossierType(id: string, req: CustomDossierTypeWriteRequest): Promise<CustomDossierTypeSaveResponse> {
    return this.request('PUT', `/api/inrichting/custom-dossier-types/${id}`, { body: req });
  }

  /** Verwijderen, of archiveren wanneer de soort in gebruik is (`outcome`). */
  deleteCustomDossierType(id: string): Promise<CustomDossierTypeDeleteResult> {
    return this.request('DELETE', `/api/inrichting/custom-dossier-types/${id}`);
  }

  // ─── Inrichting / Templates (mail, sms en WhatsApp) ────────────────────────
  //
  // Story `be-fe-templates-beheer`. De templates die de vestiging in de keuzelijsten
  // ziet; writes BE-zijdig via Interaction.UA_ReportAdd/Update/Delete + het
  // parameterpad van de legacy-creators.

  listMessageTemplates(channel: MessageTemplateChannel): Promise<MessageTemplateList> {
    return this.request('GET', '/api/inrichting/message-templates', { query: { channel } });
  }

  getMessageTemplateFields(): Promise<MessageTemplateField[]> {
    return this.request('GET', '/api/inrichting/message-templates/fields');
  }

  getMessageTemplate(id: string): Promise<MessageTemplateDetail> {
    return this.request('GET', `/api/inrichting/message-templates/${id}`);
  }

  createMessageTemplate(req: MessageTemplateWriteRequest): Promise<MessageTemplateDetail> {
    return this.request('POST', '/api/inrichting/message-templates', { body: req });
  }

  updateMessageTemplate(id: string, req: MessageTemplateWriteRequest): Promise<MessageTemplateDetail> {
    return this.request('PUT', `/api/inrichting/message-templates/${id}`, { body: req });
  }

  /** 409 `template_in_use` met `usages` als het template nog ergens gebruikt wordt. */
  deleteMessageTemplate(id: string): Promise<{ outcome: 'deleted' }> {
    return this.request('DELETE', `/api/inrichting/message-templates/${id}`);
  }

  copyMessageTemplate(id: string, name?: string | null): Promise<MessageTemplateDetail> {
    return this.request('POST', `/api/inrichting/message-templates/${id}/copy`, { body: { name: name ?? null } });
  }

  /** Voorbeeld op een dossier van de eigen vestiging; er wordt niets verstuurd of opgeslagen. */
  previewMessageTemplate(req: MessageTemplatePreviewRequest): Promise<MessageTemplatePreview> {
    return this.request('POST', '/api/inrichting/message-templates/preview', { body: req });
  }

  // ─── Inrichting / Monteurs (UA_Fitter) ─────────────────────────────────────
  //
  // Story `fe-monteurs-beheer-crud` (story 1). Monteur-CRUD via de nieuwe
  // FittersController; writes lopen BE-zijdig via Interaction.UA_FitterAdd/
  // Update/Delete (geen raw SQL). De monteur↔gebruiker-koppeling (variant B)
  // loopt via de BESTAANDE S012.7-route `setFitterUser` (PUT resources/{id}/user)
  // — niet hier herbouwd. De lijst-respons draagt óók de kiesbare company-
  // gebruikers (`users`) zodat het scherm geen tweede fetch nodig heeft.

  /** GET /api/inrichting/fitters — monteurs + kiesbare users. `includeInactive`
   *  = ook gedeactiveerde (Archive) monteurs tonen. */
  listFitters(includeInactive = false): Promise<FittersListResponse> {
    const q = includeInactive ? '?includeInactive=true' : '';
    return this.request('GET', `/api/inrichting/fitters${q}`);
  }

  getFitter(id: string): Promise<Fitter> {
    return this.request('GET', `/api/inrichting/fitters/${encodeURIComponent(id)}`);
  }

  createFitter(req: FitterWriteRequest): Promise<Fitter> {
    return this.request('POST', '/api/inrichting/fitters', { body: req });
  }

  updateFitter(id: string, req: FitterWriteRequest): Promise<Fitter> {
    return this.request('PUT', `/api/inrichting/fitters/${encodeURIComponent(id)}`, { body: req });
  }

  /** PUT /api/inrichting/fitters/{id}/status — (de)activeren (Active↔Archive).
   *  Idempotent op de BE. */
  setFitterStatus(id: string, status: FitterStatusValue): Promise<Fitter> {
    return this.request('PUT', `/api/inrichting/fitters/${encodeURIComponent(id)}/status`, {
      body: { status },
    });
  }

  /** DELETE /api/inrichting/fitters/{id} — hard-delete (0 koppelingen) óf
   *  soft-fallback (≥1 koppeling → deactiveren). De respons onderscheidt de twee
   *  takken (`deleted` / `deactivated`). */
  deleteFitter(id: string): Promise<FitterDeleteResult | null> {
    return this.request<FitterDeleteResult | null>(
      'DELETE',
      `/api/inrichting/fitters/${encodeURIComponent(id)}`,
    );
  }

  /**
   * POST /api/inrichting/fitters/{id}/create-user — maakt een NIEUWE inlog-
   * gebruiker voor een BESTAANDE monteur (story `fe-monteurs-nieuwe-gebruiker`,
   * variant C). De gebruikersnaam wordt server-side bepaald; de respons draagt de
   * read-only `loginName` + het `temporaryPassword` (ÉÉNMALIG te tonen) +
   * `emailSent`. 409 `user_limit_reached` (15-grens) / `fitter_already_linked`;
   * 400 bij ontbrekende/ongeldige velden (BE her-valideert, defense-in-depth).
   */
  /**
   * PUT /api/inrichting/fitters/{id}/fitter-only — zet "Alleen monteur-app" van de
   * gebruiker die aan deze monteur gekoppeld is (story `fe-be-monteur-s1-basis`).
   * 404 `fitter_not_found` / `fitter_user_not_linked`; 422
   * `fitter_only_self_not_allowed` / `fitter_only_system_user_not_allowed` (alleen bij
   * `true`). CSRF-header automatisch.
   */
  setFitterOnly(id: string, isFitterOnly: boolean): Promise<FitterOnlyResult> {
    const body: FitterOnlyRequest = { isFitterOnly };
    return this.request('PUT', `/api/inrichting/fitters/${encodeURIComponent(id)}/fitter-only`, { body });
  }

  /**
   * POST /api/fitter/capability/refresh — de heel-route achter "Opnieuw proberen" op
   * `/monteur` (TA-review F-2). Herberekent de monteur-capability op de sessie, ook
   * binnen de fout-TTL. Haal daarna whoami opnieuw op.
   */
  refreshFitterCapability(): Promise<FitterCapabilityRefreshResponse> {
    return this.request('POST', '/api/fitter/capability/refresh', { body: {} });
  }

  /**
   * Story `fe-be-monteur-s5a-agenda-en-meer` (S021.5a) — GET /api/fitter/agenda: de
   * afspraken van de eigen monteur(s) op één dag. Zonder `date` kiest de SERVER de dag
   * van vandaag (PB-4) — de FE zet dan bewust niets op de draad.
   */
  getFitterAgenda(date?: string | null, signal?: AbortSignal): Promise<FitterAgendaResponse> {
    const query: Record<string, string | undefined> = {};
    if (date) query.date = date;
    return this.request('GET', '/api/fitter/agenda', { query, signal });
  }

  /**
   * Story `fe-be-monteur-s8a-nieuw-dossier` (S021.8a) — POST /api/fitter/dossiers: een nieuw
   * dossier vanuit de monteur-app met alleen kenteken en soort werk. 201 = nieuw, 200 met
   * `replayed: true` = een herhaling van dezelfde `clientRequestId`. CSRF-header automatisch.
   */
  createFitterDossier(req: FitterDossierCreateRequest): Promise<FitterDossierCreateResponse> {
    return this.request('POST', '/api/fitter/dossiers', { body: req });
  }

  /**
   * Story `fe-be-monteur-s5b-installeerbaar` (S021.5b §4) — GET /api/fitter/branding: naam,
   * kleur en iconen van de monteur-app. Anoniem en lock-vrij (wacht niet op de sessie-lock);
   * ook vóór het inloggen bruikbaar.
   */
  getFitterBranding(signal?: AbortSignal): Promise<FitterBranding> {
    return this.request('GET', '/api/fitter/branding', { signal });
  }

  createFitterUser(id: string, req: FitterCreateUserRequest): Promise<FitterCreateUserResult> {
    return this.request('POST', `/api/inrichting/fitters/${encodeURIComponent(id)}/create-user`, {
      body: req,
    });
  }

  // ─── Polis-reply-mailbox (MailAccount) ─────────────────────────────────────

  listMailAccounts(): Promise<MailAccount[]> {
    return this.request('GET', '/api/inrichting/mail-accounts');
  }

  getMailAccount(id: string): Promise<MailAccount> {
    return this.request('GET', `/api/inrichting/mail-accounts/${id}`);
  }

  createMailAccount(req: MailAccountWriteRequest): Promise<MailAccount> {
    return this.request('POST', '/api/inrichting/mail-accounts', { body: req });
  }

  updateMailAccount(id: string, req: MailAccountWriteRequest): Promise<MailAccount> {
    return this.request('PUT', `/api/inrichting/mail-accounts/${id}`, { body: req });
  }

  deleteMailAccount(id: string): Promise<null> {
    return this.request<null>('DELETE', `/api/inrichting/mail-accounts/${id}`);
  }

  /**
   * GET /api/inrichting/mail-accounts/repairers — read-only lookup voor de
   * hersteller-picker (story `fe-be-herstellerbijlagen-mailbox`). System-only.
   * Levert per hersteller de bedrijfsnaam + RepairerCode (besluit (b)).
   */
  listMailRepairers(): Promise<MailRepairer[]> {
    return this.request('GET', '/api/inrichting/mail-accounts/repairers');
  }

  /**
   * POST /api/inrichting/mail-accounts/test-connection — controleert (read-only,
   * test vóór opslaan) of de Microsoft-Graph-koppeling het postvak kan lezen met
   * de HUIDIGE form-velden. Stuur `id` mee + leeg `clientSecret` om het opgeslagen
   * secret te hergebruiken. De respons bevat NOOIT het secret/token — alleen een
   * schone message + de deel-checks.
   */
  testMailAccountConnection(req: MailConnectionTestRequest): Promise<MailConnectionTestResult> {
    return this.request('POST', '/api/inrichting/mail-accounts/test-connection', { body: req });
  }

  // ─── NH1816-partijstructuur (DA_Chain / DA_Branch) ──────────────────────
  //
  // Story `be-fe-nh1816-partijstructuur-en-aanlevering`. System-only — de
  // server-gate is de poort; de route-gate is voor de gebruiker.
  // ⚠ Er is bewust GEEN verwijder-endpoint voor een keten of een vestiging
  //   (AC-19): daar hangen submissions aan. Een KENMERK en een CODE zijn wél
  //   verwijderbaar — zonder dat is de unieke kenmerk-index nooit meer vrij te
  //   geven wanneer een vestiging sluit en een andere op hetzelfde IBAN doorstart.

  listDeclarationChains(): Promise<DeclarationChain[]> {
    return this.request('GET', '/api/system/declaration-parties/chains');
  }

  createDeclarationChain(req: DeclarationChainWriteRequest): Promise<DeclarationChain> {
    return this.request('POST', '/api/system/declaration-parties/chains', { body: req });
  }

  updateDeclarationChain(id: string, req: DeclarationChainWriteRequest): Promise<DeclarationChain> {
    return this.request('PUT', `/api/system/declaration-parties/chains/${encodeURIComponent(id)}`, {
      body: req,
    });
  }

  listDeclarationBranches(chainId?: string): Promise<DeclarationBranch[]> {
    const qs = chainId ? `?chainId=${encodeURIComponent(chainId)}` : '';
    return this.request('GET', `/api/system/declaration-parties/branches${qs}`);
  }

  getDeclarationBranch(id: string): Promise<DeclarationBranch> {
    return this.request('GET', `/api/system/declaration-parties/branches/${encodeURIComponent(id)}`);
  }

  createDeclarationBranch(req: DeclarationBranchWriteRequest): Promise<DeclarationBranch> {
    return this.request('POST', '/api/system/declaration-parties/branches', { body: req });
  }

  updateDeclarationBranch(id: string, req: DeclarationBranchWriteRequest): Promise<DeclarationBranch> {
    return this.request('PUT', `/api/system/declaration-parties/branches/${encodeURIComponent(id)}`, {
      body: req,
    });
  }

  listDeclarationTraits(branchId: string): Promise<DeclarationTrait[]> {
    return this.request(
      'GET',
      `/api/system/declaration-parties/branches/${encodeURIComponent(branchId)}/traits`,
    );
  }

  createDeclarationTrait(
    branchId: string,
    req: DeclarationTraitWriteRequest,
  ): Promise<DeclarationTrait> {
    return this.request(
      'POST',
      `/api/system/declaration-parties/branches/${encodeURIComponent(branchId)}/traits`,
      { body: req },
    );
  }

  deleteDeclarationTrait(id: string): Promise<null> {
    return this.request<null>(
      'DELETE',
      `/api/system/declaration-parties/traits/${encodeURIComponent(id)}`,
    );
  }

  listDeclarationCodes(branchId: string): Promise<DeclarationBranchCode[]> {
    return this.request(
      'GET',
      `/api/system/declaration-parties/branches/${encodeURIComponent(branchId)}/codes`,
    );
  }

  createDeclarationCode(
    branchId: string,
    req: DeclarationCodeWriteRequest,
  ): Promise<DeclarationBranchCode> {
    return this.request(
      'POST',
      `/api/system/declaration-parties/branches/${encodeURIComponent(branchId)}/codes`,
      { body: req },
    );
  }

  deleteDeclarationCode(id: string): Promise<null> {
    return this.request<null>(
      'DELETE',
      `/api/system/declaration-parties/codes/${encodeURIComponent(id)}`,
    );
  }

  /** Keuzelijst verzekeraars. ⚠ De name wordt alleen GETOOND en nooit in een
   *  `DA_`-kolom gekopieerd — anders is er een tweede verzekeraarslijst (AC-3). */
  listDeclarationInsurers(): Promise<DeclarationPartyOption[]> {
    return this.request('GET', '/api/system/declaration-parties/insurers');
  }

  /** Keuzelijst herstellers voor de optionele koppeling (AC-12). ⚠ Die koppeling
   *  verandert NIETS aan de code-resolutie: de code komt uitsluitend uit
   *  `DA_BranchCode`. */
  listDeclarationRepairers(): Promise<DeclarationPartyOption[]> {
    return this.request('GET', '/api/system/declaration-parties/repairers');
  }

  // ─── Keten-herkenningsmethode (K2/AC-8, story mail-intake-declaraties) ─────
  //
  // ⚠ Leeg is toegestaan: een keten zonder enkele methode is geldig, en dan is
  //   de outcome later "niet te herleiden" in plaats van een gok. Een ONBEKENDE
  //   kind wordt geweigerd met `kind_unknown`.

  listDeclarationChainRecognition(chainId: string): Promise<DeclarationChainRecognition[]> {
    return this.request(
      'GET',
      `/api/system/declaration-parties/chains/${encodeURIComponent(chainId)}/recognition`,
    );
  }

  createDeclarationChainRecognition(
    chainId: string,
    req: DeclarationChainRecognitionWriteRequest,
  ): Promise<DeclarationChainRecognition> {
    return this.request(
      'POST',
      `/api/system/declaration-parties/chains/${encodeURIComponent(chainId)}/recognition`,
      { body: req },
    );
  }

  deleteDeclarationChainRecognition(id: string): Promise<null> {
    return this.request<null>(
      'DELETE',
      `/api/system/declaration-parties/recognition/${encodeURIComponent(id)}`,
    );
  }

  /** Het vocabulaire van herkenningssoorten. ⚠ Wordt van de server gehaald en
   *  niet in de FE gedupliceerd: een gedupliceerde lijst loopt stil achter zodra
   *  er een kind bijkomt, en dan bestaat er een geldige kind die niemand kan
   *  kiezen. */
  listDeclarationRecognitionKinds(): Promise<string[]> {
    return this.request('GET', '/api/system/declaration-parties/recognition-kinds');
  }

  // ─── Declaratie-mail-intake (story `be-fe-nh1816-mail-intake-declaraties`) ──
  //
  // System-only; de server-gate is de poort, de route-gate is voor de gebruiker.
  // ⚠ READ-ONLY: er is bewust géén bewerk-, verwijder- of opnieuw-proberen-
  //   endpoint — dat is de werkvoorraad en die valt buiten scope.

  /**
   * GET /api/system/declaration-submissions — de twee helften van de lijst:
   * de ontvangen submissions én de messages waar géén rij uit voortkwam.
   *
   * ⚠⚠ Die tweede helft is geen extraatje. Álle vier de faalvormen van deze
   * story leveren géén aanlevering op, dus zonder haar is "leeg" niet te
   * onderscheiden van "het mechanisme staat stil".
   */
  getDeclarationIntakeOverview(max?: number): Promise<DeclarationIntakeOverview> {
    const qs = max ? `?max=${encodeURIComponent(String(max))}` : '';
    return this.request('GET', `/api/system/declaration-submissions${qs}`);
  }

  /** Het pad naar de PDF van één aanlevering — een eigen, `system`-gepoort
   *  endpoint (AC-15), géén statisch pad en géén `GD_Attachment`-download. */
  declarationSubmissionPdfUrl(id: string): string {
    return `/api/system/declaration-submissions/${encodeURIComponent(id)}/pdf`;
  }

  /**
   * POST /api/system/declaration-resolution/{id}/link — story
   * `be-fe-nh1816-vestiging-herleiden` (AC-9 t/m AC-11).
   *
   * Koppelt de aanlevering aan een vestiging, legt het fromAddress-kenmerk vast, en
   * beoordeelt in DEZELFDE handeling de overige wachtende submissions opnieuw.
   *
   * ⚠⚠ Er gaat GEEN afzenderadres mee. Dat is geen omissie maar de hele borg:
   * het adres komt van de RIJ (`DA_Submission.Afzender`). Zou het uit de
   * browser komen, dan is een routeringssleutel clientinvoer en kan een
   * gebruiker rij X aan adres Y koppelen zonder dat iets dat tegenhoudt.
   *
   * ⚠ Een EIGEN route-prefix (`declaratie-herleiding`), niet die van de lijst:
   * de lijst is en blijft read-only, en twee controllers op exact dezelfde route
   * leveren in Web API een 500 op.
   */
  linkDeclarationSubmission(
    submissionId: string,
    branchId: string,
  ): Promise<DeclarationLinkResult> {
    return this.request(
      'POST',
      `/api/system/declaration-resolution/${encodeURIComponent(submissionId)}/link`,
      { body: { branchId } },
    );
  }

  // ─── Systeemberichten (GS_SystemMessage) ───────────────────────────────────

  /**
   * GET /api/system/messages/active — de banner-feed: GLOBAAL, alle actieve
   * messages (`dateFrom <= nu <= dateTill`). Bereikbaar voor ELKE ingelogde
   * gebruiker (geen system-gate, geen company/rol-filter).
   */
  listActiveSystemMessages(): Promise<ActiveSystemMessage[]> {
    return this.request('GET', '/api/system/messages/active');
  }

  /** GET /api/system/messages — beheer-lijst (alle berichten). System-only. */
  listSystemMessages(): Promise<SystemMessage[]> {
    return this.request('GET', '/api/system/messages');
  }

  /** GET /api/system/messages/{id} — detail voor het bewerk-formulier. System-only. */
  getSystemMessage(id: string): Promise<SystemMessage> {
    return this.request('GET', `/api/system/messages/${id}`);
  }

  /** POST /api/system/messages — aanmaken. System-only. */
  createSystemMessage(req: SystemMessageWriteRequest): Promise<SystemMessage> {
    return this.request('POST', '/api/system/messages', { body: req });
  }

  /** PUT /api/system/messages/{id} — bewerken. System-only. */
  updateSystemMessage(id: string, req: SystemMessageWriteRequest): Promise<SystemMessage> {
    return this.request('PUT', `/api/system/messages/${id}`, { body: req });
  }

  /** DELETE /api/system/messages/{id} — verwijderen. System-only. */
  deleteSystemMessage(id: string): Promise<null> {
    return this.request<null>('DELETE', `/api/system/messages/${id}`);
  }

  // ─── Nieuwsberichten (GS_NewsMessage) ──────────────────────────────────────

  /**
   * GET /api/news/messages/active — de GESCOOPTE actieve feed (dashboard + menu +
   * popup). Elke ingelogde user; de BE filtert server-side op keten-audience +
   * het actieve datumvenster en sorteert op de gedeelde feed-sleutel
   * (DateFrom desc, Created desc).
   */
  listActiveNews(): Promise<NewsMessage[]> {
    return this.request('GET', '/api/news/messages/active');
  }

  /** GET /api/news/messages — beheer-lijst (system ‖ keten; keten-gescoopt). */
  listNewsMessages(): Promise<NewsMessage[]> {
    return this.request('GET', '/api/news/messages');
  }

  /**
   * GET /api/news/messages/{id} — detail (met bijlagen). Beheerder óf
   * zichtbaar-voor-lezer; anders 404 (geen bestaan-lek).
   */
  getNewsMessage(id: string, signal?: AbortSignal): Promise<NewsMessage> {
    return this.request('GET', `/api/news/messages/${encodeURIComponent(id)}`, { signal });
  }

  /** POST /api/news/messages — aanmaken. System ‖ keten. */
  createNewsMessage(req: NewsMessageWriteRequest): Promise<NewsMessage> {
    return this.request('POST', '/api/news/messages', { body: req });
  }

  /** PUT /api/news/messages/{id} — bewerken (version-guard → 409). System ‖ keten. */
  updateNewsMessage(id: string, req: NewsMessageWriteRequest): Promise<NewsMessage> {
    return this.request('PUT', `/api/news/messages/${encodeURIComponent(id)}`, { body: req });
  }

  /** DELETE /api/news/messages/{id} — verwijderen (+ cascade bijlagen). */
  deleteNewsMessage(id: string): Promise<null> {
    return this.request<null>('DELETE', `/api/news/messages/${encodeURIComponent(id)}`);
  }

  /** POST /api/news/messages/{id}/attachments — multipart-upload van bijlage(n). */
  uploadNewsAttachments(messageId: string, files: File[], signal?: AbortSignal): Promise<{ ids: string[]; count: number }> {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f, f.name));
    return this.request(
      'POST',
      `/api/news/messages/${encodeURIComponent(messageId)}/attachments`,
      { formData: fd, signal },
    );
  }

  /** DELETE /api/news/messages/attachments/{id} — één bijlage verwijderen. */
  deleteNewsAttachment(id: string): Promise<void> {
    return this.request('DELETE', `/api/news/messages/attachments/${encodeURIComponent(id)}`);
  }

  // ─── Inrichting / UE_PriceList ─────────────────────────────────────────

  listPriceLists(): Promise<PriceListListItem[]> {
    return this.request('GET', '/api/inrichting/pricelists');
  }

  getPriceListsSchema(): Promise<EntitySchema> {
    return this.request('GET', '/api/inrichting/pricelists/schema');
  }

  linkPriceList(id: string): Promise<void> {
    return this.request('POST', `/api/inrichting/pricelists/${id}/link`, { body: {} });
  }

  unlinkPriceList(id: string): Promise<void> {
    return this.request('DELETE', `/api/inrichting/pricelists/${id}/link`);
  }

  createPriceList(req: PriceListWriteRequest): Promise<PriceListListItem> {
    return this.request('POST', '/api/inrichting/pricelists', { body: req });
  }

  updatePriceList(id: string, req: PriceListWriteRequest): Promise<PriceListListItem> {
    return this.request('PUT', `/api/inrichting/pricelists/${id}`, { body: req });
  }

  getPriceList(id: string): Promise<PriceListListItem> {
    return this.request('GET', `/api/inrichting/pricelists/${id}`);
  }

  deletePriceList(id: string): Promise<void> {
    return this.request('DELETE', `/api/inrichting/pricelists/${id}`);
  }

  /**
   * GET /api/inrichting/pricelists/{id}/tiers — de staffel van een prijslijst:
   * de banden per glassoort plus of je ze mag wijzigen (story S1).
   */
  getPriceListTiers(id: string): Promise<PriceListTiersResponse> {
    return this.request('GET', `/api/inrichting/pricelists/${encodeURIComponent(id)}/tiers`);
  }

  /**
   * PUT /api/inrichting/pricelists/{id}/tiers — vervangt de HELE set banden en
   * schrijft de bijbehorende prijsregel (story S2). Een lege `groups` gedraagt
   * zich als de DELETE.
   */
  updatePriceListTiers(
    id: string,
    req: PriceListTiersWriteRequest,
  ): Promise<{ ok: boolean; unitPriceRuleKind: string }> {
    return this.request('PUT', `/api/inrichting/pricelists/${encodeURIComponent(id)}/tiers`, {
      body: req,
    });
  }

  /**
   * DELETE /api/inrichting/pricelists/{id}/tiers — banden weg én de prijsregel
   * leeg, zodat de prijslijst terugkeert naar "nog geen staffel".
   */
  deletePriceListTiers(id: string): Promise<{ ok: boolean; unitPriceRuleKind: string }> {
    return this.request('DELETE', `/api/inrichting/pricelists/${encodeURIComponent(id)}/tiers`);
  }

  // ─── Inrichting / prijslijst op leveranciersprijzen ──────────────────

  /**
   * GET /api/inrichting/pricelists/{id}/supplier-prices — de inrichting van de
   * leveranciersprijzen plus alles wat het scherm nodig heeft om te bepalen wat
   * er wél en niet kan (`tagAvailable`, `suppliersUnavailable`, `ownPriceCount`).
   *
   * Deze GET valt nooit om op een onbereikbare stock-API: dan komt er
   * `suppliersUnavailable: true` met een lege lijst, en blijven `mode`,
   * `rounding` en `supplierIds` gewoon gevuld — anders zou de gebruiker de
   * inrichting niet eens kunnen zien, laat staan uitzetten.
   */
  getPriceListSupplierPrices(id: string): Promise<PriceListSupplierPricesResponse> {
    return this.request(
      'GET',
      `/api/inrichting/pricelists/${encodeURIComponent(id)}/supplier-prices`,
    );
  }

  /**
   * PUT /api/inrichting/pricelists/{id}/supplier-prices — vervangt de hele
   * inrichting. `isEnabled: false` schrijft een LEGE prijsregel en is de
   * nooduitgang: de backend slaat dan de beschikbaarheids- en invoercontroles
   * bewust over.
   */
  updatePriceListSupplierPrices(
    id: string,
    req: PriceListSupplierPricesWriteRequest,
  ): Promise<{ ok: boolean; unitPriceRuleKind: string }> {
    return this.request(
      'PUT',
      `/api/inrichting/pricelists/${encodeURIComponent(id)}/supplier-prices`,
      { body: req },
    );
  }

  /** DELETE /api/inrichting/pricelists/{id}/supplier-prices — maakt de prijsregel leeg. */
  deletePriceListSupplierPrices(id: string): Promise<{ ok: boolean; unitPriceRuleKind: string }> {
    return this.request(
      'DELETE',
      `/api/inrichting/pricelists/${encodeURIComponent(id)}/supplier-prices`,
    );
  }

  /**
   * POST /api/inrichting/pricelists/{id}/supplier-prices/preview — vraagt één
   * eurocode op bij de gekozen leveranciers.
   *
   * ⚠⚠ **Niet bijwerkingsvrij.** Hij wijzigt geen prijslijst en geen prijs-rij,
   * maar de legacy-bevraging schrijft élk antwoord in de gedeelde prijscache —
   * óók een `0` wanneer een leverancier niets geeft. Wat hier opgehaald wordt
   * geldt dus ook voor de echte calculatie zolang die prijs bewaard blijft.
   */
  previewPriceListSupplierPrices(
    id: string,
    req: PriceListSupplierPricePreviewRequest,
  ): Promise<PriceListSupplierPricePreviewResponse> {
    return this.request(
      'POST',
      `/api/inrichting/pricelists/${encodeURIComponent(id)}/supplier-prices/preview`,
      { body: req },
    );
  }

  // ─── S009.5 — Items-search + add-row ───────────────────────────────────

  /**
   * GET /api/items/search — autocomplete-feed voor de "+ nieuwe regel"-strook
   * op de Calculatie-tab (story S009.5).
   *
   * @param prefix Genormaliseerde prefix (FE-side al getrimd + >= 3 chars).
   * @param pricelistId Verplicht — server gebruikt 'm voor cross-tenant-scope
   *   en (toekomstig) voor prijs-preview per suggestie.
   * @param calculationCode Strict-lowercase `"filiaal"` — andere waardes → 400.
   * @param limit 1..25, default 10. Server clampt zelf bij out-of-range.
   * @param signal Optionele AbortSignal — bij nieuwe keystroke aborteert de
   *   hook de vorige call (M-eur.03; voorkomt out-of-order rendering).
   */
  searchItems(
    prefix: string,
    pricelistId: string,
    calculationCode: string = 'filiaal',
    limit: number = 10,
    signal?: AbortSignal,
  ): Promise<ItemSuggestionDto[]> {
    return this.request('GET', '/api/items/search', {
      query: { prefix, pricelistId, calculationCode, limit },
      signal,
    });
  }

  /**
   * GET /api/eurocodes/search — vrij zoeken in de AutoQraft-glascatalogus
   * (story S015.5, EPIC-015). Spiegelt {@link searchItems}: querystring +
   * optionele AbortSignal.
   *
   * @param q Vrije-tekst-zoekgrond (merk/model/omschrijving of (deel van een)
   *   eurocode). De caller dient zelf te gaten op `q.trim().length >= 3` (of
   *   een gevuld merk/model); de BE geeft anders 400 `query_too_short`.
   * @param merk Optionele merk-filter (exact-veld). Weggelaten wanneer leeg.
   * @param model Optionele model-filter (exact-veld). Weggelaten wanneer leeg.
   * @param page 1-based pagina (default 1).
   * @param pageSize Paginagrootte (default 25 = BE-default).
   * @param signal Optionele AbortSignal — bij een nieuwe zoekterm aborteert de
   *   hook de vorige call (laatste-wint, geen out-of-order rendering).
   *
   * **Fail-soft**: een 200 met `reason:"not_configured"` (geen API-key
   * ingericht) komt gewoon als resolved value binnen (`items:[]`); een echte
   * upstream-fout (502) gooit een typed error via de wrapper-laag.
   */
  searchEurocodes(
    q: string,
    merk?: string,
    model?: string,
    page: number = 1,
    pageSize: number = 25,
    signal?: AbortSignal,
  ): Promise<EurocodeSearchResponseDto> {
    return this.request('GET', '/api/eurocodes/search', {
      // Lege merk/model worden weggelaten (request() skipt `undefined`-waarden).
      query: {
        q,
        merk: merk && merk.length > 0 ? merk : undefined,
        model: model && model.length > 0 ? model : undefined,
        page,
        pageSize,
      },
      signal,
    });
  }

  /**
   * GET /api/eurocodes/suggest — chassis-voorstel uit de AutoQraft-lookup
   * (story S015.6, EPIC-015). Spiegelt {@link searchEurocodes}: querystring +
   * optionele AbortSignal. Levert de meest waarschijnlijke eurocode(s) voor een
   * chassisnummer + schadepositie.
   *
   * @param chassis Het chassisnummer (VIN). De caller dient zelf te gaten op
   *   `chassis.trim().length >= 9` (mirror BE `MinChassisLength`); de BE 400't
   *   anders `chassis_too_short`.
   * @param positie De schadepositie-ID (1:1 met de CarPosition-zones — géén
   *   vertaaltabel). > 0 vereist; de caller gate't dat.
   * @param signal Optionele AbortSignal — bij een positie-wijziging aborteert
   *   de hook de vorige call (laatste-wint, geen out-of-order rendering).
   *
   * **Fail-soft**: een 200 met `reason:"not_configured"` (geen API-key
   * ingericht) komt gewoon als resolved value binnen (`resultaten:[]`); een
   * echte upstream-fout (502) gooit een typed error via de wrapper-laag.
   */
  suggestEurocodes(
    chassis: string,
    positie: number,
    signal?: AbortSignal,
  ): Promise<EurocodeSuggestResponseDto> {
    return this.request('GET', '/api/eurocodes/suggest', {
      query: { chassis, positie },
      signal,
    });
  }

  /**
   * POST /api/dossiers/{dossierId}/calculation-rows — voegt één
   * calculatie-regel toe (story S009.5, AC 4.1).
   *
   * Body is een discriminator-payload:
   *   - `kind: "byItemId"` met `itemId` — directe item-resolve.
   *   - `kind: "byCode"` met `code` — natural-key-resolve; 404 ItemNotFound
   *     bij onbekende code (AC 3.2).
   *   - `kind: "vt"` — lege VT-rij (AC 4.5).
   *
   * Server retourneert 201 + `AddCalculationRowResponseDto`. 404 / 5xx /
   * 403 (CSRF) gooien typed errors via de wrapper-laag.
   */
  addCalculationRow(
    dossierId: string,
    body: AddCalculationRowRequestDto,
    /**
     * Optionele voortgang-id. Aanwezig → meegestuurd als
     * `X-Calc-Progress-Id`-header zodat de BE de calc-fasen naar de
     * poll-store schrijft (uit te lezen via {@link getCalcProgress}).
     * Afwezig → exact het oude gedrag (geen tracking).
     */
    progressId?: string,
  ): Promise<AddCalculationRowResponseDto> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(dossierId)}/calculation-rows`,
      {
        body,
        headers: progressId ? { 'X-Calc-Progress-Id': progressId } : undefined,
      },
    );
  }

  // ─── Losse calculatie ("kladblok") — story fe-calculatie-kladblok-glas ──

  /**
   * POST /api/calculation/calculate — rekent ÉÉN regel ZONDER dossier
   * (Story 1 "kladblok"). Het endpoint wrapt uitsluitend legacy
   * `Interaction.GetCalculationForItem(...)` (geen `UA_DossierSaveCalculation`,
   * geen raw SQL); set-/child-componenten (bv. Plakset bij een ruit-eurocode)
   * komen automatisch mee in het resultaat. Response = exact dezelfde
   * row-DTO-vorm als `getDossierCalculationRows` (incl. `childFrom`-relatie).
   *
   * @param progressId Optionele voortgang-id (`X-Calc-Progress-Id`) zodat de
   *   BE de calc-fasen naar de poll-store schrijft — uit te lezen via
   *   {@link getCalcProgress} (gedeeld met het dossier-add-pad).
   */
  calculate(
    body: import('./types.js').CalculateRequestDto,
    progressId?: string,
  ): Promise<CalculationRowDto[]> {
    return this.request('POST', '/api/calculation/calculate', {
      body,
      headers: progressId ? { 'X-Calc-Progress-Id': progressId } : undefined,
    });
  }

  /**
   * POST /api/calculation/calculate-batch — bulk-variant van {@link calculate}
   * voor de extra-/eigen-artikelen-flow. Body spiegelt veld-voor-veld de
   * single-body (hard-rule "bulk mirrors single shape"). Response = de
   * berekende regels (parent + child-componenten) in row-DTO-vorm.
   */
  calculateBatch(
    body: import('./types.js').CalculateBatchRequestDto,
  ): Promise<CalculationRowDto[]> {
    return this.request('POST', '/api/calculation/calculate-batch', { body });
  }

  /**
   * Zelfde call als {@link calculateBatch}, plus de aangevraagde id's die niets
   * opleverden (KB-06, 19-10-2026): de BE noemt ze in de response-header
   * `X-Calculation-Skipped-Items` (komma-gescheiden; geen header = niets
   * overgeslagen). Het antwoord zelf blijft dezelfde array, dus dit is additief.
   * Foutpaden (bv. 422 `NoPriceAvailable` met `skippedItemIds` in de body) gooien
   * zoals altijd een `ApiError`.
   */
  async calculateBatchDetailed(
    body: import('./types.js').CalculateBatchRequestDto,
  ): Promise<import('./types.js').CalculateBatchResultDto> {
    const res = await this.request<Response>('POST', '/api/calculation/calculate-batch', {
      body,
      responseType: 'raw',
    });
    const ct = res.headers.get('content-type') ?? '';
    const rows = ct.includes('application/json') ? ((await res.json()) as CalculationRowDto[]) : [];
    return {
      rows: Array.isArray(rows) ? rows : [],
      skippedItemIds: parseSkippedItemsHeader(res.headers.get('X-Calculation-Skipped-Items')),
    };
  }

  /**
   * POST /api/calculation/create-dossier — promoveert het wegwerp-kladblok tot
   * één echt regulier `UA_Dossier` (status 210) en persisteert de verzamelde
   * regels in ÉÉN `UA_DossierSaveCalculation` (story `fe-calculatie-kladblok-
   * glas`, Story 2 / AC-8). Body = de kladblok-regels (kind + item-id/code +
   * inline-edits) + pricelistId/vatId + optioneel kenteken/chassis/verzekeraar.
   * Respons = het nieuwe dossier-id (FE navigeert naar `/dossiers/{id}`) + het
   * aantal gepersisteerde regels. Set-/child-componenten (bv. Plakset) leidt de
   * BE af via `GetCalculationForItem` — niet door de FE meegestuurd.
   */
  createDossierFromKladblok(
    body: import('./types.js').CreateDossierFromKladblokRequestDto,
  ): Promise<import('./types.js').CreateDossierFromKladblokResponseDto> {
    return this.request('POST', '/api/calculation/create-dossier', { body });
  }

  /**
   * POST /api/calculation/print — drukt het kladblok af (story
   * `be-fe-kladblok-afdrukken`). De BE maakt onderwater een calculatie-dossier
   * aan (eigen nummerreeks, geen workflow, nergens zichtbaar) en geeft de PDF van
   * het document "Calculatie" terug als `Blob`. Foutbodies blijven JSON →
   * `ApiError` met `body.reason` (o.a. `print_report_not_configured`).
   */
  printKladblok(body: import('./types.js').KladblokPrintRequestDto): Promise<Blob> {
    return this.request('POST', '/api/calculation/print', { body, responseType: 'blob' });
  }

  /**
   * POST /api/vehicles/lookup-chassis — dossier-vrije chassis-lookup voor het
   * kladblok. Body `{ license }`, respons `{ status, chassisNumber?, error? }`.
   * Spiegelt het dossier-gebonden {@link lookupChassis} maar zonder
   * version/delta — er is nog geen dossier om in te schrijven.
   */
  lookupChassisNoDossier(
    body: import('./types.js').ChassisLookupNoDossierRequest,
  ): Promise<import('./types.js').ChassisLookupNoDossierResponse> {
    return this.request('POST', '/api/vehicles/lookup-chassis', { body });
  }

  /**
   * Pollt de voortgang van een lopende calc-POST. `operationId` is de id die
   * als `X-Calc-Progress-Id` is meegestuurd aan {@link addCalculationRow}.
   * `found:false` = nog niets / verlopen; `done:true` = calc afgerond.
   */
  getCalcProgress(operationId: string): Promise<CalcProgressResponseDto> {
    return this.request(
      'GET',
      `/api/calc-progress/${encodeURIComponent(operationId)}`,
    );
  }

  /**
   * GET /api/dossiers/{dossierId}/additive-groups — extra-artikelen-modal,
   * story `extra-artikelen-modal`.
   *
   * Levert de Standaard/Eigen/Keten-groepen die voor de huidige session en
   * dit dossier zichtbaar zijn (`UA_DossierSelect`-access-keten). FE gebruikt
   * dit voor de modal-tab-strip + categorie-lijst.
   *
   * Cache met `staleTime: 5min` op de FE — beheer-actie, muteert zelden.
   */
  getAdditiveGroups(dossierId: string): Promise<AdditiveGroupsResponseDto> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/additive-groups`,
    );
  }

  /**
   * POST /api/dossiers/{dossierId}/calculation-rows/bulk-add — atomic
   * multi-item add vanuit de Extra-artikelen-modal.
   *
   * Server-side:
   *   - Dedupe `additiveItemIds` (M-eam.08).
   *   - IDOR-guard: elke ID moet in de "wat-mag-de-user-zien"-set zitten
   *     (anders 422 InvalidAdditiveItemId — AC 4.6).
   *   - PolicyService-mapping naar de juiste ItemCode (Carculate/Exchange/
   *     Dispatch); items zonder ItemCode → skip (AC 4.3, NIET een error).
   *   - Eén `UA_DossierSaveCalculation`-call (atomic op de UE_Calculation).
   *
   * Response = 201 (Created) wanneer er minstens 1 rij is toegevoegd, anders
   * 200 (OK) bij all-skip. FE leest `addedCount` / `requestedCount` voor de
   * toast (M-eam.01 — geen requestedCount-leak).
   */
  bulkAddCalculationRows(
    dossierId: string,
    body: BulkAddCalculationRowsRequestDto,
  ): Promise<BulkAddCalculationRowsResponseDto> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(dossierId)}/calculation-rows/bulk-add`,
      { body },
    );
  }

  /**
   * `POST /api/gross-price/refresh` — haal de brutoprijs van één eurocode
   * binnen dezelfde dag opnieuw op (story `be-fe-brutoprijs-bij-calculeren`,
   * scenario 6).
   *
   * ⚠ **Geen dossier-id.** Een brutoprijs geldt landelijk en hangt niet aan een
   * dossier, dus het recht om hem te verversen hangt er ook niet aan.
   *
   * ⚠ Alleen support, system en de keten-HK-gebruiker mogen dit; de backend
   * antwoordt anders met **403** en doet géén bevraging en géén markering. De
   * FE-zichtbaarheid leest uitsluitend `canRefresh` uit de calculatieregel —
   * verbergen is geen autorisatie.
   *
   * De backend markeert eerst en haalt daarna pas op: de markering is het enige
   * wat de "één ophaling per dag"-regel opzij zet, dus andersom zou de knop stil
   * niets doen op precies de rij waarvoor hij bedoeld is.
   */
  refreshGrossPrice(
    body: GrossPriceRefreshRequestDto,
  ): Promise<GrossPriceRefreshResponseDto> {
    return this.request('POST', '/api/gross-price/refresh', { body });
  }

  // ─── Eigen artikelen (story fe-eigen-artikelen-picker, 2026-06-01) ──────

  /**
   * GET /api/dossiers/{id}/own-items/lookups — item-types + leveranciers +
   * `defaultItemTypeId` (invoice-setting `DefaultItemTypeOwnItems`) +
   * `isCentralCustomer`-vlag voor de Eigen-artikelen-picker.
   *
   * De endpoint weigert NIET bij een centraal dossier — hij retourneert
   * `isCentralCustomer: true` zodat de FE een consistente disabled-/toast-
   * state kan tonen. De data-endpoints (search + bulk-add-own-items) zijn de
   * harde gate.
   */
  getOwnItemsLookups(dossierId: string): Promise<OwnItemsLookupsDto> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/own-items/lookups`,
    );
  }

  /**
   * GET /api/dossiers/{id}/own-items?code=&description=&itemTypeId=&supplierId=
   * — doorzoekt de company-eigen `UE_Item`-catalogus. Alleen de gevulde
   * filter-velden worden als query-param meegestuurd; lege velden laat de
   * BE 1-op-1 door als `("", "", null, null)`.
   *
   * 403 `central_customer_no_own_items` bij een centraal dossier (defense-in-
   * depth — de FE-knop hoort dan al disabled te zijn).
   */
  searchOwnItems(
    dossierId: string,
    params: OwnItemsSearchParams = {},
  ): Promise<OwnItemDto[]> {
    // Alleen niet-lege filter-velden als query-param meesturen — een lege
    // string zou de cache-/URL-key onnodig vervuilen en de BE behandelt
    // ontbrekend identiek aan leeg.
    const query: Record<string, string | number | boolean | undefined> = {};
    if (params.query) query.query = params.query;
    if (params.code) query.code = params.code;
    if (params.description) query.description = params.description;
    if (params.itemTypeId) query.itemTypeId = params.itemTypeId;
    if (params.supplierId) query.supplierId = params.supplierId;
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/own-items`,
      { query },
    );
  }

  /**
   * POST /api/dossiers/{id}/calculation-rows/bulk-add-own-items — atomic
   * multi-item add vanuit de Eigen-artikelen-picker.
   *
   * Body-key is `itemIds` (directe `UE_Item.ID`'s), NIET `additiveItemIds`.
   * Response hergebruikt de additieven-bulk-add-counts-shape
   * (`{ requestedCount, addedCount, addedRowNumbers, totalRowsAfter }`).
   *
   * Errors: 403 `central_customer_no_own_items` (centraal dossier),
   * 422 `invalid_own_item_id` (niet-eigen/onbekende id — ownership-guard),
   * 400 bij body-validatie.
   */
  bulkAddOwnItems(
    dossierId: string,
    body: BulkAddOwnItemsRequestDto,
  ): Promise<BulkAddCalculationRowsResponseDto> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(dossierId)}/calculation-rows/bulk-add-own-items`,
      { body },
    );
  }

  // ─── Dossier-loze lookups voor het calculatie-kladblok ──────────────────
  // (story fe-calculatie-kladblok-glas, 2026-06-23: "Extra artikelen" +
  // "Eigen artikelen" functioneel maken). Spiegelen de dossier-gebonden
  // varianten hierboven maar zonder dossier — company-scoped via de
  // session-company. Toevoegen aan de calculatie loopt via calculateBatch
  // (byItemId), NIET via een dossier-persist-route.

  /**
   * GET /api/lookups/additive-groups/no-dossier — dossier-loze variant van
   * {@link getAdditiveGroups}. PolicyService-kind vast op Carculate (geen
   * dossier-PolicyService). Zelfde {@link AdditiveGroupsResponseDto}-shape.
   *
   * Eigen route sinds 14-10-2026 (KB-06): op `/api/lookups/additive-groups`
   * botste hij met de keten-lookup ({@link getChainAdditiveGroupLookup}) en gaf
   * de BE bij elke aanroep een 500 — "Extra artikelen…" stond daardoor altijd uit.
   */
  getAdditiveGroupsNoDossier(): Promise<AdditiveGroupsResponseDto> {
    return this.request('GET', '/api/lookups/additive-groups/no-dossier');
  }

  /**
   * GET /api/lookups/own-items/lookups — dossier-loze variant van
   * {@link getOwnItemsLookups}. `isCentralCustomer` is altijd `false` (er is
   * geen verzekeraar-pair zonder dossier), zodat de kladblok-knop nooit
   * onterecht disabled raakt.
   */
  getOwnItemsLookupsNoDossier(): Promise<OwnItemsLookupsDto> {
    return this.request('GET', '/api/lookups/own-items/lookups');
  }

  /**
   * GET /api/lookups/own-items — dossier-loze variant van {@link searchOwnItems}.
   * Geen central-gate (geen dossier → niet centraal); company-scoped via de
   * session-company. Alleen niet-lege filter-velden worden meegestuurd.
   */
  searchOwnItemsNoDossier(
    params: OwnItemsSearchParams = {},
  ): Promise<OwnItemDto[]> {
    const query: Record<string, string | number | boolean | undefined> = {};
    if (params.query) query.query = params.query;
    if (params.code) query.code = params.code;
    if (params.description) query.description = params.description;
    if (params.itemTypeId) query.itemTypeId = params.itemTypeId;
    if (params.supplierId) query.supplierId = params.supplierId;
    return this.request('GET', '/api/lookups/own-items', { query });
  }

  /**
   * POST /api/dossiers/{id}/purchase-prices — V1 read-only proxy die de
   * inkoopprijzen per eurocode-calc-regel ophaalt uit de externe stock-API
   * (story fe-inkoopprijzen-calculatieregel).
   *
   * De FE stuurt ALLEEN het dossierId (in de route) — de eurocode-`parts`
   * worden server-side uit de calculatie opgebouwd (D-2). De bearer-token +
   * stock-API-host blijven server-side (AC 4.2). POST → CSRF-header wordt
   * automatisch toegevoegd door `request`.
   *
   * Uniforme shape (C-4): álle "geen prijzen"-paden (gate uit, geen
   * eurocode-regels, lege token, ontbrekende host, stock-API-fout/timeout)
   * geven `{ available:false, items:[] }` — geen 500, de Calculatie-tab
   * blijft bruikbaar (AC 3.3). De body is leeg.
   */
  getDossierPurchasePrices(dossierId: string): Promise<PurchasePricesResult> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(dossierId)}/purchase-prices`,
    );
  }

  /**
   * POST /api/dossiers/{id}/order-list — schrijf-actie "Plaats in bestellijst"
   * voor een eurocode-calc-regel (story fe-be-plaats-op-bestellijst).
   *
   * De FE stuurt enkel `{ euroCode, quantity }`; de proxy resolvt server-side
   * het default-magazijn (D-A) + bouwt de stock-API-`PersistStockInformation`-
   * call. Bearer-token + host blijven server-side (AC 4.2). De response is
   * altijd `{ placed, reason }` — geen 500 op een stock-API-fout (AC 3.2),
   * `placed:false` + reason. POST → CSRF-header wordt door `request`
   * toegevoegd.
   */
  placeDossierOnOrderList(
    dossierId: string,
    body: PlaceOnOrderListRequest,
  ): Promise<OrderListResult> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(dossierId)}/order-list`,
      { body },
    );
  }

  /**
   * GET /api/order-list — de bestellijst-basislijst (story
   * fe-be-bestellijst-overzicht, S-INK.1, read-only). Company-breed (server-side
   * gescoped via de sessie/token). Bearer-token + host blijven server-side
   * (AC 5.2). Een geslaagde call met een lege lijst is `{ lines: [],
   * suppliers: [] }`; een laad-FOUT (stock-API-fout/ontbrekende token/host) is
   * een HTTP-4xx/5xx → `ApiError` (AC 4.3 — fout ≠ leeg), zodat de FE een
   * fout-staat met retry kan tonen, onderscheidbaar van een echt lege lijst.
   */
  getOrderList(signal?: AbortSignal): Promise<OrderListDto> {
    return this.request('GET', '/api/order-list', { signal });
  }

  /**
   * GET /api/orders?status=<segment>&q=<zoekterm> — de orderlijst (story
   * S-INK.3, V1 read-only). Het FE-segment wordt server-side naar de 4
   * legacy-booleans (`open`/`delivered`/`retour`/`retournotcompleted`) vertaald;
   * `q` gaat als `itemcode` mee (server-side eurocode-match). Company-scoping +
   * bearer-token + host blijven server-side (AC 5). Een geslaagde lege lijst is
   * `{ orders: [] }`; een laad-FOUT is een HTTP-4xx/5xx → `ApiError` (AC 4.1 —
   * fout ≠ leeg), onderscheidbaar van een echt lege lijst.
   */
  getOrders(
    params: { status?: OrderStatusSegment; q?: string },
    signal?: AbortSignal,
  ): Promise<OrderListSearchDto> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.q) qs.set('q', params.q);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.request('GET', `/api/orders${suffix}`, { signal });
  }

  /**
   * GET /api/orders/{orderId} — het order-detail (story S-INK.3, V1 read-only).
   * Een geslaagde 200 met `order: null` betekent "niet gevonden" (AC 2.7) — dat
   * is GÉÉN laad-fout; een laad-fout is een HTTP-4xx/5xx → `ApiError`.
   */
  getOrder(orderId: string, signal?: AbortSignal): Promise<OrderDetailDto> {
    return this.request('GET', `/api/orders/${encodeURIComponent(orderId)}`, { signal });
  }

  /**
   * PUT /api/orders/{orderId}/lines/{orderLineId}/receipt — boekt het
   * nu-ontvangen aantal (DELTA) op een orderregel (story S-INK.4 — ontvangst
   * boeken, concept STATE 1). De body draagt UITSLUITEND `{ numberToProcess,
   * remark }`; de BE resolvet `supplierId`/`contactId` server-side uit de
   * orderregel en stuurt de stock-API `PUT /api/order/ProcessOrder` (waar
   * `NumberReceived += NumberToProcess`). Een HTTP-4xx/5xx → `ApiError` (400 bij
   * een ongeldig aantal, 404 bij onbekende/cross-company regel, 502
   * `stock_error` bij een stock-API-fout). PUT → CSRF-header wordt door
   * `request` toegevoegd.
   */
  bookOrderReceipt(
    orderId: string,
    orderLineId: string,
    body: OrderReceiptRequest,
    signal?: AbortSignal,
  ): Promise<OrderReceiptResult> {
    return this.request(
      'PUT',
      `/api/orders/${encodeURIComponent(orderId)}/lines/${encodeURIComponent(orderLineId)}/receipt`,
      { body, signal },
    );
  }

  /**
   * POST /api/orders/{orderId}/lines/{orderLineId}/receipt/{receiptId}/complete — EPIC-022 S022.5 (AC 10): een
   * ontvangst waarvan de order nog niet bijgewerkt is opnieuw verwerken (geen body). Idempotent; 404 `not_found`.
   */
  completeOrderReceipt(orderId: string, orderLineId: string, receiptId: string): Promise<OrderReceiptCompleteResult> {
    return this.request(
      'POST',
      `/api/orders/${encodeURIComponent(orderId)}/lines/${encodeURIComponent(orderLineId)}/receipt/${encodeURIComponent(receiptId)}/complete`,
      { body: {} },
    );
  }

  /**
   * GET /api/dossiers/{id}/inventory/invoice-check — EPIC-022 S022.5 (AC 14): de voorraadcontrole vóór factureren.
   * Alleen aanroepen bij whoami `inventoryInCarculate === true`. `{ applicable: false }` bij een stock-API-hersteller.
   */
  getDossierInventoryInvoiceCheck(id: string, signal?: AbortSignal): Promise<InventoryInvoiceCheck> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(id)}/inventory/invoice-check`, { signal });
  }

  /**
   * PUT /api/orders/{orderId}/lines/{orderLineId}/retour — maakt een retour
   * (DELTA + Ja/Nee-keuze) op een orderregel (story S-INK.5 — retour aanmaken,
   * concept STATE 1 Retour-kolom + STATE 2 bevestigingsdialog). De body draagt
   * `{ numberToProcess, remark?, createNewPreOrderLine }`; de BE resolvet
   * `supplierId`/`contactId` server-side en stuurt de stock-API `PUT
   * /api/order/ProcessOrderCreateRetour` (waar éérst `NumberReceived +=
   * NumberToProcess` en dáárna een retour-order/backorder ontstaat). Een
   * HTTP-4xx/5xx → `ApiError` (400 bij ongeldig aantal/ontbrekende keuze, 404 bij
   * onbekende/cross-company regel, 422 `warehouse_required`/`supplier_required`
   * wanneer de orderregel geen magazijn/leverancier heeft — dan belt de BE de
   * stock-API NIET, 502 `stock_error` bij een stock-API-fout). PUT → CSRF-header
   * wordt door `request` toegevoegd.
   */
  createOrderRetour(
    orderId: string,
    orderLineId: string,
    body: OrderRetourRequest,
    signal?: AbortSignal,
  ): Promise<OrderRetourResult> {
    return this.request(
      'PUT',
      `/api/orders/${encodeURIComponent(orderId)}/lines/${encodeURIComponent(orderLineId)}/retour`,
      { body, signal },
    );
  }

  /**
   * PUT /api/orders/{orderId}/lines/{orderLineId}/mark-received — markeer een
   * retour-credit als ontvangen (story S-INK.6, concept STATE 3). Lege body; de BE
   * resolvet de regel server-side en stuurt de stock-API `PUT
   * /api/order/MarkRetourOrderAsReceived` (die de regel van `BackOrderCreditNotReceived`
   * naar `BackOrderCreditReceived` kantelt). **Idempotent**: een 2e markering / een
   * al-ontvangen regel is een veilige no-op → 200 (geen fout). Een HTTP-4xx/5xx →
   * `ApiError` (404 bij onbekende/cross-company regel, 502 `stock_error` bij een
   * stock-API-fout). PUT → CSRF-header wordt door `request` toegevoegd.
   */
  markRetourReceived(
    orderId: string,
    orderLineId: string,
    signal?: AbortSignal,
  ): Promise<OrderMarkReceivedResult> {
    return this.request(
      'PUT',
      `/api/orders/${encodeURIComponent(orderId)}/lines/${encodeURIComponent(orderLineId)}/mark-received`,
      { body: {}, signal },
    );
  }

  /**
   * POST /api/order-list/prices/supplier — de inkoopprijzen van ÉÉN
   * provider-leverancier (story S-INK.2b — progressief, lock-vrij + cache). De FE
   * vuurt N van deze calls **parallel** (één per kolom) en merge't elke kolom
   * zodra 'ie binnen is — geen aggregate-cap die trage-maar-leverbare leveranciers
   * als "—" verbergt. De endpoint draait **lock-vrij**
   * (`SessionStateBehavior.Disabled`) zodat 'ie andere dossier-calls niet achter de
   * sessie-lock blokkeert (cross-tab). Server-side cache + single-flight; bearer-
   * token + host komen NOOIT op de wire.
   *
   * **Faal-veilig**: een fout (transport/non-2xx/parse/timeout) → `{ ok:false,
   * reason:"stock_error" }` (HTTP 200, geen 500) → de FE rendert de kolom als
   * **fout/retry**, nooit als "geen voorraad". `bypassCache:true` ("Prijzen
   * verversen"/retry) forceert een verse ophaal. POST → CSRF-header wordt door
   * `request` toegevoegd.
   */
  getSupplierPricesForSupplier(
    body: SupplierPriceRequest,
    signal?: AbortSignal,
  ): Promise<SupplierPriceResult> {
    return this.request('POST', '/api/order-list/prices/supplier', { body, signal });
  }

  /**
   * POST /api/order-list/orders — bestellen bij één leverancier (story S-INK.7). Lock-vrij; kan tot ~100 s
   * duren (60 s per magazijn bij de provider). Ná het claimen is het antwoord altijd 200 met uitkomsten per
   * groep en regel; een 4xx/5xx komt alleen van een fout vóór het claimen (dan is er niets besteld).
   *
   * ⚠ Nooit automatisch herhalen: een herhaling gaat alleen met hetzelfde `clientRequestId` (de server geeft
   * dan het opgeslagen antwoord terug, `replayed: true`).
   */
  placePurchaseOrder(body: PurchaseOrderRequest): Promise<PurchaseOrderRoundResult> {
    return this.request('POST', '/api/order-list/orders', { body });
  }

  /** POST /api/order-list/order-rounds/{roundId}/verify — onzekere regels opnieuw controleren (S-INK.7, AC 35). */
  verifyPurchaseOrderRound(roundId: string): Promise<PurchaseOrderRoundActionResult> {
    return this.request('POST', `/api/order-list/order-rounds/${encodeURIComponent(roundId)}/verify`, { body: {} });
  }

  /**
   * POST /api/order-list/order-rounds/{roundId}/release — onzekere regels vrijgeven (S-INK.7, AC 35). Alleen
   * na de bevestiging dat er bij de leverancier gecontroleerd is (`confirmNotOrdered: true`).
   */
  releasePurchaseOrderRound(roundId: string): Promise<PurchaseOrderRoundActionResult> {
    return this.request('POST', `/api/order-list/order-rounds/${encodeURIComponent(roundId)}/release`, {
      body: { confirmNotOrdered: true },
    });
  }

  /**
   * PATCH /api/dossiers/{dossierId}/calculation-rows/{rowId} — sparse
   * inline-edit van één calculatie-regel (story
   * `calculatie-inline-edit-en-delete`).
   *
   * Server retourneert 200 + de complete updated `CalculationRowDto[]`
   * (zelfde shape als GET) zodat de FE de cache met één hap kan refreshen.
   * 403 bij `PriceNotEditable` / `QuantityNotEditable` (defense-in-depth
   * — BE re-evalueert edit-rechten per request, F1/MR-1). 404 bij
   * niet-gevonden rij of cross-tenant.
   *
   * Optionele `signal` voor `useUpdateCalculationRow` cancel-bij-delete-keten
   * (TA-fix F3/MR-3 — AbortController-cancel bij prullenbak-klik op zelfde rij).
   */
  updateCalculationRow(
    dossierId: string,
    rowId: string,
    patch: PartialCalculationRowUpdate,
    signal?: AbortSignal,
  ): Promise<CalculationRowDto[]> {
    return this.request(
      'PATCH',
      `/api/dossiers/${encodeURIComponent(dossierId)}/calculation-rows/${encodeURIComponent(rowId)}`,
      { body: patch, signal },
    );
  }

  /**
   * DELETE /api/dossiers/{dossierId}/calculation-rows/{rowId} — verwijdert
   * één regel + cascade naar directe child-rijen (story AC 3.5). 422 bij
   * grandchild-hiërarchie (F5/MR-5 — out-of-scope V1). 204 bij succes.
   *
   * Optionele `keepalive` voor `beforeunload`-pad: zonder dit zet de
   * browser de fetch af zodra de pagina sluit. AC 3.4 force-commit-keten.
   */
  deleteCalculationRow(
    dossierId: string,
    rowId: string,
    opts?: { keepalive?: boolean },
  ): Promise<void> {
    return this.request(
      'DELETE',
      `/api/dossiers/${encodeURIComponent(dossierId)}/calculation-rows/${encodeURIComponent(rowId)}`,
      { keepalive: opts?.keepalive },
    );
  }

  /**
   * POST /api/dossiers/{dossierId}/calculation-rows/bulk-delete — verwijdert
   * meerdere regels in ÉÉN save (story `fix-calculatie-batch-delete`,
   * 2026-06-11). Nodig omdat `UA_DossierSaveCalculation` delete-and-replace
   * doet en daarbij alle row-GUID's hernummert; N losse DELETEs in rapid
   * succession landen daardoor op dode id's → 404 → regels blijven staan.
   *
   * Niet-bestaande id's tellen server-side als `notFoundCount` (idempotent),
   * geen 404. `keepalive` voor het unmount/`beforeunload`-force-commit-pad.
   */
  bulkDeleteCalculationRows(
    dossierId: string,
    body: BulkDeleteCalculationRowsRequestDto,
    opts?: { keepalive?: boolean },
  ): Promise<BulkDeleteCalculationRowsResponseDto> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(dossierId)}/calculation-rows/bulk-delete`,
      { body, keepalive: opts?.keepalive },
    );
  }

  /**
   * GET /api/lookups/vat-rates — company-scoped BTW-tarieven voor de
   * inline-edit BTW-dropdown (story Scenario 5). Gesorteerd aflopend op
   * percentage (hoogste eerst).
   *
   * Geen retry-config; de FE-cache via TanStack (`useVatRates`) heeft
   * z'n eigen staleTime (1u) + gcTime (24u) per AC 5.2.
   */
  getVatRates(): Promise<VatRateDto[]> {
    return this.request('GET', '/api/lookups/vat-rates');
  }

  // ─── PhotoProtocols (story fotoprotocol-beheer-v1, 2026-05-29) ─────────

  /** GET /api/photoprotocols — lijst met optionele filters. */
  listPhotoProtocols(filter?: import('./types.js').PhotoProtocolListFilter): Promise<import('./types.js').PhotoProtocolDto[]> {
    const query: Record<string, string | number | boolean | undefined> = {};
    if (filter?.status !== undefined) query.status = filter.status;
    if (filter?.ownerId) query.ownerId = filter.ownerId;
    if (filter?.scopeType !== undefined) query.scopeType = filter.scopeType;
    return this.request('GET', '/api/photoprotocols', { query });
  }

  /** GET /api/photoprotocols/{id} — aggregate-detail. */
  getPhotoProtocol(id: string): Promise<import('./types.js').PhotoProtocolDto> {
    return this.request('GET', `/api/photoprotocols/${encodeURIComponent(id)}`);
  }

  /** POST /api/photoprotocols. */
  createPhotoProtocol(body: import('./types.js').PhotoProtocolDto): Promise<import('./types.js').PhotoProtocolDto> {
    return this.request('POST', '/api/photoprotocols', { body });
  }

  /** PUT /api/photoprotocols/{id} — header-update. */
  updatePhotoProtocol(id: string, body: import('./types.js').PhotoProtocolDto): Promise<import('./types.js').PhotoProtocolDto> {
    return this.request('PUT', `/api/photoprotocols/${encodeURIComponent(id)}`, { body });
  }

  /** DELETE /api/photoprotocols/{id} — hard-delete (alleen Concept-nooit-actief). */
  deletePhotoProtocol(id: string): Promise<void> {
    return this.request('DELETE', `/api/photoprotocols/${encodeURIComponent(id)}`);
  }

  /** POST /api/photoprotocols/{id}/archive — Status → Gearchiveerd. */
  archivePhotoProtocol(id: string): Promise<import('./types.js').PhotoProtocolDto> {
    return this.request('POST', `/api/photoprotocols/${encodeURIComponent(id)}/archive`, { body: {} });
  }

  /** POST /api/photoprotocols/{id}/duplicate — dupliceer-flow. */
  duplicatePhotoProtocol(
    id: string,
    req: import('./types.js').PhotoProtocolDuplicateRequest,
  ): Promise<import('./types.js').PhotoProtocolDto> {
    return this.request('POST', `/api/photoprotocols/${encodeURIComponent(id)}/duplicate`, { body: req });
  }

  /** PUT /api/photoprotocols/{id}/scopes — full-replace scope-set. */
  replacePhotoProtocolScopes(
    id: string,
    scopes: import('./types.js').PhotoProtocolScopeDto[],
  ): Promise<import('./types.js').PhotoProtocolDto> {
    return this.request('PUT', `/api/photoprotocols/${encodeURIComponent(id)}/scopes`, { body: scopes });
  }

  /** POST /api/photoprotocols/{id}/scenarios — nieuw scenario. */
  createPhotoProtocolScenario(
    id: string,
    scenario: import('./types.js').PhotoProtocolScenarioDto,
  ): Promise<import('./types.js').PhotoProtocolDto> {
    return this.request('POST', `/api/photoprotocols/${encodeURIComponent(id)}/scenarios`, { body: scenario });
  }

  /** PUT /api/photoprotocols/{id}/scenarios/{sid} — full-replace scenario. */
  replacePhotoProtocolScenario(
    id: string,
    sid: string,
    scenario: import('./types.js').PhotoProtocolScenarioDto,
  ): Promise<import('./types.js').PhotoProtocolDto> {
    return this.request('PUT', `/api/photoprotocols/${encodeURIComponent(id)}/scenarios/${encodeURIComponent(sid)}`, { body: scenario });
  }

  /** DELETE /api/photoprotocols/{id}/scenarios/{sid}. */
  deletePhotoProtocolScenario(id: string, sid: string): Promise<void> {
    return this.request('DELETE', `/api/photoprotocols/${encodeURIComponent(id)}/scenarios/${encodeURIComponent(sid)}`);
  }

  /** GET /api/photoprotocols/preview?dossierId=… — debug-info voor "Test op dossier". */
  previewPhotoProtocolForDossier(dossierId: string): Promise<import('./types.js').PhotoProtocolPreviewResponse> {
    return this.request('GET', '/api/photoprotocols/preview', { query: { dossierId } });
  }

  /**
   * GET /api/photoprotocols/lookups/attachment-categories — dropdown-data
   * voor de items-CRUD-rij. Read-only systeem-brede `GD_AttachmentCategory`.
   * Cacht heel lang via TanStack (geen company-filter, geen staleness).
   */
  listAttachmentCategories(): Promise<import('./types.js').AttachmentCategoryLookup[]> {
    return this.request('GET', '/api/photoprotocols/lookups/attachment-categories');
  }

  /**
   * GET /api/photoprotocols/lookups/attachment-categories/glas — de
   * categorielijst voor de bijlagen-flow van een glasdossier + de
   * default-foto-categorie-ID.
   *
   * De lijst is de samenvoeging van (a) de categorieën die het geldende
   * fotoprotocol voor dit dossier eist en (b) de geconfigureerde extra
   * categorieën (`GS_SystemParameter.ExtraAttachmentCategoryCodes`).
   *
   * `dossierId` is optioneel maar in de dossier-context wél nodig: zonder
   * dossier kan het BE geen protocol resolven en blijft alleen de
   * configuratie over — dan zouden verplichte protocol-categorieën kunnen
   * ontbreken.
   */
  listGlasAttachmentCategories(
    dossierId?: string,
  ): Promise<import('./types.js').GlasAttachmentCategoryLookupResponse> {
    return this.request('GET', '/api/photoprotocols/lookups/attachment-categories/glas', {
      query: dossierId ? { dossierId } : undefined,
    });
  }

  /**
   * GET /api/photoprotocols/resolve?dossierId=… — het geldende fotoprotocol
   * voor dit dossier, met de items (verplicht en optioneel) van het gekozen
   * scenario. Levert `null` wanneer geen protocol matcht.
   *
   * Dit is dezelfde resolver die het BE gebruikt om de bijlage-categorielijst
   * samen te stellen — bewust één bron, geen tweede protocol-afleiding.
   */
  resolvePhotoProtocolForDossier(
    dossierId: string,
  ): Promise<import('./types.js').PhotoProtocolResolverOutput | null> {
    return this.request('GET', '/api/photoprotocols/resolve', { query: { dossierId } });
  }

  /**
   * GET /api/photoprotocols/lookups/insurance-companies — dropdown-data voor
   * scope-rij ScopeType=InsuranceCompany (zodat user GUID niet hoeft te kennen).
   */
  listInsuranceCompaniesLookup(): Promise<import('./types.js').IdNameLookup[]> {
    return this.request('GET', '/api/photoprotocols/lookups/insurance-companies');
  }

  /**
   * GET /api/photoprotocols/lookups/custom-dossier-types — dropdown-data voor
   * scope-rij ScopeType=CustomDossierType.
   */
  listCustomDossierTypesLookup(): Promise<import('./types.js').IdNameLookup[]> {
    return this.request('GET', '/api/photoprotocols/lookups/custom-dossier-types');
  }

  // ─── Kilometerstand uit de bijlage-foto (story fe-be-kilometerstand-uit-foto)

  /**
   * GET /api/dossiers/{dossierId}/odometer-read-status — status van de
   * achtergrond-uitlezing.
   *
   * ⚠ Deze route draait **lock-vrij** op de server en is daarom veilig om te
   * pollen: hij zet, anders dan het dossier-detail, geen andere calls van
   * dezelfde gebruiker in de wachtrij. Poll dus hierop en haal het detail pas
   * opnieuw op wanneer er echt iets veranderd is.
   *
   * `pending` is het enige veld waar een poller op hoort te keyen. Wordt het
   * `false`, dan is de uitkomst definitief — óók wanneer er niets geschreven
   * is; dat is een normale afloop, geen fout.
   */
  getOdometerReadStatus(dossierId: string): Promise<OdometerReadStatus> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/odometer-read-status`,
    );
  }

  /**
   * GET /api/dossiers/{id}/greencard-read-status — status + resultaten van de
   * automatische groene-kaart-uitlezing.
   *
   * ⚠⚠ **Lock-vrij** (`SessionStateBehavior.Disabled`), en dat is geen detail
   * maar de reden dat deze route gepolld mág worden: het dossier-detail loopt
   * achter de exclusieve per-sessie-lock, dus een poller daarop zou álle andere
   * calls van dezelfde gebruiker in de wachtrij zetten — precies terwijl zij op
   * de popup wacht.
   *
   * `pending` is het enige veld waar een poller op hoort te keyen. Wordt het
   * `false`, dan is de uitkomst definitief — óók wanneer er niets te lezen
   * viel; dat is een normale afloop, geen fout.
   */
  getGreenCardReadStatus(dossierId: string): Promise<GreenCardReadStatus> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/greencard-read-status`,
    );
  }

  // ─── Bijlagen-tab (story bijlagen-tab-v1, 2026-05-30) ──────────────────

  /**
   * GET /api/dossiers/{dossierId}/attachments — lijst van bijlagen voor dit
   * dossier. Zonder Data-blob — die wordt apart gestreamd via
   * `getAttachmentDataUrl` / `getAttachmentThumbUrl`.
   */
  listDossierAttachments(dossierId: string): Promise<AttachmentDto[]> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(dossierId)}/attachments`);
  }

  /**
   * POST /api/dossiers/{dossierId}/attachments — multipart-upload van een
   * lijst bestanden met master + per-rij-override-categorie.
   *
   * @param signal optionele AbortSignal voor cancel-mid-upload (AC-2.8).
   */
  uploadDossierAttachments(
    dossierId: string,
    files: AttachmentUploadFile[],
    defaultCategoryId: string | null,
    signal?: AbortSignal,
  ): Promise<AttachmentUploadResponse> {
    const fd = new FormData();
    if (defaultCategoryId) {
      fd.append('defaultCategoryId', defaultCategoryId);
    }
    files.forEach((f, idx) => {
      fd.append('files', f.file, f.file.name);
      if (f.categoryIdOverride !== null && f.categoryIdOverride !== undefined) {
        fd.append(`categoryIdsByIndex[${idx}]`, f.categoryIdOverride);
      }
      if (f.description !== null && f.description !== undefined && f.description !== '') {
        fd.append(`descriptionsByIndex[${idx}]`, f.description);
      }
    });
    return this.request('POST', `/api/dossiers/${encodeURIComponent(dossierId)}/attachments`, {
      formData: fd,
      signal,
    });
  }

  /**
   * Story `fe-be-monteur-s4-fotos` (S021.4 §9.2) — POST
   * /api/dossiers/{dossierId}/attachments voor ÉÉN foto uit de wachtrij van de monteur
   * (PB-2: één foto per request). Stuurt naast bestand, categorie en omschrijving:
   * `attachmentIdsByIndex[0]` (het id dat de telefoon koos — een herhaling slaat de foto
   * nooit dubbel op) en `clientWatermark=true` (geen tweede datumstempel van de server;
   * bij ELKE monteur-upload, review A-3).
   */
  uploadDossierPhoto(
    dossierId: string,
    photo: DossierPhotoUpload,
    signal?: AbortSignal,
  ): Promise<AttachmentUploadResponse> {
    const fd = new FormData();
    fd.append('files', photo.blob, photo.fileName);
    if (photo.categoryId) fd.append('categoryIdsByIndex[0]', photo.categoryId);
    if (photo.description) fd.append('descriptionsByIndex[0]', photo.description);
    fd.append('attachmentIdsByIndex[0]', photo.attachmentId);
    fd.append('clientWatermark', 'true');
    return this.request('POST', `/api/dossiers/${encodeURIComponent(dossierId)}/attachments`, {
      formData: fd,
      signal,
    });
  }

  /**
   * Story `fe-be-monteur-s4-fotos` (S021.4 §9.1) — GET
   * /api/dossiers/{dossierId}/photo-checklist: protocol, toewijzing aan de kopjes,
   * categorieën en de km-categorie in één call. Lees via
   * `photoChecklistQueryOptions` (`@/lib/photo/photoChecklistQuery`), nooit met een
   * eigen `queryFn` op die key.
   */
  getPhotoChecklist(dossierId: string): Promise<PhotoChecklist> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(dossierId)}/photo-checklist`);
  }

  /**
   * Story `fe-be-monteur-s4-fotos` (S021.4, B-2) — DELETE
   * /api/dossiers/{dossierId}/photos/{attachmentId}: een EIGEN foto verwijderen. De
   * server toetst eigenaarschap (403 `attachment_not_owned`) en het slot (403 met de
   * lock-reden). De gedeelde `deleteAttachment` is voor de monteur dicht.
   */
  deleteDossierPhoto(dossierId: string, attachmentId: string): Promise<void> {
    return this.request(
      'DELETE',
      `/api/dossiers/${encodeURIComponent(dossierId)}/photos/${encodeURIComponent(attachmentId)}`,
    );
  }

  /**
   * Story `fe-be-monteur-s7-ondertekenen` (S021.7 §2.1) — GET
   * /api/dossiers/{dossierId}/signing/status: is er ingericht, en is er al getekend (PB-5).
   * Lees via `signingStatusQueryOptions` (één definitie op de key).
   */
  getSigningStatus(dossierId: string): Promise<SigningStatus> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(dossierId)}/signing/status`);
  }

  /**
   * S021.7 §2.1 — GET /api/dossiers/{dossierId}/signing/documents: de tekendocumenten van dit
   * dossier plus de formulier-defaults (e-mail, ondertekenaar, mail aan/uit).
   */
  getSigningDocuments(dossierId: string): Promise<SigningDocumentsResponse> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(dossierId)}/signing/documents`);
  }

  /**
   * S021.7 §2.1 — POST /api/dossiers/{dossierId}/signing/preview (CSRF): het voorbeeld als PDF
   * (`Blob`). Schrijft niets in het dossier. Foutbodies blijven JSON (`{ reason, correlationId,
   * documentId? }`) → `ApiError`.
   */
  previewSigning(dossierId: string, body: SigningPreviewRequest): Promise<Blob> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(dossierId)}/signing/preview`, {
      body,
      responseType: 'blob',
    });
  }

  /**
   * S021.7 §2.1/§2.5 — POST /api/dossiers/{dossierId}/signing (CSRF): tekenen, opslaan als
   * bijlage(n) en (deel B) mailen. De body draagt precies de sleutels van {@link SignRequest}
   * (monteur-allowlist); `clientRequestId` maakt een herhaling idempotent (PB-16).
   */
  signDocuments(dossierId: string, body: SignRequest): Promise<SignResponse> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(dossierId)}/signing`, { body });
  }

  /**
   * Story `fe-be-monteur-s6-onderdelen` (S021.6 §2.1) — GET /api/dossiers/{dossierId}/used-parts:
   * de onderdelenlijst van de hersteller van het dossier, wat er aanstaat, de vlag "geen onderdelen
   * gebruikt" en of deze gebruiker mag afvinken. Lees via `usedPartsQueryOptions` (één definitie).
   */
  getDossierUsedParts(dossierId: string): Promise<DossierUsedParts> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(dossierId)}/used-parts`);
  }

  /**
   * S021.6 §2.1/§2.2 — PATCH /api/dossiers/{dossierId}/used-parts (CSRF): één wijziging (onderdelen
   * aan/uit en/of de vlag). Idempotent; het antwoord is de volledige nieuwe stand. Fouten
   * (`{ reason, correlationId, partId?, state? }`) → `ApiError`.
   */
  patchDossierUsedParts(dossierId: string, body: UsedPartsPatchRequest): Promise<DossierUsedParts> {
    return this.request('PATCH', `/api/dossiers/${encodeURIComponent(dossierId)}/used-parts`, { body });
  }

  /** S021.6 deel C — GET /api/inrichting/checklist-parts: de onderdelenlijst van de eigen hersteller. */
  listChecklistParts(): Promise<ChecklistPartsList> {
    return this.request('GET', '/api/inrichting/checklist-parts');
  }

  /** S021.6 deel C — POST /api/inrichting/checklist-parts (CSRF): een onderdeel toevoegen (achteraan). */
  createChecklistPart(name: string): Promise<ChecklistPart> {
    return this.request('POST', '/api/inrichting/checklist-parts', { body: { name } });
  }

  /** S021.6 deel C — PUT /api/inrichting/checklist-parts/{id} (CSRF): omschrijving wijzigen (409 bij een oude versie). */
  renameChecklistPart(id: string, name: string, version: number): Promise<ChecklistPart> {
    return this.request('PUT', `/api/inrichting/checklist-parts/${encodeURIComponent(id)}`, { body: { name, version } });
  }

  /** S021.6 deel C — PUT /api/inrichting/checklist-parts/{id}/status (CSRF): archiveren of weer in gebruik nemen. */
  setChecklistPartStatus(id: string, active: boolean): Promise<ChecklistPart> {
    return this.request('PUT', `/api/inrichting/checklist-parts/${encodeURIComponent(id)}/status`, { body: { active } });
  }

  /** S021.6 deel C — PUT /api/inrichting/checklist-parts/order (CSRF): de volledige lijst actieve id's in de nieuwe volgorde. */
  reorderChecklistParts(ids: string[]): Promise<ChecklistPartsList> {
    return this.request('PUT', '/api/inrichting/checklist-parts/order', { body: { ids } });
  }

  /** S021.6 deel C — DELETE /api/inrichting/checklist-parts/{id} (CSRF): verwijderen, of archiveren als het in gebruik is. */
  deleteChecklistPart(id: string): Promise<ChecklistPartDeleteResult> {
    return this.request('DELETE', `/api/inrichting/checklist-parts/${encodeURIComponent(id)}`);
  }

  /**
   * Story `fe-be-monteur-s4-fotos` (S021.4, B-4) — PUT /api/me/photo-watermark: het
   * watermerk op de foto's van deze gebruiker aan of uit. De keuze hoort bij de
   * gebruiker, niet bij het toestel.
   */
  setPhotoWatermark(enabled: boolean): Promise<{ enabled: boolean }> {
    return this.request('PUT', '/api/me/photo-watermark', { body: { enabled } });
  }

  /**
   * PUT /api/attachments/{id} — name/description/categoryId update met
   * optimistic-concurrency (`version` verplicht). Bij mismatch geeft de
   * server 409 met `currentVersion`.
   */
  updateAttachment(id: string, body: AttachmentPutRequest): Promise<AttachmentPutResponse> {
    return this.request('PUT', `/api/attachments/${encodeURIComponent(id)}`, { body });
  }

  /** DELETE /api/attachments/{id} — hard-delete (AC-4). */
  deleteAttachment(id: string): Promise<void> {
    return this.request('DELETE', `/api/attachments/${encodeURIComponent(id)}`);
  }

  /**
   * URL-builder voor `/api/attachments/{id}/data` — direct injecteren in
   * `<iframe src>` (PDF-preview) of `<a href>` (download). De server stuurt
   * het origineel met juist Content-Type.
   */
  getAttachmentDataUrl(id: string): string {
    return `${this.baseUrl}/api/attachments/${encodeURIComponent(id)}/data`;
  }

  /**
   * URL-builder voor `/api/attachments/{id}/thumb?w=...` — direct injecteren
   * in `<img src>`. Server resize naar JPG; HEIC wordt server-side
   * geconverteerd. PDF/Office → 404 (FE valt terug op file-type-icoon).
   */
  getAttachmentThumbUrl(id: string, width = 240): string {
    return `${this.baseUrl}/api/attachments/${encodeURIComponent(id)}/thumb?w=${encodeURIComponent(String(width))}`;
  }

  // ─── Annotaties (story annotaties-mvp / S-BL.2, 2026-05-30) ────────────

  /**
   * GET /api/attachments/{id}/annotation — laad de annotation-payload +
   * Version voor optimistic-concurrency. Bij 404 met `annotation_not_found`:
   * de attachment heeft nog geen annotation-rij (AC-2.1).
   */
  getAttachmentAnnotation(id: string): Promise<AnnotationGetResponse> {
    return this.request('GET', `/api/attachments/${encodeURIComponent(id)}/annotation`);
  }

  /**
   * PUT /api/attachments/{id}/annotation — upsert van de annotation-payload
   * met optimistic-concurrency via `expectedVersion`. Ontbreken =
   * create-intent (alleen succes als rij nog niet bestaat). Aanwezig =
   * must-match-or-409. Server rondt coords af op 4 decimalen.
   */
  putAttachmentAnnotation(id: string, body: AnnotationPutRequest): Promise<AnnotationPutResponse> {
    return this.request('PUT', `/api/attachments/${encodeURIComponent(id)}/annotation`, { body });
  }

  // ─── Planbord (EPIC-012, story S012.1) ─────────────────────────────────

  /**
   * GET /api/planbord?from=…&to=… — resources (kolommen) + afspraken-in-range
   * + beschikbaarheid (open-dagen + werktijden) voor een datumrange. Read-only
   * GET (geen CSRF). `from`/`to` zijn inclusieve ISO-dates ("yyyy-MM-dd"); de
   * server geeft 400 bij ontbrekende/ongeldige params, `from > to`, of een te
   * brede range.
   */
  getPlanbord(params: { from: string; to: string }): Promise<PlanbordResponse> {
    return this.request('GET', '/api/planbord', {
      query: { from: params.from, to: params.to },
    });
  }

  /**
   * GET /api/planbord/unscheduled?from=…&to=… — de niet-ingepland-backlog:
   * dossiers met een hersteldatum binnen de zichtbare planbord-range, in actieve
   * status, zonder afspraak-met-resource die alle herstel-dagen dekt. `from`/`to`
   * zijn inclusieve ISO-dates ("yyyy-MM-dd") = exact de range die het bord toont,
   * zodat de rail meebeweegt met dag-/weeknavigatie. Read-only GET (geen CSRF),
   * company-scoped server-side; 400 bij ontbrekende/ongeldige/te brede range.
   */
  getUnscheduled(params: { from: string; to: string }): Promise<UnscheduledResponse> {
    return this.request('GET', '/api/planbord/unscheduled', {
      query: { from: params.from, to: params.to },
    });
  }

  // ─── Planbord write-laag (EPIC-012, story S012.5) ──────────────────────

  /**
   * GET /api/repair-locations — de RepairLocation-contacten van de ingelogde
   * company (story S012.10c). Voeden de herstellocatie-dropdown op de
   * Locatie-tab van de afspraak-modal; bij keuze worden naam (`locationText`) +
   * adres-snapshot (`intake.repairLocation`) in de write-payload meegestuurd.
   * Company-scoped BE-side; lege array wanneer er geen RepairLocation-contacten
   * zijn.
   */
  getRepairLocations(): Promise<RepairLocationOption[]> {
    return this.request('GET', '/api/repair-locations');
  }

  /**
   * GET /api/company/workshop-address — het herstel-/bezoekadres van de eigen
   * werkplaats (`_default`-contact van de ingelogde company, story S012.10e).
   * Voedt de "Eigen werkplaats"-autofill op de Locatie-tab (naam + adres).
   * Company-scoped BE-side; een leeg/niet-gevonden contact geeft een 200 met
   * lege velden (geen fout).
   */
  getWorkshopAddress(signal?: AbortSignal): Promise<RepairLocationAddress> {
    return this.request('GET', '/api/company/workshop-address', { signal });
  }

  /**
   * GET /api/debtors/{id}/repair-address — het herstel-/bezoekadres van de
   * opdrachtgever (debiteur, story S012.10e). Voedt de "Bij opdrachtgever"-
   * autofill op de Locatie-tab (adres; de naam blijft de opdrachtgever-naam).
   * Company-scoped BE-side: onbekende/cross-tenant debtor → 404; een debtor
   * zonder gekoppeld contact/adres → 200 met lege velden (geen fout).
   */
  getDebtorRepairAddress(debtorId: string, signal?: AbortSignal): Promise<RepairLocationAddress> {
    return this.request(
      'GET',
      `/api/debtors/${encodeURIComponent(debtorId)}/repair-address`,
      { signal },
    );
  }

  /**
   * POST /api/appointments — afspraak aanmaken. Bij Declaratie/Werkbon (type
   * 1/2) zet de BE meteen een (nieuw of gekoppeld) dossier op + de intake/polis.
   * CSRF-header wordt automatisch meegestuurd (mutating method). 201 → de
   * aangemaakte afspraak in de read-shape (incl. `dossierId`/`workType`).
   */
  createAppointment(body: CreateAppointmentRequest): Promise<AppointmentResponse> {
    return this.request('POST', '/api/appointments', { body });
  }

  /**
   * PATCH /api/appointments/{id} — verplaatsen/bewerken met optimistic
   * concurrency. Een version-mismatch levert een 409 op (via de wrapper een
   * `ApiError` met `{ reason: 'version_mismatch', currentVersion }` in
   * `payload`). CSRF-header automatisch.
   */
  updateAppointment(id: string, body: UpdateAppointmentRequest): Promise<AppointmentResponse> {
    return this.request('PATCH', `/api/appointments/${encodeURIComponent(id)}`, { body });
  }

  /**
   * DELETE /api/appointments/{id} — soft-delete (`IsDeleted=1`). 204 No Content
   * bij succes. CSRF-header automatisch. Het eventueel gekoppelde dossier blijft
   * bestaan (komt terug in de niet-ingepland-backlog).
   */
  deleteAppointment(id: string): Promise<void> {
    return this.request('DELETE', `/api/appointments/${encodeURIComponent(id)}`);
  }

  // ─── Planbord-beschikbaarheid (EPIC-012, story S012.4) ─────────────────

  /**
   * GET /api/repairer/open-days — open-dagen van de hersteller (company-breed).
   * Levert altijd alle 7 weekdagen; ontbrekende DB-rijen worden server-side
   * aangevuld met de default (ma-vr open, za-zo dicht). Read-only GET (geen CSRF).
   */
  getRepairerOpenDays(): Promise<RepairerOpenDaysResponse> {
    return this.request('GET', '/api/repairer/open-days');
  }

  /**
   * PUT /api/repairer/open-days — vervangt de open-dag-set voor de hersteller.
   * CSRF-header automatisch. Retourneert de genormaliseerde 7-daagse set.
   */
  putRepairerOpenDays(body: PutRepairerOpenDaysRequest): Promise<RepairerOpenDaysResponse> {
    return this.request('PUT', '/api/repairer/open-days', { body });
  }

  /**
   * GET /api/resources/{fitterId}/working-hours — werktijd-tijdvakken van één
   * monteur (company-scope). 404 wanneer de fitter niet bij deze company hoort.
   * Tijden als "HH:mm"; meerdere rijen per weekdag = gebroken dienst.
   */
  getFitterWorkingHours(fitterId: string): Promise<FitterWorkingHoursResponse> {
    return this.request(
      'GET',
      `/api/resources/${encodeURIComponent(fitterId)}/working-hours`,
    );
  }

  /**
   * PUT /api/resources/{fitterId}/working-hours — vervangt de werktijd-set voor
   * één monteur. `endTime > startTime` wordt server-side afgedwongen (400 bij
   * schending). CSRF-header automatisch. Retourneert de opgeslagen set.
   */
  putFitterWorkingHours(
    fitterId: string,
    body: PutFitterWorkingHoursRequest,
  ): Promise<FitterWorkingHoursResponse> {
    return this.request(
      'PUT',
      `/api/resources/${encodeURIComponent(fitterId)}/working-hours`,
      { body },
    );
  }

  // ─── Planbord-resource-inrichting (EPIC-012, story S012.3) ─────────────

  /**
   * GET /api/resources/profiles — álle company-fitters (ook verborgen/
   * ongeconfigureerde) met hun resource-profiel (soort/kleur/volgorde/
   * zichtbaarheid, defaults toegepast) + voor bussen de leden-ids. Read-only
   * GET (geen CSRF).
   */
  getResourceProfiles(): Promise<ResourceProfilesResponse> {
    return this.request('GET', '/api/resources/profiles');
  }

  /**
   * PUT /api/resources/{fitterId}/profile — upsert het resource-profiel van één
   * fitter (soort/kleur/volgorde/zichtbaarheid). CSRF-header automatisch.
   * Retourneert het opgeslagen profiel (incl. eventuele bus-leden).
   */
  putResourceProfile(
    fitterId: string,
    body: PutResourceProfileRequest,
  ): Promise<ResourceProfile> {
    return this.request(
      'PUT',
      `/api/resources/${encodeURIComponent(fitterId)}/profile`,
      { body },
    );
  }

  /**
   * PUT /api/resources/{fitterId}/user — zet of wist de gekoppelde inlog-gebruiker
   * van één monteur (S012.7). `userId: null` → koppeling wissen (DELETE); een GUID
   * → upsert (vervangt een bestaande koppeling, `UNIQUE(FitterID)`). CSRF-header
   * automatisch. De BE valideert fitter + user tegen de eigen company-scope (404
   * `fitter_not_found` / 400 `user_not_found`). Retourneert de opgeslagen koppeling.
   */
  setFitterUser(fitterId: string, userId: string | null): Promise<FitterUserResponse> {
    const body: PutFitterUserRequest = { userId };
    return this.request(
      'PUT',
      `/api/resources/${encodeURIComponent(fitterId)}/user`,
      { body },
    );
  }

  // ─── Eigen werkbontypes per hersteller (EPIC-012, story S012.11) ───────

  /**
   * GET /api/work-order-types — de eigen ACTIEVE werkbontypes van de ingelogde
   * company (naam + kleur + standaard-duur). Read-only GET (geen CSRF),
   * company-scoped server-side (AC 7 — nooit andermans types). Gedeactiveerde
   * types ontbreken; hun kleur blijft wél leesbaar op bestaande afspraken via
   * het planbord-contract.
   */
  getWorkOrderTypes(): Promise<WorkOrderTypesResponse> {
    return this.request('GET', '/api/work-order-types');
  }

  /**
   * POST /api/work-order-types — werkbontype aanmaken. 201 → het aangemaakte
   * type. 400 met `reason` bij ongeldige naam/kleur/duur of dubbele naam.
   * CSRF-header automatisch.
   */
  createWorkOrderType(body: SaveWorkOrderTypeRequest): Promise<WorkOrderType> {
    return this.request('POST', '/api/work-order-types', { body });
  }

  /**
   * PUT /api/work-order-types/{id} — werkbontype bewerken. 404 wanneer het type
   * niet van de eigen company is (AC 8). 400 bij ongeldige invoer / dubbele naam.
   * CSRF-header automatisch.
   */
  updateWorkOrderType(id: string, body: SaveWorkOrderTypeRequest): Promise<WorkOrderType> {
    return this.request('PUT', `/api/work-order-types/${encodeURIComponent(id)}`, { body });
  }

  /**
   * DELETE /api/work-order-types/{id} — soft-deactivate (het type verdwijnt uit
   * de keuze; bestaande afspraken behouden hun type + kleur, AC 3/14). 204 bij
   * succes; 404 wanneer het type niet van de eigen company is of al inactief
   * (AC 8). CSRF-header automatisch.
   */
  deactivateWorkOrderType(id: string): Promise<void> {
    return this.request('DELETE', `/api/work-order-types/${encodeURIComponent(id)}`);
  }

  // ─── Bestaande afspraken importeren (EPIC-012, story S012.8 Fase 3) ─────

  /**
   * POST /api/planbord/migrate-appointments — self-service één-keer-import die
   * bestaande dossiers (hersteldatum vanaf vandaag) als planbord-afspraken
   * neerzet. Company-scoped + CSRF-gated server-side.
   *
   * `dryRun` stuurt de stand: `true` = preview (niets aangemaakt;
   * `appointmentsCreated` = "zou aanmaken"), `false` = echte import
   * (`appointmentsCreated` = daadwerkelijk aangemaakt). De BE leest `dryRun` uit
   * de querystring (die wint van een eventuele body); we sturen 'm daar mee +
   * een lege body zodat de CSRF-/credentials-mutatiepad van `request()` loopt.
   */
  migratePlanbordAppointments(dryRun: boolean): Promise<PlanbordMigrationResult> {
    return this.request('POST', '/api/planbord/migrate-appointments', {
      query: { dryRun },
      body: {},
    });
  }

  /**
   * POST /api/dossiers/{dossierId}/ruitid — maakt een RuitID-aanvraag aan in
   * Bindrix (EPIC-014, story S014.2). State-changing (kost geld in prod) →
   * `request()` draagt automatisch het CSRF-token. Retourneert het Bindrix-
   * record-ID + de initiële status.
   */
  createRuitId(
    dossierId: string,
    body: RuitIdCreateRequest,
  ): Promise<RuitIdCreateResponse> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(dossierId)}/ruitid`,
      { body },
    );
  }

  /**
   * GET /api/dossiers/{dossierId}/ruitid/{ruitId} — pollt de status + het
   * resultaat van een RuitID-aanvraag (read-only, geen CSRF). Status 1/2 →
   * resultaatvelden `null`; 3 → eurocode/oemcode/grossPrice/calibration;
   * 4 → `errorMessage`.
   */
  getRuitId(dossierId: string, ruitId: string): Promise<RuitIdPollResponse> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/ruitid/${encodeURIComponent(ruitId)}`,
    );
  }

  /**
   * GET /api/dossiers/{dossierId}/ruitid — lijst van de server-side RuitID-
   * registraties voor dit dossier (EPIC-014, story S014.5 D-3). Read-only,
   * company-gescoped, no-leak 404 (zelfde gate als create/poll). De FE leest
   * deze lijst bij het openen van de aanvraag-popup om de dubbel-lock-staat per
   * glaspositie te kennen (over sessies/gebruikers/ronden) en om het resultaat-
   * paneel na een refresh te herstellen — onafhankelijk van de in-memory
   * poll-registry.
   */
  getRuitIdList(dossierId: string): Promise<RuitIdListResponse> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/ruitid`,
    );
  }

  // ─── Meldingencentrum (story S014.8 — server-backed inbox) ───────────────

  /**
   * GET /api/notifications — meldingen van de company van de huidige user,
   * nieuwste eerst, elk met de afgeleide `isRead` voor déze user. Lazy: de FE
   * roept dit pas aan wanneer de bel-dropdown opent (D-3, niet bij page-load).
   * Company-/user-gescoped, no-leak.
   */
  getNotifications(): Promise<NotificationListResponse> {
    return this.request('GET', '/api/notifications');
  }

  /**
   * GET /api/notifications/unread-count — het badge-getal (ALLE company-
   * meldingen zonder read-rij voor deze user; niet door de lijst-limiet
   * afgekapt, D-14). Lichtgewicht poll-endpoint (~45s, ná page-load — D-3).
   */
  getNotificationsUnreadCount(): Promise<UnreadCountResponse> {
    return this.request('GET', '/api/notifications/unread-count');
  }

  /**
   * POST /api/notifications/{id}/read — markeert één melding als gelezen
   * (upsert, idempotent). Een vreemde-company-melding → 404 (no-leak). CSRF
   * wordt automatisch meegestuurd door `request()`.
   */
  markNotificationRead(id: string): Promise<{ read: boolean }> {
    return this.request('POST', `/api/notifications/${encodeURIComponent(id)}/read`, {
      body: {},
    });
  }

  /**
   * POST /api/notifications/read-all — markeert alle nog-ongelezen company-
   * meldingen van deze user als gelezen (idempotent).
   */
  markAllNotificationsRead(): Promise<{ read: boolean }> {
    return this.request('POST', '/api/notifications/read-all', { body: {} });
  }

  /**
   * POST /api/notifications/{id}/unread — markeert één melding weer als
   * ongelezen (verwijdert de read-rij van deze user). Idempotent; een
   * vreemde-company-melding → 404 (no-leak). 204 No Content bij succes. CSRF
   * wordt automatisch meegestuurd door `request()`.
   */
  markNotificationUnread(id: string): Promise<void> {
    return this.request('POST', `/api/notifications/${encodeURIComponent(id)}/unread`, {
      body: {},
    });
  }

  /**
   * DELETE /api/notifications/{id} — verbergt één melding voor déze user
   * (per-user dismiss; collega's in dezelfde company houden de melding).
   * Idempotent; een vreemde-company-melding → 404 (no-leak). 204 No Content bij
   * succes. CSRF wordt automatisch meegestuurd door `request()`.
   */
  dismissNotification(id: string): Promise<void> {
    return this.request('DELETE', `/api/notifications/${encodeURIComponent(id)}`);
  }

  // ─── Melding hersteller accept/weiger (story fe-be-melding-hersteller-...) ──

  /**
   * GET /api/intakes/{nummer} — detail van een melding (`UA_Intake`) op het
   * MELDINGSNUMMER (niet het dossiernummer). Gooit `NotFoundError` (404) zowel
   * bij niet-bestaand als bij geen-toegang (geen 403-leak). Read-only (geen CSRF).
   */
  getIntake(intakeNumber: number): Promise<IntakeDetail> {
    return this.request('GET', `/api/intakes/${encodeURIComponent(String(intakeNumber))}`);
  }

  /**
   * GET /api/intakes — read-only meldingen-overzicht (story
   * `fe-be-meldingen-overzicht`, 2026-06-14). Gescoped op de eigen hersteller +
   * HK/`WF_CaseUser`-toegang (server-side, uit de sessie). Geeft één pagina
   * (`{ items, hasMore }`) terug; de FE laadt vervolg-pagina's met `page + 1`
   * tot `hasMore === false`. Server sorteert op meldingsnummer aflopend.
   *
   * Query-param-regels (alleen meesturen wanneer afwijkend van de default):
   *  - `page` 0-based; `pageSize` (FE stuurt 50, BE capt op 100);
   *  - `q` vrije zoekterm (kenteken/klant/nummer/maatschappij); leeg → weggelaten;
   *  - `status` `'all'` toont ook verwerkte meldingen; default (`'open'`) wordt
   *    weggelaten (BE-default = openstaand);
   *  - `urgent=true` filtert op spoed; `false`/`undefined` → weggelaten.
   *
   * `signal` (optioneel) laat de caller een lopende fetch annuleren bij een
   * snelle herzoek (laatste-wint).
   */
  getIntakes(
    opts?: GetIntakesParams,
    signal?: AbortSignal,
  ): Promise<IntakeListResponse> {
    const query: Record<string, string | number | boolean | string[] | undefined> = {};
    if (typeof opts?.page === 'number' && opts.page > 0) {
      query.page = opts.page;
    }
    if (typeof opts?.pageSize === 'number') {
      query.pageSize = opts.pageSize;
    }
    if (opts?.q) {
      query.q = opts.q;
    }
    // Default-stand 'open' → niet meesturen (BE-default); alleen 'all' expliciet.
    if (opts?.status === 'all') {
      query.status = 'all';
    }
    if (opts?.urgent === true) {
      query.urgent = 'true';
    }
    return this.request('GET', '/api/intakes', { query, signal });
  }

  /**
   * POST /api/intakes/{nummer}/accept — accepteert de melding en maakt een
   * dossier aan. Bij 200 navigeert de FE direct naar `/dossiers/{dossierId}`.
   * Faal-responsen (400 invalid_appointment_choice / 404 / 409 already_processed
   * / 500) komen als `ApiError` met een `{ reason }`-body; call-sites
   * discrimineren op `error.status` + `error.body.reason`.
   */
  acceptIntake(intakeNumber: number, body: IntakeAcceptRequest): Promise<IntakeAcceptResponse> {
    return this.request('POST', `/api/intakes/${encodeURIComponent(String(intakeNumber))}/accept`, { body });
  }

  /**
   * POST /api/intakes/{nummer}/reject — weigert de melding met een verplichte
   * reden. 400 `reject_reason_required` bij een ontbrekende reden; 409
   * `already_processed` bij een al-verwerkte melding.
   */
  rejectIntake(intakeNumber: number, body: IntakeRejectRequest): Promise<IntakeRejectResponse> {
    return this.request('POST', `/api/intakes/${encodeURIComponent(String(intakeNumber))}/reject`, { body });
  }

  // ─── Melding context-rail (story fe-be-melding-detail-contextrail) ──────────

  /**
   * GET /api/intakes/{nummer}/audit — read-only WF_Trace-audit-historie van de
   * melding-case, voor de audit-drawer in de context-rail. Nieuwste boven (BE
   * sorteert), gecapt op 200 met `truncated`-signaal. Lazy: de drawer roept dit
   * pas bij openen aan. Cross-tenant / geen toegang → 404. Geen traces →
   * `{ items: [], truncated: false }`. Spiegelt `getDossierAudit`. Read-only GET
   * (geen CSRF). Cache-key `['intake-audit', nummer]` (los van `['intakes', nr]`).
   */
  getIntakeAudit(intakeNumber: number): Promise<DossierAudit> {
    return this.request('GET', `/api/intakes/${encodeURIComponent(String(intakeNumber))}/audit`);
  }

  /**
   * POST /api/intakes/{nummer}/comments — opmerking op een melding aanmaken (via
   * `Interaction.GD_CommunicationAdd`, server-side; `Entity="UA_Intake"`). Het
   * communicatietype wordt server-side per-request op "Opmerking" geresolved, dus
   * de body bevat alleen `{ message? }`. CSRF-header automatisch (mutating). 201
   * met de aangemaakte opmerking (zelfde `DossierComment`-shape als het dossier).
   * Geen toegang → 404; write-fout → 500.
   */
  addIntakeComment(
    intakeNumber: number,
    body: import('./types.js').IntakeCommentCreateRequest,
  ): Promise<import('./types.js').DossierComment> {
    return this.request(
      'POST',
      `/api/intakes/${encodeURIComponent(String(intakeNumber))}/comments`,
      { body },
    );
  }

  // ─── Globaal zoeken — ⌘K command-palette (story fe-be-globaal-zoeken) ──────

  /**
   * GET /api/search?q=<term>[&license=<12-AB-CD>] — het lichte read-only
   * zoek-endpoint dat in één call drie gescopede SELECTs draait (dossiers /
   * klanten / meldingen) en per groep top-5 weergavevelden + id teruggeeft.
   * Eén sessie-lock i.p.v. drie losse FE-calls (AppServices serialiseert per
   * sessie).
   *
   * Param-regels:
   *  - `q` — de getrimde zoekterm; de caller stuurt nooit < 2 tekens (de hook
   *    gate't op `term.trim().length >= 2`). De BE is defense-in-depth en geeft
   *    bij < 2 tekens een lege payload terug zonder DB-hit (AC20).
   *  - `license` — optionele kenteken-hint (canonieke streepjes-vorm via
   *    `formatNlLicense`) wanneer de term — na strippen — exact 6 alfanumerieke
   *    tekens is (`isLicenseLookupReady`). De oorspronkelijke `q` blijft ook
   *    meegaan zodat een 6-cijferig getal óók op dossier-/factuurnummer matcht
   *    (AC7). Leeg/`undefined` → niet op de wire.
   *
   * `signal` (optioneel) laat de hook een lopende fetch annuleren bij een
   * volgende toetsaanslag (AbortController; laatste-term-wint, AC6).
   */
  searchGlobal(
    q: string,
    license?: string | null,
    signal?: AbortSignal,
  ): Promise<GlobalSearchResponse> {
    const query: Record<string, string | number | boolean | string[] | undefined> = { q };
    // Kenteken-hint alleen meesturen wanneer aanwezig; lege string/null → weg.
    if (license != null && license !== '') {
      query.license = license;
    }
    return this.request('GET', '/api/search', { query, signal });
  }

  // ─── Sets-tab (story 3 artikelbeheer) ─────────────────────────────────────

  /** Laadt alle sets + componenten voor een artikel (gefilterd via HasAccess). */
  getItemSets(itemId: string, signal?: AbortSignal): Promise<ItemSetDto[]> {
    return this.request('GET', `/api/items/${encodeURIComponent(itemId)}/sets`, { signal });
  }

  /**
   * Levert de prijslijsten waarop voor dit artikel nog een set kan worden
   * aangemaakt (eigen prijslijsten; System: alle), reeds gesorteerd op naam.
   * Autoritatieve bron voor de "Set aanmaken"-dropdown.
   */
  getItemSetAvailablePriceLists(itemId: string, signal?: AbortSignal): Promise<ItemSetPriceListOptionDto[]> {
    return this.request('GET', `/api/items/${encodeURIComponent(itemId)}/sets/available-pricelists`, { signal });
  }

  /** Maakt een nieuwe set-header aan voor het artikel op de gegeven prijslijst. */
  createItemSet(itemId: string, body: ItemSetCreateRequest): Promise<ItemSetDto> {
    return this.request('POST', `/api/items/${encodeURIComponent(itemId)}/sets`, { body });
  }

  // ─── Glasdefinitie (Story 4, System-only) ────────────────────────────────

  /** Laadt de glasdefinitie (UA_Eurocode-velden) van een artikel. */
  getGlassDefinition(itemId: string, signal?: AbortSignal): Promise<GlassDefinitionDto> {
    return this.request('GET', `/api/items/${encodeURIComponent(itemId)}/glass-definition`, { signal });
  }

  /** Laadt de 9 lookup-lijsten voor de Glasdefinitie-dropdowns. */
  getGlassLookups(signal?: AbortSignal): Promise<GlassLookups> {
    return this.request('GET', '/api/items/glass-definition/lookups', { signal });
  }

  /** "Maak definities": maakt het UA_Eurocode-record aan (auto-init uit de code). */
  createGlassDefinition(itemId: string): Promise<GlassDefinitionDto> {
    return this.request('POST', `/api/items/${encodeURIComponent(itemId)}/glass-definition`, {});
  }

  /** Slaat de 9 glas-FK-velden op. */
  updateGlassDefinition(itemId: string, body: GlassDefinitionWritePayload): Promise<GlassDefinitionDto> {
    return this.request('PUT', `/api/items/${encodeURIComponent(itemId)}/glass-definition`, { body });
  }

  // ─── Interchangeables (Story 4, System-only) ─────────────────────────────

  /** Laadt de uitwisselgroep van een artikel (parent + gekoppelde leden). */
  getInterchangeables(itemId: string, signal?: AbortSignal): Promise<InterchangeablesDto> {
    return this.request('GET', `/api/items/${encodeURIComponent(itemId)}/interchangeables`, { signal });
  }

  /** Maakt van het artikel de parent van een nieuwe uitwisselgroep. */
  makeInterchangeableGroup(itemId: string): Promise<InterchangeablesDto> {
    return this.request('POST', `/api/items/${encodeURIComponent(itemId)}/interchangeables/group`, {});
  }

  /** Voegt een artikel toe aan de uitwisselgroep van dit artikel. */
  addInterchangeableMember(itemId: string, memberItemId: string): Promise<InterchangeablesDto> {
    return this.request('POST', `/api/items/${encodeURIComponent(itemId)}/interchangeables/members`, {
      body: { memberItemId },
    });
  }

  /** Ontkoppelt een lid van de uitwisselgroep. */
  removeInterchangeableMember(itemId: string, memberId: string): Promise<InterchangeablesDto> {
    return this.request('DELETE', `/api/items/${encodeURIComponent(itemId)}/interchangeables/members/${encodeURIComponent(memberId)}`);
  }

  /**
   * Verwijdert een hele set (componenten + header). 204 bij succes; 403
   * `set_owner_mismatch`, 404 als de set niet (meer) bestaat, 409
   * `set_version_conflict` als de set na het verwijderen nog bestaat.
   */
  deleteItemSet(itemId: string, setId: string): Promise<void> {
    return this.request('DELETE', `/api/items/${encodeURIComponent(itemId)}/sets/${encodeURIComponent(setId)}`);
  }

  /** Werkt de set-header bij (Quantity). */
  updateItemSet(itemId: string, setId: string, body: ItemSetUpdateRequest): Promise<ItemSetDto> {
    return this.request('PUT', `/api/items/${encodeURIComponent(itemId)}/sets/${encodeURIComponent(setId)}`, { body });
  }

  /** Voegt een component toe aan de set. */
  addItemSetComponent(itemId: string, setId: string, body: ItemSetComponentCreateRequest): Promise<ItemSetComponentDto> {
    return this.request('POST', `/api/items/${encodeURIComponent(itemId)}/sets/${encodeURIComponent(setId)}/components`, { body });
  }

  /** Werkt een component bij (Quantity, UnitPrice, Description). */
  updateItemSetComponent(itemId: string, setId: string, rowId: number, body: ItemSetComponentUpdateRequest): Promise<ItemSetComponentDto> {
    return this.request('PUT', `/api/items/${encodeURIComponent(itemId)}/sets/${encodeURIComponent(setId)}/components/${rowId}`, { body });
  }

  /** Verwijdert een component. */
  deleteItemSetComponent(itemId: string, setId: string, rowId: number): Promise<void> {
    return this.request('DELETE', `/api/items/${encodeURIComponent(itemId)}/sets/${encodeURIComponent(setId)}/components/${rowId}`);
  }

  // ─── Facturatie-export (story fe-be-facturatie-export-achtergrond) ──────────

  /**
   * GET /api/facturatie/export/status — de machine-leesbare start-status
   * (State 1 vs 1b) + e-mail-prefill (AC 10). BE = source-of-truth: de FE leest
   * `canStart`/`blockReason`/`runningSince` en bepaalt alleen de presentatie-
   * tekst. Read-only (geen CSRF). Geen polling — de bel-melding is het klaar-
   * signaal; de FE refetcht deze status alleen op mount + na een geslaagde POST.
   */
  getFacturatieExportStatus(signal?: AbortSignal): Promise<FacturatieExportStatus> {
    return this.request('GET', '/api/facturatie/export/status', { signal });
  }

  /**
   * POST /api/facturatie/export — start de export op de achtergrond (AC 11).
   * `email` gaat als enige body-veld mee (de gebruiker-editbare prefill). CSRF
   * automatisch (mutating). Bij succes: **202** met `{ status, runId }`. Bij een
   * al-actieve export: **409** met `{ reason: "export_already_running" }` → komt
   * als `ApiError` (status 409) terug; de call-site mapt dat naar de "loopt al"-
   * staat (State 1b), geen harde fout-toast.
   */
  startFacturatieExport(email: string): Promise<FacturatieExportStarted> {
    return this.request('POST', '/api/facturatie/export', { body: { email } });
  }

  /**
   * GET /api/facturatie/exports — de laatste export-bestanden van dit bedrijf
   * ("Recente exports", AC 20). Company-gescoped server-side via de journaalentry
   * (no-leak). Read-only (geen CSRF).
   */
  getFacturatieExports(signal?: AbortSignal): Promise<FacturatieExportList> {
    return this.request('GET', '/api/facturatie/exports', { signal });
  }

  /**
   * Bouwt de absolute download-URL voor één export-attachment (AC 21). De
   * download zelf is een gewone GET die de browser afhandelt (via een echte
   * `<a href>` of `window.open`) zodat de `Content-Disposition: attachment` van
   * de BE de bestandsnaam bepaalt — geen fetch+blob nodig. Company-gescoped +
   * 404-no-leak server-side.
   */
  facturatieExportDownloadUrl(attachmentId: string): string {
    return `${this.baseUrl}/api/facturatie/exports/${encodeURIComponent(attachmentId)}/download`;
  }

  // ─── Facturen-overzicht + journaalregels (story fe-be-facturen-overzicht-journaalregels) ──

  /**
   * GET /api/facturatie/invoices — de company-gescopede, keyset-gepagineerde
   * facturenlijst (gearchiveerd uitgesloten, server-side). Mirror van
   * {@link getDossiers}: `cursor` opaque uit de vorige respons (leeg = eerste
   * pagina), `q` vrije zoekterm (debounced FE-side), `sort`/`dir` uit de
   * allowlist (default `invoiceDate`/`desc`), `pageSize` default 50 (BE cap 100).
   * `signal` annuleert een lopende fetch bij een snelle herzoek.
   */
  getFacturen(opts?: GetFacturenParams, signal?: AbortSignal): Promise<FacturenListResponse> {
    const query: Record<string, string | number | undefined> = {};
    if (opts?.cursor) query.cursor = opts.cursor;
    if (opts?.q) query.q = opts.q;
    if (opts?.sort) query.sort = opts.sort;
    if (opts?.dir) query.dir = opts.dir;
    if (typeof opts?.pageSize === 'number') query.pageSize = opts.pageSize;
    return this.request('GET', '/api/facturatie/invoices', { query, signal });
  }

  /** GET /api/facturatie/invoices/{id} — factuur-detail (header + factuurregels).
   * Cross-company/onbekend → 404 (no-leak). */
  getFactuur(id: string, signal?: AbortSignal): Promise<FactuurDetail> {
    return this.request('GET', `/api/facturatie/invoices/${encodeURIComponent(id)}`, { signal });
  }

  /** GET /api/facturatie/invoices/{id}/journal-lines — de journaalregels van de
   * (al-gescopede) factuur + `hasExported`-vlag voor de vrijgeef-knop. */
  getFactuurJournalLines(id: string, signal?: AbortSignal): Promise<FactuurJournalLines> {
    return this.request('GET', `/api/facturatie/invoices/${encodeURIComponent(id)}/journal-lines`, { signal });
  }

  /**
   * PATCH /api/facturatie/invoices/{id}/journal-lines — grootboek-batch-opslag
   * (all-or-nothing). CSRF automatisch. 422 = een geëxporteerde regel; 400 =
   * ongeldige grootboekwaarde; 409 = SameVersion-conflict (herlaad). Bij succes:
   * de ververste journaalregels (zonder handmatige refetch).
   */
  saveFactuurLedgerNumbers(
    id: string,
    lines: JournalLedgerEdit[],
  ): Promise<FactuurJournalLines> {
    return this.request(
      'PATCH',
      `/api/facturatie/invoices/${encodeURIComponent(id)}/journal-lines`,
      { body: { lines } },
    );
  }

  /**
   * POST /api/facturatie/invoices/{id}/journal-lines/release — "Alle regels
   * vrijgeven" (zet JournalEntryID=null op alle geëxporteerde regels, factuur-
   * niveau, all-or-nothing). CSRF automatisch. 409 = SameVersion-conflict.
   */
  releaseFactuurJournalLines(id: string): Promise<FactuurJournalLines> {
    return this.request(
      'POST',
      `/api/facturatie/invoices/${encodeURIComponent(id)}/journal-lines/release`,
    );
  }

  /**
   * GET /api/facturatie/invoices/{id}/pdf — de "altijd werkt"-factuur-PDF via de
   * default-template (story `factuur-default-template-afdrukken`). Config-
   * onafhankelijk (geen afdruklijst/VisibleCondition/inrichting). Geeft de PDF
   * als `Blob` terug (open/download FE-side). Cross-company/onbekend → 404.
   */
  getFactuurPdf(id: string): Promise<Blob> {
    return this.request(
      'GET',
      `/api/facturatie/invoices/${encodeURIComponent(id)}/pdf`,
      { responseType: 'blob' },
    );
  }

  /**
   * POST /api/facturatie/invoices/{id}/credit — crediteert één factuur (story
   * `fe-be-factuur-crediteren`). CSRF automatisch. Guards → 409 met `reason`
   * (`invoice_is_credit` / `credit_function_disabled` / `no_dossier` /
   * `already_credited`); cross-company/onbekend → 404. Respons is best-effort
   * `{ ok, creditInvoiceNumber? }` (AC2).
   */
  crediteerFactuur(id: string): Promise<CrediteerFactuurResult> {
    return this.request(
      'POST',
      `/api/facturatie/invoices/${encodeURIComponent(id)}/credit`,
    );
  }

  /**
   * POST /api/facturatie/invoices/{id}/mail — mailt dezelfde default-render als
   * bijlage naar de debiteur (of een `to`-override). CSRF automatisch. Geen adres
   * → 400 (`no_recipient`); ontbrekende SMTP-key → 500 (`mail_config_missing`).
   */
  mailFactuur(id: string, body?: FactuurMailRequest): Promise<{ ok: boolean }> {
    return this.request(
      'POST',
      `/api/facturatie/invoices/${encodeURIComponent(id)}/mail`,
      { body: body ?? {} },
    );
  }

  /**
   * GET /api/facturatie/dashboard — het facturatie-dashboard-aggregaat (story
   * `fe-be-facturatie-dashboard`, 2026-07-15). Eén call → de omzet-reeks (3 jaar
   * × 12 maandpunten) + het aantal facturen van het lopende jaar. Owner-only
   * gescoopt server-side op `CurrentSession.CompanyId`. Degradeert als één
   * eenheid; de FE leidt KPI 1/2/4 af uit `series` en leest KPI 3 uit
   * `invoiceCountCurrentYear`.
   */
  getFacturatieDashboard(signal?: AbortSignal): Promise<FacturatieDashboard> {
    return this.request('GET', '/api/facturatie/dashboard', { signal });
  }

  // ─── Grootboeken inrichten (story grootboeken-inrichten) ────────────────────

  // ─── Factuur mailen (rijke popup, story fe-be-factuur-mailen-popup) ──────────

  /**
   * GET /api/facturatie/invoices/{id}/mail-recipients — ontvanger-suggesties;
   * `defaultTo` = de debiteur-e-mail. Bij een aan een dossier gekoppelde factuur
   * ook de dossier-relatiegraaf (klant/bestuurder/opdrachtgever/hersteller/
   * verzekeraar).
   */
  getInvoiceMailRecipients(id: string, signal?: AbortSignal): Promise<MailRecipientsResponse> {
    return this.request(
      'GET',
      `/api/facturatie/invoices/${encodeURIComponent(id)}/mail-recipients`,
      { signal },
    );
  }

  /**
   * GET /api/facturatie/invoices/{id}/mail-templates — de company-mailtemplates +
   * de per-hersteller `defaultTemplateId` (`null` als de default niet in de
   * zichtbare lijst zit — F-04).
   */
  getInvoiceMailTemplates(id: string, signal?: AbortSignal): Promise<InvoiceMailTemplatesResponse> {
    return this.request(
      'GET',
      `/api/facturatie/invoices/${encodeURIComponent(id)}/mail-templates`,
      { signal },
    );
  }

  /**
   * GET /api/facturatie/invoices/{id}/mail-templates/{templateId}/render —
   * rendert de template tegen het gekoppelde DOSSIER (F-01) → `{ subject, body }`
   * (body = HTML). Zonder dossier → 200 met lege body (best-effort, F-02).
   */
  renderInvoiceMailTemplate(
    id: string,
    templateId: string,
    signal?: AbortSignal,
  ): Promise<MailTemplateRender> {
    return this.request(
      'GET',
      `/api/facturatie/invoices/${encodeURIComponent(id)}/mail-templates/${encodeURIComponent(templateId)}/render`,
      { signal },
    );
  }

  /**
   * POST /api/facturatie/invoices/{id}/mail (rijke popup) — verstuurt de factuur
   * met ontvangers/onderwerp/bericht + optionele dossier-bijlagen & combine-opties.
   * CSRF automatisch. Ongeldige ontvanger → 400 (`invalid_recipient`); ontbrekende
   * SMTP-key → 500 (`mail_config_missing`).
   */
  sendInvoiceMail(id: string, body: InvoiceMailSendRequest): Promise<{ ok: boolean }> {
    return this.request(
      'POST',
      `/api/facturatie/invoices/${encodeURIComponent(id)}/mail`,
      { body },
    );
  }

  /**
   * GET /api/profile/invoice-mail-template — de per-hersteller standaard
   * factuur-mail-template (`defaultTemplateId` = `null` als er geen default is).
   */
  getInvoiceMailDefaultTemplate(signal?: AbortSignal): Promise<InvoiceMailDefaultTemplateResponse> {
    return this.request('GET', '/api/profile/invoice-mail-template', { signal });
  }

  /**
   * PUT /api/profile/invoice-mail-template — zet (of wist met `null`) de
   * per-hersteller standaard factuur-mail-template. CSRF automatisch. Een
   * template-id buiten de eigen company → 400 (`invalid_template`).
   */
  putInvoiceMailDefaultTemplate(
    templateId: string | null,
  ): Promise<InvoiceMailDefaultTemplateResponse> {
    return this.request('PUT', '/api/profile/invoice-mail-template', {
      body: { defaultTemplateId: templateId },
    });
  }

  /**
   * GET /api/facturatie/ledgers — bootstrap: maakt de 3 debiteurengroepen
   * (NL/EEG/buiten EEG) aan als ze ontbreken + seedt de missende grootboek-rijen
   * voor alle vier de sets (Geen/null + NL + EEG + buiten EEG) in één handeling,
   * idempotent. Retourneert de volledige structuur (groepen + alle rijen; FE
   * filtert per groep × sectie).
   */
  getLedgers(signal?: AbortSignal): Promise<LedgersBootstrap> {
    return this.request('GET', '/api/facturatie/ledgers', { signal });
  }

  /**
   * PUT /api/facturatie/ledgers/{id} — werkt UITSLUITEND het verkoop-
   * grootboeknummer bij (read-modify-write; inkoop round-trippt). Leeg én niet-
   * numeriek is geldig (geen 400). CSRF automatisch. Cross-company/onbekend → 404.
   */
  updateLedgerSalesNumber(id: string, salesLedgerNumber: string): Promise<LedgerRow> {
    return this.request(
      'PUT',
      `/api/facturatie/ledgers/${encodeURIComponent(id)}`,
      { body: { salesLedgerNumber } },
    );
  }

  /**
   * POST /api/facturatie/ledgers/items — voegt een artikelcode toe als grootboek-
   * rij in ÉLKE debiteurengroep (incl. "Geen"/null). Dubbele/lege code = no-op.
   * CSRF automatisch. Retourneert de ververste structuur.
   */
  addLedgerItem(code: string): Promise<LedgersBootstrap> {
    return this.request('POST', '/api/facturatie/ledgers/items', { body: { code } });
  }

  /**
   * DELETE /api/facturatie/ledgers/items/{id} — verwijdert de artikelcode-rijen in
   * ÁLLE debiteurengroepen (symmetrisch met toevoegen). CSRF automatisch. Cross-
   * company/onbekend → 404. Retourneert de ververste structuur.
   */
  deleteLedgerItem(id: string): Promise<LedgersBootstrap> {
    return this.request('DELETE', `/api/facturatie/ledgers/items/${encodeURIComponent(id)}`);
  }

  // ─── Exportinstellingen (story exportinstellingen) ──────────────────────────

  /**
   * GET /api/facturatie/export-settings — bootstrap voor het scherm
   * "Exportinstellingen": seedt idempotent de ontbrekende
   * `UF_CompanyInvoiceSetting`-keys (A3-defaults) en retourneert de huidige
   * waarden, de exportroutine + de per-request geresolveerde routine-opties/btw-
   * tarieven. Hersteller-ONLY (BE 403 no_repairer_context
   * voor System/RegularUser). Read-only (geen CSRF).
   */
  getExportSettings(signal?: AbortSignal): Promise<ExportSettingsBootstrap> {
    return this.request('GET', '/api/facturatie/export-settings', { signal });
  }

  /**
   * PUT /api/facturatie/export-settings — slaat de gewijzigde instellingen op in
   * één atomaire transactie (alles-of-niets, A7). Per key read-modify-write met
   * Version-round-trip → 409 `version_conflict` bij een concurrente wijziging;
   * `DiscountOwnRisk*` buiten "incl"/"excl" → 400 `invalid_field_value`;
   * een routine buiten de toegestane set → 400. Company/repairer worden
   * server-side afgeleid (nooit uit de body). CSRF
   * automatisch. Retourneert de ververste structuur.
   */
  saveExportSettings(body: ExportSettingsSaveRequest): Promise<ExportSettingsBootstrap> {
    return this.request('PUT', '/api/facturatie/export-settings', { body });
  }

  // ─── Nummering — factuurreeks per jaar (story nummering-factuurreeks-per-jaar) ─

  /**
   * GET /api/facturatie/numbering — bootstrap voor het scherm "Nummering":
   * seedt idempotent de ontbrekende voorkeur-keys (`InvoiceNumberYearly="false"`,
   * `InvoiceNumberYearDigits="4"`) en retourneert de globale kill-switch, de
   * voorkeur, de bewerkbare factuur-reeksen en of een 4→2-formaatwissel nu
   * geblokkeerd is. Hersteller-ONLY (BE 403 no_repairer_context voor
   * System/RegularUser). Read-only (geen CSRF).
   */
  getNumbering(signal?: AbortSignal): Promise<NumberingBootstrap> {
    return this.request('GET', '/api/facturatie/numbering', { signal });
  }

  /**
   * PUT /api/facturatie/numbering — slaat de opt-in + formaat + de bewerkte
   * reeksen op in één atomaire transactie (alles-of-niets). Per key/reeks
   * read-modify-write met Version-round-trip → 409 `version_conflict`; ongeldig
   * formaat/reeks-nummer → 400 `invalid_field_value`; opt-in aanzetten terwijl de
   * globale switch uit is → 400 `yearly_globally_disabled`; formaatwissel 4→2 met
   * bestaande 4-cijferige reeks → 400 `year_format_downgrade_blocked`. Company/
   * repairer server-side afgeleid (nooit uit de body). CSRF automatisch.
   */
  saveNumbering(body: NumberingSaveRequest): Promise<NumberingBootstrap> {
    return this.request('PUT', '/api/facturatie/numbering', { body });
  }

  // ─── Factuurdocumenten (story fe-be-factuurdocumenten-story1, READ) ─────────

  /**
   * GET /api/facturatie/invoice-documents — de READ-ONLY factuurdocumenten-matrix
   * van de eigen company: per factuurtype (alleen types met ≥1 rij) vier cellen
   * (`IsInsured` × `IsCredit`) met de geconfigureerde documenten. De volledige
   * config komt in ÉÉN call → de factuurtype-switch is client-side (geen tweede
   * call). Company-impliciet via de sessie; company zonder config →
   * `usesDefaultTemplate = true`. Read-only (geen CSRF).
   */
  getInvoiceDocuments(signal?: AbortSignal): Promise<InvoiceDocumentsMatrix> {
    return this.request('GET', '/api/facturatie/invoice-documents', { signal });
  }

  // ─── Factuurdocumenten bewerken (story fe-be-factuurdocumenten-story2) ──────

  /**
   * GET /api/facturatie/invoice-documents/reports — de company-zichtbare
   * `UA_Report`-lijst voor de "+ document toevoegen"-picker (Story 2, AC4).
   * Company-impliciet via de sessie. Read-only (geen CSRF).
   */
  getInvoiceDocumentReports(signal?: AbortSignal): Promise<InvoiceReportOptions> {
    return this.request('GET', '/api/facturatie/invoice-documents/reports', { signal });
  }

  /**
   * GET /api/facturatie/invoice-documents/types — de ALGEMENE `UF_InvoiceType`-set
   * (System-owned, voor iedereen zichtbaar) voor de "+ factuurtype"-picker (story 2b).
   * Company-ONAFHANKELIJK (geen OwnerID-filter). Read-only (geen CSRF).
   */
  getInvoiceTypes(signal?: AbortSignal): Promise<InvoiceTypeOptions> {
    return this.request('GET', '/api/facturatie/invoice-documents/types', { signal });
  }

  // ─── Inrichting / Dossierdocumenten-verzendregels (DD_*) ─────────────────
  //
  // Story `fe-be-dossierdocumenten-story1-beheerscherm`. Beslistabel per partij
  // + standaard mailtemplate. Company-gescoped BE-zijdig (sessie-company).

  /** Volledige beheerscherm-config (4 partijen + picker-bronnen) van de eigen company. */
  getDeliveryRulesConfig(): Promise<DeliveryConfig> {
    return this.request('GET', '/api/inrichting/delivery-rules/config');
  }

  /** Maakt één regel + zijn documenten aan (atomair BE-zijdig). */
  createDeliveryRule(req: DeliveryRuleWriteRequest): Promise<DeliveryRule> {
    return this.request('POST', '/api/inrichting/delivery-rules/rules', { body: req });
  }

  /** Werkt één regel bij (version-gescheckt; 409 `stale_version` bij stale write). */
  updateDeliveryRule(id: string, req: DeliveryRuleWriteRequest): Promise<DeliveryRule> {
    return this.request('PUT', `/api/inrichting/delivery-rules/rules/${id}`, { body: req });
  }

  /**
   * Verwijdert één regel (+ documenten) en hernummert de resterende regels
   * contigu. De optionele `version` gaat als query-param mee (409 bij stale).
   */
  deleteDeliveryRule(id: string, version?: number): Promise<{ id: string }> {
    return this.request('DELETE', `/api/inrichting/delivery-rules/rules/${id}`, {
      query: version === undefined ? undefined : { version },
    });
  }

  /** Zet de standaard mailtemplate van een partij (`mailTemplateId: null` = "— geen —"). */
  setDeliveryPartyMailTemplate(
    req: PartyMailTemplateWriteRequest,
  ): Promise<{ party: number; mailTemplateId: string | null }> {
    return this.request('PUT', '/api/inrichting/delivery-rules/mail-template', { body: req });
  }

  // ─── Inrichting / Standaarddocumenten (story be-fe-standaarddocumenten, G2) ──
  //
  // Eén lijst per instantie (DD_DefaultDeliveryRule), alleen voor System (BE 403 `role_not_allowed`).
  // Bron 3 van het voorstel op het dossier.

  /** De standaardlijst (4 partijen) + de documentkiezer over de hele instantie. */
  getDefaultDeliveryRulesConfig(): Promise<DefaultDeliveryConfig> {
    return this.request('GET', '/api/inrichting/default-delivery-rules/config');
  }

  /** Maakt één standaardregel aan. */
  createDefaultDeliveryRule(req: DefaultDeliveryRuleWriteRequest): Promise<DefaultDeliveryRule> {
    return this.request('POST', '/api/inrichting/default-delivery-rules/rules', { body: req });
  }

  /** Werkt één standaardregel bij (version-gescheckt; 409 `stale_version`). */
  updateDefaultDeliveryRule(id: string, req: DefaultDeliveryRuleWriteRequest): Promise<DefaultDeliveryRule> {
    return this.request('PUT', `/api/inrichting/default-delivery-rules/rules/${id}`, { body: req });
  }

  /** Verwijdert één standaardregel; de optionele `version` gaat als query-param mee (409 bij stale). */
  deleteDefaultDeliveryRule(id: string, version?: number): Promise<{ id: string }> {
    return this.request('DELETE', `/api/inrichting/default-delivery-rules/rules/${id}`, {
      query: version === undefined ? undefined : { version },
    });
  }

  /** Vult een LEGE lijst eenmalig uit de geschiedenis (409 `defaults_not_empty` als hij al gevuld is). */
  seedDefaultDeliveryRulesFromHistory(): Promise<DefaultDeliverySeedResult> {
    return this.request('POST', '/api/inrichting/default-delivery-rules/seed-from-history');
  }

  /**
   * POST /api/facturatie/invoice-documents — voegt een document toe aan een cel.
   * De BE valideert `invoiceTypeId` + `reportId` binnen de sessie-company (AC-W4)
   * en zet `CompanyID = @SessionCompanyId` + `Ordinal = MAX+1` + `AutoMail = false`.
   */
  addInvoiceDocument(body: AddInvoiceDocumentRequest): Promise<InvoiceDocument> {
    return this.request('POST', '/api/facturatie/invoice-documents', { body });
  }

  /**
   * PATCH /api/facturatie/invoice-documents/{rowId} — zet de opt-in mail-schakelaar
   * (Story 2, toggle-1). Company-gescoped → onbekend/andere company → 404.
   */
  setInvoiceDocumentAutoMail(rowId: string, autoMail: boolean): Promise<InvoiceDocument> {
    return this.request(
      'PATCH',
      `/api/facturatie/invoice-documents/${encodeURIComponent(rowId)}`,
      { body: { autoMail } },
    );
  }

  /**
   * DELETE /api/facturatie/invoice-documents/{rowId} — verwijdert PRECIES die rij
   * (Story 2, AC7/AC8 — ook bij exacte duplicaten). Company-gescoped → 404.
   */
  deleteInvoiceDocument(rowId: string): Promise<{ rowId: string }> {
    return this.request(
      'DELETE',
      `/api/facturatie/invoice-documents/${encodeURIComponent(rowId)}`,
    );
  }

  /**
   * PATCH /api/facturatie/invoice-documents/reorder — herschrijft `Ordinal` binnen
   * ÉÉN cel (Story 2, AC9). De set moet compleet + eigen-company + één-cel zijn
   * (11a) → anders 400. Retourneert de nieuwe geordende cel.
   */
  reorderInvoiceDocuments(body: ReorderInvoiceDocumentsRequest): Promise<InvoiceDocumentCell> {
    return this.request('PATCH', '/api/facturatie/invoice-documents/reorder', { body });
  }

  // ─── Documentatie-module (story fe-be-documentatie-module) ──────────────────

  /**
   * GET /api/documentation/tree — de volledige documentatie-boom (groepen →
   * categorieën → documenten), BE-side gefilterd op de effectieve doelgroep van
   * de caller (afgeschermde items ontbreken volledig — geen titel-/bestaan-lek).
   * `canManage` in de respons is BE-autoritatief. Read-only (geen CSRF).
   */
  getDocumentationTree(signal?: AbortSignal): Promise<DocTreeResponse> {
    return this.request('GET', '/api/documentation/tree', { signal });
  }

  /**
   * GET /api/documentation/documents/{id} — de inhoud van één document
   * (gesanitiseerde `bodyHtml` + bijlagen). 404 wanneer het document niet
   * zichtbaar is voor de caller (geen bestaan-lek).
   */
  getDocument(id: string, signal?: AbortSignal): Promise<DocDocumentDetail> {
    return this.request('GET', `/api/documentation/documents/${encodeURIComponent(id)}`, {
      signal,
    });
  }

  /** POST /api/documentation/groups — nieuwe groep (system/keten). 403 bij lezer. */
  createDocGroup(req: DocGroupCreateRequest): Promise<DocNodeMutationResponse> {
    return this.request('POST', '/api/documentation/groups', { body: req });
  }

  /** PUT /api/documentation/groups/{id} — groep bijwerken. */
  updateDocGroup(id: string, req: DocGroupUpdateRequest): Promise<DocNodeMutationResponse> {
    return this.request('PUT', `/api/documentation/groups/${encodeURIComponent(id)}`, { body: req });
  }

  /**
   * DELETE /api/documentation/groups/{id} — verwijderen. 409
   * `{ reason:'not_empty' }` wanneer de groep nog categorieën bevat (eerst legen).
   */
  deleteDocGroup(id: string): Promise<void> {
    return this.request('DELETE', `/api/documentation/groups/${encodeURIComponent(id)}`);
  }

  /** POST /api/documentation/categories — nieuwe categorie onder een groep. */
  createDocCategory(req: DocCategoryCreateRequest): Promise<DocNodeMutationResponse> {
    return this.request('POST', '/api/documentation/categories', { body: req });
  }

  /** PUT /api/documentation/categories/{id} — categorie bijwerken. */
  updateDocCategory(id: string, req: DocCategoryUpdateRequest): Promise<DocNodeMutationResponse> {
    return this.request('PUT', `/api/documentation/categories/${encodeURIComponent(id)}`, {
      body: req,
    });
  }

  /**
   * DELETE /api/documentation/categories/{id} — verwijderen. 409
   * `{ reason:'not_empty' }` wanneer de categorie nog documenten bevat.
   */
  deleteDocCategory(id: string): Promise<void> {
    return this.request('DELETE', `/api/documentation/categories/${encodeURIComponent(id)}`);
  }

  /**
   * POST /api/documentation/documents — nieuw document. `visibility: null`
   * (default) = erft de categorie. Geeft `{ id, version }` terug.
   */
  createDocument(req: DocDocumentCreateRequest): Promise<DocNodeMutationResponse> {
    return this.request('POST', '/api/documentation/documents', { body: req });
  }

  /**
   * PUT /api/documentation/documents/{id} — document bijwerken (inhoud/velden).
   * `version` verplicht → **409** `{ reason:'version_conflict', currentVersion }`
   * bij een verouderd token (optimistic locking). Bijlage-upload/-delete bumpt
   * de version NIET (aparte endpoints).
   */
  updateDocument(id: string, req: DocDocumentUpdateRequest): Promise<DocNodeMutationResponse> {
    return this.request('PUT', `/api/documentation/documents/${encodeURIComponent(id)}`, {
      body: req,
    });
  }

  /** DELETE /api/documentation/documents/{id} — verwijdert document + eigen bijlagen. */
  deleteDocument(id: string): Promise<void> {
    return this.request('DELETE', `/api/documentation/documents/${encodeURIComponent(id)}`);
  }

  /**
   * POST /api/documentation/documents/{id}/attachments — multipart-upload van
   * één of meer bestanden (PDF/afbeelding). Form-field-naam is `files` (spiegelt
   * `DossierAttachmentsController`). CSRF automatisch. Geeft `{ ids, count }`.
   *
   * @param signal optionele AbortSignal voor cancel-mid-upload.
   */
  uploadDocumentAttachments(
    documentId: string,
    files: File[],
    signal?: AbortSignal,
  ): Promise<DocAttachmentUploadResponse> {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f, f.name));
    return this.request(
      'POST',
      `/api/documentation/documents/${encodeURIComponent(documentId)}/attachments`,
      { formData: fd, signal },
    );
  }

  /** DELETE /api/documentation/attachments/{id} — verwijdert één bijlage. */
  deleteDocAttachment(id: string): Promise<void> {
    return this.request('DELETE', `/api/documentation/attachments/${encodeURIComponent(id)}`);
  }

  /**
   * URL-builder voor `/api/documentation/attachments/{id}/data` — direct
   * bruikbaar als `<a href>` voor de download. Identiek aan het `downloadUrl`-veld
   * in {@link DocAttachment}; hier als helper voor callers die alleen de id hebben.
   */
  documentationAttachmentDataUrl(id: string): string {
    return `${this.baseUrl}/api/documentation/attachments/${encodeURIComponent(id)}/data`;
  }

  // ─── Rapportage per rol (story rapportage-per-rol-fase1) ───────────────────

  /**
   * GET /api/rapportage/menu — Scherm B: de rapportenboom van de rol van de
   * INGELOGDE user (precedence System > Keten > Verzekeraar > Monteur >
   * Hersteller). Geen rol / rol zonder menu / kapotte root → lege staat
   * (`hasMenu:false`, `nodes:[]`), nooit een 500. Read-only (geen CSRF).
   */
  getRapportageMenu(signal?: AbortSignal): Promise<ReportMenuResponse> {
    return this.request('GET', '/api/rapportage/menu', { signal });
  }

  /**
   * GET /api/rapportage/admin/roles — Scherm A: de 5 vaste rollen (Hersteller,
   * Monteur, Verzekeraar, Keten, System) + hun huidige menu-toewijzing.
   * System-only (403 anders).
   */
  getRapportageRoles(signal?: AbortSignal): Promise<ReportRoleAssignment[]> {
    return this.request('GET', '/api/rapportage/admin/roles', { signal });
  }

  /**
   * GET /api/rapportage/admin/menus — de kiesbare rapportage-startmenu's
   * (GF_Function's van type Menu/MenuGroup) voor de dropdown. System-only.
   */
  getRapportageMenuOptions(signal?: AbortSignal): Promise<ReportMenuOption[]> {
    return this.request('GET', '/api/rapportage/admin/menus', { signal });
  }

  /**
   * GET /api/rapportage/admin/menus/{id}/preview — read-only recursieve
   * boom-preview van één menu (Scherm A, rechterpaneel). System-only.
   */
  getRapportageMenuPreview(menuFunctionId: string, signal?: AbortSignal): Promise<ReportMenuResponse> {
    return this.request(
      'GET',
      `/api/rapportage/admin/menus/${encodeURIComponent(menuFunctionId)}/preview`,
      { signal },
    );
  }

  /**
   * PUT /api/rapportage/admin/roles/{roleCode} — koppelt (upsert) of wist
   * (`menuFunctionId: null`) het menu van een rol. System-only + CSRF. 400 bij
   * onbekende rol / ongeldig of onbestaand menu-id.
   */
  assignRapportageRoleMenu(
    roleCode: string,
    body: AssignRoleMenuRequest,
  ): Promise<ReportRoleAssignment> {
    return this.request('PUT', `/api/rapportage/admin/roles/${encodeURIComponent(roleCode)}`, {
      body,
    });
  }

  // ─── Rapportage per rol — Fase 2 (parameterscherm + rapport draaien) ───────

  /**
   * GET /api/rapportage/rapport/{functionId}/params — het parameterscherm van een
   * rapport (rapportnaam, response-type, "opslaan-als-default"-gate + VISIBLE velden).
   * 403 buiten het eigen rol-menu, 404 op onbekend/niet-rapport. Read-only (geen CSRF).
   */
  getRapportageReportParams(functionId: string, signal?: AbortSignal): Promise<ReportParamScreen> {
    return this.request(
      'GET',
      `/api/rapportage/rapport/${encodeURIComponent(functionId)}/params`,
      { signal },
    );
  }

  /**
   * POST /api/rapportage/rapport/{functionId}/run — draait het rapport met de
   * ingevulde `fieldName → value`-paren. Geeft het rauwe `Response` terug zodat de
   * caller zelf kan splitsen: `application/json` → link-rapport (`{ url }`); anders →
   * bytes-download (PDF/Xlsx/HTML/XML/TXT, filename via `Content-Disposition`). CSRF.
   * 400 bij mandatory-leeg/coercie-fout, 403/404 op toegang, 500 bij engine-fout.
   */
  runRapportageReport(functionId: string, values: ReportRunValues): Promise<Response> {
    return this.request(
      'POST',
      `/api/rapportage/rapport/${encodeURIComponent(functionId)}/run`,
      { body: values, responseType: 'raw' },
    );
  }

  /**
   * POST /api/rapportage/rapport/{functionId}/save-defaults — bewaart de ingevulde
   * waarden als persoonlijke user-defaults (voor-invulling bij heropenen). CSRF.
   */
  saveRapportageReportDefaults(
    functionId: string,
    values: ReportRunValues,
  ): Promise<{ ok: boolean }> {
    return this.request(
      'POST',
      `/api/rapportage/rapport/${encodeURIComponent(functionId)}/save-defaults`,
      { body: values },
    );
  }

  // ─── Claimfilter-beheerscherm (story `fe-be-claimfilter-beheerscherm`) ────
  //
  // Zes READ-ONLY routes. ⚠ Ze prefetchen NIET parallel: `/api` serialiseert per
  // sessie, dus vier subschermen tegelijk ophalen zet ze achter elkaar in de rij
  // en vertraagt de rest van de app (AC-33).

  /** GET /api/claimfilter/overview — statusstrip, controles, vandaag-tegels én
   *  het scan-interval (dat laatste NOOIT uit een getal in de FE — AC-45). */
  getClaimFilterOverview(signal?: AbortSignal): Promise<ClaimFilterOverview> {
    return this.request('GET', '/api/claimfilter/overview', { signal });
  }

  /** GET /api/claimfilter/rounds — gepagineerd, stabiel gesorteerd op
   *  (StartedAt DESC, ID DESC) zodat rijen bij het bladeren niet verspringen. */
  getClaimFilterRounds(
    page: number,
    pageSize: number,
    signal?: AbortSignal,
  ): Promise<ClaimFilterRoundList> {
    return this.request('GET', '/api/claimfilter/rounds', { query: { page, pageSize }, signal });
  }

  /** GET /api/claimfilter/rounds/{id} — één ronde uitgeklapt. */
  getClaimFilterRound(id: string, signal?: AbortSignal): Promise<ClaimFilterRoundDetail> {
    return this.request('GET', `/api/claimfilter/rounds/${encodeURIComponent(id)}`, { signal });
  }

  /** GET /api/claimfilter/dossiers — de cumulatieve blik. */
  getClaimFilterDossiers(
    filter: string,
    signal?: AbortSignal,
  ): Promise<ClaimFilterDossierList> {
    return this.request('GET', '/api/claimfilter/dossiers', { query: { filter }, signal });
  }

  /** GET /api/claimfilter/health — de vier staande controles MÉT hun rijen. */
  getClaimFilterHealth(signal?: AbortSignal): Promise<ClaimFilterHealth> {
    return this.request('GET', '/api/claimfilter/health', { signal });
  }

  /**
   * GET /api/claimfilter/offered-count — "wachtend op 630".
   *
   * ⚠⚠ UITSLUITEND na een expliciete klik (AC-8, AC-19). Het enige selectieve
   * predicaat staat op `WF_Case.CaseStateID` zonder ondersteunende index
   * (386.588 rijen). Hem op een poll of bij het laden draaien is het
   * dashboard-incident van 2026-06-20 nabouwen.
   */
  getClaimFilterOfferedCount(signal?: AbortSignal): Promise<ClaimFilterOfferedCount> {
    return this.request('GET', '/api/claimfilter/offered-count', { signal });
  }

  /**
   * GET /api/claimfilter/claimfilters — het vijfde subscherm (§5.7, AC-46).
   *
   * ⚠ Het regel-aantal komt uit dezelfde query als de lijst (AC-57.3); er is
   * dus geen tweede call per rij. De route poll't niet (AC-57.5) — een
   * claimfilter verandert na zijn oordeel niet meer.
   */
  getClaimFilterRecords(
    params: {
      filter?: string;
      q?: string;
      dossierId?: string;
      from?: string;
      to?: string;
      page?: number;
      pageSize?: number;
    },
    signal?: AbortSignal,
  ): Promise<ClaimFilterRecordList> {
    return this.request('GET', '/api/claimfilter/claimfilters', { query: { ...params }, signal });
  }

  /**
   * GET /api/claimfilter/claimfilters/{number} — kop + regels + oordeel.
   *
   * ⚠⚠ **De enige route die de oordeel-reden meestuurt** (AC-56). De sleutel is
   * `Number` en niet de GUID: dat is wat een mens in de lijst ziet en wat een
   * support-medewerker aan de telefoon voorleest.
   *
   * Een onbekend nummer levert **404** mét het gevraagde nummer — nooit een 200
   * met lege velden (AC-55).
   */
  getClaimFilterRecord(number: number, signal?: AbortSignal): Promise<ClaimFilterRecordDetail> {
    return this.request(
      'GET',
      `/api/claimfilter/claimfilters/${encodeURIComponent(String(number))}`,
      { signal },
    );
  }

  // ─── De oordeelsregels (story `fe-be-claimfilter-oordeelsregels-beheer`) ───
  //
  // ⚠⚠ ANDER PREFIX EN ANDER PUBLIEK dan `/api/claimfilter/*` hierboven. Die
  //    routes zijn system/support-only en tonen afgeleiden van de oordeel-reden;
  //    deze zijn voor de VERZEKERAAR, over haar eigen inrichting. Zet ze nooit
  //    onder hetzelfde prefix "omdat het allebei claimfilter is" — dan wordt elke
  //    volgende route daar stilzwijgend een beslissing over wie hem mag zien.

  /** GET /api/claimfilter-rules/scopes — de basisset per maatschappij + haar uitzonderingen. */
  getClaimFilterRuleScopes(signal?: AbortSignal): Promise<ClaimFilterRuleScopes> {
    return this.request('GET', '/api/claimfilter-rules/scopes', { signal });
  }

  /**
   * GET /api/claimfilter-rules/scope — de zes metingen + de steekproef van één scope.
   *
   * ⚠ `repairerId` weglaten betekent DE BASISSET, niet "maakt niet uit". Die twee
   * mogen nooit samenvallen: de basisset is de scope die moet bestaan.
   */
  getClaimFilterRuleScope(
    insurerId: string,
    repairerId?: string,
    signal?: AbortSignal,
  ): Promise<ClaimFilterRuleSet> {
    return this.request('GET', '/api/claimfilter-rules/scope', {
      query: repairerId ? { insurerId, repairerId } : { insurerId },
      signal,
    });
  }

  /**
   * PUT /api/claimfilter-rules/scope — vervangt de inhoud van één scope.
   *
   * ⚠ Stuur ALTIJD alle zes de metingen mee. Een weggelaten meting is voor de
   * motor hetzelfde als een uitgeschakelde, maar dan zonder dat iemand die keuze
   * heeft vastgelegd — en dan is "bewust uit" niet te onderscheiden van "vergeten".
   * De BE weigert een onvolledige set met `incomplete_set`.
   */
  saveClaimFilterRuleScope(body: ClaimFilterRuleSaveRequest): Promise<ClaimFilterRuleSet> {
    return this.request('PUT', '/api/claimfilter-rules/scope', { body });
  }

  /**
   * DELETE /api/claimfilter-rules/scope — verwijdert één hersteller-uitzondering.
   *
   * ⚠ `repairerId` is VERPLICHT: de basisset is niet verwijderbaar. Zonder basisset
   * gaat élk dossier van die maatschappij naar expertise.
   */
  deleteClaimFilterRuleException(insurerId: string, repairerId: string): Promise<{ ok: boolean }> {
    return this.request('DELETE', '/api/claimfilter-rules/scope', {
      query: { insurerId, repairerId },
    });
  }

  /**
   * GET /api/claimfilter-rules/repairers — de keuzelijst achter "uitzondering
   * toevoegen" (09-09-2026).
   *
   * ⚠ ALLE herstellers, niet alleen die waarmee deze maatschappij al dossiers heeft
   * (beslissing eigenaar). Een afspraak met een hersteller moet vastgelegd kunnen
   * worden vóórdat het eerste dossier binnen is.
   *
   * ⚠ Hij hangt onder `claimfilter-rules` en niet onder de bestaande lookup
   * `/api/inrichting/mail-accounts/repairers`: die is system-only, en die gate
   * oprekken zou het mailbox-beheer voor de verzekeraar openzetten.
   */
  getClaimFilterRuleRepairers(signal?: AbortSignal): Promise<ClaimFilterRuleRepairers> {
    return this.request('GET', '/api/claimfilter-rules/repairers', { signal });
  }

  /** GET /api/claimfilter-rules/history — de wijzigingen van één scope, nieuwste eerst. */
  getClaimFilterRuleHistory(
    insurerId: string,
    repairerId?: string,
    signal?: AbortSignal,
  ): Promise<ClaimFilterRuleHistory> {
    return this.request('GET', '/api/claimfilter-rules/history', {
      query: repairerId ? { insurerId, repairerId } : { insurerId },
      signal,
    });
  }

  // ─── eXchange notities (story fe-be-exchange-notities, 2026-09-29) ───────

  /**
   * GET /api/dossiers/{id}/exchange-notes — het draadje én de `Aan`-lijst in
   * één antwoord.
   *
   * ⚠ Bewust één aanroep voor twee consumenten: het getal achter het menu-item
   * en de inhoud van de popup komen uit dezelfde respons, onder dezelfde
   * cache-sleutel. Twee endpoints zouden bij het openen van de popup een
   * tweede externe aanroep betekenen, en die loopt onder de sessie-lock.
   *
   * Foutpaden: **502** `exchange_notes_unreachable` met `exchangeMessage` (de
   * letterlijke faultstring van eXchange, of `null` bij een onverwachte fout).
   */
  getExchangeNotes(dossierId: string, signal?: AbortSignal): Promise<ExchangeNotesThread> {
    return this.request(
      'GET',
      `/api/dossiers/${encodeURIComponent(dossierId)}/exchange-notes`,
      { signal },
    );
  }

  /**
   * POST /api/dossiers/{id}/exchange-notes — nieuw bericht.
   *
   * Foutpaden: **400** `exchange_notes_recipient_required` /
   * `exchange_notes_message_required` / `exchange_notes_message_too_long`,
   * **502** `exchange_notes_parties_unavailable` (+ `partiesState`) of
   * `exchange_notes_unreachable` (+ `exchangeMessage`).
   */
  createExchangeNote(dossierId: string, body: ExchangeNoteWriteRequest): Promise<{ ok: boolean }> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(dossierId)}/exchange-notes`,
      { body },
    );
  }

  /**
   * POST /api/dossiers/{id}/exchange-notes/{parentId}/reply — antwoord op een
   * bericht. Eén niveau diep; eXchange kent geen antwoord-op-antwoord.
   */
  replyExchangeNote(
    dossierId: string,
    parentId: number,
    body: ExchangeNoteWriteRequest,
  ): Promise<{ ok: boolean }> {
    return this.request(
      'POST',
      `/api/dossiers/${encodeURIComponent(dossierId)}/exchange-notes/${parentId}/reply`,
      { body },
    );
  }

  /** Het configuratie-overzicht (system-only). De server stuurt geen enkele
   *  geheime waarde mee; zie `ConfigurationRow.secret`. */
  getConfigurationOverview(): Promise<ConfigurationOverview> {
    return this.request('GET', '/api/diagnostics/configuration');
  }

  /* ─── Systeem → Menu (story be-fe-menu-aan-uit, system-only) ─── */

  /** GET /api/system/menu — de opgeslagen menu-waarden (geen rij = aan). Niet-System → `ForbiddenError`. */
  getSystemMenu(): Promise<MenuSettings> {
    return this.request('GET', '/api/system/menu');
  }

  /**
   * PUT /api/system/menu — één schakelaar, direct opgeslagen. `value: null` = "Zelfde als iedereen" (de rij voor
   * de rol weghalen). Antwoord = de verse stand. 400 met `reason` (`key_invalid`, `role_group_invalid`).
   */
  putSystemMenu(body: MenuSettingChangeRequest): Promise<MenuSettings> {
    return this.request('PUT', '/api/system/menu', { body });
  }

  /* ─── System → Partnerkoppelingen (story be-fe-partner-api-dossier-aanmaken, system-only) ─── */

  /** GET /api/system/partners — de partners. Bevat nooit een sleutel of hash. Niet-System → `ForbiddenError`. */
  getPartners(): Promise<PartnerList> {
    return this.request('GET', '/api/system/partners');
  }

  /**
   * GET /api/system/partners/headquarters-preview?code=… — het hoofdkantoor bij een EuroMotive-code, met het aantal
   * herstellers eronder. 400 met `reason` `company_not_found` / `company_ambiguous` / `company_not_allowed`.
   */
  getPartnerHeadquartersPreview(code: string): Promise<PartnerHeadquartersPreview> {
    return this.request('GET', '/api/system/partners/headquarters-preview', { query: { code } });
  }

  /** POST /api/system/partners — nieuwe partner (inactief, zonder sleutel). */
  createPartner(body: PartnerCreateRequest): Promise<Partner> {
    return this.request('POST', '/api/system/partners', { body });
  }

  /** PATCH /api/system/partners/{id} — naam, hoofdkantoor en/of aan/uit. 409 `partner_has_no_api_key` bij aanzetten zonder sleutel. */
  updatePartner(id: string, body: PartnerPatchRequest): Promise<Partner> {
    return this.request('PATCH', `/api/system/partners/${encodeURIComponent(id)}`, { body });
  }

  /**
   * POST /api/system/partners/{id}/api-key — een nieuwe sleutel; de oude werkt direct niet meer. Bestaat er al een,
   * dan is `confirmReplace: true` verplicht (anders 409 `api_key_exists`). ⚠ Het antwoord is eenmalig: roep dit aan
   * buiten `useMutation`, zodat de sleutel niet in de mutation-cache belandt.
   */
  createPartnerApiKey(id: string, body: { confirmReplace?: boolean } = {}): Promise<PartnerApiKeyCreated> {
    return this.request('POST', `/api/system/partners/${encodeURIComponent(id)}/api-key`, { body });
  }

  /** GET /api/system/partners/{id}/pending-requests — de reserveringen zonder dossier. */
  getPartnerPendingRequests(id: string): Promise<PartnerPendingRequestList> {
    return this.request('GET', `/api/system/partners/${encodeURIComponent(id)}/pending-requests`);
  }

  /** DELETE /api/system/partners/{id}/pending-requests/{requestId} — een vastgelopen aanvraag vrijgeven (409 `request_not_stale`). */
  releasePartnerPendingRequest(id: string, requestId: string): Promise<{ released: boolean }> {
    return this.request(
      'DELETE',
      `/api/system/partners/${encodeURIComponent(id)}/pending-requests/${encodeURIComponent(requestId)}`,
    );
  }

  /**
   * PUT /api/system/partners/{id}/callback-domains (S020.3) — vervangt de domeinset; antwoord = de genormaliseerde,
   * ontdubbelde set. 400 `domain_invalid` (met `index` in de ingestuurde array), `too_many_domains`, `field_invalid`.
   */
  setPartnerCallbackDomains(id: string, domains: string[]): Promise<PartnerCallbackDomains> {
    return this.request('PUT', `/api/system/partners/${encodeURIComponent(id)}/callback-domains`, { body: { domains } });
  }

  /**
   * POST /api/system/partners/{id}/callback-secret (S020.3) — een nieuw callback-geheim. Bestaat er al een, dan is
   * `confirmReplace: true` verplicht (anders 409 `callback_secret_exists`); 409 `callback_cipher_not_configured` als de
   * server geen sleutels heeft. ⚠ Eenmalig antwoord: roep dit aan buiten `useMutation`.
   */
  createPartnerCallbackSecret(
    id: string,
    body: { confirmReplace?: boolean } = {},
  ): Promise<PartnerCallbackSecretCreated> {
    return this.request('POST', `/api/system/partners/${encodeURIComponent(id)}/callback-secret`, { body });
  }

  /** GET /api/system/partners/{id}/callback-events (S020.3) — de laatste 20 terugmeldingen, alleen lezen. */
  getPartnerCallbackEvents(id: string): Promise<PartnerCallbackEventList> {
    return this.request('GET', `/api/system/partners/${encodeURIComponent(id)}/callback-events`);
  }

  /* ─── Trainingsomgeving (story fe-be-cursusmodus-hersteller-mvp) ─────────── */

  /**
   * GET /api/training/practice-dossier — het eigen oefendossier van de ingelogde cursist.
   * 404 (lege body of `practice_dossier_not_found`) → `NotFoundError`; 409
   * `practice_dossier_closed` en 403 `role_not_allowed` → `ApiError` met de `reason` in `body`.
   */
  getPracticeDossier(): Promise<PracticeDossierDto> {
    return this.request('GET', '/api/training/practice-dossier');
  }

  /**
   * GET /api/training/safety — de veiligheidscontrole van de trainingsomgeving (system-only).
   * `refresh: true` forceert een verse controle (AC-A3.2). 404 met een lege body wanneer
   * `Training.Enabled` uit staat.
   */
  getTrainingSafety(options: { refresh?: boolean } = {}): Promise<TrainingSafetyResponse> {
    return this.request('GET', '/api/training/safety', {
      query: options.refresh ? { refresh: true } : undefined,
    });
  }

  /**
   * POST /api/training/seed — de beginstand van de trainingsomgeving bouwen (system-only, backlog 50).
   * Synchroon en traag (minuten); een tweede tegelijk ⇒ 409 `training_seed_in_progress`.
   */
  seedTraining(body: TrainingSeedRequest): Promise<TrainingSeedResponse> {
    return this.request('POST', '/api/training/seed', { body });
  }

  /* ─── Tarieven van betaalde opvragingen (migratie 129) ─────────────────── */

  /** GET /api/service-tariffs — geldend en eerstvolgend tarief voor chassisnummer en RuitID. */
  getServiceTariffs(): Promise<ServiceTariffsResponse> {
    return this.request('GET', '/api/service-tariffs');
  }

  /** GET /api/system/service-tariffs — alle tarieven (system-only). */
  getSystemServiceTariffs(): Promise<ServiceTariffAdminResponse> {
    return this.request('GET', '/api/system/service-tariffs');
  }

  /** POST /api/system/service-tariffs — een tarief toevoegen (system-only). */
  createServiceTariff(body: ServiceTariffCreateRequest): Promise<ServiceTariffAdminResponse> {
    return this.request('POST', '/api/system/service-tariffs', { body });
  }

  /** DELETE /api/system/service-tariffs/{id} — een tarief dat nog niet is ingegaan weghalen (system-only). */
  deleteServiceTariff(id: string): Promise<ServiceTariffAdminResponse> {
    return this.request('DELETE', `/api/system/service-tariffs/${encodeURIComponent(id)}`);
  }

  /* ─── Callcenter: melding aannemen ────────────────────────────────────── */

  /** Maakt een melding aan via de workflow; start op status 501. */
  createIntake(): Promise<IntakeCreateResponse> {
    return this.request('POST', '/api/intakes');
  }

  /** GET /api/lookups/alarm-centers — de alarmcentrales voor de keuzelijst. */
  getAlarmCenters(): Promise<AlarmCenterOption[]> {
    return this.request('GET', '/api/lookups/alarm-centers');
  }

  /**
   * Het lopende gesprek terugladen — nodig zodra het scherm op een bestaand
   * meldingnummer opent (`/intakes/aannemen/{nummer}`).
   *
   * ⚠ `patchIntakeDetails` schrijft de HELE veldset weg. Een scherm dat een
   * bestaand nummer opent zonder deze read zou bij de eerste opslag de klant,
   * het adres en het kenteken wissen — een geldige opslag, dus zonder fout.
   */
  getIntakeCallCenter(nummer: number): Promise<IntakeCallCenterSnapshot> {
    return this.request('GET', `/api/intakes/${nummer}/callcenter`);
  }

  patchIntakeTriage(nummer: number, body: IntakeTriageRequest): Promise<IntakeUpdateResult> {
    return this.request('PATCH', `/api/intakes/${nummer}/triage`, { body });
  }

  patchIntakeDetails(nummer: number, body: IntakeDetailsRequest): Promise<IntakeUpdateResult> {
    return this.request('PATCH', `/api/intakes/${nummer}/details`, { body });
  }

  /** Wie is actief op de herstelpostcode van deze melding. */
  getIntakeRepairerMatch(nummer: number): Promise<RepairerMatchResult> {
    return this.request('GET', `/api/intakes/${nummer}/repairer-match`);
  }

  searchIntakeRepairers(nummer: number, q: string): Promise<RepairerSearchResult> {
    return this.request(
      'GET',
      `/api/intakes/${nummer}/repairer-search?q=${encodeURIComponent(q)}`,
    );
  }

  assignIntakeRepairer(nummer: number, body: IntakeOutcomeRequest): Promise<IntakeOutcomeResult> {
    return this.request('POST', `/api/intakes/${nummer}/assign-repairer`, { body });
  }

  notifyIntakeRepairerSms(nummer: number, body: IntakeOutcomeRequest): Promise<IntakeOutcomeResult> {
    return this.request('POST', `/api/intakes/${nummer}/notify-repairer-sms`, { body });
  }

  closeIntakeWithoutRepairer(nummer: number, body: IntakeOutcomeRequest): Promise<IntakeOutcomeResult> {
    return this.request('POST', `/api/intakes/${nummer}/no-repairer`, { body });
  }

  putIntakeOnHold(nummer: number, body: IntakeOutcomeRequest): Promise<IntakeOutcomeResult> {
    return this.request('POST', `/api/intakes/${nummer}/on-hold`, { body });
  }

  /**
   * "Opslaan en verzenden" op de terugbelroute: het hoofdkantoor krijgt bericht.
   * Géén statusstap (`statusValue: null`). 409 `intake_route_not_callback` wanneer
   * de vastgelegde triage geen terugbelverzoek is. Eerst `patchIntakeDetails`.
   */
  recordIntakeCallback(nummer: number, body: IntakeOutcomeRequest): Promise<IntakeOutcomeResult> {
    return this.request('POST', `/api/intakes/${nummer}/callback`, { body });
  }

  // ─── Supportmeldingen (story be-fe-support-melden-en-volgen, S1) ──────────

  /** De soorten die deze gebruiker kan kiezen, met de ontvanger per soort (server-beslist). */
  getSupportRequestTypes(): Promise<SupportRequestTypesResponse> {
    return this.request('GET', '/api/support/request-types');
  }

  /** Melding maken (multipart). 201 nieuw, 200 met `replayed` bij dezelfde `clientRequestId`. */
  createSupportRequest(input: CreateSupportRequestInput): Promise<CreateSupportRequestResult> {
    const fd = new FormData();
    fd.append('requestTypeCode', input.requestTypeCode);
    fd.append('subject', input.subject);
    fd.append('description', input.description);
    fd.append('clientRequestId', input.clientRequestId);
    if (input.dossierId) fd.append('dossierId', input.dossierId);
    if (input.screen) fd.append('screen', input.screen);
    if (input.correlationId) fd.append('correlationId', input.correlationId);
    // S4: de calculatie-context van "Fout in calculatie" als JSON-veld (de server valideert en serialiseert opnieuw).
    if (input.calculation) fd.append('calculation', JSON.stringify(input.calculation));
    for (const file of input.files ?? []) fd.append('files', file, file.name);
    return this.request('POST', '/api/support/requests', { formData: fd });
  }

  /**
   * Mijn supportmeldingen (eigen + eigen bedrijf). Standaard alleen meldingen uit de nieuwe app; `includeLegacy` toont
   * ook die uit de oude helpdesk (gebruiker 23-10-2026).
   */
  getSupportRequests(page = 1, pageSize = 25, includeLegacy = false): Promise<SupportRequestList> {
    return this.request('GET', '/api/support/requests', {
      query: includeLegacy ? { page, pageSize, includeLegacy: 'true' } : { page, pageSize },
    });
  }

  /** Detail met het gesprek; 404 voor onbestaand én voor geen toegang. */
  getSupportRequest(number: number): Promise<SupportRequestDetail> {
    return this.request('GET', `/api/support/requests/${number}`);
  }

  /** Reageren (multipart). 409 `support_request_resolved` op een opgeloste melding. */
  replyToSupportRequest(number: number, input: ReplySupportRequestInput): Promise<ReplySupportRequestResult> {
    const fd = new FormData();
    fd.append('text', input.text);
    fd.append('clientRequestId', input.clientRequestId);
    for (const file of input.files ?? []) fd.append('files', file, file.name);
    return this.request('POST', `/api/support/requests/${number}/replies`, { formData: fd });
  }

  /** De download-URL van een bijlage (eigen route met eigenaarstoets; cookie-auth, dus een gewone `<a href>`). */
  getSupportAttachmentUrl(number: number, attachmentId: string): string {
    return `${this.baseUrl}/api/support/requests/${number}/attachments/${encodeURIComponent(attachmentId)}`;
  }

  // ─── App Support: soorten beheren (story be-fe-support-soorten-foutmelding-monteur, S3) ─────

  /** Alle soorten (ook uitgezet). Alleen System: 403 `support_types_system_only` / `support_handler_only`. */
  getSupportTypes(): Promise<SupportTypesAdminList> {
    return this.request('GET', '/api/support/types');
  }

  /** Nieuwe soort. 400 `request_type_label_taken` bij een bestaande naam (ook een oude helpdesksoort). */
  createSupportType(input: CreateSupportTypeInput): Promise<SupportTypeAdmin> {
    return this.request('POST', '/api/support/types', { body: input });
  }

  /** Wijzigen (sparse). 409 `request_type_changed` (met `current`) of `last_euromotive_type`. */
  patchSupportType(id: string, input: PatchSupportTypeInput): Promise<SupportTypeAdmin> {
    return this.request('PATCH', `/api/support/types/${encodeURIComponent(id)}`, { body: input });
  }

  /** De volgorde (alle soorten, met hun versie). 409 `request_type_changed` = niets gewijzigd. */
  reorderSupportTypes(items: { id: string; version: number }[]): Promise<SupportTypesAdminList> {
    return this.request('PUT', '/api/support/types/order', { body: { items } });
  }

  // ─── App Support: behandelen (story be-fe-support-app-behandelen, S2) ──────

  /** De lijst van de behandelaar. 403 `support_handler_only`, 503 `chain_state_unknown`. */
  getSupportInbox(query: SupportInboxQuery): Promise<SupportInboxList> {
    return this.request('GET', '/api/support/inbox', {
      query: {
        view: query.view,
        assigned: query.assigned,
        q: query.q && query.q.trim().length > 0 ? query.q.trim() : undefined,
        includeLegacy: query.includeLegacy ? 'true' : undefined,
        requestType: query.requestType && query.requestType.length > 0 ? query.requestType : undefined,
        page: query.page,
        pageSize: query.pageSize ?? 25,
      },
    });
  }

  /** S4: de standaardantwoorden voor de snelkeuze (alleen System; 403 `default_replies_system_only`). */
  getSupportDefaultReplies(): Promise<{ items: SupportDefaultReply[] }> {
    return this.request('GET', '/api/support/inbox/default-replies');
  }

  /** Het detail voor de behandelaar; 404 buiten de scope. */
  getSupportInboxRequest(number: number): Promise<SupportInboxDetail> {
    return this.request('GET', `/api/support/inbox/${number}`);
  }

  /** De download-URL van een bijlage in de app Support (eigen route met poort, scope en eigenaarstoets). */
  getSupportInboxAttachmentUrl(number: number, attachmentId: string): string {
    return `${this.baseUrl}/api/support/inbox/${number}/attachments/${encodeURIComponent(attachmentId)}`;
  }

  /** Antwoord aan de melder (multipart; bijlagen mogen). */
  answerSupportRequest(number: number, input: SupportInboxMessageInput): Promise<SupportInboxActionResult> {
    const fd = new FormData();
    fd.append('text', input.text);
    fd.append('clientRequestId', input.clientRequestId);
    for (const file of input.files ?? []) fd.append('files', file, file.name);
    return this.request('POST', `/api/support/inbox/${number}/answers`, { formData: fd });
  }

  /** Interne notitie (JSON; nooit bijlagen — de server weigert ze met 400). */
  addSupportInternalNote(number: number, input: { text: string; clientRequestId: string }): Promise<SupportInboxActionResult> {
    return this.request('POST', `/api/support/inbox/${number}/internal-notes`, { body: input });
  }

  /**
   * De keuzelijst voor toewijzen: de supportmedewerkers van het behandelend bedrijf, of (`fallback: true`) alle actieve
   * gebruikers zolang er nog niemand gemarkeerd is (story `be-fe-systeem-gebruikers`, AC 26).
   */
  getSupportHandlers(number: number): Promise<SupportHandlerList> {
    return this.request('GET', `/api/support/inbox/${number}/handlers`);
  }

  assignSupportRequest(number: number, userId: string | null): Promise<SupportInboxActionResult> {
    return this.request('PUT', `/api/support/inbox/${number}/assignment`, { body: { userId } });
  }

  getSupportPriorities(): Promise<{ items: SupportPriority[] }> {
    return this.request('GET', '/api/support/priorities');
  }

  setSupportPriority(number: number, priorityId: string | null): Promise<SupportInboxActionResult> {
    return this.request('PUT', `/api/support/inbox/${number}/priority`, { body: { priorityId } });
  }

  resolveSupportRequest(number: number, input: { text?: string; clientRequestId: string }): Promise<SupportInboxActionResult> {
    return this.request('POST', `/api/support/inbox/${number}/resolve`, { body: input });
  }

  reopenSupportRequest(number: number, input: { clientRequestId: string }): Promise<SupportInboxActionResult> {
    return this.request('POST', `/api/support/inbox/${number}/reopen`, { body: input });
  }

  forwardSupportRequest(
    number: number,
    input: { target: SupportTarget; note?: string; clientRequestId: string },
  ): Promise<SupportInboxActionResult> {
    return this.request('POST', `/api/support/inbox/${number}/forward`, { body: input });
  }

  // ─── Systeem → Logboek (story be-fe-systeem-logboek) — alleen System, lock-vrije GET-routes ──

  /** GET /api/system/trace-log — 100 regels, nieuwste eerst; `nextCursor` voor *Oudere regels*. */
  getTraceLog(query: TraceLogListQuery = {}): Promise<TraceLogList> {
    const nonEmpty = (v: string | undefined) => (v && v.trim() !== '' ? v.trim() : undefined);
    return this.request('GET', '/api/system/trace-log', {
      query: {
        level: query.level && query.level.length > 0 ? query.level : undefined,
        component: nonEmpty(query.component),
        from: nonEmpty(query.from),
        to: nonEmpty(query.to),
        correlationId: nonEmpty(query.correlationId),
        q: nonEmpty(query.q),
        machine: nonEmpty(query.machine),
        cursor: nonEmpty(query.cursor),
      },
    });
  }

  /** GET /api/system/trace-log/{id} — één regel met de volledige tekst. */
  getTraceLogEntry(id: number): Promise<TraceLogDetail> {
    return this.request('GET', `/api/system/trace-log/${encodeURIComponent(String(id))}`);
  }

  /** GET /api/system/trace-log/components — de onderdelen in de tabel (server-side 5 minuten gecachet). */
  getTraceLogComponents(): Promise<TraceLogComponents> {
    return this.request('GET', '/api/system/trace-log/components');
  }

  /** GET /api/system/trace-log/status — aan/uit, tabel, oudste regel en de schrijver in dit proces. */
  getTraceLogStatus(): Promise<TraceLogStatus> {
    return this.request('GET', '/api/system/trace-log/status');
  }

  // ─── Systeem → Gebruikers (story be-fe-systeem-gebruikers) ──────────────

  /** GET /api/system/users — de lijst (50 per pagina, op naam en inlognaam). Alleen System en het keten-HK. */
  getSystemUsers(query: UserAdminListQuery = {}): Promise<UserAdminList> {
    return this.request('GET', '/api/system/users', {
      query: {
        q: query.q && query.q.trim() !== '' ? query.q.trim() : undefined,
        companyId: query.companyId || undefined,
        active: query.active && query.active !== 'all' ? query.active : undefined,
        page: query.page && query.page > 1 ? query.page : undefined,
      },
    });
  }

  /** GET /api/system/users/companies — de keuzelijst met bedrijven (dezelfde scope als de lijst). */
  getSystemUserCompanies(): Promise<{ items: UserAdminCompany[] }> {
    return this.request('GET', '/api/system/users/companies');
  }

  /** GET /api/system/users/{id} — het detail; 404 buiten de scope (identiek aan onbestaand). */
  getSystemUser(id: string): Promise<UserAdminDetail> {
    return this.request('GET', `/api/system/users/${encodeURIComponent(id)}`);
  }

  /** PATCH /api/system/users/{id} — naam, e-mail en telefoon (op het contact). 409 `user_changed` bij een oude versie. */
  updateSystemUser(id: string, body: UserAdminPatchRequest): Promise<UserAdminDetail> {
    return this.request('PATCH', `/api/system/users/${encodeURIComponent(id)}`, { body });
  }

  /** PUT /api/system/users/{id}/active — actief of inactief (het venster); inactief logt de gebruiker overal uit. */
  setSystemUserActive(id: string, active: boolean, version: number): Promise<UserAdminDetail> {
    return this.request('PUT', `/api/system/users/${encodeURIComponent(id)}/active`, { body: { active, version } });
  }

  /**
   * PATCH /api/system/users/{id}/active-window — Actief van / Actief tot (`yyyy-MM-dd`, beide verplicht). Wordt de
   * gebruiker daardoor inactief, dan logt hij overal uit (zoals de schakelaar). Besluit gebruiker 25-10-2026.
   */
  setSystemUserActiveWindow(id: string, body: UserAdminActiveWindowRequest): Promise<UserAdminDetail> {
    return this.request('PATCH', `/api/system/users/${encodeURIComponent(id)}/active-window`, { body });
  }

  /**
   * POST /api/system/users/{id}/password-reset — stuurt de resetlink van "Wachtwoord vergeten" naar het e-mailadres van
   * de gebruiker (besluit 24-10-2026). Antwoord `{ linkSent, sentTo }` met een gemaskeerd adres; geen wachtwoord.
   */
  resetSystemUserPassword(id: string): Promise<UserAdminPasswordReset> {
    return this.request('POST', `/api/system/users/${encodeURIComponent(id)}/password-reset`, { body: {} });
  }

  /** POST /api/system/users/{id}/mfa-reset — 2FA resetten (idempotent: geen 2FA ⇒ `reset: false`). */
  resetSystemUserMfa(id: string): Promise<{ reset: boolean }> {
    return this.request('POST', `/api/system/users/${encodeURIComponent(id)}/mfa-reset`, { body: {} });
  }

  /** PUT /api/system/users/{id}/support-staff — de supportvlag (alleen waar de kijker hem mag zetten). */
  setSystemUserSupportStaff(id: string, isSupportStaff: boolean, version: number): Promise<UserAdminDetail> {
    return this.request('PUT', `/api/system/users/${encodeURIComponent(id)}/support-staff`, {
      body: { isSupportStaff, version },
    });
  }
  // ─── Meerprijs dealer: kortingen (story be-fe-meerprijs-dealer-kortingen) ────────────────────────────────

  /** GET /api/dealer-surcharge/insurers — het overzicht (per maatschappij de geldende en de eerstvolgende set). */
  getDealerSurchargeOverview(query: { search?: string; announcedOnly?: boolean } = {}): Promise<DealerSurchargeOverview> {
    return this.request('GET', '/api/dealer-surcharge/insurers', {
      query: { search: query.search || undefined, announcedOnly: query.announcedOnly ? 'true' : undefined },
    });
  }

  /** GET /api/dealer-surcharge/insurers/{companyId} — alle sets van één maatschappij, nieuwste eerst. */
  getDealerSurchargeInsurer(companyId: string): Promise<DealerSurchargeInsurerDetail> {
    return this.request('GET', `/api/dealer-surcharge/insurers/${encodeURIComponent(companyId)}`);
  }

  /** GET /api/dealer-surcharge/insurer-candidates — maatschappij-bedrijven zonder set (keuzelijst "toevoegen"). */
  getDealerSurchargeCandidates(search?: string): Promise<{ items: DealerSurchargeCandidate[] }> {
    return this.request('GET', '/api/dealer-surcharge/insurer-candidates', { query: { search: search || undefined } });
  }

  /** GET …/insurers/{companyId}/rate-set-template?validFrom= — de set die op die datum zou gelden, als startwaarde. */
  getDealerSurchargeTemplate(companyId: string, validFrom: string): Promise<DealerSurchargeTemplate> {
    return this.request('GET', `/api/dealer-surcharge/insurers/${encodeURIComponent(companyId)}/rate-set-template`, {
      query: { validFrom },
    });
  }

  /** POST …/insurers/{companyId}/rate-sets — een nieuwe set. */
  createDealerSurchargeRateSet(companyId: string, body: DealerSurchargeRateSetWrite): Promise<DealerSurchargeRateSet> {
    return this.request('POST', `/api/dealer-surcharge/insurers/${encodeURIComponent(companyId)}/rate-sets`, { body });
  }

  /** Story be-fe-meerprijs-dealer-exchange-keten — GET /api/dealer-surcharge/exchange: de eXchange-sets, nieuwste eerst. */
  getDealerSurchargeExchange(): Promise<DealerSurchargeExchangeDetail> {
    return this.request('GET', '/api/dealer-surcharge/exchange');
  }

  /** GET /api/dealer-surcharge/exchange/rate-set-template?validFrom= — de eXchange-set die op die datum zou gelden. */
  getDealerSurchargeExchangeTemplate(validFrom: string): Promise<DealerSurchargeTemplate> {
    return this.request('GET', '/api/dealer-surcharge/exchange/rate-set-template', { query: { validFrom } });
  }

  /** POST /api/dealer-surcharge/exchange/rate-sets — een nieuwe eXchange-set (de eigenaar komt uit de route). */
  createDealerSurchargeExchangeRateSet(body: DealerSurchargeRateSetWrite): Promise<DealerSurchargeRateSet> {
    return this.request('POST', '/api/dealer-surcharge/exchange/rate-sets', { body });
  }

  /** PUT /api/dealer-surcharge/rate-sets/{id} — een set wijzigen (met `version`; 409 `rate_set_changed` bij een oude). */
  updateDealerSurchargeRateSet(id: string, body: DealerSurchargeRateSetWrite): Promise<DealerSurchargeRateSet> {
    return this.request('PUT', `/api/dealer-surcharge/rate-sets/${encodeURIComponent(id)}`, { body });
  }

  /** DELETE /api/dealer-surcharge/rate-sets/{id}?version= — een aangekondigde set verwijderen. */
  deleteDealerSurchargeRateSet(id: string, version: number): Promise<void> {
    return this.request('DELETE', `/api/dealer-surcharge/rate-sets/${encodeURIComponent(id)}`, { query: { version } });
  }

  /** GET /api/dealer-surcharge/settings — de vaste waarden; met `validFrom` ook het sjabloon voor die datum. */
  getDealerSurchargeSettings(validFrom?: string): Promise<DealerSurchargeSettings> {
    return this.request('GET', '/api/dealer-surcharge/settings', { query: { validFrom: validFrom || undefined } });
  }

  createDealerSurchargeSetting(body: DealerSurchargeSettingWrite): Promise<DealerSurchargeSetting> {
    return this.request('POST', '/api/dealer-surcharge/settings', { body });
  }

  updateDealerSurchargeSetting(id: string, body: DealerSurchargeSettingWrite): Promise<DealerSurchargeSetting> {
    return this.request('PUT', `/api/dealer-surcharge/settings/${encodeURIComponent(id)}`, { body });
  }

  deleteDealerSurchargeSetting(id: string, version: number): Promise<void> {
    return this.request('DELETE', `/api/dealer-surcharge/settings/${encodeURIComponent(id)}`, { query: { version } });
  }

  /** POST /api/dealer-surcharge/bulk-changes/preview — de wijziging voor meerdere maatschappijen, vooraf. */
  previewDealerSurchargeBulkChange(body: DealerSurchargeBulkRequest): Promise<DealerSurchargeBulkPreview> {
    return this.request('POST', '/api/dealer-surcharge/bulk-changes/preview', { body });
  }

  /** POST /api/dealer-surcharge/bulk-changes — alles of niets; 409 `bulk_change_conflict` met de maatschappijen. */
  applyDealerSurchargeBulkChange(body: DealerSurchargeBulkApply): Promise<{ created: number; updated: number }> {
    return this.request('POST', '/api/dealer-surcharge/bulk-changes', { body });
  }

  /** POST /api/dealer-surcharge/import/preview — multipart `file`; alleen System. Het bestand wordt niet bewaard. */
  previewDealerSurchargeImport(file: File): Promise<DealerSurchargeImportPreview> {
    const fd = new FormData();
    fd.append('file', file, file.name);
    return this.request('POST', '/api/dealer-surcharge/import/preview', { formData: fd });
  }

  /** POST /api/dealer-surcharge/import — multipart `file` (hetzelfde bestand) + `mapping`; alleen System. */
  importDealerSurcharge(file: File, mapping: DealerSurchargeImportMapping): Promise<DealerSurchargeImportResult> {
    const fd = new FormData();
    fd.append('file', file, file.name);
    fd.append('mapping', JSON.stringify(mapping));
    return this.request('POST', '/api/dealer-surcharge/import', { formData: fd });
  }

  // ─── Story be-fe-meerprijs-dealer-berekenen ──────────────────────────────────────────────

  /** GET /api/dossiers/{id}/dealer-surcharge — de context van het venster Meerprijs dealer. Vlag uit ⇒ 404 (lege body). */
  getDossierDealerSurcharge(dossierId: string): Promise<DossierSurchargeContext> {
    return this.request('GET', `/api/dossiers/${encodeURIComponent(dossierId)}/dealer-surcharge`);
  }

  /** POST /api/dossiers/{id}/dealer-surcharge/preview — rekent met de geldende set; schrijft niets. */
  previewDossierDealerSurcharge(dossierId: string, body: DossierSurchargeInput): Promise<DossierSurchargePreview> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(dossierId)}/dealer-surcharge/preview`, { body });
  }

  /** POST /api/dossiers/{id}/dealer-surcharge/calculations — multipart `payload` (JSON) + `file` (de inkoopfactuur). */
  saveDossierDealerSurcharge<TRow = unknown>(
    dossierId: string,
    payload: DossierSurchargeSavePayload,
    file: File,
  ): Promise<DossierSurchargeSaveResponse<TRow>> {
    const fd = new FormData();
    fd.append('payload', JSON.stringify(payload));
    fd.append('file', file, file.name);
    return this.request('POST', `/api/dossiers/${encodeURIComponent(dossierId)}/dealer-surcharge/calculations`, { formData: fd });
  }

  /**
   * POST /api/dossiers/{id}/dealer-surcharge/withdrawals — story `be-fe-compensatie-inkoop-intrekken`: de MD-subregel onder
   * de ruit weg en de intrekking vastgelegd.
   */
  withdrawDossierDealerSurcharge<TRow = unknown>(
    dossierId: string,
    body: DossierSurchargeWithdrawPayload,
  ): Promise<DossierSurchargeWithdrawResponse<TRow>> {
    return this.request('POST', `/api/dossiers/${encodeURIComponent(dossierId)}/dealer-surcharge/withdrawals`, { body });
  }

  // ─── Voorraad in CARculate (EPIC-022 S022.1, be-fe-voorraad-fundament) ────────────────────────────────

  /** GET /api/repairers/{id}/inventory — de schakelaar "Voorraad in CARculate" van deze hersteller (tab Werkwijze). */
  getRepairerInventory(id: string, signal?: AbortSignal): Promise<RepairerInventory> {
    return this.request('GET', `/api/repairers/${encodeURIComponent(id)}/inventory`, { signal });
  }

  /**
   * PUT /api/repairers/{id}/inventory — aan/uit + voorraadbedrijf (alleen System). 409 `inventory_switch_off_confirmation_required`
   * met `mutationCount` ⇒ opnieuw met `confirmSwitchOff: true`.
   */
  putRepairerInventory(id: string, body: RepairerInventoryPutRequest): Promise<RepairerInventory> {
    return this.request('PUT', `/api/repairers/${encodeURIComponent(id)}/inventory`, { body });
  }

  /** GET /api/inventory/warehouses — de magazijnen van het voorraadbedrijf (gedeactiveerde alleen met `includeInactive`). */
  getInventoryWarehouses(includeInactive: boolean, signal?: AbortSignal): Promise<InventoryWarehouseList> {
    return this.request('GET', '/api/inventory/warehouses', { signal, query: { includeInactive: includeInactive ? 'true' : undefined } });
  }

  /** POST /api/inventory/warehouses — magazijn toevoegen (code wordt in hoofdletters opgeslagen). */
  createInventoryWarehouse(body: InventoryWarehouseCreateRequest): Promise<InventoryWarehouse> {
    return this.request('POST', '/api/inventory/warehouses', { body });
  }

  /** PATCH /api/inventory/warehouses/{id} — naam en/of actief. */
  patchInventoryWarehouse(id: string, body: InventoryWarehousePatchRequest): Promise<InventoryWarehouse> {
    return this.request('PATCH', `/api/inventory/warehouses/${encodeURIComponent(id)}`, { body });
  }

  /** GET /api/inventory/warehouses/{id}/locations — de locaties van één magazijn. */
  getInventoryLocations(warehouseId: string, includeInactive: boolean, signal?: AbortSignal): Promise<InventoryLocationList> {
    return this.request('GET', `/api/inventory/warehouses/${encodeURIComponent(warehouseId)}/locations`, {
      signal,
      query: { includeInactive: includeInactive ? 'true' : undefined },
    });
  }

  /** POST /api/inventory/warehouses/{id}/locations — locatie toevoegen (opgeslagen zoals ingevoerd, getrimd). */
  createInventoryLocation(warehouseId: string, code: string): Promise<InventoryLocation> {
    return this.request('POST', `/api/inventory/warehouses/${encodeURIComponent(warehouseId)}/locations`, { body: { code } });
  }

  /** PATCH /api/inventory/warehouses/locations/{id} — code en/of actief. */
  patchInventoryLocation(id: string, body: InventoryLocationPatchRequest): Promise<InventoryLocation> {
    return this.request('PATCH', `/api/inventory/warehouses/locations/${encodeURIComponent(id)}`, { body });
  }

  // ─── Voorraad inlezen (EPIC-022 S022.3, be-fe-voorraad-inlezen) ────────────────────────────────────────

  /**
   * POST /api/inventory/count-imports/preview — het voorbeeld van een telling (nu → geteld → verschil). Schrijft niets.
   * Het bestand wordt in de browser gelezen; de body bevat per regel de toegewezen kolommen als tekst.
   */
  previewInventoryCount(body: InventoryCountPreviewRequest): Promise<InventoryCountPreview> {
    return this.request('POST', '/api/inventory/count-imports/preview', { body });
  }

  /**
   * POST /api/inventory/count-imports — de telling opslaan. 409 `inventory_changed_since_preview` met een nieuw `preview`,
   * 409 `full_count_confirmation_required` met `zeroCount`, 409 `inventory_count_in_progress`; een herhaling met dezelfde
   * `clientRequestId` geeft het opgeslagen resultaat met `replayed: true`.
   */
  commitInventoryCount(body: InventoryCountCommitRequest): Promise<InventoryCountResult> {
    return this.request('POST', '/api/inventory/count-imports', { body });
  }

  // ─── Interne leverancier (EPIC-022 S022.9, be-fe-interne-leverancier) ──────────────────────────────────

  /** GET /api/internal-suppliers — de pools die de gebruiker mag zien + `canManagePools` (System of eigen bedrijf) + `ownCompany`. */
  listInternalSuppliers(signal?: AbortSignal): Promise<InternalSupplierList> {
    return this.request('GET', '/api/internal-suppliers', { signal });
  }

  /** POST /api/internal-suppliers — nieuwe pool (System, of een hersteller voor zijn eigen bedrijf). 409 `internal_supplier_name_taken`. */
  createInternalSupplier(body: InternalSupplierCreateRequest): Promise<InternalSupplierPool> {
    return this.request('POST', '/api/internal-suppliers', { body });
  }

  /** PATCH /api/internal-suppliers/{poolId} — naam en/of actief (System of de eigenaar). 409 `internal_supplier_changed`. */
  patchInternalSupplier(poolId: string, body: InternalSupplierPatchRequest): Promise<InternalSupplierPool> {
    return this.request('PATCH', `/api/internal-suppliers/${encodeURIComponent(poolId)}`, { body });
  }

  /** GET /api/internal-suppliers/owner-candidates?search= — actieve herstellerbedrijven als eigenaar (alleen System, max 50). */
  getInternalSupplierOwnerCandidates(search: string, signal?: AbortSignal): Promise<InternalSupplierOwnerCandidate[]> {
    return this.request('GET', '/api/internal-suppliers/owner-candidates', { signal, query: { search } });
  }

  /** POST /api/internal-suppliers/sync — "Nu bijwerken" (System: alle pools; hersteller: alleen de eigen pools); zonder `poolId` alle toegestane pools. */
  syncInternalSuppliers(body: { poolId?: string } = {}): Promise<InternalSupplierSyncResult> {
    return this.request('POST', '/api/internal-suppliers/sync', { body });
  }

  /** GET /api/internal-suppliers/{poolId} — één pool. */
  getInternalSupplier(poolId: string, signal?: AbortSignal): Promise<InternalSupplierPool> {
    return this.request('GET', `/api/internal-suppliers/${encodeURIComponent(poolId)}`, { signal });
  }

  /** GET /api/internal-suppliers/{poolId}/stock?search= — de leveranciersvoorraad (server-side zoeken). */
  getInternalSupplierStock(poolId: string, search: string, signal?: AbortSignal): Promise<InternalSupplierStockOverview> {
    return this.request('GET', `/api/internal-suppliers/${encodeURIComponent(poolId)}/stock`, {
      signal,
      query: { search: search === '' ? undefined : search },
    });
  }

  /**
   * PATCH /api/internal-suppliers/{poolId}/stock/{stockId} — correctie van aantal en/of prijs met de getoonde versie.
   * 409 `internal_supplier_stock_changed` met `{ row }`; 400 `quantity_invalid`/`price_invalid`.
   */
  patchInternalSupplierStock(poolId: string, stockId: string, body: InternalSupplierStockPatchRequest): Promise<InternalSupplierStockRow> {
    return this.request('PATCH', `/api/internal-suppliers/${encodeURIComponent(poolId)}/stock/${encodeURIComponent(stockId)}`, { body });
  }

  /**
   * POST /api/internal-suppliers/{poolId}/stock — "Artikel toevoegen": één nieuw artikel (201, het resultaat van een
   * inleesronde van één regel). 409 `stock_item_exists` met `{ row }`; 400 `item_not_found`/`item_ambiguous`/
   * `quantity_invalid`/`price_invalid`/`location_invalid`; een herhaling met dezelfde `clientRequestId` ⇒ `replayed: true`.
   */
  addInternalSupplierStock(poolId: string, body: InternalSupplierAddStockRequest): Promise<InternalSupplierImportResult> {
    return this.request('POST', `/api/internal-suppliers/${encodeURIComponent(poolId)}/stock`, { body });
  }

  /** POST /api/internal-suppliers/{poolId}/imports/preview — het voorbeeld van een inleesronde. Schrijft niets. */
  previewInternalSupplierImport(poolId: string, body: InternalSupplierImportRequest): Promise<InternalSupplierImportPreview> {
    return this.request('POST', `/api/internal-suppliers/${encodeURIComponent(poolId)}/imports/preview`, { body });
  }

  /**
   * POST /api/internal-suppliers/{poolId}/imports — opslaan. 409 `internal_supplier_changed_since_preview` met `{ preview }`,
   * 409 `zeroing_confirmation_required` met `{ zeroCount }`; een herhaling met dezelfde `clientRequestId` ⇒ `replayed: true`.
   */
  commitInternalSupplierImport(poolId: string, body: InternalSupplierImportCommitRequest): Promise<InternalSupplierImportResult> {
    return this.request('POST', `/api/internal-suppliers/${encodeURIComponent(poolId)}/imports`, { body });
  }

  // ─── Eigen magazijn als bron (EPIC-022 S022.4, be-fe-voorraad-eigen-magazijn-bron) ─────────────────────

  /**
   * POST /api/order-list/reservations — een voorbestelregel reserveren uit een eigen magazijn. Eerst vastgelegd, daarna
   * wordt de regel uit de stock-API opgeruimd; ná het vastleggen altijd 200 (`cleanupState` + eventueel `warning`).
   * ⚠ Nooit automatisch herhalen; een herhaling met hetzelfde `clientRequestId` geeft `replayed: true`.
   */
  reserveOrderListLine(body: ReserveOrderListLineRequest): Promise<ReservationResult> {
    return this.request('POST', '/api/order-list/reservations', { body });
  }

  /** POST /api/order-list/reservations/{id}/cleanup — de regel opnieuw opruimen (idempotent). */
  cleanupReservation(reservationId: string): Promise<ReservationCleanupResult> {
    return this.request('POST', `/api/order-list/reservations/${encodeURIComponent(reservationId)}/cleanup`, { body: {} });
  }

  /** POST /api/inventory/reservations/{id}/release — de reservering vrijgeven (vanaf het dossier met `dossierId`). */
  releaseReservation(reservationId: string, body: ReleaseReservationRequest): Promise<ReservationReleaseResult> {
    return this.request('POST', `/api/inventory/reservations/${encodeURIComponent(reservationId)}/release`, { body });
  }
}
